var CONST = {
  LIGHTING_ENABLED: true,
  DIRECTIONAL_SHADOWS_ENABLED: true,
  POINT_SHADOWS_ENABLED: false,
  SHADOWS_VSM_ENABLED: false,
  DEFAULT_SHADOW_BIAS: 0.00002,
  MAX_BONES: 8,
  MAX_LIGHTS: 4
};

/* Copyright (c) 2015, Brandon Jones, Colin MacKenzie IV.

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in
all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
THE SOFTWARE. */

/**
* Common utilities
* @module glMatrix
*/

// Configuration Constants
var EPSILON = 0.000001;
var ARRAY_TYPE = typeof Float32Array !== 'undefined' ? Float32Array : Array;
var RANDOM = Math.random;

/**
* Sets the type of array used when creating new vectors and matrices
*
* @param {Type} type Array type, such as Float32Array or Array
*/
function setMatrixArrayType(type) {
ARRAY_TYPE = type;
}

var degree = Math.PI / 180;

/**
* Convert Degree To Radian
*
* @param {Number} a Angle in Degrees
*/
function toRadian(a) {
return a * degree;
}

/**
* Tests whether or not the arguments have approximately the same value, within an absolute
* or relative tolerance of glMatrix.EPSILON (an absolute tolerance is used for values less
* than or equal to 1.0, and a relative tolerance is used for larger values)
*
* @param {Number} a The first number to test.
* @param {Number} b The second number to test.
* @returns {Boolean} True if the numbers are approximately equal, false otherwise.
*/
function equals(a, b) {
return Math.abs(a - b) <= EPSILON * Math.max(1.0, Math.abs(a), Math.abs(b));
}

var common = Object.freeze({
EPSILON: EPSILON,
get ARRAY_TYPE () { return ARRAY_TYPE; },
RANDOM: RANDOM,
setMatrixArrayType: setMatrixArrayType,
toRadian: toRadian,
equals: equals
});

/* Copyright (c) 2015, Brandon Jones, Colin MacKenzie IV.

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in
all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
THE SOFTWARE. */

/**
* 2x2 Matrix
* @module mat2
*/

/**
* Creates a new identity mat2
*
* @returns {mat2} a new 2x2 matrix
*/
function create() {
var out = new ARRAY_TYPE(4);
out[0] = 1;
out[1] = 0;
out[2] = 0;
out[3] = 1;
return out;
}

/**
* Creates a new mat2 initialized with values from an existing matrix
*
* @param {mat2} a matrix to clone
* @returns {mat2} a new 2x2 matrix
*/
function clone(a) {
var out = new ARRAY_TYPE(4);
out[0] = a[0];
out[1] = a[1];
out[2] = a[2];
out[3] = a[3];
return out;
}

/**
* Copy the values from one mat2 to another
*
* @param {mat2} out the receiving matrix
* @param {mat2} a the source matrix
* @returns {mat2} out
*/
function copy(out, a) {
out[0] = a[0];
out[1] = a[1];
out[2] = a[2];
out[3] = a[3];
return out;
}

/**
* Set a mat2 to the identity matrix
*
* @param {mat2} out the receiving matrix
* @returns {mat2} out
*/
function identity(out) {
out[0] = 1;
out[1] = 0;
out[2] = 0;
out[3] = 1;
return out;
}

/**
* Create a new mat2 with the given values
*
* @param {Number} m00 Component in column 0, row 0 position (index 0)
* @param {Number} m01 Component in column 0, row 1 position (index 1)
* @param {Number} m10 Component in column 1, row 0 position (index 2)
* @param {Number} m11 Component in column 1, row 1 position (index 3)
* @returns {mat2} out A new 2x2 matrix
*/
function fromValues(m00, m01, m10, m11) {
var out = new ARRAY_TYPE(4);
out[0] = m00;
out[1] = m01;
out[2] = m10;
out[3] = m11;
return out;
}

/**
* Set the components of a mat2 to the given values
*
* @param {mat2} out the receiving matrix
* @param {Number} m00 Component in column 0, row 0 position (index 0)
* @param {Number} m01 Component in column 0, row 1 position (index 1)
* @param {Number} m10 Component in column 1, row 0 position (index 2)
* @param {Number} m11 Component in column 1, row 1 position (index 3)
* @returns {mat2} out
*/
function set(out, m00, m01, m10, m11) {
out[0] = m00;
out[1] = m01;
out[2] = m10;
out[3] = m11;
return out;
}

/**
* Transpose the values of a mat2
*
* @param {mat2} out the receiving matrix
* @param {mat2} a the source matrix
* @returns {mat2} out
*/
function transpose(out, a) {
// If we are transposing ourselves we can skip a few steps but have to cache
// some values
if (out === a) {
  var a1 = a[1];
  out[1] = a[2];
  out[2] = a1;
} else {
  out[0] = a[0];
  out[1] = a[2];
  out[2] = a[1];
  out[3] = a[3];
}

return out;
}

/**
* Inverts a mat2
*
* @param {mat2} out the receiving matrix
* @param {mat2} a the source matrix
* @returns {mat2} out
*/
function invert(out, a) {
var a0 = a[0],
    a1 = a[1],
    a2 = a[2],
    a3 = a[3];

// Calculate the determinant
var det = a0 * a3 - a2 * a1;

if (!det) {
  return null;
}
det = 1.0 / det;

out[0] = a3 * det;
out[1] = -a1 * det;
out[2] = -a2 * det;
out[3] = a0 * det;

return out;
}

/**
* Calculates the adjugate of a mat2
*
* @param {mat2} out the receiving matrix
* @param {mat2} a the source matrix
* @returns {mat2} out
*/
function adjoint(out, a) {
// Caching this value is nessecary if out == a
var a0 = a[0];
out[0] = a[3];
out[1] = -a[1];
out[2] = -a[2];
out[3] = a0;

return out;
}

/**
* Calculates the determinant of a mat2
*
* @param {mat2} a the source matrix
* @returns {Number} determinant of a
*/
function determinant(a) {
return a[0] * a[3] - a[2] * a[1];
}

/**
* Multiplies two mat2's
*
* @param {mat2} out the receiving matrix
* @param {mat2} a the first operand
* @param {mat2} b the second operand
* @returns {mat2} out
*/
function multiply$1(out, a, b) {
var a0 = a[0],
    a1 = a[1],
    a2 = a[2],
    a3 = a[3];
var b0 = b[0],
    b1 = b[1],
    b2 = b[2],
    b3 = b[3];
out[0] = a0 * b0 + a2 * b1;
out[1] = a1 * b0 + a3 * b1;
out[2] = a0 * b2 + a2 * b3;
out[3] = a1 * b2 + a3 * b3;
return out;
}

/**
* Rotates a mat2 by the given angle
*
* @param {mat2} out the receiving matrix
* @param {mat2} a the matrix to rotate
* @param {Number} rad the angle to rotate the matrix by
* @returns {mat2} out
*/
function rotate(out, a, rad) {
var a0 = a[0],
    a1 = a[1],
    a2 = a[2],
    a3 = a[3];
var s = Math.sin(rad);
var c = Math.cos(rad);
out[0] = a0 * c + a2 * s;
out[1] = a1 * c + a3 * s;
out[2] = a0 * -s + a2 * c;
out[3] = a1 * -s + a3 * c;
return out;
}

/**
* Scales the mat2 by the dimensions in the given vec2
*
* @param {mat2} out the receiving matrix
* @param {mat2} a the matrix to rotate
* @param {vec2} v the vec2 to scale the matrix by
* @returns {mat2} out
**/
function scale(out, a, v) {
var a0 = a[0],
    a1 = a[1],
    a2 = a[2],
    a3 = a[3];
var v0 = v[0],
    v1 = v[1];
out[0] = a0 * v0;
out[1] = a1 * v0;
out[2] = a2 * v1;
out[3] = a3 * v1;
return out;
}

/**
* Creates a matrix from a given angle
* This is equivalent to (but much faster than):
*
*     mat2.identity(dest);
*     mat2.rotate(dest, dest, rad);
*
* @param {mat2} out mat2 receiving operation result
* @param {Number} rad the angle to rotate the matrix by
* @returns {mat2} out
*/
function fromRotation(out, rad) {
var s = Math.sin(rad);
var c = Math.cos(rad);
out[0] = c;
out[1] = s;
out[2] = -s;
out[3] = c;
return out;
}

/**
* Creates a matrix from a vector scaling
* This is equivalent to (but much faster than):
*
*     mat2.identity(dest);
*     mat2.scale(dest, dest, vec);
*
* @param {mat2} out mat2 receiving operation result
* @param {vec2} v Scaling vector
* @returns {mat2} out
*/
function fromScaling(out, v) {
out[0] = v[0];
out[1] = 0;
out[2] = 0;
out[3] = v[1];
return out;
}

/**
* Returns a string representation of a mat2
*
* @param {mat2} a matrix to represent as a string
* @returns {String} string representation of the matrix
*/
function str(a) {
return 'mat2(' + a[0] + ', ' + a[1] + ', ' + a[2] + ', ' + a[3] + ')';
}

/**
* Returns Frobenius norm of a mat2
*
* @param {mat2} a the matrix to calculate Frobenius norm of
* @returns {Number} Frobenius norm
*/
function frob(a) {
return Math.sqrt(Math.pow(a[0], 2) + Math.pow(a[1], 2) + Math.pow(a[2], 2) + Math.pow(a[3], 2));
}

/**
* Returns L, D and U matrices (Lower triangular, Diagonal and Upper triangular) by factorizing the input matrix
* @param {mat2} L the lower triangular matrix
* @param {mat2} D the diagonal matrix
* @param {mat2} U the upper triangular matrix
* @param {mat2} a the input matrix to factorize
*/

function LDU(L, D, U, a) {
L[2] = a[2] / a[0];
U[0] = a[0];
U[1] = a[1];
U[3] = a[3] - L[2] * U[1];
return [L, D, U];
}

/**
* Adds two mat2's
*
* @param {mat2} out the receiving matrix
* @param {mat2} a the first operand
* @param {mat2} b the second operand
* @returns {mat2} out
*/
function add(out, a, b) {
out[0] = a[0] + b[0];
out[1] = a[1] + b[1];
out[2] = a[2] + b[2];
out[3] = a[3] + b[3];
return out;
}

/**
* Subtracts matrix b from matrix a
*
* @param {mat2} out the receiving matrix
* @param {mat2} a the first operand
* @param {mat2} b the second operand
* @returns {mat2} out
*/
function subtract(out, a, b) {
out[0] = a[0] - b[0];
out[1] = a[1] - b[1];
out[2] = a[2] - b[2];
out[3] = a[3] - b[3];
return out;
}

/**
* Returns whether or not the matrices have exactly the same elements in the same position (when compared with ===)
*
* @param {mat2} a The first matrix.
* @param {mat2} b The second matrix.
* @returns {Boolean} True if the matrices are equal, false otherwise.
*/
function exactEquals(a, b) {
return a[0] === b[0] && a[1] === b[1] && a[2] === b[2] && a[3] === b[3];
}

/**
* Returns whether or not the matrices have approximately the same elements in the same position.
*
* @param {mat2} a The first matrix.
* @param {mat2} b The second matrix.
* @returns {Boolean} True if the matrices are equal, false otherwise.
*/
function equals$1(a, b) {
var a0 = a[0],
    a1 = a[1],
    a2 = a[2],
    a3 = a[3];
var b0 = b[0],
    b1 = b[1],
    b2 = b[2],
    b3 = b[3];
return Math.abs(a0 - b0) <= EPSILON * Math.max(1.0, Math.abs(a0), Math.abs(b0)) && Math.abs(a1 - b1) <= EPSILON * Math.max(1.0, Math.abs(a1), Math.abs(b1)) && Math.abs(a2 - b2) <= EPSILON * Math.max(1.0, Math.abs(a2), Math.abs(b2)) && Math.abs(a3 - b3) <= EPSILON * Math.max(1.0, Math.abs(a3), Math.abs(b3));
}

/**
* Multiply each element of the matrix by a scalar.
*
* @param {mat2} out the receiving matrix
* @param {mat2} a the matrix to scale
* @param {Number} b amount to scale the matrix's elements by
* @returns {mat2} out
*/
function multiplyScalar(out, a, b) {
out[0] = a[0] * b;
out[1] = a[1] * b;
out[2] = a[2] * b;
out[3] = a[3] * b;
return out;
}

/**
* Adds two mat2's after multiplying each element of the second operand by a scalar value.
*
* @param {mat2} out the receiving vector
* @param {mat2} a the first operand
* @param {mat2} b the second operand
* @param {Number} scale the amount to scale b's elements by before adding
* @returns {mat2} out
*/
function multiplyScalarAndAdd(out, a, b, scale) {
out[0] = a[0] + b[0] * scale;
out[1] = a[1] + b[1] * scale;
out[2] = a[2] + b[2] * scale;
out[3] = a[3] + b[3] * scale;
return out;
}

/**
* Alias for {@link mat2.multiply}
* @function
*/
var mul = multiply$1;

/**
* Alias for {@link mat2.subtract}
* @function
*/
var sub = subtract;

var mat2 = Object.freeze({
create: create,
clone: clone,
copy: copy,
identity: identity,
fromValues: fromValues,
set: set,
transpose: transpose,
invert: invert,
adjoint: adjoint,
determinant: determinant,
multiply: multiply$1,
rotate: rotate,
scale: scale,
fromRotation: fromRotation,
fromScaling: fromScaling,
str: str,
frob: frob,
LDU: LDU,
add: add,
subtract: subtract,
exactEquals: exactEquals,
equals: equals$1,
multiplyScalar: multiplyScalar,
multiplyScalarAndAdd: multiplyScalarAndAdd,
mul: mul,
sub: sub
});

/* Copyright (c) 2015, Brandon Jones, Colin MacKenzie IV.

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in
all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
THE SOFTWARE. */

/**
* 2x3 Matrix
* @module mat2d
*
* @description
* A mat2d contains six elements defined as:
* <pre>
* [a, c, tx,
*  b, d, ty]
* </pre>
* This is a short form for the 3x3 matrix:
* <pre>
* [a, c, tx,
*  b, d, ty,
*  0, 0, 1]
* </pre>
* The last row is ignored so the array is shorter and operations are faster.
*/

/**
* Creates a new identity mat2d
*
* @returns {mat2d} a new 2x3 matrix
*/
function create$1() {
var out = new ARRAY_TYPE(6);
out[0] = 1;
out[1] = 0;
out[2] = 0;
out[3] = 1;
out[4] = 0;
out[5] = 0;
return out;
}

/**
* Creates a new mat2d initialized with values from an existing matrix
*
* @param {mat2d} a matrix to clone
* @returns {mat2d} a new 2x3 matrix
*/
function clone$1(a) {
var out = new ARRAY_TYPE(6);
out[0] = a[0];
out[1] = a[1];
out[2] = a[2];
out[3] = a[3];
out[4] = a[4];
out[5] = a[5];
return out;
}

/**
* Copy the values from one mat2d to another
*
* @param {mat2d} out the receiving matrix
* @param {mat2d} a the source matrix
* @returns {mat2d} out
*/
function copy$1(out, a) {
out[0] = a[0];
out[1] = a[1];
out[2] = a[2];
out[3] = a[3];
out[4] = a[4];
out[5] = a[5];
return out;
}

/**
* Set a mat2d to the identity matrix
*
* @param {mat2d} out the receiving matrix
* @returns {mat2d} out
*/
function identity$1(out) {
out[0] = 1;
out[1] = 0;
out[2] = 0;
out[3] = 1;
out[4] = 0;
out[5] = 0;
return out;
}

/**
* Create a new mat2d with the given values
*
* @param {Number} a Component A (index 0)
* @param {Number} b Component B (index 1)
* @param {Number} c Component C (index 2)
* @param {Number} d Component D (index 3)
* @param {Number} tx Component TX (index 4)
* @param {Number} ty Component TY (index 5)
* @returns {mat2d} A new mat2d
*/
function fromValues$1(a, b, c, d, tx, ty) {
var out = new ARRAY_TYPE(6);
out[0] = a;
out[1] = b;
out[2] = c;
out[3] = d;
out[4] = tx;
out[5] = ty;
return out;
}

/**
* Set the components of a mat2d to the given values
*
* @param {mat2d} out the receiving matrix
* @param {Number} a Component A (index 0)
* @param {Number} b Component B (index 1)
* @param {Number} c Component C (index 2)
* @param {Number} d Component D (index 3)
* @param {Number} tx Component TX (index 4)
* @param {Number} ty Component TY (index 5)
* @returns {mat2d} out
*/
function set$1(out, a, b, c, d, tx, ty) {
out[0] = a;
out[1] = b;
out[2] = c;
out[3] = d;
out[4] = tx;
out[5] = ty;
return out;
}

/**
* Inverts a mat2d
*
* @param {mat2d} out the receiving matrix
* @param {mat2d} a the source matrix
* @returns {mat2d} out
*/
function invert$1(out, a) {
var aa = a[0],
    ab = a[1],
    ac = a[2],
    ad = a[3];
var atx = a[4],
    aty = a[5];

var det = aa * ad - ab * ac;
if (!det) {
  return null;
}
det = 1.0 / det;

out[0] = ad * det;
out[1] = -ab * det;
out[2] = -ac * det;
out[3] = aa * det;
out[4] = (ac * aty - ad * atx) * det;
out[5] = (ab * atx - aa * aty) * det;
return out;
}

/**
* Calculates the determinant of a mat2d
*
* @param {mat2d} a the source matrix
* @returns {Number} determinant of a
*/
function determinant$1(a) {
return a[0] * a[3] - a[1] * a[2];
}

/**
* Multiplies two mat2d's
*
* @param {mat2d} out the receiving matrix
* @param {mat2d} a the first operand
* @param {mat2d} b the second operand
* @returns {mat2d} out
*/
function multiply$2(out, a, b) {
var a0 = a[0],
    a1 = a[1],
    a2 = a[2],
    a3 = a[3],
    a4 = a[4],
    a5 = a[5];
var b0 = b[0],
    b1 = b[1],
    b2 = b[2],
    b3 = b[3],
    b4 = b[4],
    b5 = b[5];
out[0] = a0 * b0 + a2 * b1;
out[1] = a1 * b0 + a3 * b1;
out[2] = a0 * b2 + a2 * b3;
out[3] = a1 * b2 + a3 * b3;
out[4] = a0 * b4 + a2 * b5 + a4;
out[5] = a1 * b4 + a3 * b5 + a5;
return out;
}

/**
* Rotates a mat2d by the given angle
*
* @param {mat2d} out the receiving matrix
* @param {mat2d} a the matrix to rotate
* @param {Number} rad the angle to rotate the matrix by
* @returns {mat2d} out
*/
function rotate$1(out, a, rad) {
var a0 = a[0],
    a1 = a[1],
    a2 = a[2],
    a3 = a[3],
    a4 = a[4],
    a5 = a[5];
var s = Math.sin(rad);
var c = Math.cos(rad);
out[0] = a0 * c + a2 * s;
out[1] = a1 * c + a3 * s;
out[2] = a0 * -s + a2 * c;
out[3] = a1 * -s + a3 * c;
out[4] = a4;
out[5] = a5;
return out;
}

/**
* Scales the mat2d by the dimensions in the given vec2
*
* @param {mat2d} out the receiving matrix
* @param {mat2d} a the matrix to translate
* @param {vec2} v the vec2 to scale the matrix by
* @returns {mat2d} out
**/
function scale$1(out, a, v) {
var a0 = a[0],
    a1 = a[1],
    a2 = a[2],
    a3 = a[3],
    a4 = a[4],
    a5 = a[5];
var v0 = v[0],
    v1 = v[1];
out[0] = a0 * v0;
out[1] = a1 * v0;
out[2] = a2 * v1;
out[3] = a3 * v1;
out[4] = a4;
out[5] = a5;
return out;
}

/**
* Translates the mat2d by the dimensions in the given vec2
*
* @param {mat2d} out the receiving matrix
* @param {mat2d} a the matrix to translate
* @param {vec2} v the vec2 to translate the matrix by
* @returns {mat2d} out
**/
function translate(out, a, v) {
var a0 = a[0],
    a1 = a[1],
    a2 = a[2],
    a3 = a[3],
    a4 = a[4],
    a5 = a[5];
var v0 = v[0],
    v1 = v[1];
out[0] = a0;
out[1] = a1;
out[2] = a2;
out[3] = a3;
out[4] = a0 * v0 + a2 * v1 + a4;
out[5] = a1 * v0 + a3 * v1 + a5;
return out;
}

/**
* Creates a matrix from a given angle
* This is equivalent to (but much faster than):
*
*     mat2d.identity(dest);
*     mat2d.rotate(dest, dest, rad);
*
* @param {mat2d} out mat2d receiving operation result
* @param {Number} rad the angle to rotate the matrix by
* @returns {mat2d} out
*/
function fromRotation$1(out, rad) {
var s = Math.sin(rad),
    c = Math.cos(rad);
out[0] = c;
out[1] = s;
out[2] = -s;
out[3] = c;
out[4] = 0;
out[5] = 0;
return out;
}

/**
* Creates a matrix from a vector scaling
* This is equivalent to (but much faster than):
*
*     mat2d.identity(dest);
*     mat2d.scale(dest, dest, vec);
*
* @param {mat2d} out mat2d receiving operation result
* @param {vec2} v Scaling vector
* @returns {mat2d} out
*/
function fromScaling$1(out, v) {
out[0] = v[0];
out[1] = 0;
out[2] = 0;
out[3] = v[1];
out[4] = 0;
out[5] = 0;
return out;
}

/**
* Creates a matrix from a vector translation
* This is equivalent to (but much faster than):
*
*     mat2d.identity(dest);
*     mat2d.translate(dest, dest, vec);
*
* @param {mat2d} out mat2d receiving operation result
* @param {vec2} v Translation vector
* @returns {mat2d} out
*/
function fromTranslation(out, v) {
out[0] = 1;
out[1] = 0;
out[2] = 0;
out[3] = 1;
out[4] = v[0];
out[5] = v[1];
return out;
}

/**
* Returns a string representation of a mat2d
*
* @param {mat2d} a matrix to represent as a string
* @returns {String} string representation of the matrix
*/
function str$1(a) {
return 'mat2d(' + a[0] + ', ' + a[1] + ', ' + a[2] + ', ' + a[3] + ', ' + a[4] + ', ' + a[5] + ')';
}

/**
* Returns Frobenius norm of a mat2d
*
* @param {mat2d} a the matrix to calculate Frobenius norm of
* @returns {Number} Frobenius norm
*/
function frob$1(a) {
return Math.sqrt(Math.pow(a[0], 2) + Math.pow(a[1], 2) + Math.pow(a[2], 2) + Math.pow(a[3], 2) + Math.pow(a[4], 2) + Math.pow(a[5], 2) + 1);
}

/**
* Adds two mat2d's
*
* @param {mat2d} out the receiving matrix
* @param {mat2d} a the first operand
* @param {mat2d} b the second operand
* @returns {mat2d} out
*/
function add$1(out, a, b) {
out[0] = a[0] + b[0];
out[1] = a[1] + b[1];
out[2] = a[2] + b[2];
out[3] = a[3] + b[3];
out[4] = a[4] + b[4];
out[5] = a[5] + b[5];
return out;
}

/**
* Subtracts matrix b from matrix a
*
* @param {mat2d} out the receiving matrix
* @param {mat2d} a the first operand
* @param {mat2d} b the second operand
* @returns {mat2d} out
*/
function subtract$1(out, a, b) {
out[0] = a[0] - b[0];
out[1] = a[1] - b[1];
out[2] = a[2] - b[2];
out[3] = a[3] - b[3];
out[4] = a[4] - b[4];
out[5] = a[5] - b[5];
return out;
}

/**
* Multiply each element of the matrix by a scalar.
*
* @param {mat2d} out the receiving matrix
* @param {mat2d} a the matrix to scale
* @param {Number} b amount to scale the matrix's elements by
* @returns {mat2d} out
*/
function multiplyScalar$1(out, a, b) {
out[0] = a[0] * b;
out[1] = a[1] * b;
out[2] = a[2] * b;
out[3] = a[3] * b;
out[4] = a[4] * b;
out[5] = a[5] * b;
return out;
}

/**
* Adds two mat2d's after multiplying each element of the second operand by a scalar value.
*
* @param {mat2d} out the receiving vector
* @param {mat2d} a the first operand
* @param {mat2d} b the second operand
* @param {Number} scale the amount to scale b's elements by before adding
* @returns {mat2d} out
*/
function multiplyScalarAndAdd$1(out, a, b, scale) {
out[0] = a[0] + b[0] * scale;
out[1] = a[1] + b[1] * scale;
out[2] = a[2] + b[2] * scale;
out[3] = a[3] + b[3] * scale;
out[4] = a[4] + b[4] * scale;
out[5] = a[5] + b[5] * scale;
return out;
}

/**
* Returns whether or not the matrices have exactly the same elements in the same position (when compared with ===)
*
* @param {mat2d} a The first matrix.
* @param {mat2d} b The second matrix.
* @returns {Boolean} True if the matrices are equal, false otherwise.
*/
function exactEquals$1(a, b) {
return a[0] === b[0] && a[1] === b[1] && a[2] === b[2] && a[3] === b[3] && a[4] === b[4] && a[5] === b[5];
}

/**
* Returns whether or not the matrices have approximately the same elements in the same position.
*
* @param {mat2d} a The first matrix.
* @param {mat2d} b The second matrix.
* @returns {Boolean} True if the matrices are equal, false otherwise.
*/
function equals$2(a, b) {
var a0 = a[0],
    a1 = a[1],
    a2 = a[2],
    a3 = a[3],
    a4 = a[4],
    a5 = a[5];
var b0 = b[0],
    b1 = b[1],
    b2 = b[2],
    b3 = b[3],
    b4 = b[4],
    b5 = b[5];
return Math.abs(a0 - b0) <= EPSILON * Math.max(1.0, Math.abs(a0), Math.abs(b0)) && Math.abs(a1 - b1) <= EPSILON * Math.max(1.0, Math.abs(a1), Math.abs(b1)) && Math.abs(a2 - b2) <= EPSILON * Math.max(1.0, Math.abs(a2), Math.abs(b2)) && Math.abs(a3 - b3) <= EPSILON * Math.max(1.0, Math.abs(a3), Math.abs(b3)) && Math.abs(a4 - b4) <= EPSILON * Math.max(1.0, Math.abs(a4), Math.abs(b4)) && Math.abs(a5 - b5) <= EPSILON * Math.max(1.0, Math.abs(a5), Math.abs(b5));
}

/**
* Alias for {@link mat2d.multiply}
* @function
*/
var mul$1 = multiply$2;

/**
* Alias for {@link mat2d.subtract}
* @function
*/
var sub$1 = subtract$1;

var mat2d = Object.freeze({
create: create$1,
clone: clone$1,
copy: copy$1,
identity: identity$1,
fromValues: fromValues$1,
set: set$1,
invert: invert$1,
determinant: determinant$1,
multiply: multiply$2,
rotate: rotate$1,
scale: scale$1,
translate: translate,
fromRotation: fromRotation$1,
fromScaling: fromScaling$1,
fromTranslation: fromTranslation,
str: str$1,
frob: frob$1,
add: add$1,
subtract: subtract$1,
multiplyScalar: multiplyScalar$1,
multiplyScalarAndAdd: multiplyScalarAndAdd$1,
exactEquals: exactEquals$1,
equals: equals$2,
mul: mul$1,
sub: sub$1
});

/* Copyright (c) 2015, Brandon Jones, Colin MacKenzie IV.

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in
all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
THE SOFTWARE. */

/**
* 3x3 Matrix
* @module mat3
*/

/**
* Creates a new identity mat3
*
* @returns {mat3} a new 3x3 matrix
*/
function create$2() {
var out = new ARRAY_TYPE(9);
out[0] = 1;
out[1] = 0;
out[2] = 0;
out[3] = 0;
out[4] = 1;
out[5] = 0;
out[6] = 0;
out[7] = 0;
out[8] = 1;
return out;
}

/**
* Copies the upper-left 3x3 values into the given mat3.
*
* @param {mat3} out the receiving 3x3 matrix
* @param {mat4} a   the source 4x4 matrix
* @returns {mat3} out
*/
function fromMat4(out, a) {
out[0] = a[0];
out[1] = a[1];
out[2] = a[2];
out[3] = a[4];
out[4] = a[5];
out[5] = a[6];
out[6] = a[8];
out[7] = a[9];
out[8] = a[10];
return out;
}

/**
* Creates a new mat3 initialized with values from an existing matrix
*
* @param {mat3} a matrix to clone
* @returns {mat3} a new 3x3 matrix
*/
function clone$2(a) {
var out = new ARRAY_TYPE(9);
out[0] = a[0];
out[1] = a[1];
out[2] = a[2];
out[3] = a[3];
out[4] = a[4];
out[5] = a[5];
out[6] = a[6];
out[7] = a[7];
out[8] = a[8];
return out;
}

/**
* Copy the values from one mat3 to another
*
* @param {mat3} out the receiving matrix
* @param {mat3} a the source matrix
* @returns {mat3} out
*/
function copy$2(out, a) {
out[0] = a[0];
out[1] = a[1];
out[2] = a[2];
out[3] = a[3];
out[4] = a[4];
out[5] = a[5];
out[6] = a[6];
out[7] = a[7];
out[8] = a[8];
return out;
}

/**
* Create a new mat3 with the given values
*
* @param {Number} m00 Component in column 0, row 0 position (index 0)
* @param {Number} m01 Component in column 0, row 1 position (index 1)
* @param {Number} m02 Component in column 0, row 2 position (index 2)
* @param {Number} m10 Component in column 1, row 0 position (index 3)
* @param {Number} m11 Component in column 1, row 1 position (index 4)
* @param {Number} m12 Component in column 1, row 2 position (index 5)
* @param {Number} m20 Component in column 2, row 0 position (index 6)
* @param {Number} m21 Component in column 2, row 1 position (index 7)
* @param {Number} m22 Component in column 2, row 2 position (index 8)
* @returns {mat3} A new mat3
*/
function fromValues$2(m00, m01, m02, m10, m11, m12, m20, m21, m22) {
var out = new ARRAY_TYPE(9);
out[0] = m00;
out[1] = m01;
out[2] = m02;
out[3] = m10;
out[4] = m11;
out[5] = m12;
out[6] = m20;
out[7] = m21;
out[8] = m22;
return out;
}

/**
* Set the components of a mat3 to the given values
*
* @param {mat3} out the receiving matrix
* @param {Number} m00 Component in column 0, row 0 position (index 0)
* @param {Number} m01 Component in column 0, row 1 position (index 1)
* @param {Number} m02 Component in column 0, row 2 position (index 2)
* @param {Number} m10 Component in column 1, row 0 position (index 3)
* @param {Number} m11 Component in column 1, row 1 position (index 4)
* @param {Number} m12 Component in column 1, row 2 position (index 5)
* @param {Number} m20 Component in column 2, row 0 position (index 6)
* @param {Number} m21 Component in column 2, row 1 position (index 7)
* @param {Number} m22 Component in column 2, row 2 position (index 8)
* @returns {mat3} out
*/
function set$2(out, m00, m01, m02, m10, m11, m12, m20, m21, m22) {
out[0] = m00;
out[1] = m01;
out[2] = m02;
out[3] = m10;
out[4] = m11;
out[5] = m12;
out[6] = m20;
out[7] = m21;
out[8] = m22;
return out;
}

/**
* Set a mat3 to the identity matrix
*
* @param {mat3} out the receiving matrix
* @returns {mat3} out
*/
function identity$2(out) {
out[0] = 1;
out[1] = 0;
out[2] = 0;
out[3] = 0;
out[4] = 1;
out[5] = 0;
out[6] = 0;
out[7] = 0;
out[8] = 1;
return out;
}

/**
* Transpose the values of a mat3
*
* @param {mat3} out the receiving matrix
* @param {mat3} a the source matrix
* @returns {mat3} out
*/
function transpose$1(out, a) {
// If we are transposing ourselves we can skip a few steps but have to cache some values
if (out === a) {
  var a01 = a[1],
      a02 = a[2],
      a12 = a[5];
  out[1] = a[3];
  out[2] = a[6];
  out[3] = a01;
  out[5] = a[7];
  out[6] = a02;
  out[7] = a12;
} else {
  out[0] = a[0];
  out[1] = a[3];
  out[2] = a[6];
  out[3] = a[1];
  out[4] = a[4];
  out[5] = a[7];
  out[6] = a[2];
  out[7] = a[5];
  out[8] = a[8];
}

return out;
}

/**
* Inverts a mat3
*
* @param {mat3} out the receiving matrix
* @param {mat3} a the source matrix
* @returns {mat3} out
*/
function invert$2(out, a) {
var a00 = a[0],
    a01 = a[1],
    a02 = a[2];
var a10 = a[3],
    a11 = a[4],
    a12 = a[5];
var a20 = a[6],
    a21 = a[7],
    a22 = a[8];

var b01 = a22 * a11 - a12 * a21;
var b11 = -a22 * a10 + a12 * a20;
var b21 = a21 * a10 - a11 * a20;

// Calculate the determinant
var det = a00 * b01 + a01 * b11 + a02 * b21;

if (!det) {
  return null;
}
det = 1.0 / det;

out[0] = b01 * det;
out[1] = (-a22 * a01 + a02 * a21) * det;
out[2] = (a12 * a01 - a02 * a11) * det;
out[3] = b11 * det;
out[4] = (a22 * a00 - a02 * a20) * det;
out[5] = (-a12 * a00 + a02 * a10) * det;
out[6] = b21 * det;
out[7] = (-a21 * a00 + a01 * a20) * det;
out[8] = (a11 * a00 - a01 * a10) * det;
return out;
}

/**
* Calculates the adjugate of a mat3
*
* @param {mat3} out the receiving matrix
* @param {mat3} a the source matrix
* @returns {mat3} out
*/
function adjoint$1(out, a) {
var a00 = a[0],
    a01 = a[1],
    a02 = a[2];
var a10 = a[3],
    a11 = a[4],
    a12 = a[5];
var a20 = a[6],
    a21 = a[7],
    a22 = a[8];

out[0] = a11 * a22 - a12 * a21;
out[1] = a02 * a21 - a01 * a22;
out[2] = a01 * a12 - a02 * a11;
out[3] = a12 * a20 - a10 * a22;
out[4] = a00 * a22 - a02 * a20;
out[5] = a02 * a10 - a00 * a12;
out[6] = a10 * a21 - a11 * a20;
out[7] = a01 * a20 - a00 * a21;
out[8] = a00 * a11 - a01 * a10;
return out;
}

/**
* Calculates the determinant of a mat3
*
* @param {mat3} a the source matrix
* @returns {Number} determinant of a
*/
function determinant$2(a) {
var a00 = a[0],
    a01 = a[1],
    a02 = a[2];
var a10 = a[3],
    a11 = a[4],
    a12 = a[5];
var a20 = a[6],
    a21 = a[7],
    a22 = a[8];

return a00 * (a22 * a11 - a12 * a21) + a01 * (-a22 * a10 + a12 * a20) + a02 * (a21 * a10 - a11 * a20);
}

/**
* Multiplies two mat3's
*
* @param {mat3} out the receiving matrix
* @param {mat3} a the first operand
* @param {mat3} b the second operand
* @returns {mat3} out
*/
function multiply$3(out, a, b) {
var a00 = a[0],
    a01 = a[1],
    a02 = a[2];
var a10 = a[3],
    a11 = a[4],
    a12 = a[5];
var a20 = a[6],
    a21 = a[7],
    a22 = a[8];

var b00 = b[0],
    b01 = b[1],
    b02 = b[2];
var b10 = b[3],
    b11 = b[4],
    b12 = b[5];
var b20 = b[6],
    b21 = b[7],
    b22 = b[8];

out[0] = b00 * a00 + b01 * a10 + b02 * a20;
out[1] = b00 * a01 + b01 * a11 + b02 * a21;
out[2] = b00 * a02 + b01 * a12 + b02 * a22;

out[3] = b10 * a00 + b11 * a10 + b12 * a20;
out[4] = b10 * a01 + b11 * a11 + b12 * a21;
out[5] = b10 * a02 + b11 * a12 + b12 * a22;

out[6] = b20 * a00 + b21 * a10 + b22 * a20;
out[7] = b20 * a01 + b21 * a11 + b22 * a21;
out[8] = b20 * a02 + b21 * a12 + b22 * a22;
return out;
}

/**
* Translate a mat3 by the given vector
*
* @param {mat3} out the receiving matrix
* @param {mat3} a the matrix to translate
* @param {vec2} v vector to translate by
* @returns {mat3} out
*/
function translate$1(out, a, v) {
var a00 = a[0],
    a01 = a[1],
    a02 = a[2],
    a10 = a[3],
    a11 = a[4],
    a12 = a[5],
    a20 = a[6],
    a21 = a[7],
    a22 = a[8],
    x = v[0],
    y = v[1];

out[0] = a00;
out[1] = a01;
out[2] = a02;

out[3] = a10;
out[4] = a11;
out[5] = a12;

out[6] = x * a00 + y * a10 + a20;
out[7] = x * a01 + y * a11 + a21;
out[8] = x * a02 + y * a12 + a22;
return out;
}

/**
* Rotates a mat3 by the given angle
*
* @param {mat3} out the receiving matrix
* @param {mat3} a the matrix to rotate
* @param {Number} rad the angle to rotate the matrix by
* @returns {mat3} out
*/
function rotate$2(out, a, rad) {
var a00 = a[0],
    a01 = a[1],
    a02 = a[2],
    a10 = a[3],
    a11 = a[4],
    a12 = a[5],
    a20 = a[6],
    a21 = a[7],
    a22 = a[8],
    s = Math.sin(rad),
    c = Math.cos(rad);

out[0] = c * a00 + s * a10;
out[1] = c * a01 + s * a11;
out[2] = c * a02 + s * a12;

out[3] = c * a10 - s * a00;
out[4] = c * a11 - s * a01;
out[5] = c * a12 - s * a02;

out[6] = a20;
out[7] = a21;
out[8] = a22;
return out;
}

/**
* Scales the mat3 by the dimensions in the given vec2
*
* @param {mat3} out the receiving matrix
* @param {mat3} a the matrix to rotate
* @param {vec2} v the vec2 to scale the matrix by
* @returns {mat3} out
**/
function scale$2(out, a, v) {
var x = v[0],
    y = v[1];

out[0] = x * a[0];
out[1] = x * a[1];
out[2] = x * a[2];

out[3] = y * a[3];
out[4] = y * a[4];
out[5] = y * a[5];

out[6] = a[6];
out[7] = a[7];
out[8] = a[8];
return out;
}

/**
* Creates a matrix from a vector translation
* This is equivalent to (but much faster than):
*
*     mat3.identity(dest);
*     mat3.translate(dest, dest, vec);
*
* @param {mat3} out mat3 receiving operation result
* @param {vec2} v Translation vector
* @returns {mat3} out
*/
function fromTranslation$1(out, v) {
out[0] = 1;
out[1] = 0;
out[2] = 0;
out[3] = 0;
out[4] = 1;
out[5] = 0;
out[6] = v[0];
out[7] = v[1];
out[8] = 1;
return out;
}

/**
* Creates a matrix from a given angle
* This is equivalent to (but much faster than):
*
*     mat3.identity(dest);
*     mat3.rotate(dest, dest, rad);
*
* @param {mat3} out mat3 receiving operation result
* @param {Number} rad the angle to rotate the matrix by
* @returns {mat3} out
*/
function fromRotation$2(out, rad) {
var s = Math.sin(rad),
    c = Math.cos(rad);

out[0] = c;
out[1] = s;
out[2] = 0;

out[3] = -s;
out[4] = c;
out[5] = 0;

out[6] = 0;
out[7] = 0;
out[8] = 1;
return out;
}

/**
* Creates a matrix from a vector scaling
* This is equivalent to (but much faster than):
*
*     mat3.identity(dest);
*     mat3.scale(dest, dest, vec);
*
* @param {mat3} out mat3 receiving operation result
* @param {vec2} v Scaling vector
* @returns {mat3} out
*/
function fromScaling$2(out, v) {
out[0] = v[0];
out[1] = 0;
out[2] = 0;

out[3] = 0;
out[4] = v[1];
out[5] = 0;

out[6] = 0;
out[7] = 0;
out[8] = 1;
return out;
}

/**
* Copies the values from a mat2d into a mat3
*
* @param {mat3} out the receiving matrix
* @param {mat2d} a the matrix to copy
* @returns {mat3} out
**/
function fromMat2d(out, a) {
out[0] = a[0];
out[1] = a[1];
out[2] = 0;

out[3] = a[2];
out[4] = a[3];
out[5] = 0;

out[6] = a[4];
out[7] = a[5];
out[8] = 1;
return out;
}

/**
* Calculates a 3x3 matrix from the given quaternion
*
* @param {mat3} out mat3 receiving operation result
* @param {quat} q Quaternion to create matrix from
*
* @returns {mat3} out
*/
function fromQuat(out, q) {
var x = q[0],
    y = q[1],
    z = q[2],
    w = q[3];
var x2 = x + x;
var y2 = y + y;
var z2 = z + z;

var xx = x * x2;
var yx = y * x2;
var yy = y * y2;
var zx = z * x2;
var zy = z * y2;
var zz = z * z2;
var wx = w * x2;
var wy = w * y2;
var wz = w * z2;

out[0] = 1 - yy - zz;
out[3] = yx - wz;
out[6] = zx + wy;

out[1] = yx + wz;
out[4] = 1 - xx - zz;
out[7] = zy - wx;

out[2] = zx - wy;
out[5] = zy + wx;
out[8] = 1 - xx - yy;

return out;
}

/**
* Calculates a 3x3 normal matrix (transpose inverse) from the 4x4 matrix
*
* @param {mat3} out mat3 receiving operation result
* @param {mat4} a Mat4 to derive the normal matrix from
*
* @returns {mat3} out
*/
function normalFromMat4(out, a) {
var a00 = a[0],
    a01 = a[1],
    a02 = a[2],
    a03 = a[3];
var a10 = a[4],
    a11 = a[5],
    a12 = a[6],
    a13 = a[7];
var a20 = a[8],
    a21 = a[9],
    a22 = a[10],
    a23 = a[11];
var a30 = a[12],
    a31 = a[13],
    a32 = a[14],
    a33 = a[15];

var b00 = a00 * a11 - a01 * a10;
var b01 = a00 * a12 - a02 * a10;
var b02 = a00 * a13 - a03 * a10;
var b03 = a01 * a12 - a02 * a11;
var b04 = a01 * a13 - a03 * a11;
var b05 = a02 * a13 - a03 * a12;
var b06 = a20 * a31 - a21 * a30;
var b07 = a20 * a32 - a22 * a30;
var b08 = a20 * a33 - a23 * a30;
var b09 = a21 * a32 - a22 * a31;
var b10 = a21 * a33 - a23 * a31;
var b11 = a22 * a33 - a23 * a32;

// Calculate the determinant
var det = b00 * b11 - b01 * b10 + b02 * b09 + b03 * b08 - b04 * b07 + b05 * b06;

if (!det) {
  return null;
}
det = 1.0 / det;

out[0] = (a11 * b11 - a12 * b10 + a13 * b09) * det;
out[1] = (a12 * b08 - a10 * b11 - a13 * b07) * det;
out[2] = (a10 * b10 - a11 * b08 + a13 * b06) * det;

out[3] = (a02 * b10 - a01 * b11 - a03 * b09) * det;
out[4] = (a00 * b11 - a02 * b08 + a03 * b07) * det;
out[5] = (a01 * b08 - a00 * b10 - a03 * b06) * det;

out[6] = (a31 * b05 - a32 * b04 + a33 * b03) * det;
out[7] = (a32 * b02 - a30 * b05 - a33 * b01) * det;
out[8] = (a30 * b04 - a31 * b02 + a33 * b00) * det;

return out;
}

/**
* Generates a 2D projection matrix with the given bounds
*
* @param {mat3} out mat3 frustum matrix will be written into
* @param {number} width Width of your gl context
* @param {number} height Height of gl context
* @returns {mat3} out
*/
function projection(out, width, height) {
out[0] = 2 / width;
out[1] = 0;
out[2] = 0;
out[3] = 0;
out[4] = -2 / height;
out[5] = 0;
out[6] = -1;
out[7] = 1;
out[8] = 1;
return out;
}

/**
* Returns a string representation of a mat3
*
* @param {mat3} a matrix to represent as a string
* @returns {String} string representation of the matrix
*/
function str$2(a) {
return 'mat3(' + a[0] + ', ' + a[1] + ', ' + a[2] + ', ' + a[3] + ', ' + a[4] + ', ' + a[5] + ', ' + a[6] + ', ' + a[7] + ', ' + a[8] + ')';
}

/**
* Returns Frobenius norm of a mat3
*
* @param {mat3} a the matrix to calculate Frobenius norm of
* @returns {Number} Frobenius norm
*/
function frob$2(a) {
return Math.sqrt(Math.pow(a[0], 2) + Math.pow(a[1], 2) + Math.pow(a[2], 2) + Math.pow(a[3], 2) + Math.pow(a[4], 2) + Math.pow(a[5], 2) + Math.pow(a[6], 2) + Math.pow(a[7], 2) + Math.pow(a[8], 2));
}

/**
* Adds two mat3's
*
* @param {mat3} out the receiving matrix
* @param {mat3} a the first operand
* @param {mat3} b the second operand
* @returns {mat3} out
*/
function add$2(out, a, b) {
out[0] = a[0] + b[0];
out[1] = a[1] + b[1];
out[2] = a[2] + b[2];
out[3] = a[3] + b[3];
out[4] = a[4] + b[4];
out[5] = a[5] + b[5];
out[6] = a[6] + b[6];
out[7] = a[7] + b[7];
out[8] = a[8] + b[8];
return out;
}

/**
* Subtracts matrix b from matrix a
*
* @param {mat3} out the receiving matrix
* @param {mat3} a the first operand
* @param {mat3} b the second operand
* @returns {mat3} out
*/
function subtract$2(out, a, b) {
out[0] = a[0] - b[0];
out[1] = a[1] - b[1];
out[2] = a[2] - b[2];
out[3] = a[3] - b[3];
out[4] = a[4] - b[4];
out[5] = a[5] - b[5];
out[6] = a[6] - b[6];
out[7] = a[7] - b[7];
out[8] = a[8] - b[8];
return out;
}

/**
* Multiply each element of the matrix by a scalar.
*
* @param {mat3} out the receiving matrix
* @param {mat3} a the matrix to scale
* @param {Number} b amount to scale the matrix's elements by
* @returns {mat3} out
*/
function multiplyScalar$2(out, a, b) {
out[0] = a[0] * b;
out[1] = a[1] * b;
out[2] = a[2] * b;
out[3] = a[3] * b;
out[4] = a[4] * b;
out[5] = a[5] * b;
out[6] = a[6] * b;
out[7] = a[7] * b;
out[8] = a[8] * b;
return out;
}

/**
* Adds two mat3's after multiplying each element of the second operand by a scalar value.
*
* @param {mat3} out the receiving vector
* @param {mat3} a the first operand
* @param {mat3} b the second operand
* @param {Number} scale the amount to scale b's elements by before adding
* @returns {mat3} out
*/
function multiplyScalarAndAdd$2(out, a, b, scale) {
out[0] = a[0] + b[0] * scale;
out[1] = a[1] + b[1] * scale;
out[2] = a[2] + b[2] * scale;
out[3] = a[3] + b[3] * scale;
out[4] = a[4] + b[4] * scale;
out[5] = a[5] + b[5] * scale;
out[6] = a[6] + b[6] * scale;
out[7] = a[7] + b[7] * scale;
out[8] = a[8] + b[8] * scale;
return out;
}

/**
* Returns whether or not the matrices have exactly the same elements in the same position (when compared with ===)
*
* @param {mat3} a The first matrix.
* @param {mat3} b The second matrix.
* @returns {Boolean} True if the matrices are equal, false otherwise.
*/
function exactEquals$2(a, b) {
return a[0] === b[0] && a[1] === b[1] && a[2] === b[2] && a[3] === b[3] && a[4] === b[4] && a[5] === b[5] && a[6] === b[6] && a[7] === b[7] && a[8] === b[8];
}

/**
* Returns whether or not the matrices have approximately the same elements in the same position.
*
* @param {mat3} a The first matrix.
* @param {mat3} b The second matrix.
* @returns {Boolean} True if the matrices are equal, false otherwise.
*/
function equals$3(a, b) {
var a0 = a[0],
    a1 = a[1],
    a2 = a[2],
    a3 = a[3],
    a4 = a[4],
    a5 = a[5],
    a6 = a[6],
    a7 = a[7],
    a8 = a[8];
var b0 = b[0],
    b1 = b[1],
    b2 = b[2],
    b3 = b[3],
    b4 = b[4],
    b5 = b[5],
    b6 = b[6],
    b7 = b[7],
    b8 = b[8];
return Math.abs(a0 - b0) <= EPSILON * Math.max(1.0, Math.abs(a0), Math.abs(b0)) && Math.abs(a1 - b1) <= EPSILON * Math.max(1.0, Math.abs(a1), Math.abs(b1)) && Math.abs(a2 - b2) <= EPSILON * Math.max(1.0, Math.abs(a2), Math.abs(b2)) && Math.abs(a3 - b3) <= EPSILON * Math.max(1.0, Math.abs(a3), Math.abs(b3)) && Math.abs(a4 - b4) <= EPSILON * Math.max(1.0, Math.abs(a4), Math.abs(b4)) && Math.abs(a5 - b5) <= EPSILON * Math.max(1.0, Math.abs(a5), Math.abs(b5)) && Math.abs(a6 - b6) <= EPSILON * Math.max(1.0, Math.abs(a6), Math.abs(b6)) && Math.abs(a7 - b7) <= EPSILON * Math.max(1.0, Math.abs(a7), Math.abs(b7)) && Math.abs(a8 - b8) <= EPSILON * Math.max(1.0, Math.abs(a8), Math.abs(b8));
}

/**
* Alias for {@link mat3.multiply}
* @function
*/
var mul$2 = multiply$3;

/**
* Alias for {@link mat3.subtract}
* @function
*/
var sub$2 = subtract$2;

var mat3 = Object.freeze({
create: create$2,
fromMat4: fromMat4,
clone: clone$2,
copy: copy$2,
fromValues: fromValues$2,
set: set$2,
identity: identity$2,
transpose: transpose$1,
invert: invert$2,
adjoint: adjoint$1,
determinant: determinant$2,
multiply: multiply$3,
translate: translate$1,
rotate: rotate$2,
scale: scale$2,
fromTranslation: fromTranslation$1,
fromRotation: fromRotation$2,
fromScaling: fromScaling$2,
fromMat2d: fromMat2d,
fromQuat: fromQuat,
normalFromMat4: normalFromMat4,
projection: projection,
str: str$2,
frob: frob$2,
add: add$2,
subtract: subtract$2,
multiplyScalar: multiplyScalar$2,
multiplyScalarAndAdd: multiplyScalarAndAdd$2,
exactEquals: exactEquals$2,
equals: equals$3,
mul: mul$2,
sub: sub$2
});

/* Copyright (c) 2015, Brandon Jones, Colin MacKenzie IV.

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in
all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
THE SOFTWARE. */

/**
* @class 4x4 Matrix<br>Format: column-major, when typed out it looks like row-major<br>The matrices are being post multiplied.
* @name mat4
*/

/**
* Creates a new identity mat4
*
* @returns {mat4} a new 4x4 matrix
*/
function create$3() {
var out = new ARRAY_TYPE(16);
out[0] = 1;
out[1] = 0;
out[2] = 0;
out[3] = 0;
out[4] = 0;
out[5] = 1;
out[6] = 0;
out[7] = 0;
out[8] = 0;
out[9] = 0;
out[10] = 1;
out[11] = 0;
out[12] = 0;
out[13] = 0;
out[14] = 0;
out[15] = 1;
return out;
}

/**
* Creates a new mat4 initialized with values from an existing matrix
*
* @param {mat4} a matrix to clone
* @returns {mat4} a new 4x4 matrix
*/
function clone$3(a) {
var out = new ARRAY_TYPE(16);
out[0] = a[0];
out[1] = a[1];
out[2] = a[2];
out[3] = a[3];
out[4] = a[4];
out[5] = a[5];
out[6] = a[6];
out[7] = a[7];
out[8] = a[8];
out[9] = a[9];
out[10] = a[10];
out[11] = a[11];
out[12] = a[12];
out[13] = a[13];
out[14] = a[14];
out[15] = a[15];
return out;
}

/**
* Copy the values from one mat4 to another
*
* @param {mat4} out the receiving matrix
* @param {mat4} a the source matrix
* @returns {mat4} out
*/
function copy$3(out, a) {
out[0] = a[0];
out[1] = a[1];
out[2] = a[2];
out[3] = a[3];
out[4] = a[4];
out[5] = a[5];
out[6] = a[6];
out[7] = a[7];
out[8] = a[8];
out[9] = a[9];
out[10] = a[10];
out[11] = a[11];
out[12] = a[12];
out[13] = a[13];
out[14] = a[14];
out[15] = a[15];
return out;
}

/**
* Create a new mat4 with the given values
*
* @param {Number} m00 Component in column 0, row 0 position (index 0)
* @param {Number} m01 Component in column 0, row 1 position (index 1)
* @param {Number} m02 Component in column 0, row 2 position (index 2)
* @param {Number} m03 Component in column 0, row 3 position (index 3)
* @param {Number} m10 Component in column 1, row 0 position (index 4)
* @param {Number} m11 Component in column 1, row 1 position (index 5)
* @param {Number} m12 Component in column 1, row 2 position (index 6)
* @param {Number} m13 Component in column 1, row 3 position (index 7)
* @param {Number} m20 Component in column 2, row 0 position (index 8)
* @param {Number} m21 Component in column 2, row 1 position (index 9)
* @param {Number} m22 Component in column 2, row 2 position (index 10)
* @param {Number} m23 Component in column 2, row 3 position (index 11)
* @param {Number} m30 Component in column 3, row 0 position (index 12)
* @param {Number} m31 Component in column 3, row 1 position (index 13)
* @param {Number} m32 Component in column 3, row 2 position (index 14)
* @param {Number} m33 Component in column 3, row 3 position (index 15)
* @returns {mat4} A new mat4
*/
function fromValues$3(m00, m01, m02, m03, m10, m11, m12, m13, m20, m21, m22, m23, m30, m31, m32, m33) {
var out = new ARRAY_TYPE(16);
out[0] = m00;
out[1] = m01;
out[2] = m02;
out[3] = m03;
out[4] = m10;
out[5] = m11;
out[6] = m12;
out[7] = m13;
out[8] = m20;
out[9] = m21;
out[10] = m22;
out[11] = m23;
out[12] = m30;
out[13] = m31;
out[14] = m32;
out[15] = m33;
return out;
}

/**
* Set the components of a mat4 to the given values
*
* @param {mat4} out the receiving matrix
* @param {Number} m00 Component in column 0, row 0 position (index 0)
* @param {Number} m01 Component in column 0, row 1 position (index 1)
* @param {Number} m02 Component in column 0, row 2 position (index 2)
* @param {Number} m03 Component in column 0, row 3 position (index 3)
* @param {Number} m10 Component in column 1, row 0 position (index 4)
* @param {Number} m11 Component in column 1, row 1 position (index 5)
* @param {Number} m12 Component in column 1, row 2 position (index 6)
* @param {Number} m13 Component in column 1, row 3 position (index 7)
* @param {Number} m20 Component in column 2, row 0 position (index 8)
* @param {Number} m21 Component in column 2, row 1 position (index 9)
* @param {Number} m22 Component in column 2, row 2 position (index 10)
* @param {Number} m23 Component in column 2, row 3 position (index 11)
* @param {Number} m30 Component in column 3, row 0 position (index 12)
* @param {Number} m31 Component in column 3, row 1 position (index 13)
* @param {Number} m32 Component in column 3, row 2 position (index 14)
* @param {Number} m33 Component in column 3, row 3 position (index 15)
* @returns {mat4} out
*/
function set$3(out, m00, m01, m02, m03, m10, m11, m12, m13, m20, m21, m22, m23, m30, m31, m32, m33) {
out[0] = m00;
out[1] = m01;
out[2] = m02;
out[3] = m03;
out[4] = m10;
out[5] = m11;
out[6] = m12;
out[7] = m13;
out[8] = m20;
out[9] = m21;
out[10] = m22;
out[11] = m23;
out[12] = m30;
out[13] = m31;
out[14] = m32;
out[15] = m33;
return out;
}

/**
* Set a mat4 to the identity matrix
*
* @param {mat4} out the receiving matrix
* @returns {mat4} out
*/
function identity$3(out) {
out[0] = 1;
out[1] = 0;
out[2] = 0;
out[3] = 0;
out[4] = 0;
out[5] = 1;
out[6] = 0;
out[7] = 0;
out[8] = 0;
out[9] = 0;
out[10] = 1;
out[11] = 0;
out[12] = 0;
out[13] = 0;
out[14] = 0;
out[15] = 1;
return out;
}

/**
* Transpose the values of a mat4
*
* @param {mat4} out the receiving matrix
* @param {mat4} a the source matrix
* @returns {mat4} out
*/
function transpose$2(out, a) {
// If we are transposing ourselves we can skip a few steps but have to cache some values
if (out === a) {
  var a01 = a[1],
      a02 = a[2],
      a03 = a[3];
  var a12 = a[6],
      a13 = a[7];
  var a23 = a[11];

  out[1] = a[4];
  out[2] = a[8];
  out[3] = a[12];
  out[4] = a01;
  out[6] = a[9];
  out[7] = a[13];
  out[8] = a02;
  out[9] = a12;
  out[11] = a[14];
  out[12] = a03;
  out[13] = a13;
  out[14] = a23;
} else {
  out[0] = a[0];
  out[1] = a[4];
  out[2] = a[8];
  out[3] = a[12];
  out[4] = a[1];
  out[5] = a[5];
  out[6] = a[9];
  out[7] = a[13];
  out[8] = a[2];
  out[9] = a[6];
  out[10] = a[10];
  out[11] = a[14];
  out[12] = a[3];
  out[13] = a[7];
  out[14] = a[11];
  out[15] = a[15];
}

return out;
}

/**
* Inverts a mat4
*
* @param {mat4} out the receiving matrix
* @param {mat4} a the source matrix
* @returns {mat4} out
*/
function invert$3(out, a) {
var a00 = a[0],
    a01 = a[1],
    a02 = a[2],
    a03 = a[3];
var a10 = a[4],
    a11 = a[5],
    a12 = a[6],
    a13 = a[7];
var a20 = a[8],
    a21 = a[9],
    a22 = a[10],
    a23 = a[11];
var a30 = a[12],
    a31 = a[13],
    a32 = a[14],
    a33 = a[15];

var b00 = a00 * a11 - a01 * a10;
var b01 = a00 * a12 - a02 * a10;
var b02 = a00 * a13 - a03 * a10;
var b03 = a01 * a12 - a02 * a11;
var b04 = a01 * a13 - a03 * a11;
var b05 = a02 * a13 - a03 * a12;
var b06 = a20 * a31 - a21 * a30;
var b07 = a20 * a32 - a22 * a30;
var b08 = a20 * a33 - a23 * a30;
var b09 = a21 * a32 - a22 * a31;
var b10 = a21 * a33 - a23 * a31;
var b11 = a22 * a33 - a23 * a32;

// Calculate the determinant
var det = b00 * b11 - b01 * b10 + b02 * b09 + b03 * b08 - b04 * b07 + b05 * b06;

if (!det) {
  return null;
}
det = 1.0 / det;

out[0] = (a11 * b11 - a12 * b10 + a13 * b09) * det;
out[1] = (a02 * b10 - a01 * b11 - a03 * b09) * det;
out[2] = (a31 * b05 - a32 * b04 + a33 * b03) * det;
out[3] = (a22 * b04 - a21 * b05 - a23 * b03) * det;
out[4] = (a12 * b08 - a10 * b11 - a13 * b07) * det;
out[5] = (a00 * b11 - a02 * b08 + a03 * b07) * det;
out[6] = (a32 * b02 - a30 * b05 - a33 * b01) * det;
out[7] = (a20 * b05 - a22 * b02 + a23 * b01) * det;
out[8] = (a10 * b10 - a11 * b08 + a13 * b06) * det;
out[9] = (a01 * b08 - a00 * b10 - a03 * b06) * det;
out[10] = (a30 * b04 - a31 * b02 + a33 * b00) * det;
out[11] = (a21 * b02 - a20 * b04 - a23 * b00) * det;
out[12] = (a11 * b07 - a10 * b09 - a12 * b06) * det;
out[13] = (a00 * b09 - a01 * b07 + a02 * b06) * det;
out[14] = (a31 * b01 - a30 * b03 - a32 * b00) * det;
out[15] = (a20 * b03 - a21 * b01 + a22 * b00) * det;

return out;
}

/**
* Calculates the adjugate of a mat4
*
* @param {mat4} out the receiving matrix
* @param {mat4} a the source matrix
* @returns {mat4} out
*/
function adjoint$2(out, a) {
var a00 = a[0],
    a01 = a[1],
    a02 = a[2],
    a03 = a[3];
var a10 = a[4],
    a11 = a[5],
    a12 = a[6],
    a13 = a[7];
var a20 = a[8],
    a21 = a[9],
    a22 = a[10],
    a23 = a[11];
var a30 = a[12],
    a31 = a[13],
    a32 = a[14],
    a33 = a[15];

out[0] = a11 * (a22 * a33 - a23 * a32) - a21 * (a12 * a33 - a13 * a32) + a31 * (a12 * a23 - a13 * a22);
out[1] = -(a01 * (a22 * a33 - a23 * a32) - a21 * (a02 * a33 - a03 * a32) + a31 * (a02 * a23 - a03 * a22));
out[2] = a01 * (a12 * a33 - a13 * a32) - a11 * (a02 * a33 - a03 * a32) + a31 * (a02 * a13 - a03 * a12);
out[3] = -(a01 * (a12 * a23 - a13 * a22) - a11 * (a02 * a23 - a03 * a22) + a21 * (a02 * a13 - a03 * a12));
out[4] = -(a10 * (a22 * a33 - a23 * a32) - a20 * (a12 * a33 - a13 * a32) + a30 * (a12 * a23 - a13 * a22));
out[5] = a00 * (a22 * a33 - a23 * a32) - a20 * (a02 * a33 - a03 * a32) + a30 * (a02 * a23 - a03 * a22);
out[6] = -(a00 * (a12 * a33 - a13 * a32) - a10 * (a02 * a33 - a03 * a32) + a30 * (a02 * a13 - a03 * a12));
out[7] = a00 * (a12 * a23 - a13 * a22) - a10 * (a02 * a23 - a03 * a22) + a20 * (a02 * a13 - a03 * a12);
out[8] = a10 * (a21 * a33 - a23 * a31) - a20 * (a11 * a33 - a13 * a31) + a30 * (a11 * a23 - a13 * a21);
out[9] = -(a00 * (a21 * a33 - a23 * a31) - a20 * (a01 * a33 - a03 * a31) + a30 * (a01 * a23 - a03 * a21));
out[10] = a00 * (a11 * a33 - a13 * a31) - a10 * (a01 * a33 - a03 * a31) + a30 * (a01 * a13 - a03 * a11);
out[11] = -(a00 * (a11 * a23 - a13 * a21) - a10 * (a01 * a23 - a03 * a21) + a20 * (a01 * a13 - a03 * a11));
out[12] = -(a10 * (a21 * a32 - a22 * a31) - a20 * (a11 * a32 - a12 * a31) + a30 * (a11 * a22 - a12 * a21));
out[13] = a00 * (a21 * a32 - a22 * a31) - a20 * (a01 * a32 - a02 * a31) + a30 * (a01 * a22 - a02 * a21);
out[14] = -(a00 * (a11 * a32 - a12 * a31) - a10 * (a01 * a32 - a02 * a31) + a30 * (a01 * a12 - a02 * a11));
out[15] = a00 * (a11 * a22 - a12 * a21) - a10 * (a01 * a22 - a02 * a21) + a20 * (a01 * a12 - a02 * a11);
return out;
}

/**
* Calculates the determinant of a mat4
*
* @param {mat4} a the source matrix
* @returns {Number} determinant of a
*/
function determinant$3(a) {
var a00 = a[0],
    a01 = a[1],
    a02 = a[2],
    a03 = a[3];
var a10 = a[4],
    a11 = a[5],
    a12 = a[6],
    a13 = a[7];
var a20 = a[8],
    a21 = a[9],
    a22 = a[10],
    a23 = a[11];
var a30 = a[12],
    a31 = a[13],
    a32 = a[14],
    a33 = a[15];

var b00 = a00 * a11 - a01 * a10;
var b01 = a00 * a12 - a02 * a10;
var b02 = a00 * a13 - a03 * a10;
var b03 = a01 * a12 - a02 * a11;
var b04 = a01 * a13 - a03 * a11;
var b05 = a02 * a13 - a03 * a12;
var b06 = a20 * a31 - a21 * a30;
var b07 = a20 * a32 - a22 * a30;
var b08 = a20 * a33 - a23 * a30;
var b09 = a21 * a32 - a22 * a31;
var b10 = a21 * a33 - a23 * a31;
var b11 = a22 * a33 - a23 * a32;

// Calculate the determinant
return b00 * b11 - b01 * b10 + b02 * b09 + b03 * b08 - b04 * b07 + b05 * b06;
}

/**
* Multiplies two mat4s
*
* @param {mat4} out the receiving matrix
* @param {mat4} a the first operand
* @param {mat4} b the second operand
* @returns {mat4} out
*/
function multiply$4(out, a, b) {
var a00 = a[0],
    a01 = a[1],
    a02 = a[2],
    a03 = a[3];
var a10 = a[4],
    a11 = a[5],
    a12 = a[6],
    a13 = a[7];
var a20 = a[8],
    a21 = a[9],
    a22 = a[10],
    a23 = a[11];
var a30 = a[12],
    a31 = a[13],
    a32 = a[14],
    a33 = a[15];

// Cache only the current line of the second matrix
var b0 = b[0],
    b1 = b[1],
    b2 = b[2],
    b3 = b[3];
out[0] = b0 * a00 + b1 * a10 + b2 * a20 + b3 * a30;
out[1] = b0 * a01 + b1 * a11 + b2 * a21 + b3 * a31;
out[2] = b0 * a02 + b1 * a12 + b2 * a22 + b3 * a32;
out[3] = b0 * a03 + b1 * a13 + b2 * a23 + b3 * a33;

b0 = b[4];b1 = b[5];b2 = b[6];b3 = b[7];
out[4] = b0 * a00 + b1 * a10 + b2 * a20 + b3 * a30;
out[5] = b0 * a01 + b1 * a11 + b2 * a21 + b3 * a31;
out[6] = b0 * a02 + b1 * a12 + b2 * a22 + b3 * a32;
out[7] = b0 * a03 + b1 * a13 + b2 * a23 + b3 * a33;

b0 = b[8];b1 = b[9];b2 = b[10];b3 = b[11];
out[8] = b0 * a00 + b1 * a10 + b2 * a20 + b3 * a30;
out[9] = b0 * a01 + b1 * a11 + b2 * a21 + b3 * a31;
out[10] = b0 * a02 + b1 * a12 + b2 * a22 + b3 * a32;
out[11] = b0 * a03 + b1 * a13 + b2 * a23 + b3 * a33;

b0 = b[12];b1 = b[13];b2 = b[14];b3 = b[15];
out[12] = b0 * a00 + b1 * a10 + b2 * a20 + b3 * a30;
out[13] = b0 * a01 + b1 * a11 + b2 * a21 + b3 * a31;
out[14] = b0 * a02 + b1 * a12 + b2 * a22 + b3 * a32;
out[15] = b0 * a03 + b1 * a13 + b2 * a23 + b3 * a33;
return out;
}

/**
* Translate a mat4 by the given vector
*
* @param {mat4} out the receiving matrix
* @param {mat4} a the matrix to translate
* @param {vec3} v vector to translate by
* @returns {mat4} out
*/
function translate$2(out, a, v) {
var x = v[0],
    y = v[1],
    z = v[2];
var a00 = void 0,
    a01 = void 0,
    a02 = void 0,
    a03 = void 0;
var a10 = void 0,
    a11 = void 0,
    a12 = void 0,
    a13 = void 0;
var a20 = void 0,
    a21 = void 0,
    a22 = void 0,
    a23 = void 0;

if (a === out) {
  out[12] = a[0] * x + a[4] * y + a[8] * z + a[12];
  out[13] = a[1] * x + a[5] * y + a[9] * z + a[13];
  out[14] = a[2] * x + a[6] * y + a[10] * z + a[14];
  out[15] = a[3] * x + a[7] * y + a[11] * z + a[15];
} else {
  a00 = a[0];a01 = a[1];a02 = a[2];a03 = a[3];
  a10 = a[4];a11 = a[5];a12 = a[6];a13 = a[7];
  a20 = a[8];a21 = a[9];a22 = a[10];a23 = a[11];

  out[0] = a00;out[1] = a01;out[2] = a02;out[3] = a03;
  out[4] = a10;out[5] = a11;out[6] = a12;out[7] = a13;
  out[8] = a20;out[9] = a21;out[10] = a22;out[11] = a23;

  out[12] = a00 * x + a10 * y + a20 * z + a[12];
  out[13] = a01 * x + a11 * y + a21 * z + a[13];
  out[14] = a02 * x + a12 * y + a22 * z + a[14];
  out[15] = a03 * x + a13 * y + a23 * z + a[15];
}

return out;
}

/**
* Scales the mat4 by the dimensions in the given vec3 not using vectorization
*
* @param {mat4} out the receiving matrix
* @param {mat4} a the matrix to scale
* @param {vec3} v the vec3 to scale the matrix by
* @returns {mat4} out
**/
function scale$3(out, a, v) {
var x = v[0],
    y = v[1],
    z = v[2];

out[0] = a[0] * x;
out[1] = a[1] * x;
out[2] = a[2] * x;
out[3] = a[3] * x;
out[4] = a[4] * y;
out[5] = a[5] * y;
out[6] = a[6] * y;
out[7] = a[7] * y;
out[8] = a[8] * z;
out[9] = a[9] * z;
out[10] = a[10] * z;
out[11] = a[11] * z;
out[12] = a[12];
out[13] = a[13];
out[14] = a[14];
out[15] = a[15];
return out;
}

/**
* Rotates a mat4 by the given angle around the given axis
*
* @param {mat4} out the receiving matrix
* @param {mat4} a the matrix to rotate
* @param {Number} rad the angle to rotate the matrix by
* @param {vec3} axis the axis to rotate around
* @returns {mat4} out
*/
function rotate$3(out, a, rad, axis) {
var x = axis[0],
    y = axis[1],
    z = axis[2];
var len = Math.sqrt(x * x + y * y + z * z);
var s = void 0,
    c = void 0,
    t = void 0;
var a00 = void 0,
    a01 = void 0,
    a02 = void 0,
    a03 = void 0;
var a10 = void 0,
    a11 = void 0,
    a12 = void 0,
    a13 = void 0;
var a20 = void 0,
    a21 = void 0,
    a22 = void 0,
    a23 = void 0;
var b00 = void 0,
    b01 = void 0,
    b02 = void 0;
var b10 = void 0,
    b11 = void 0,
    b12 = void 0;
var b20 = void 0,
    b21 = void 0,
    b22 = void 0;

if (Math.abs(len) < EPSILON) {
  return null;
}

len = 1 / len;
x *= len;
y *= len;
z *= len;

s = Math.sin(rad);
c = Math.cos(rad);
t = 1 - c;

a00 = a[0];a01 = a[1];a02 = a[2];a03 = a[3];
a10 = a[4];a11 = a[5];a12 = a[6];a13 = a[7];
a20 = a[8];a21 = a[9];a22 = a[10];a23 = a[11];

// Construct the elements of the rotation matrix
b00 = x * x * t + c;b01 = y * x * t + z * s;b02 = z * x * t - y * s;
b10 = x * y * t - z * s;b11 = y * y * t + c;b12 = z * y * t + x * s;
b20 = x * z * t + y * s;b21 = y * z * t - x * s;b22 = z * z * t + c;

// Perform rotation-specific matrix multiplication
out[0] = a00 * b00 + a10 * b01 + a20 * b02;
out[1] = a01 * b00 + a11 * b01 + a21 * b02;
out[2] = a02 * b00 + a12 * b01 + a22 * b02;
out[3] = a03 * b00 + a13 * b01 + a23 * b02;
out[4] = a00 * b10 + a10 * b11 + a20 * b12;
out[5] = a01 * b10 + a11 * b11 + a21 * b12;
out[6] = a02 * b10 + a12 * b11 + a22 * b12;
out[7] = a03 * b10 + a13 * b11 + a23 * b12;
out[8] = a00 * b20 + a10 * b21 + a20 * b22;
out[9] = a01 * b20 + a11 * b21 + a21 * b22;
out[10] = a02 * b20 + a12 * b21 + a22 * b22;
out[11] = a03 * b20 + a13 * b21 + a23 * b22;

if (a !== out) {
  // If the source and destination differ, copy the unchanged last row
  out[12] = a[12];
  out[13] = a[13];
  out[14] = a[14];
  out[15] = a[15];
}
return out;
}

/**
* Rotates a matrix by the given angle around the X axis
*
* @param {mat4} out the receiving matrix
* @param {mat4} a the matrix to rotate
* @param {Number} rad the angle to rotate the matrix by
* @returns {mat4} out
*/
function rotateX(out, a, rad) {
var s = Math.sin(rad);
var c = Math.cos(rad);
var a10 = a[4];
var a11 = a[5];
var a12 = a[6];
var a13 = a[7];
var a20 = a[8];
var a21 = a[9];
var a22 = a[10];
var a23 = a[11];

if (a !== out) {
  // If the source and destination differ, copy the unchanged rows
  out[0] = a[0];
  out[1] = a[1];
  out[2] = a[2];
  out[3] = a[3];
  out[12] = a[12];
  out[13] = a[13];
  out[14] = a[14];
  out[15] = a[15];
}

// Perform axis-specific matrix multiplication
out[4] = a10 * c + a20 * s;
out[5] = a11 * c + a21 * s;
out[6] = a12 * c + a22 * s;
out[7] = a13 * c + a23 * s;
out[8] = a20 * c - a10 * s;
out[9] = a21 * c - a11 * s;
out[10] = a22 * c - a12 * s;
out[11] = a23 * c - a13 * s;
return out;
}

/**
* Rotates a matrix by the given angle around the Y axis
*
* @param {mat4} out the receiving matrix
* @param {mat4} a the matrix to rotate
* @param {Number} rad the angle to rotate the matrix by
* @returns {mat4} out
*/
function rotateY(out, a, rad) {
var s = Math.sin(rad);
var c = Math.cos(rad);
var a00 = a[0];
var a01 = a[1];
var a02 = a[2];
var a03 = a[3];
var a20 = a[8];
var a21 = a[9];
var a22 = a[10];
var a23 = a[11];

if (a !== out) {
  // If the source and destination differ, copy the unchanged rows
  out[4] = a[4];
  out[5] = a[5];
  out[6] = a[6];
  out[7] = a[7];
  out[12] = a[12];
  out[13] = a[13];
  out[14] = a[14];
  out[15] = a[15];
}

// Perform axis-specific matrix multiplication
out[0] = a00 * c - a20 * s;
out[1] = a01 * c - a21 * s;
out[2] = a02 * c - a22 * s;
out[3] = a03 * c - a23 * s;
out[8] = a00 * s + a20 * c;
out[9] = a01 * s + a21 * c;
out[10] = a02 * s + a22 * c;
out[11] = a03 * s + a23 * c;
return out;
}

/**
* Rotates a matrix by the given angle around the Z axis
*
* @param {mat4} out the receiving matrix
* @param {mat4} a the matrix to rotate
* @param {Number} rad the angle to rotate the matrix by
* @returns {mat4} out
*/
function rotateZ(out, a, rad) {
var s = Math.sin(rad);
var c = Math.cos(rad);
var a00 = a[0];
var a01 = a[1];
var a02 = a[2];
var a03 = a[3];
var a10 = a[4];
var a11 = a[5];
var a12 = a[6];
var a13 = a[7];

if (a !== out) {
  // If the source and destination differ, copy the unchanged last row
  out[8] = a[8];
  out[9] = a[9];
  out[10] = a[10];
  out[11] = a[11];
  out[12] = a[12];
  out[13] = a[13];
  out[14] = a[14];
  out[15] = a[15];
}

// Perform axis-specific matrix multiplication
out[0] = a00 * c + a10 * s;
out[1] = a01 * c + a11 * s;
out[2] = a02 * c + a12 * s;
out[3] = a03 * c + a13 * s;
out[4] = a10 * c - a00 * s;
out[5] = a11 * c - a01 * s;
out[6] = a12 * c - a02 * s;
out[7] = a13 * c - a03 * s;
return out;
}

/**
* Creates a matrix from a vector translation
* This is equivalent to (but much faster than):
*
*     mat4.identity(dest);
*     mat4.translate(dest, dest, vec);
*
* @param {mat4} out mat4 receiving operation result
* @param {vec3} v Translation vector
* @returns {mat4} out
*/
function fromTranslation$2(out, v) {
out[0] = 1;
out[1] = 0;
out[2] = 0;
out[3] = 0;
out[4] = 0;
out[5] = 1;
out[6] = 0;
out[7] = 0;
out[8] = 0;
out[9] = 0;
out[10] = 1;
out[11] = 0;
out[12] = v[0];
out[13] = v[1];
out[14] = v[2];
out[15] = 1;
return out;
}

/**
* Creates a matrix from a vector scaling
* This is equivalent to (but much faster than):
*
*     mat4.identity(dest);
*     mat4.scale(dest, dest, vec);
*
* @param {mat4} out mat4 receiving operation result
* @param {vec3} v Scaling vector
* @returns {mat4} out
*/
function fromScaling$3(out, v) {
out[0] = v[0];
out[1] = 0;
out[2] = 0;
out[3] = 0;
out[4] = 0;
out[5] = v[1];
out[6] = 0;
out[7] = 0;
out[8] = 0;
out[9] = 0;
out[10] = v[2];
out[11] = 0;
out[12] = 0;
out[13] = 0;
out[14] = 0;
out[15] = 1;
return out;
}

/**
* Creates a matrix from a given angle around a given axis
* This is equivalent to (but much faster than):
*
*     mat4.identity(dest);
*     mat4.rotate(dest, dest, rad, axis);
*
* @param {mat4} out mat4 receiving operation result
* @param {Number} rad the angle to rotate the matrix by
* @param {vec3} axis the axis to rotate around
* @returns {mat4} out
*/
function fromRotation$3(out, rad, axis) {
var x = axis[0],
    y = axis[1],
    z = axis[2];
var len = Math.sqrt(x * x + y * y + z * z);
var s = void 0,
    c = void 0,
    t = void 0;

if (Math.abs(len) < EPSILON) {
  return null;
}

len = 1 / len;
x *= len;
y *= len;
z *= len;

s = Math.sin(rad);
c = Math.cos(rad);
t = 1 - c;

// Perform rotation-specific matrix multiplication
out[0] = x * x * t + c;
out[1] = y * x * t + z * s;
out[2] = z * x * t - y * s;
out[3] = 0;
out[4] = x * y * t - z * s;
out[5] = y * y * t + c;
out[6] = z * y * t + x * s;
out[7] = 0;
out[8] = x * z * t + y * s;
out[9] = y * z * t - x * s;
out[10] = z * z * t + c;
out[11] = 0;
out[12] = 0;
out[13] = 0;
out[14] = 0;
out[15] = 1;
return out;
}

/**
* Creates a matrix from the given angle around the X axis
* This is equivalent to (but much faster than):
*
*     mat4.identity(dest);
*     mat4.rotateX(dest, dest, rad);
*
* @param {mat4} out mat4 receiving operation result
* @param {Number} rad the angle to rotate the matrix by
* @returns {mat4} out
*/
function fromXRotation(out, rad) {
var s = Math.sin(rad);
var c = Math.cos(rad);

// Perform axis-specific matrix multiplication
out[0] = 1;
out[1] = 0;
out[2] = 0;
out[3] = 0;
out[4] = 0;
out[5] = c;
out[6] = s;
out[7] = 0;
out[8] = 0;
out[9] = -s;
out[10] = c;
out[11] = 0;
out[12] = 0;
out[13] = 0;
out[14] = 0;
out[15] = 1;
return out;
}

/**
* Creates a matrix from the given angle around the Y axis
* This is equivalent to (but much faster than):
*
*     mat4.identity(dest);
*     mat4.rotateY(dest, dest, rad);
*
* @param {mat4} out mat4 receiving operation result
* @param {Number} rad the angle to rotate the matrix by
* @returns {mat4} out
*/
function fromYRotation(out, rad) {
var s = Math.sin(rad);
var c = Math.cos(rad);

// Perform axis-specific matrix multiplication
out[0] = c;
out[1] = 0;
out[2] = -s;
out[3] = 0;
out[4] = 0;
out[5] = 1;
out[6] = 0;
out[7] = 0;
out[8] = s;
out[9] = 0;
out[10] = c;
out[11] = 0;
out[12] = 0;
out[13] = 0;
out[14] = 0;
out[15] = 1;
return out;
}

/**
* Creates a matrix from the given angle around the Z axis
* This is equivalent to (but much faster than):
*
*     mat4.identity(dest);
*     mat4.rotateZ(dest, dest, rad);
*
* @param {mat4} out mat4 receiving operation result
* @param {Number} rad the angle to rotate the matrix by
* @returns {mat4} out
*/
function fromZRotation(out, rad) {
var s = Math.sin(rad);
var c = Math.cos(rad);

// Perform axis-specific matrix multiplication
out[0] = c;
out[1] = s;
out[2] = 0;
out[3] = 0;
out[4] = -s;
out[5] = c;
out[6] = 0;
out[7] = 0;
out[8] = 0;
out[9] = 0;
out[10] = 1;
out[11] = 0;
out[12] = 0;
out[13] = 0;
out[14] = 0;
out[15] = 1;
return out;
}

/**
* Creates a matrix from a quaternion rotation and vector translation
* This is equivalent to (but much faster than):
*
*     mat4.identity(dest);
*     mat4.translate(dest, vec);
*     let quatMat = mat4.create();
*     quat4.toMat4(quat, quatMat);
*     mat4.multiply(dest, quatMat);
*
* @param {mat4} out mat4 receiving operation result
* @param {quat4} q Rotation quaternion
* @param {vec3} v Translation vector
* @returns {mat4} out
*/
function fromRotationTranslation(out, q, v) {
// Quaternion math
var x = q[0],
    y = q[1],
    z = q[2],
    w = q[3];
var x2 = x + x;
var y2 = y + y;
var z2 = z + z;

var xx = x * x2;
var xy = x * y2;
var xz = x * z2;
var yy = y * y2;
var yz = y * z2;
var zz = z * z2;
var wx = w * x2;
var wy = w * y2;
var wz = w * z2;

out[0] = 1 - (yy + zz);
out[1] = xy + wz;
out[2] = xz - wy;
out[3] = 0;
out[4] = xy - wz;
out[5] = 1 - (xx + zz);
out[6] = yz + wx;
out[7] = 0;
out[8] = xz + wy;
out[9] = yz - wx;
out[10] = 1 - (xx + yy);
out[11] = 0;
out[12] = v[0];
out[13] = v[1];
out[14] = v[2];
out[15] = 1;

return out;
}

/**
* Creates a new mat4 from a dual quat.
*
* @param {mat4} out Matrix
* @param {quat2} a Dual Quaternion
* @returns {mat4} mat4 receiving operation result
*/
function fromQuat2(out, a) {
var translation = new ARRAY_TYPE(3);
var bx = -a[0],
    by = -a[1],
    bz = -a[2],
    bw = a[3],
    ax = a[4],
    ay = a[5],
    az = a[6],
    aw = a[7];

var magnitude = bx * bx + by * by + bz * bz + bw * bw;
//Only scale if it makes sense
if (magnitude > 0) {
  translation[0] = (ax * bw + aw * bx + ay * bz - az * by) * 2 / magnitude;
  translation[1] = (ay * bw + aw * by + az * bx - ax * bz) * 2 / magnitude;
  translation[2] = (az * bw + aw * bz + ax * by - ay * bx) * 2 / magnitude;
} else {
  translation[0] = (ax * bw + aw * bx + ay * bz - az * by) * 2;
  translation[1] = (ay * bw + aw * by + az * bx - ax * bz) * 2;
  translation[2] = (az * bw + aw * bz + ax * by - ay * bx) * 2;
}
fromRotationTranslation(out, a, translation);
return out;
}

/**
* Returns the translation vector component of a transformation
*  matrix. If a matrix is built with fromRotationTranslation,
*  the returned vector will be the same as the translation vector
*  originally supplied.
* @param  {vec3} out Vector to receive translation component
* @param  {mat4} mat Matrix to be decomposed (input)
* @return {vec3} out
*/
function getTranslation(out, mat) {
out[0] = mat[12];
out[1] = mat[13];
out[2] = mat[14];

return out;
}

/**
* Returns the scaling factor component of a transformation
*  matrix. If a matrix is built with fromRotationTranslationScale
*  with a normalized Quaternion paramter, the returned vector will be
*  the same as the scaling vector
*  originally supplied.
* @param  {vec3} out Vector to receive scaling factor component
* @param  {mat4} mat Matrix to be decomposed (input)
* @return {vec3} out
*/
function getScaling(out, mat) {
var m11 = mat[0];
var m12 = mat[1];
var m13 = mat[2];
var m21 = mat[4];
var m22 = mat[5];
var m23 = mat[6];
var m31 = mat[8];
var m32 = mat[9];
var m33 = mat[10];

out[0] = Math.sqrt(m11 * m11 + m12 * m12 + m13 * m13);
out[1] = Math.sqrt(m21 * m21 + m22 * m22 + m23 * m23);
out[2] = Math.sqrt(m31 * m31 + m32 * m32 + m33 * m33);

return out;
}

/**
* Returns a quaternion representing the rotational component
*  of a transformation matrix. If a matrix is built with
*  fromRotationTranslation, the returned quaternion will be the
*  same as the quaternion originally supplied.
* @param {quat} out Quaternion to receive the rotation component
* @param {mat4} mat Matrix to be decomposed (input)
* @return {quat} out
*/
function getRotation(out, mat) {
// Algorithm taken from http://www.euclideanspace.com/maths/geometry/rotations/conversions/matrixToQuaternion/index.htm
var trace = mat[0] + mat[5] + mat[10];
var S = 0;

if (trace > 0) {
  S = Math.sqrt(trace + 1.0) * 2;
  out[3] = 0.25 * S;
  out[0] = (mat[6] - mat[9]) / S;
  out[1] = (mat[8] - mat[2]) / S;
  out[2] = (mat[1] - mat[4]) / S;
} else if (mat[0] > mat[5] && mat[0] > mat[10]) {
  S = Math.sqrt(1.0 + mat[0] - mat[5] - mat[10]) * 2;
  out[3] = (mat[6] - mat[9]) / S;
  out[0] = 0.25 * S;
  out[1] = (mat[1] + mat[4]) / S;
  out[2] = (mat[8] + mat[2]) / S;
} else if (mat[5] > mat[10]) {
  S = Math.sqrt(1.0 + mat[5] - mat[0] - mat[10]) * 2;
  out[3] = (mat[8] - mat[2]) / S;
  out[0] = (mat[1] + mat[4]) / S;
  out[1] = 0.25 * S;
  out[2] = (mat[6] + mat[9]) / S;
} else {
  S = Math.sqrt(1.0 + mat[10] - mat[0] - mat[5]) * 2;
  out[3] = (mat[1] - mat[4]) / S;
  out[0] = (mat[8] + mat[2]) / S;
  out[1] = (mat[6] + mat[9]) / S;
  out[2] = 0.25 * S;
}

return out;
}

/**
* Creates a matrix from a quaternion rotation, vector translation and vector scale
* This is equivalent to (but much faster than):
*
*     mat4.identity(dest);
*     mat4.translate(dest, vec);
*     let quatMat = mat4.create();
*     quat4.toMat4(quat, quatMat);
*     mat4.multiply(dest, quatMat);
*     mat4.scale(dest, scale)
*
* @param {mat4} out mat4 receiving operation result
* @param {quat4} q Rotation quaternion
* @param {vec3} v Translation vector
* @param {vec3} s Scaling vector
* @returns {mat4} out
*/
function fromRotationTranslationScale(out, q, v, s) {
// Quaternion math
var x = q[0],
    y = q[1],
    z = q[2],
    w = q[3];
var x2 = x + x;
var y2 = y + y;
var z2 = z + z;

var xx = x * x2;
var xy = x * y2;
var xz = x * z2;
var yy = y * y2;
var yz = y * z2;
var zz = z * z2;
var wx = w * x2;
var wy = w * y2;
var wz = w * z2;
var sx = s[0];
var sy = s[1];
var sz = s[2];

out[0] = (1 - (yy + zz)) * sx;
out[1] = (xy + wz) * sx;
out[2] = (xz - wy) * sx;
out[3] = 0;
out[4] = (xy - wz) * sy;
out[5] = (1 - (xx + zz)) * sy;
out[6] = (yz + wx) * sy;
out[7] = 0;
out[8] = (xz + wy) * sz;
out[9] = (yz - wx) * sz;
out[10] = (1 - (xx + yy)) * sz;
out[11] = 0;
out[12] = v[0];
out[13] = v[1];
out[14] = v[2];
out[15] = 1;

return out;
}

/**
* Creates a matrix from a quaternion rotation, vector translation and vector scale, rotating and scaling around the given origin
* This is equivalent to (but much faster than):
*
*     mat4.identity(dest);
*     mat4.translate(dest, vec);
*     mat4.translate(dest, origin);
*     let quatMat = mat4.create();
*     quat4.toMat4(quat, quatMat);
*     mat4.multiply(dest, quatMat);
*     mat4.scale(dest, scale)
*     mat4.translate(dest, negativeOrigin);
*
* @param {mat4} out mat4 receiving operation result
* @param {quat4} q Rotation quaternion
* @param {vec3} v Translation vector
* @param {vec3} s Scaling vector
* @param {vec3} o The origin vector around which to scale and rotate
* @returns {mat4} out
*/
function fromRotationTranslationScaleOrigin(out, q, v, s, o) {
// Quaternion math
var x = q[0],
    y = q[1],
    z = q[2],
    w = q[3];
var x2 = x + x;
var y2 = y + y;
var z2 = z + z;

var xx = x * x2;
var xy = x * y2;
var xz = x * z2;
var yy = y * y2;
var yz = y * z2;
var zz = z * z2;
var wx = w * x2;
var wy = w * y2;
var wz = w * z2;

var sx = s[0];
var sy = s[1];
var sz = s[2];

var ox = o[0];
var oy = o[1];
var oz = o[2];

var out0 = (1 - (yy + zz)) * sx;
var out1 = (xy + wz) * sx;
var out2 = (xz - wy) * sx;
var out4 = (xy - wz) * sy;
var out5 = (1 - (xx + zz)) * sy;
var out6 = (yz + wx) * sy;
var out8 = (xz + wy) * sz;
var out9 = (yz - wx) * sz;
var out10 = (1 - (xx + yy)) * sz;

out[0] = out0;
out[1] = out1;
out[2] = out2;
out[3] = 0;
out[4] = out4;
out[5] = out5;
out[6] = out6;
out[7] = 0;
out[8] = out8;
out[9] = out9;
out[10] = out10;
out[11] = 0;
out[12] = v[0] + ox - (out0 * ox + out4 * oy + out8 * oz);
out[13] = v[1] + oy - (out1 * ox + out5 * oy + out9 * oz);
out[14] = v[2] + oz - (out2 * ox + out6 * oy + out10 * oz);
out[15] = 1;

return out;
}

/**
* Calculates a 4x4 matrix from the given quaternion
*
* @param {mat4} out mat4 receiving operation result
* @param {quat} q Quaternion to create matrix from
*
* @returns {mat4} out
*/
function fromQuat$1(out, q) {
var x = q[0],
    y = q[1],
    z = q[2],
    w = q[3];
var x2 = x + x;
var y2 = y + y;
var z2 = z + z;

var xx = x * x2;
var yx = y * x2;
var yy = y * y2;
var zx = z * x2;
var zy = z * y2;
var zz = z * z2;
var wx = w * x2;
var wy = w * y2;
var wz = w * z2;

out[0] = 1 - yy - zz;
out[1] = yx + wz;
out[2] = zx - wy;
out[3] = 0;

out[4] = yx - wz;
out[5] = 1 - xx - zz;
out[6] = zy + wx;
out[7] = 0;

out[8] = zx + wy;
out[9] = zy - wx;
out[10] = 1 - xx - yy;
out[11] = 0;

out[12] = 0;
out[13] = 0;
out[14] = 0;
out[15] = 1;

return out;
}

/**
* Generates a frustum matrix with the given bounds
*
* @param {mat4} out mat4 frustum matrix will be written into
* @param {Number} left Left bound of the frustum
* @param {Number} right Right bound of the frustum
* @param {Number} bottom Bottom bound of the frustum
* @param {Number} top Top bound of the frustum
* @param {Number} near Near bound of the frustum
* @param {Number} far Far bound of the frustum
* @returns {mat4} out
*/
function frustum(out, left, right, bottom, top, near, far) {
var rl = 1 / (right - left);
var tb = 1 / (top - bottom);
var nf = 1 / (near - far);
out[0] = near * 2 * rl;
out[1] = 0;
out[2] = 0;
out[3] = 0;
out[4] = 0;
out[5] = near * 2 * tb;
out[6] = 0;
out[7] = 0;
out[8] = (right + left) * rl;
out[9] = (top + bottom) * tb;
out[10] = (far + near) * nf;
out[11] = -1;
out[12] = 0;
out[13] = 0;
out[14] = far * near * 2 * nf;
out[15] = 0;
return out;
}

/**
* Generates a perspective projection matrix with the given bounds
*
* @param {mat4} out mat4 frustum matrix will be written into
* @param {number} fovy Vertical field of view in radians
* @param {number} aspect Aspect ratio. typically viewport width/height
* @param {number} near Near bound of the frustum
* @param {number} far Far bound of the frustum
* @returns {mat4} out
*/
function perspective(out, fovy, aspect, near, far) {
var f = 1.0 / Math.tan(fovy / 2);
var nf = 1 / (near - far);
out[0] = f / aspect;
out[1] = 0;
out[2] = 0;
out[3] = 0;
out[4] = 0;
out[5] = f;
out[6] = 0;
out[7] = 0;
out[8] = 0;
out[9] = 0;
out[10] = (far + near) * nf;
out[11] = -1;
out[12] = 0;
out[13] = 0;
out[14] = 2 * far * near * nf;
out[15] = 0;
return out;
}

/**
* Generates a perspective projection matrix with the given field of view.
* This is primarily useful for generating projection matrices to be used
* with the still experiemental WebVR API.
*
* @param {mat4} out mat4 frustum matrix will be written into
* @param {Object} fov Object containing the following values: upDegrees, downDegrees, leftDegrees, rightDegrees
* @param {number} near Near bound of the frustum
* @param {number} far Far bound of the frustum
* @returns {mat4} out
*/
function perspectiveFromFieldOfView(out, fov, near, far) {
var upTan = Math.tan(fov.upDegrees * Math.PI / 180.0);
var downTan = Math.tan(fov.downDegrees * Math.PI / 180.0);
var leftTan = Math.tan(fov.leftDegrees * Math.PI / 180.0);
var rightTan = Math.tan(fov.rightDegrees * Math.PI / 180.0);
var xScale = 2.0 / (leftTan + rightTan);
var yScale = 2.0 / (upTan + downTan);

out[0] = xScale;
out[1] = 0.0;
out[2] = 0.0;
out[3] = 0.0;
out[4] = 0.0;
out[5] = yScale;
out[6] = 0.0;
out[7] = 0.0;
out[8] = -((leftTan - rightTan) * xScale * 0.5);
out[9] = (upTan - downTan) * yScale * 0.5;
out[10] = far / (near - far);
out[11] = -1.0;
out[12] = 0.0;
out[13] = 0.0;
out[14] = far * near / (near - far);
out[15] = 0.0;
return out;
}

/**
* Generates a orthogonal projection matrix with the given bounds
*
* @param {mat4} out mat4 frustum matrix will be written into
* @param {number} left Left bound of the frustum
* @param {number} right Right bound of the frustum
* @param {number} bottom Bottom bound of the frustum
* @param {number} top Top bound of the frustum
* @param {number} near Near bound of the frustum
* @param {number} far Far bound of the frustum
* @returns {mat4} out
*/
function ortho(out, left, right, bottom, top, near, far) {
var lr = 1 / (left - right);
var bt = 1 / (bottom - top);
var nf = 1 / (near - far);
out[0] = -2 * lr;
out[1] = 0;
out[2] = 0;
out[3] = 0;
out[4] = 0;
out[5] = -2 * bt;
out[6] = 0;
out[7] = 0;
out[8] = 0;
out[9] = 0;
out[10] = 2 * nf;
out[11] = 0;
out[12] = (left + right) * lr;
out[13] = (top + bottom) * bt;
out[14] = (far + near) * nf;
out[15] = 1;
return out;
}

/**
* Generates a look-at matrix with the given eye position, focal point, and up axis. 
* If you want a matrix that actually makes an object look at another object, you should use targetTo instead.
*
* @param {mat4} out mat4 frustum matrix will be written into
* @param {vec3} eye Position of the viewer
* @param {vec3} center Point the viewer is looking at
* @param {vec3} up vec3 pointing up
* @returns {mat4} out
*/
function lookAt(out, eye, center, up) {
var x0 = void 0,
    x1 = void 0,
    x2 = void 0,
    y0 = void 0,
    y1 = void 0,
    y2 = void 0,
    z0 = void 0,
    z1 = void 0,
    z2 = void 0,
    len = void 0;
var eyex = eye[0];
var eyey = eye[1];
var eyez = eye[2];
var upx = up[0];
var upy = up[1];
var upz = up[2];
var centerx = center[0];
var centery = center[1];
var centerz = center[2];

if (Math.abs(eyex - centerx) < EPSILON && Math.abs(eyey - centery) < EPSILON && Math.abs(eyez - centerz) < EPSILON) {
  return identity$3(out);
}

z0 = eyex - centerx;
z1 = eyey - centery;
z2 = eyez - centerz;

len = 1 / Math.sqrt(z0 * z0 + z1 * z1 + z2 * z2);
z0 *= len;
z1 *= len;
z2 *= len;

x0 = upy * z2 - upz * z1;
x1 = upz * z0 - upx * z2;
x2 = upx * z1 - upy * z0;
len = Math.sqrt(x0 * x0 + x1 * x1 + x2 * x2);
if (!len) {
  x0 = 0;
  x1 = 0;
  x2 = 0;
} else {
  len = 1 / len;
  x0 *= len;
  x1 *= len;
  x2 *= len;
}

y0 = z1 * x2 - z2 * x1;
y1 = z2 * x0 - z0 * x2;
y2 = z0 * x1 - z1 * x0;

len = Math.sqrt(y0 * y0 + y1 * y1 + y2 * y2);
if (!len) {
  y0 = 0;
  y1 = 0;
  y2 = 0;
} else {
  len = 1 / len;
  y0 *= len;
  y1 *= len;
  y2 *= len;
}

out[0] = x0;
out[1] = y0;
out[2] = z0;
out[3] = 0;
out[4] = x1;
out[5] = y1;
out[6] = z1;
out[7] = 0;
out[8] = x2;
out[9] = y2;
out[10] = z2;
out[11] = 0;
out[12] = -(x0 * eyex + x1 * eyey + x2 * eyez);
out[13] = -(y0 * eyex + y1 * eyey + y2 * eyez);
out[14] = -(z0 * eyex + z1 * eyey + z2 * eyez);
out[15] = 1;

return out;
}

/**
* Generates a matrix that makes something look at something else.
*
* @param {mat4} out mat4 frustum matrix will be written into
* @param {vec3} eye Position of the viewer
* @param {vec3} center Point the viewer is looking at
* @param {vec3} up vec3 pointing up
* @returns {mat4} out
*/
function targetTo(out, eye, target, up) {
var eyex = eye[0],
    eyey = eye[1],
    eyez = eye[2],
    upx = up[0],
    upy = up[1],
    upz = up[2];

var z0 = eyex - target[0],
    z1 = eyey - target[1],
    z2 = eyez - target[2];

var len = z0 * z0 + z1 * z1 + z2 * z2;
if (len > 0) {
  len = 1 / Math.sqrt(len);
  z0 *= len;
  z1 *= len;
  z2 *= len;
}

var x0 = upy * z2 - upz * z1,
    x1 = upz * z0 - upx * z2,
    x2 = upx * z1 - upy * z0;

len = x0 * x0 + x1 * x1 + x2 * x2;
if (len > 0) {
  len = 1 / Math.sqrt(len);
  x0 *= len;
  x1 *= len;
  x2 *= len;
}

out[0] = x0;
out[1] = x1;
out[2] = x2;
out[3] = 0;
out[4] = z1 * x2 - z2 * x1;
out[5] = z2 * x0 - z0 * x2;
out[6] = z0 * x1 - z1 * x0;
out[7] = 0;
out[8] = z0;
out[9] = z1;
out[10] = z2;
out[11] = 0;
out[12] = eyex;
out[13] = eyey;
out[14] = eyez;
out[15] = 1;
return out;
}

/**
* Returns a string representation of a mat4
*
* @param {mat4} a matrix to represent as a string
* @returns {String} string representation of the matrix
*/
function str$3(a) {
return 'mat4(' + a[0] + ', ' + a[1] + ', ' + a[2] + ', ' + a[3] + ', ' + a[4] + ', ' + a[5] + ', ' + a[6] + ', ' + a[7] + ', ' + a[8] + ', ' + a[9] + ', ' + a[10] + ', ' + a[11] + ', ' + a[12] + ', ' + a[13] + ', ' + a[14] + ', ' + a[15] + ')';
}

/**
* Returns Frobenius norm of a mat4
*
* @param {mat4} a the matrix to calculate Frobenius norm of
* @returns {Number} Frobenius norm
*/
function frob$3(a) {
return Math.sqrt(Math.pow(a[0], 2) + Math.pow(a[1], 2) + Math.pow(a[2], 2) + Math.pow(a[3], 2) + Math.pow(a[4], 2) + Math.pow(a[5], 2) + Math.pow(a[6], 2) + Math.pow(a[7], 2) + Math.pow(a[8], 2) + Math.pow(a[9], 2) + Math.pow(a[10], 2) + Math.pow(a[11], 2) + Math.pow(a[12], 2) + Math.pow(a[13], 2) + Math.pow(a[14], 2) + Math.pow(a[15], 2));
}

/**
* Adds two mat4's
*
* @param {mat4} out the receiving matrix
* @param {mat4} a the first operand
* @param {mat4} b the second operand
* @returns {mat4} out
*/
function add$3(out, a, b) {
out[0] = a[0] + b[0];
out[1] = a[1] + b[1];
out[2] = a[2] + b[2];
out[3] = a[3] + b[3];
out[4] = a[4] + b[4];
out[5] = a[5] + b[5];
out[6] = a[6] + b[6];
out[7] = a[7] + b[7];
out[8] = a[8] + b[8];
out[9] = a[9] + b[9];
out[10] = a[10] + b[10];
out[11] = a[11] + b[11];
out[12] = a[12] + b[12];
out[13] = a[13] + b[13];
out[14] = a[14] + b[14];
out[15] = a[15] + b[15];
return out;
}

/**
* Subtracts matrix b from matrix a
*
* @param {mat4} out the receiving matrix
* @param {mat4} a the first operand
* @param {mat4} b the second operand
* @returns {mat4} out
*/
function subtract$3(out, a, b) {
out[0] = a[0] - b[0];
out[1] = a[1] - b[1];
out[2] = a[2] - b[2];
out[3] = a[3] - b[3];
out[4] = a[4] - b[4];
out[5] = a[5] - b[5];
out[6] = a[6] - b[6];
out[7] = a[7] - b[7];
out[8] = a[8] - b[8];
out[9] = a[9] - b[9];
out[10] = a[10] - b[10];
out[11] = a[11] - b[11];
out[12] = a[12] - b[12];
out[13] = a[13] - b[13];
out[14] = a[14] - b[14];
out[15] = a[15] - b[15];
return out;
}

/**
* Multiply each element of the matrix by a scalar.
*
* @param {mat4} out the receiving matrix
* @param {mat4} a the matrix to scale
* @param {Number} b amount to scale the matrix's elements by
* @returns {mat4} out
*/
function multiplyScalar$3(out, a, b) {
out[0] = a[0] * b;
out[1] = a[1] * b;
out[2] = a[2] * b;
out[3] = a[3] * b;
out[4] = a[4] * b;
out[5] = a[5] * b;
out[6] = a[6] * b;
out[7] = a[7] * b;
out[8] = a[8] * b;
out[9] = a[9] * b;
out[10] = a[10] * b;
out[11] = a[11] * b;
out[12] = a[12] * b;
out[13] = a[13] * b;
out[14] = a[14] * b;
out[15] = a[15] * b;
return out;
}

/**
* Adds two mat4's after multiplying each element of the second operand by a scalar value.
*
* @param {mat4} out the receiving vector
* @param {mat4} a the first operand
* @param {mat4} b the second operand
* @param {Number} scale the amount to scale b's elements by before adding
* @returns {mat4} out
*/
function multiplyScalarAndAdd$3(out, a, b, scale) {
out[0] = a[0] + b[0] * scale;
out[1] = a[1] + b[1] * scale;
out[2] = a[2] + b[2] * scale;
out[3] = a[3] + b[3] * scale;
out[4] = a[4] + b[4] * scale;
out[5] = a[5] + b[5] * scale;
out[6] = a[6] + b[6] * scale;
out[7] = a[7] + b[7] * scale;
out[8] = a[8] + b[8] * scale;
out[9] = a[9] + b[9] * scale;
out[10] = a[10] + b[10] * scale;
out[11] = a[11] + b[11] * scale;
out[12] = a[12] + b[12] * scale;
out[13] = a[13] + b[13] * scale;
out[14] = a[14] + b[14] * scale;
out[15] = a[15] + b[15] * scale;
return out;
}

/**
* Returns whether or not the matrices have exactly the same elements in the same position (when compared with ===)
*
* @param {mat4} a The first matrix.
* @param {mat4} b The second matrix.
* @returns {Boolean} True if the matrices are equal, false otherwise.
*/
function exactEquals$3(a, b) {
return a[0] === b[0] && a[1] === b[1] && a[2] === b[2] && a[3] === b[3] && a[4] === b[4] && a[5] === b[5] && a[6] === b[6] && a[7] === b[7] && a[8] === b[8] && a[9] === b[9] && a[10] === b[10] && a[11] === b[11] && a[12] === b[12] && a[13] === b[13] && a[14] === b[14] && a[15] === b[15];
}

/**
* Returns whether or not the matrices have approximately the same elements in the same position.
*
* @param {mat4} a The first matrix.
* @param {mat4} b The second matrix.
* @returns {Boolean} True if the matrices are equal, false otherwise.
*/
function equals$4(a, b) {
var a0 = a[0],
    a1 = a[1],
    a2 = a[2],
    a3 = a[3];
var a4 = a[4],
    a5 = a[5],
    a6 = a[6],
    a7 = a[7];
var a8 = a[8],
    a9 = a[9],
    a10 = a[10],
    a11 = a[11];
var a12 = a[12],
    a13 = a[13],
    a14 = a[14],
    a15 = a[15];

var b0 = b[0],
    b1 = b[1],
    b2 = b[2],
    b3 = b[3];
var b4 = b[4],
    b5 = b[5],
    b6 = b[6],
    b7 = b[7];
var b8 = b[8],
    b9 = b[9],
    b10 = b[10],
    b11 = b[11];
var b12 = b[12],
    b13 = b[13],
    b14 = b[14],
    b15 = b[15];

return Math.abs(a0 - b0) <= EPSILON * Math.max(1.0, Math.abs(a0), Math.abs(b0)) && Math.abs(a1 - b1) <= EPSILON * Math.max(1.0, Math.abs(a1), Math.abs(b1)) && Math.abs(a2 - b2) <= EPSILON * Math.max(1.0, Math.abs(a2), Math.abs(b2)) && Math.abs(a3 - b3) <= EPSILON * Math.max(1.0, Math.abs(a3), Math.abs(b3)) && Math.abs(a4 - b4) <= EPSILON * Math.max(1.0, Math.abs(a4), Math.abs(b4)) && Math.abs(a5 - b5) <= EPSILON * Math.max(1.0, Math.abs(a5), Math.abs(b5)) && Math.abs(a6 - b6) <= EPSILON * Math.max(1.0, Math.abs(a6), Math.abs(b6)) && Math.abs(a7 - b7) <= EPSILON * Math.max(1.0, Math.abs(a7), Math.abs(b7)) && Math.abs(a8 - b8) <= EPSILON * Math.max(1.0, Math.abs(a8), Math.abs(b8)) && Math.abs(a9 - b9) <= EPSILON * Math.max(1.0, Math.abs(a9), Math.abs(b9)) && Math.abs(a10 - b10) <= EPSILON * Math.max(1.0, Math.abs(a10), Math.abs(b10)) && Math.abs(a11 - b11) <= EPSILON * Math.max(1.0, Math.abs(a11), Math.abs(b11)) && Math.abs(a12 - b12) <= EPSILON * Math.max(1.0, Math.abs(a12), Math.abs(b12)) && Math.abs(a13 - b13) <= EPSILON * Math.max(1.0, Math.abs(a13), Math.abs(b13)) && Math.abs(a14 - b14) <= EPSILON * Math.max(1.0, Math.abs(a14), Math.abs(b14)) && Math.abs(a15 - b15) <= EPSILON * Math.max(1.0, Math.abs(a15), Math.abs(b15));
}

/**
* Alias for {@link mat4.multiply}
* @function
*/
var mul$3 = multiply$4;

/**
* Alias for {@link mat4.subtract}
* @function
*/
var sub$3 = subtract$3;

var mat4 = Object.freeze({
create: create$3,
clone: clone$3,
copy: copy$3,
fromValues: fromValues$3,
set: set$3,
identity: identity$3,
transpose: transpose$2,
invert: invert$3,
adjoint: adjoint$2,
determinant: determinant$3,
multiply: multiply$4,
translate: translate$2,
scale: scale$3,
rotate: rotate$3,
rotateX: rotateX,
rotateY: rotateY,
rotateZ: rotateZ,
fromTranslation: fromTranslation$2,
fromScaling: fromScaling$3,
fromRotation: fromRotation$3,
fromXRotation: fromXRotation,
fromYRotation: fromYRotation,
fromZRotation: fromZRotation,
fromRotationTranslation: fromRotationTranslation,
fromQuat2: fromQuat2,
getTranslation: getTranslation,
getScaling: getScaling,
getRotation: getRotation,
fromRotationTranslationScale: fromRotationTranslationScale,
fromRotationTranslationScaleOrigin: fromRotationTranslationScaleOrigin,
fromQuat: fromQuat$1,
frustum: frustum,
perspective: perspective,
perspectiveFromFieldOfView: perspectiveFromFieldOfView,
ortho: ortho,
lookAt: lookAt,
targetTo: targetTo,
str: str$3,
frob: frob$3,
add: add$3,
subtract: subtract$3,
multiplyScalar: multiplyScalar$3,
multiplyScalarAndAdd: multiplyScalarAndAdd$3,
exactEquals: exactEquals$3,
equals: equals$4,
mul: mul$3,
sub: sub$3
});

/* Copyright (c) 2015, Brandon Jones, Colin MacKenzie IV.

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in
all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
THE SOFTWARE. */

/**
* 3 Dimensional Vector
* @module vec3
*/

/**
* Creates a new, empty vec3
*
* @returns {vec3} a new 3D vector
*/
function create$5() {
var out = new ARRAY_TYPE(3);
out[0] = 0;
out[1] = 0;
out[2] = 0;
return out;
}

/**
* Creates a new vec3 initialized with values from an existing vector
*
* @param {vec3} a vector to clone
* @returns {vec3} a new 3D vector
*/
function clone$5(a) {
var out = new ARRAY_TYPE(3);
out[0] = a[0];
out[1] = a[1];
out[2] = a[2];
return out;
}

/**
* Calculates the length of a vec3
*
* @param {vec3} a vector to calculate length of
* @returns {Number} length of a
*/
function length$1(a) {
var x = a[0];
var y = a[1];
var z = a[2];
return Math.sqrt(x * x + y * y + z * z);
}

/**
* Creates a new vec3 initialized with the given values
*
* @param {Number} x X component
* @param {Number} y Y component
* @param {Number} z Z component
* @returns {vec3} a new 3D vector
*/
function fromValues$5(x, y, z) {
var out = new ARRAY_TYPE(3);
out[0] = x;
out[1] = y;
out[2] = z;
return out;
}

/**
* Copy the values from one vec3 to another
*
* @param {vec3} out the receiving vector
* @param {vec3} a the source vector
* @returns {vec3} out
*/
function copy$5(out, a) {
out[0] = a[0];
out[1] = a[1];
out[2] = a[2];
return out;
}

/**
* Set the components of a vec3 to the given values
*
* @param {vec3} out the receiving vector
* @param {Number} x X component
* @param {Number} y Y component
* @param {Number} z Z component
* @returns {vec3} out
*/
function set$5(out, x, y, z) {
out[0] = x;
out[1] = y;
out[2] = z;
return out;
}

/**
* Adds two vec3's
*
* @param {vec3} out the receiving vector
* @param {vec3} a the first operand
* @param {vec3} b the second operand
* @returns {vec3} out
*/
function add$5(out, a, b) {
out[0] = a[0] + b[0];
out[1] = a[1] + b[1];
out[2] = a[2] + b[2];
return out;
}

/**
* Subtracts vector b from vector a
*
* @param {vec3} out the receiving vector
* @param {vec3} a the first operand
* @param {vec3} b the second operand
* @returns {vec3} out
*/
function subtract$4(out, a, b) {
out[0] = a[0] - b[0];
out[1] = a[1] - b[1];
out[2] = a[2] - b[2];
return out;
}

/**
* Multiplies two vec3's
*
* @param {vec3} out the receiving vector
* @param {vec3} a the first operand
* @param {vec3} b the second operand
* @returns {vec3} out
*/
function multiply$6(out, a, b) {
out[0] = a[0] * b[0];
out[1] = a[1] * b[1];
out[2] = a[2] * b[2];
return out;
}

/**
* Divides two vec3's
*
* @param {vec3} out the receiving vector
* @param {vec3} a the first operand
* @param {vec3} b the second operand
* @returns {vec3} out
*/
function divide(out, a, b) {
out[0] = a[0] / b[0];
out[1] = a[1] / b[1];
out[2] = a[2] / b[2];
return out;
}

/**
* Math.ceil the components of a vec3
*
* @param {vec3} out the receiving vector
* @param {vec3} a vector to ceil
* @returns {vec3} out
*/
function ceil(out, a) {
out[0] = Math.ceil(a[0]);
out[1] = Math.ceil(a[1]);
out[2] = Math.ceil(a[2]);
return out;
}

/**
* Math.floor the components of a vec3
*
* @param {vec3} out the receiving vector
* @param {vec3} a vector to floor
* @returns {vec3} out
*/
function floor(out, a) {
out[0] = Math.floor(a[0]);
out[1] = Math.floor(a[1]);
out[2] = Math.floor(a[2]);
return out;
}

/**
* Returns the minimum of two vec3's
*
* @param {vec3} out the receiving vector
* @param {vec3} a the first operand
* @param {vec3} b the second operand
* @returns {vec3} out
*/
function min(out, a, b) {
out[0] = Math.min(a[0], b[0]);
out[1] = Math.min(a[1], b[1]);
out[2] = Math.min(a[2], b[2]);
return out;
}

/**
* Returns the maximum of two vec3's
*
* @param {vec3} out the receiving vector
* @param {vec3} a the first operand
* @param {vec3} b the second operand
* @returns {vec3} out
*/
function max(out, a, b) {
out[0] = Math.max(a[0], b[0]);
out[1] = Math.max(a[1], b[1]);
out[2] = Math.max(a[2], b[2]);
return out;
}

/**
* Math.round the components of a vec3
*
* @param {vec3} out the receiving vector
* @param {vec3} a vector to round
* @returns {vec3} out
*/
function round(out, a) {
out[0] = Math.round(a[0]);
out[1] = Math.round(a[1]);
out[2] = Math.round(a[2]);
return out;
}

/**
* Scales a vec3 by a scalar number
*
* @param {vec3} out the receiving vector
* @param {vec3} a the vector to scale
* @param {Number} b amount to scale the vector by
* @returns {vec3} out
*/
function scale$5(out, a, b) {
out[0] = a[0] * b;
out[1] = a[1] * b;
out[2] = a[2] * b;
return out;
}

/**
* Adds two vec3's after scaling the second operand by a scalar value
*
* @param {vec3} out the receiving vector
* @param {vec3} a the first operand
* @param {vec3} b the second operand
* @param {Number} scale the amount to scale b by before adding
* @returns {vec3} out
*/
function scaleAndAdd(out, a, b, scale) {
out[0] = a[0] + b[0] * scale;
out[1] = a[1] + b[1] * scale;
out[2] = a[2] + b[2] * scale;
return out;
}

/**
* Calculates the euclidian distance between two vec3's
*
* @param {vec3} a the first operand
* @param {vec3} b the second operand
* @returns {Number} distance between a and b
*/
function distance(a, b) {
var x = b[0] - a[0];
var y = b[1] - a[1];
var z = b[2] - a[2];
return Math.sqrt(x * x + y * y + z * z);
}

/**
* Calculates the squared euclidian distance between two vec3's
*
* @param {vec3} a the first operand
* @param {vec3} b the second operand
* @returns {Number} squared distance between a and b
*/
function squaredDistance(a, b) {
var x = b[0] - a[0];
var y = b[1] - a[1];
var z = b[2] - a[2];
return x * x + y * y + z * z;
}

/**
* Calculates the squared length of a vec3
*
* @param {vec3} a vector to calculate squared length of
* @returns {Number} squared length of a
*/
function squaredLength$1(a) {
var x = a[0];
var y = a[1];
var z = a[2];
return x * x + y * y + z * z;
}

/**
* Negates the components of a vec3
*
* @param {vec3} out the receiving vector
* @param {vec3} a vector to negate
* @returns {vec3} out
*/
function negate(out, a) {
out[0] = -a[0];
out[1] = -a[1];
out[2] = -a[2];
return out;
}

/**
* Returns the inverse of the components of a vec3
*
* @param {vec3} out the receiving vector
* @param {vec3} a vector to invert
* @returns {vec3} out
*/
function inverse(out, a) {
out[0] = 1.0 / a[0];
out[1] = 1.0 / a[1];
out[2] = 1.0 / a[2];
return out;
}

/**
* Normalize a vec3
*
* @param {vec3} out the receiving vector
* @param {vec3} a vector to normalize
* @returns {vec3} out
*/
function normalize$1(out, a) {
var x = a[0];
var y = a[1];
var z = a[2];
var len = x * x + y * y + z * z;
if (len > 0) {
  //TODO: evaluate use of glm_invsqrt here?
  len = 1 / Math.sqrt(len);
  out[0] = a[0] * len;
  out[1] = a[1] * len;
  out[2] = a[2] * len;
}
return out;
}

/**
* Calculates the dot product of two vec3's
*
* @param {vec3} a the first operand
* @param {vec3} b the second operand
* @returns {Number} dot product of a and b
*/
function dot$1(a, b) {
return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

/**
* Computes the cross product of two vec3's
*
* @param {vec3} out the receiving vector
* @param {vec3} a the first operand
* @param {vec3} b the second operand
* @returns {vec3} out
*/
function cross(out, a, b) {
var ax = a[0],
    ay = a[1],
    az = a[2];
var bx = b[0],
    by = b[1],
    bz = b[2];

out[0] = ay * bz - az * by;
out[1] = az * bx - ax * bz;
out[2] = ax * by - ay * bx;
return out;
}

/**
* Performs a linear interpolation between two vec3's
*
* @param {vec3} out the receiving vector
* @param {vec3} a the first operand
* @param {vec3} b the second operand
* @param {Number} t interpolation amount between the two inputs
* @returns {vec3} out
*/
function lerp$1(out, a, b, t) {
var ax = a[0];
var ay = a[1];
var az = a[2];
out[0] = ax + t * (b[0] - ax);
out[1] = ay + t * (b[1] - ay);
out[2] = az + t * (b[2] - az);
return out;
}

/**
* Performs a hermite interpolation with two control points
*
* @param {vec3} out the receiving vector
* @param {vec3} a the first operand
* @param {vec3} b the second operand
* @param {vec3} c the third operand
* @param {vec3} d the fourth operand
* @param {Number} t interpolation amount between the two inputs
* @returns {vec3} out
*/
function hermite(out, a, b, c, d, t) {
var factorTimes2 = t * t;
var factor1 = factorTimes2 * (2 * t - 3) + 1;
var factor2 = factorTimes2 * (t - 2) + t;
var factor3 = factorTimes2 * (t - 1);
var factor4 = factorTimes2 * (3 - 2 * t);

out[0] = a[0] * factor1 + b[0] * factor2 + c[0] * factor3 + d[0] * factor4;
out[1] = a[1] * factor1 + b[1] * factor2 + c[1] * factor3 + d[1] * factor4;
out[2] = a[2] * factor1 + b[2] * factor2 + c[2] * factor3 + d[2] * factor4;

return out;
}

/**
* Performs a bezier interpolation with two control points
*
* @param {vec3} out the receiving vector
* @param {vec3} a the first operand
* @param {vec3} b the second operand
* @param {vec3} c the third operand
* @param {vec3} d the fourth operand
* @param {Number} t interpolation amount between the two inputs
* @returns {vec3} out
*/
function bezier(out, a, b, c, d, t) {
var inverseFactor = 1 - t;
var inverseFactorTimesTwo = inverseFactor * inverseFactor;
var factorTimes2 = t * t;
var factor1 = inverseFactorTimesTwo * inverseFactor;
var factor2 = 3 * t * inverseFactorTimesTwo;
var factor3 = 3 * factorTimes2 * inverseFactor;
var factor4 = factorTimes2 * t;

out[0] = a[0] * factor1 + b[0] * factor2 + c[0] * factor3 + d[0] * factor4;
out[1] = a[1] * factor1 + b[1] * factor2 + c[1] * factor3 + d[1] * factor4;
out[2] = a[2] * factor1 + b[2] * factor2 + c[2] * factor3 + d[2] * factor4;

return out;
}

/**
* Generates a random vector with the given scale
*
* @param {vec3} out the receiving vector
* @param {Number} [scale] Length of the resulting vector. If ommitted, a unit vector will be returned
* @returns {vec3} out
*/
function random(out, scale) {
scale = scale || 1.0;

var r = RANDOM() * 2.0 * Math.PI;
var z = RANDOM() * 2.0 - 1.0;
var zScale = Math.sqrt(1.0 - z * z) * scale;

out[0] = Math.cos(r) * zScale;
out[1] = Math.sin(r) * zScale;
out[2] = z * scale;
return out;
}

/**
* Transforms the vec3 with a mat4.
* 4th vector component is implicitly '1'
*
* @param {vec3} out the receiving vector
* @param {vec3} a the vector to transform
* @param {mat4} m matrix to transform with
* @returns {vec3} out
*/
function transformMat4(out, a, m) {
var x = a[0],
    y = a[1],
    z = a[2];
var w = m[3] * x + m[7] * y + m[11] * z + m[15];
w = w || 1.0;
out[0] = (m[0] * x + m[4] * y + m[8] * z + m[12]) / w;
out[1] = (m[1] * x + m[5] * y + m[9] * z + m[13]) / w;
out[2] = (m[2] * x + m[6] * y + m[10] * z + m[14]) / w;
return out;
}

/**
* Transforms the vec3 with a mat3.
*
* @param {vec3} out the receiving vector
* @param {vec3} a the vector to transform
* @param {mat3} m the 3x3 matrix to transform with
* @returns {vec3} out
*/
function transformMat3(out, a, m) {
var x = a[0],
    y = a[1],
    z = a[2];
out[0] = x * m[0] + y * m[3] + z * m[6];
out[1] = x * m[1] + y * m[4] + z * m[7];
out[2] = x * m[2] + y * m[5] + z * m[8];
return out;
}

/**
* Transforms the vec3 with a quat
* Can also be used for dual quaternions. (Multiply it with the real part)
*
* @param {vec3} out the receiving vector
* @param {vec3} a the vector to transform
* @param {quat} q quaternion to transform with
* @returns {vec3} out
*/
function transformQuat(out, a, q) {
// benchmarks: https://jsperf.com/quaternion-transform-vec3-implementations-fixed
var qx = q[0],
    qy = q[1],
    qz = q[2],
    qw = q[3];
var x = a[0],
    y = a[1],
    z = a[2];
// var qvec = [qx, qy, qz];
// var uv = vec3.cross([], qvec, a);
var uvx = qy * z - qz * y,
    uvy = qz * x - qx * z,
    uvz = qx * y - qy * x;
// var uuv = vec3.cross([], qvec, uv);
var uuvx = qy * uvz - qz * uvy,
    uuvy = qz * uvx - qx * uvz,
    uuvz = qx * uvy - qy * uvx;
// vec3.scale(uv, uv, 2 * w);
var w2 = qw * 2;
uvx *= w2;
uvy *= w2;
uvz *= w2;
// vec3.scale(uuv, uuv, 2);
uuvx *= 2;
uuvy *= 2;
uuvz *= 2;
// return vec3.add(out, a, vec3.add(out, uv, uuv));
out[0] = x + uvx + uuvx;
out[1] = y + uvy + uuvy;
out[2] = z + uvz + uuvz;
return out;
}

/**
* Rotate a 3D vector around the x-axis
* @param {vec3} out The receiving vec3
* @param {vec3} a The vec3 point to rotate
* @param {vec3} b The origin of the rotation
* @param {Number} c The angle of rotation
* @returns {vec3} out
*/
function rotateX$2(out, a, b, c) {
var p = [],
    r = [];
//Translate point to the origin
p[0] = a[0] - b[0];
p[1] = a[1] - b[1];
p[2] = a[2] - b[2];

//perform rotation
r[0] = p[0];
r[1] = p[1] * Math.cos(c) - p[2] * Math.sin(c);
r[2] = p[1] * Math.sin(c) + p[2] * Math.cos(c);

//translate to correct position
out[0] = r[0] + b[0];
out[1] = r[1] + b[1];
out[2] = r[2] + b[2];

return out;
}

/**
* Rotate a 3D vector around the y-axis
* @param {vec3} out The receiving vec3
* @param {vec3} a The vec3 point to rotate
* @param {vec3} b The origin of the rotation
* @param {Number} c The angle of rotation
* @returns {vec3} out
*/
function rotateY$2(out, a, b, c) {
var p = [],
    r = [];
//Translate point to the origin
p[0] = a[0] - b[0];
p[1] = a[1] - b[1];
p[2] = a[2] - b[2];

//perform rotation
r[0] = p[2] * Math.sin(c) + p[0] * Math.cos(c);
r[1] = p[1];
r[2] = p[2] * Math.cos(c) - p[0] * Math.sin(c);

//translate to correct position
out[0] = r[0] + b[0];
out[1] = r[1] + b[1];
out[2] = r[2] + b[2];

return out;
}

/**
* Rotate a 3D vector around the z-axis
* @param {vec3} out The receiving vec3
* @param {vec3} a The vec3 point to rotate
* @param {vec3} b The origin of the rotation
* @param {Number} c The angle of rotation
* @returns {vec3} out
*/
function rotateZ$2(out, a, b, c) {
var p = [],
    r = [];
//Translate point to the origin
p[0] = a[0] - b[0];
p[1] = a[1] - b[1];
p[2] = a[2] - b[2];

//perform rotation
r[0] = p[0] * Math.cos(c) - p[1] * Math.sin(c);
r[1] = p[0] * Math.sin(c) + p[1] * Math.cos(c);
r[2] = p[2];

//translate to correct position
out[0] = r[0] + b[0];
out[1] = r[1] + b[1];
out[2] = r[2] + b[2];

return out;
}

/**
* Get the angle between two 3D vectors
* @param {vec3} a The first operand
* @param {vec3} b The second operand
* @returns {Number} The angle in radians
*/
function angle(a, b) {
var tempA = fromValues$5(a[0], a[1], a[2]);
var tempB = fromValues$5(b[0], b[1], b[2]);

normalize$1(tempA, tempA);
normalize$1(tempB, tempB);

var cosine = dot$1(tempA, tempB);

if (cosine > 1.0) {
  return 0;
} else if (cosine < -1.0) {
  return Math.PI;
} else {
  return Math.acos(cosine);
}
}

/**
* Returns a string representation of a vector
*
* @param {vec3} a vector to represent as a string
* @returns {String} string representation of the vector
*/
function str$5(a) {
return 'vec3(' + a[0] + ', ' + a[1] + ', ' + a[2] + ')';
}

/**
* Returns whether or not the vectors have exactly the same elements in the same position (when compared with ===)
*
* @param {vec3} a The first vector.
* @param {vec3} b The second vector.
* @returns {Boolean} True if the vectors are equal, false otherwise.
*/
function exactEquals$5(a, b) {
return a[0] === b[0] && a[1] === b[1] && a[2] === b[2];
}

/**
* Returns whether or not the vectors have approximately the same elements in the same position.
*
* @param {vec3} a The first vector.
* @param {vec3} b The second vector.
* @returns {Boolean} True if the vectors are equal, false otherwise.
*/
function equals$6(a, b) {
var a0 = a[0],
    a1 = a[1],
    a2 = a[2];
var b0 = b[0],
    b1 = b[1],
    b2 = b[2];
return Math.abs(a0 - b0) <= EPSILON * Math.max(1.0, Math.abs(a0), Math.abs(b0)) && Math.abs(a1 - b1) <= EPSILON * Math.max(1.0, Math.abs(a1), Math.abs(b1)) && Math.abs(a2 - b2) <= EPSILON * Math.max(1.0, Math.abs(a2), Math.abs(b2));
}

/**
* Alias for {@link vec3.subtract}
* @function
*/
var sub$4 = subtract$4;

/**
* Alias for {@link vec3.multiply}
* @function
*/
var mul$5 = multiply$6;

/**
* Alias for {@link vec3.divide}
* @function
*/
var div = divide;

/**
* Alias for {@link vec3.distance}
* @function
*/
var dist = distance;

/**
* Alias for {@link vec3.squaredDistance}
* @function
*/
var sqrDist = squaredDistance;

/**
* Alias for {@link vec3.length}
* @function
*/
var len$1 = length$1;

/**
* Alias for {@link vec3.squaredLength}
* @function
*/
var sqrLen$1 = squaredLength$1;

/**
* Perform some operation over an array of vec3s.
*
* @param {Array} a the array of vectors to iterate over
* @param {Number} stride Number of elements between the start of each vec3. If 0 assumes tightly packed
* @param {Number} offset Number of elements to skip at the beginning of the array
* @param {Number} count Number of vec3s to iterate over. If 0 iterates over entire array
* @param {Function} fn Function to call for each vector in the array
* @param {Object} [arg] additional argument to pass to fn
* @returns {Array} a
* @function
*/
var forEach = function () {
var vec = create$5();

return function (a, stride, offset, count, fn, arg) {
  var i = void 0,
      l = void 0;
  if (!stride) {
    stride = 3;
  }

  if (!offset) {
    offset = 0;
  }

  if (count) {
    l = Math.min(count * stride + offset, a.length);
  } else {
    l = a.length;
  }

  for (i = offset; i < l; i += stride) {
    vec[0] = a[i];vec[1] = a[i + 1];vec[2] = a[i + 2];
    fn(vec, vec, arg);
    a[i] = vec[0];a[i + 1] = vec[1];a[i + 2] = vec[2];
  }

  return a;
};
}();

var vec3 = Object.freeze({
create: create$5,
clone: clone$5,
length: length$1,
fromValues: fromValues$5,
copy: copy$5,
set: set$5,
add: add$5,
subtract: subtract$4,
multiply: multiply$6,
divide: divide,
ceil: ceil,
floor: floor,
min: min,
max: max,
round: round,
scale: scale$5,
scaleAndAdd: scaleAndAdd,
distance: distance,
squaredDistance: squaredDistance,
squaredLength: squaredLength$1,
negate: negate,
inverse: inverse,
normalize: normalize$1,
dot: dot$1,
cross: cross,
lerp: lerp$1,
hermite: hermite,
bezier: bezier,
random: random,
transformMat4: transformMat4,
transformMat3: transformMat3,
transformQuat: transformQuat,
rotateX: rotateX$2,
rotateY: rotateY$2,
rotateZ: rotateZ$2,
angle: angle,
str: str$5,
exactEquals: exactEquals$5,
equals: equals$6,
sub: sub$4,
mul: mul$5,
div: div,
dist: dist,
sqrDist: sqrDist,
len: len$1,
sqrLen: sqrLen$1,
forEach: forEach
});

/* Copyright (c) 2015, Brandon Jones, Colin MacKenzie IV.

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in
all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
THE SOFTWARE. */

/**
* 4 Dimensional Vector
* @module vec4
*/

/**
* Creates a new, empty vec4
*
* @returns {vec4} a new 4D vector
*/
function create$6() {
var out = new ARRAY_TYPE(4);
out[0] = 0;
out[1] = 0;
out[2] = 0;
out[3] = 0;
return out;
}

/**
* Creates a new vec4 initialized with values from an existing vector
*
* @param {vec4} a vector to clone
* @returns {vec4} a new 4D vector
*/
function clone$6(a) {
var out = new ARRAY_TYPE(4);
out[0] = a[0];
out[1] = a[1];
out[2] = a[2];
out[3] = a[3];
return out;
}

/**
* Creates a new vec4 initialized with the given values
*
* @param {Number} x X component
* @param {Number} y Y component
* @param {Number} z Z component
* @param {Number} w W component
* @returns {vec4} a new 4D vector
*/
function fromValues$6(x, y, z, w) {
var out = new ARRAY_TYPE(4);
out[0] = x;
out[1] = y;
out[2] = z;
out[3] = w;
return out;
}

/**
* Copy the values from one vec4 to another
*
* @param {vec4} out the receiving vector
* @param {vec4} a the source vector
* @returns {vec4} out
*/
function copy$6(out, a) {
out[0] = a[0];
out[1] = a[1];
out[2] = a[2];
out[3] = a[3];
return out;
}

/**
* Set the components of a vec4 to the given values
*
* @param {vec4} out the receiving vector
* @param {Number} x X component
* @param {Number} y Y component
* @param {Number} z Z component
* @param {Number} w W component
* @returns {vec4} out
*/
function set$6(out, x, y, z, w) {
out[0] = x;
out[1] = y;
out[2] = z;
out[3] = w;
return out;
}

/**
* Adds two vec4's
*
* @param {vec4} out the receiving vector
* @param {vec4} a the first operand
* @param {vec4} b the second operand
* @returns {vec4} out
*/
function add$6(out, a, b) {
out[0] = a[0] + b[0];
out[1] = a[1] + b[1];
out[2] = a[2] + b[2];
out[3] = a[3] + b[3];
return out;
}

/**
* Subtracts vector b from vector a
*
* @param {vec4} out the receiving vector
* @param {vec4} a the first operand
* @param {vec4} b the second operand
* @returns {vec4} out
*/
function subtract$5(out, a, b) {
out[0] = a[0] - b[0];
out[1] = a[1] - b[1];
out[2] = a[2] - b[2];
out[3] = a[3] - b[3];
return out;
}

/**
* Multiplies two vec4's
*
* @param {vec4} out the receiving vector
* @param {vec4} a the first operand
* @param {vec4} b the second operand
* @returns {vec4} out
*/
function multiply$7(out, a, b) {
out[0] = a[0] * b[0];
out[1] = a[1] * b[1];
out[2] = a[2] * b[2];
out[3] = a[3] * b[3];
return out;
}

/**
* Divides two vec4's
*
* @param {vec4} out the receiving vector
* @param {vec4} a the first operand
* @param {vec4} b the second operand
* @returns {vec4} out
*/
function divide$1(out, a, b) {
out[0] = a[0] / b[0];
out[1] = a[1] / b[1];
out[2] = a[2] / b[2];
out[3] = a[3] / b[3];
return out;
}

/**
* Math.ceil the components of a vec4
*
* @param {vec4} out the receiving vector
* @param {vec4} a vector to ceil
* @returns {vec4} out
*/
function ceil$1(out, a) {
out[0] = Math.ceil(a[0]);
out[1] = Math.ceil(a[1]);
out[2] = Math.ceil(a[2]);
out[3] = Math.ceil(a[3]);
return out;
}

/**
* Math.floor the components of a vec4
*
* @param {vec4} out the receiving vector
* @param {vec4} a vector to floor
* @returns {vec4} out
*/
function floor$1(out, a) {
out[0] = Math.floor(a[0]);
out[1] = Math.floor(a[1]);
out[2] = Math.floor(a[2]);
out[3] = Math.floor(a[3]);
return out;
}

/**
* Returns the minimum of two vec4's
*
* @param {vec4} out the receiving vector
* @param {vec4} a the first operand
* @param {vec4} b the second operand
* @returns {vec4} out
*/
function min$1(out, a, b) {
out[0] = Math.min(a[0], b[0]);
out[1] = Math.min(a[1], b[1]);
out[2] = Math.min(a[2], b[2]);
out[3] = Math.min(a[3], b[3]);
return out;
}

/**
* Returns the maximum of two vec4's
*
* @param {vec4} out the receiving vector
* @param {vec4} a the first operand
* @param {vec4} b the second operand
* @returns {vec4} out
*/
function max$1(out, a, b) {
out[0] = Math.max(a[0], b[0]);
out[1] = Math.max(a[1], b[1]);
out[2] = Math.max(a[2], b[2]);
out[3] = Math.max(a[3], b[3]);
return out;
}

/**
* Math.round the components of a vec4
*
* @param {vec4} out the receiving vector
* @param {vec4} a vector to round
* @returns {vec4} out
*/
function round$1(out, a) {
out[0] = Math.round(a[0]);
out[1] = Math.round(a[1]);
out[2] = Math.round(a[2]);
out[3] = Math.round(a[3]);
return out;
}

/**
* Scales a vec4 by a scalar number
*
* @param {vec4} out the receiving vector
* @param {vec4} a the vector to scale
* @param {Number} b amount to scale the vector by
* @returns {vec4} out
*/
function scale$6(out, a, b) {
out[0] = a[0] * b;
out[1] = a[1] * b;
out[2] = a[2] * b;
out[3] = a[3] * b;
return out;
}

/**
* Adds two vec4's after scaling the second operand by a scalar value
*
* @param {vec4} out the receiving vector
* @param {vec4} a the first operand
* @param {vec4} b the second operand
* @param {Number} scale the amount to scale b by before adding
* @returns {vec4} out
*/
function scaleAndAdd$1(out, a, b, scale) {
out[0] = a[0] + b[0] * scale;
out[1] = a[1] + b[1] * scale;
out[2] = a[2] + b[2] * scale;
out[3] = a[3] + b[3] * scale;
return out;
}

/**
* Calculates the euclidian distance between two vec4's
*
* @param {vec4} a the first operand
* @param {vec4} b the second operand
* @returns {Number} distance between a and b
*/
function distance$1(a, b) {
var x = b[0] - a[0];
var y = b[1] - a[1];
var z = b[2] - a[2];
var w = b[3] - a[3];
return Math.sqrt(x * x + y * y + z * z + w * w);
}

/**
* Calculates the squared euclidian distance between two vec4's
*
* @param {vec4} a the first operand
* @param {vec4} b the second operand
* @returns {Number} squared distance between a and b
*/
function squaredDistance$1(a, b) {
var x = b[0] - a[0];
var y = b[1] - a[1];
var z = b[2] - a[2];
var w = b[3] - a[3];
return x * x + y * y + z * z + w * w;
}

/**
* Calculates the length of a vec4
*
* @param {vec4} a vector to calculate length of
* @returns {Number} length of a
*/
function length$2(a) {
var x = a[0];
var y = a[1];
var z = a[2];
var w = a[3];
return Math.sqrt(x * x + y * y + z * z + w * w);
}

/**
* Calculates the squared length of a vec4
*
* @param {vec4} a vector to calculate squared length of
* @returns {Number} squared length of a
*/
function squaredLength$2(a) {
var x = a[0];
var y = a[1];
var z = a[2];
var w = a[3];
return x * x + y * y + z * z + w * w;
}

/**
* Negates the components of a vec4
*
* @param {vec4} out the receiving vector
* @param {vec4} a vector to negate
* @returns {vec4} out
*/
function negate$1(out, a) {
out[0] = -a[0];
out[1] = -a[1];
out[2] = -a[2];
out[3] = -a[3];
return out;
}

/**
* Returns the inverse of the components of a vec4
*
* @param {vec4} out the receiving vector
* @param {vec4} a vector to invert
* @returns {vec4} out
*/
function inverse$1(out, a) {
out[0] = 1.0 / a[0];
out[1] = 1.0 / a[1];
out[2] = 1.0 / a[2];
out[3] = 1.0 / a[3];
return out;
}

/**
* Normalize a vec4
*
* @param {vec4} out the receiving vector
* @param {vec4} a vector to normalize
* @returns {vec4} out
*/
function normalize$2(out, a) {
var x = a[0];
var y = a[1];
var z = a[2];
var w = a[3];
var len = x * x + y * y + z * z + w * w;
if (len > 0) {
  len = 1 / Math.sqrt(len);
  out[0] = x * len;
  out[1] = y * len;
  out[2] = z * len;
  out[3] = w * len;
}
return out;
}

/**
* Calculates the dot product of two vec4's
*
* @param {vec4} a the first operand
* @param {vec4} b the second operand
* @returns {Number} dot product of a and b
*/
function dot$2(a, b) {
return a[0] * b[0] + a[1] * b[1] + a[2] * b[2] + a[3] * b[3];
}

/**
* Performs a linear interpolation between two vec4's
*
* @param {vec4} out the receiving vector
* @param {vec4} a the first operand
* @param {vec4} b the second operand
* @param {Number} t interpolation amount between the two inputs
* @returns {vec4} out
*/
function lerp$2(out, a, b, t) {
var ax = a[0];
var ay = a[1];
var az = a[2];
var aw = a[3];
out[0] = ax + t * (b[0] - ax);
out[1] = ay + t * (b[1] - ay);
out[2] = az + t * (b[2] - az);
out[3] = aw + t * (b[3] - aw);
return out;
}

/**
* Generates a random vector with the given scale
*
* @param {vec4} out the receiving vector
* @param {Number} [scale] Length of the resulting vector. If ommitted, a unit vector will be returned
* @returns {vec4} out
*/
function random$1(out, vectorScale) {
vectorScale = vectorScale || 1.0;

//TODO: This is a pretty awful way of doing this. Find something better.
out[0] = RANDOM();
out[1] = RANDOM();
out[2] = RANDOM();
out[3] = RANDOM();
normalize$2(out, out);
scale$6(out, out, vectorScale);
return out;
}

/**
* Transforms the vec4 with a mat4.
*
* @param {vec4} out the receiving vector
* @param {vec4} a the vector to transform
* @param {mat4} m matrix to transform with
* @returns {vec4} out
*/
function transformMat4$1(out, a, m) {
var x = a[0],
    y = a[1],
    z = a[2],
    w = a[3];
out[0] = m[0] * x + m[4] * y + m[8] * z + m[12] * w;
out[1] = m[1] * x + m[5] * y + m[9] * z + m[13] * w;
out[2] = m[2] * x + m[6] * y + m[10] * z + m[14] * w;
out[3] = m[3] * x + m[7] * y + m[11] * z + m[15] * w;
return out;
}

/**
* Transforms the vec4 with a quat
*
* @param {vec4} out the receiving vector
* @param {vec4} a the vector to transform
* @param {quat} q quaternion to transform with
* @returns {vec4} out
*/
function transformQuat$1(out, a, q) {
var x = a[0],
    y = a[1],
    z = a[2];
var qx = q[0],
    qy = q[1],
    qz = q[2],
    qw = q[3];

// calculate quat * vec
var ix = qw * x + qy * z - qz * y;
var iy = qw * y + qz * x - qx * z;
var iz = qw * z + qx * y - qy * x;
var iw = -qx * x - qy * y - qz * z;

// calculate result * inverse quat
out[0] = ix * qw + iw * -qx + iy * -qz - iz * -qy;
out[1] = iy * qw + iw * -qy + iz * -qx - ix * -qz;
out[2] = iz * qw + iw * -qz + ix * -qy - iy * -qx;
out[3] = a[3];
return out;
}

/**
* Returns a string representation of a vector
*
* @param {vec4} a vector to represent as a string
* @returns {String} string representation of the vector
*/
function str$6(a) {
return 'vec4(' + a[0] + ', ' + a[1] + ', ' + a[2] + ', ' + a[3] + ')';
}

/**
* Returns whether or not the vectors have exactly the same elements in the same position (when compared with ===)
*
* @param {vec4} a The first vector.
* @param {vec4} b The second vector.
* @returns {Boolean} True if the vectors are equal, false otherwise.
*/
function exactEquals$6(a, b) {
return a[0] === b[0] && a[1] === b[1] && a[2] === b[2] && a[3] === b[3];
}

/**
* Returns whether or not the vectors have approximately the same elements in the same position.
*
* @param {vec4} a The first vector.
* @param {vec4} b The second vector.
* @returns {Boolean} True if the vectors are equal, false otherwise.
*/
function equals$7(a, b) {
var a0 = a[0],
    a1 = a[1],
    a2 = a[2],
    a3 = a[3];
var b0 = b[0],
    b1 = b[1],
    b2 = b[2],
    b3 = b[3];
return Math.abs(a0 - b0) <= EPSILON * Math.max(1.0, Math.abs(a0), Math.abs(b0)) && Math.abs(a1 - b1) <= EPSILON * Math.max(1.0, Math.abs(a1), Math.abs(b1)) && Math.abs(a2 - b2) <= EPSILON * Math.max(1.0, Math.abs(a2), Math.abs(b2)) && Math.abs(a3 - b3) <= EPSILON * Math.max(1.0, Math.abs(a3), Math.abs(b3));
}

/**
* Alias for {@link vec4.subtract}
* @function
*/
var sub$5 = subtract$5;

/**
* Alias for {@link vec4.multiply}
* @function
*/
var mul$6 = multiply$7;

/**
* Alias for {@link vec4.divide}
* @function
*/
var div$1 = divide$1;

/**
* Alias for {@link vec4.distance}
* @function
*/
var dist$1 = distance$1;

/**
* Alias for {@link vec4.squaredDistance}
* @function
*/
var sqrDist$1 = squaredDistance$1;

/**
* Alias for {@link vec4.length}
* @function
*/
var len$2 = length$2;

/**
* Alias for {@link vec4.squaredLength}
* @function
*/
var sqrLen$2 = squaredLength$2;

/**
* Perform some operation over an array of vec4s.
*
* @param {Array} a the array of vectors to iterate over
* @param {Number} stride Number of elements between the start of each vec4. If 0 assumes tightly packed
* @param {Number} offset Number of elements to skip at the beginning of the array
* @param {Number} count Number of vec4s to iterate over. If 0 iterates over entire array
* @param {Function} fn Function to call for each vector in the array
* @param {Object} [arg] additional argument to pass to fn
* @returns {Array} a
* @function
*/
var forEach$1 = function () {
var vec = create$6();

return function (a, stride, offset, count, fn, arg) {
  var i = void 0,
      l = void 0;
  if (!stride) {
    stride = 4;
  }

  if (!offset) {
    offset = 0;
  }

  if (count) {
    l = Math.min(count * stride + offset, a.length);
  } else {
    l = a.length;
  }

  for (i = offset; i < l; i += stride) {
    vec[0] = a[i];vec[1] = a[i + 1];vec[2] = a[i + 2];vec[3] = a[i + 3];
    fn(vec, vec, arg);
    a[i] = vec[0];a[i + 1] = vec[1];a[i + 2] = vec[2];a[i + 3] = vec[3];
  }

  return a;
};
}();

var vec4$1 = Object.freeze({
create: create$6,
clone: clone$6,
fromValues: fromValues$6,
copy: copy$6,
set: set$6,
add: add$6,
subtract: subtract$5,
multiply: multiply$7,
divide: divide$1,
ceil: ceil$1,
floor: floor$1,
min: min$1,
max: max$1,
round: round$1,
scale: scale$6,
scaleAndAdd: scaleAndAdd$1,
distance: distance$1,
squaredDistance: squaredDistance$1,
length: length$2,
squaredLength: squaredLength$2,
negate: negate$1,
inverse: inverse$1,
normalize: normalize$2,
dot: dot$2,
lerp: lerp$2,
random: random$1,
transformMat4: transformMat4$1,
transformQuat: transformQuat$1,
str: str$6,
exactEquals: exactEquals$6,
equals: equals$7,
sub: sub$5,
mul: mul$6,
div: div$1,
dist: dist$1,
sqrDist: sqrDist$1,
len: len$2,
sqrLen: sqrLen$2,
forEach: forEach$1
});

/* Copyright (c) 2015, Brandon Jones, Colin MacKenzie IV.

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in
all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
THE SOFTWARE. */

/**
* Quaternion
* @module quat
*/

/**
* Creates a new identity quat
*
* @returns {quat} a new quaternion
*/
function create$4() {
var out = new ARRAY_TYPE(4);
out[0] = 0;
out[1] = 0;
out[2] = 0;
out[3] = 1;
return out;
}

/**
* Set a quat to the identity quaternion
*
* @param {quat} out the receiving quaternion
* @returns {quat} out
*/
function identity$4(out) {
out[0] = 0;
out[1] = 0;
out[2] = 0;
out[3] = 1;
return out;
}

/**
* Sets a quat from the given angle and rotation axis,
* then returns it.
*
* @param {quat} out the receiving quaternion
* @param {vec3} axis the axis around which to rotate
* @param {Number} rad the angle in radians
* @returns {quat} out
**/
function setAxisAngle(out, axis, rad) {
rad = rad * 0.5;
var s = Math.sin(rad);
out[0] = s * axis[0];
out[1] = s * axis[1];
out[2] = s * axis[2];
out[3] = Math.cos(rad);
return out;
}

/**
* Gets the rotation axis and angle for a given
*  quaternion. If a quaternion is created with
*  setAxisAngle, this method will return the same
*  values as providied in the original parameter list
*  OR functionally equivalent values.
* Example: The quaternion formed by axis [0, 0, 1] and
*  angle -90 is the same as the quaternion formed by
*  [0, 0, 1] and 270. This method favors the latter.
* @param  {vec3} out_axis  Vector receiving the axis of rotation
* @param  {quat} q     Quaternion to be decomposed
* @return {Number}     Angle, in radians, of the rotation
*/
function getAxisAngle(out_axis, q) {
var rad = Math.acos(q[3]) * 2.0;
var s = Math.sin(rad / 2.0);
if (s != 0.0) {
  out_axis[0] = q[0] / s;
  out_axis[1] = q[1] / s;
  out_axis[2] = q[2] / s;
} else {
  // If s is zero, return any axis (no rotation - axis does not matter)
  out_axis[0] = 1;
  out_axis[1] = 0;
  out_axis[2] = 0;
}
return rad;
}

/**
* Multiplies two quat's
*
* @param {quat} out the receiving quaternion
* @param {quat} a the first operand
* @param {quat} b the second operand
* @returns {quat} out
*/
function multiply$5(out, a, b) {
var ax = a[0],
    ay = a[1],
    az = a[2],
    aw = a[3];
var bx = b[0],
    by = b[1],
    bz = b[2],
    bw = b[3];

out[0] = ax * bw + aw * bx + ay * bz - az * by;
out[1] = ay * bw + aw * by + az * bx - ax * bz;
out[2] = az * bw + aw * bz + ax * by - ay * bx;
out[3] = aw * bw - ax * bx - ay * by - az * bz;
return out;
}

/**
* Rotates a quaternion by the given angle about the X axis
*
* @param {quat} out quat receiving operation result
* @param {quat} a quat to rotate
* @param {number} rad angle (in radians) to rotate
* @returns {quat} out
*/
function rotateX$1(out, a, rad) {
rad *= 0.5;

var ax = a[0],
    ay = a[1],
    az = a[2],
    aw = a[3];
var bx = Math.sin(rad),
    bw = Math.cos(rad);

out[0] = ax * bw + aw * bx;
out[1] = ay * bw + az * bx;
out[2] = az * bw - ay * bx;
out[3] = aw * bw - ax * bx;
return out;
}

/**
* Rotates a quaternion by the given angle about the Y axis
*
* @param {quat} out quat receiving operation result
* @param {quat} a quat to rotate
* @param {number} rad angle (in radians) to rotate
* @returns {quat} out
*/
function rotateY$1(out, a, rad) {
rad *= 0.5;

var ax = a[0],
    ay = a[1],
    az = a[2],
    aw = a[3];
var by = Math.sin(rad),
    bw = Math.cos(rad);

out[0] = ax * bw - az * by;
out[1] = ay * bw + aw * by;
out[2] = az * bw + ax * by;
out[3] = aw * bw - ay * by;
return out;
}

/**
* Rotates a quaternion by the given angle about the Z axis
*
* @param {quat} out quat receiving operation result
* @param {quat} a quat to rotate
* @param {number} rad angle (in radians) to rotate
* @returns {quat} out
*/
function rotateZ$1(out, a, rad) {
rad *= 0.5;

var ax = a[0],
    ay = a[1],
    az = a[2],
    aw = a[3];
var bz = Math.sin(rad),
    bw = Math.cos(rad);

out[0] = ax * bw + ay * bz;
out[1] = ay * bw - ax * bz;
out[2] = az * bw + aw * bz;
out[3] = aw * bw - az * bz;
return out;
}

/**
* Calculates the W component of a quat from the X, Y, and Z components.
* Assumes that quaternion is 1 unit in length.
* Any existing W component will be ignored.
*
* @param {quat} out the receiving quaternion
* @param {quat} a quat to calculate W component of
* @returns {quat} out
*/
function calculateW(out, a) {
var x = a[0],
    y = a[1],
    z = a[2];

out[0] = x;
out[1] = y;
out[2] = z;
out[3] = Math.sqrt(Math.abs(1.0 - x * x - y * y - z * z));
return out;
}

/**
* Performs a spherical linear interpolation between two quat
*
* @param {quat} out the receiving quaternion
* @param {quat} a the first operand
* @param {quat} b the second operand
* @param {Number} t interpolation amount between the two inputs
* @returns {quat} out
*/
function slerp(out, a, b, t) {
// benchmarks:
//    http://jsperf.com/quaternion-slerp-implementations
var ax = a[0],
    ay = a[1],
    az = a[2],
    aw = a[3];
var bx = b[0],
    by = b[1],
    bz = b[2],
    bw = b[3];

var omega = void 0,
    cosom = void 0,
    sinom = void 0,
    scale0 = void 0,
    scale1 = void 0;

// calc cosine
cosom = ax * bx + ay * by + az * bz + aw * bw;
// adjust signs (if necessary)
if (cosom < 0.0) {
  cosom = -cosom;
  bx = -bx;
  by = -by;
  bz = -bz;
  bw = -bw;
}
// calculate coefficients
if (1.0 - cosom > 0.000001) {
  // standard case (slerp)
  omega = Math.acos(cosom);
  sinom = Math.sin(omega);
  scale0 = Math.sin((1.0 - t) * omega) / sinom;
  scale1 = Math.sin(t * omega) / sinom;
} else {
  // "from" and "to" quaternions are very close
  //  ... so we can do a linear interpolation
  scale0 = 1.0 - t;
  scale1 = t;
}
// calculate final values
out[0] = scale0 * ax + scale1 * bx;
out[1] = scale0 * ay + scale1 * by;
out[2] = scale0 * az + scale1 * bz;
out[3] = scale0 * aw + scale1 * bw;

return out;
}

/**
* Calculates the inverse of a quat
*
* @param {quat} out the receiving quaternion
* @param {quat} a quat to calculate inverse of
* @returns {quat} out
*/
function invert$4(out, a) {
var a0 = a[0],
    a1 = a[1],
    a2 = a[2],
    a3 = a[3];
var dot = a0 * a0 + a1 * a1 + a2 * a2 + a3 * a3;
var invDot = dot ? 1.0 / dot : 0;

// TODO: Would be faster to return [0,0,0,0] immediately if dot == 0

out[0] = -a0 * invDot;
out[1] = -a1 * invDot;
out[2] = -a2 * invDot;
out[3] = a3 * invDot;
return out;
}

/**
* Calculates the conjugate of a quat
* If the quaternion is normalized, this function is faster than quat.inverse and produces the same result.
*
* @param {quat} out the receiving quaternion
* @param {quat} a quat to calculate conjugate of
* @returns {quat} out
*/
function conjugate(out, a) {
out[0] = -a[0];
out[1] = -a[1];
out[2] = -a[2];
out[3] = a[3];
return out;
}

/**
* Creates a quaternion from the given 3x3 rotation matrix.
*
* NOTE: The resultant quaternion is not normalized, so you should be sure
* to renormalize the quaternion yourself where necessary.
*
* @param {quat} out the receiving quaternion
* @param {mat3} m rotation matrix
* @returns {quat} out
* @function
*/
function fromMat3(out, m) {
// Algorithm in Ken Shoemake's article in 1987 SIGGRAPH course notes
// article "Quaternion Calculus and Fast Animation".
var fTrace = m[0] + m[4] + m[8];
var fRoot = void 0;

if (fTrace > 0.0) {
  // |w| > 1/2, may as well choose w > 1/2
  fRoot = Math.sqrt(fTrace + 1.0); // 2w
  out[3] = 0.5 * fRoot;
  fRoot = 0.5 / fRoot; // 1/(4w)
  out[0] = (m[5] - m[7]) * fRoot;
  out[1] = (m[6] - m[2]) * fRoot;
  out[2] = (m[1] - m[3]) * fRoot;
} else {
  // |w| <= 1/2
  var i = 0;
  if (m[4] > m[0]) i = 1;
  if (m[8] > m[i * 3 + i]) i = 2;
  var j = (i + 1) % 3;
  var k = (i + 2) % 3;

  fRoot = Math.sqrt(m[i * 3 + i] - m[j * 3 + j] - m[k * 3 + k] + 1.0);
  out[i] = 0.5 * fRoot;
  fRoot = 0.5 / fRoot;
  out[3] = (m[j * 3 + k] - m[k * 3 + j]) * fRoot;
  out[j] = (m[j * 3 + i] + m[i * 3 + j]) * fRoot;
  out[k] = (m[k * 3 + i] + m[i * 3 + k]) * fRoot;
}

return out;
}

/**
* Creates a quaternion from the given euler angle x, y, z.
*
* @param {quat} out the receiving quaternion
* @param {x} Angle to rotate around X axis in degrees.
* @param {y} Angle to rotate around Y axis in degrees.
* @param {z} Angle to rotate around Z axis in degrees.
* @returns {quat} out
* @function
*/
function fromEuler(out, x, y, z) {
var halfToRad = 0.5 * Math.PI / 180.0;
x *= halfToRad;
y *= halfToRad;
z *= halfToRad;

var sx = Math.sin(x);
var cx = Math.cos(x);
var sy = Math.sin(y);
var cy = Math.cos(y);
var sz = Math.sin(z);
var cz = Math.cos(z);

out[0] = sx * cy * cz - cx * sy * sz;
out[1] = cx * sy * cz + sx * cy * sz;
out[2] = cx * cy * sz - sx * sy * cz;
out[3] = cx * cy * cz + sx * sy * sz;

return out;
}

/**
* Returns a string representation of a quatenion
*
* @param {quat} a vector to represent as a string
* @returns {String} string representation of the vector
*/
function str$4(a) {
return 'quat(' + a[0] + ', ' + a[1] + ', ' + a[2] + ', ' + a[3] + ')';
}

/**
* Creates a new quat initialized with values from an existing quaternion
*
* @param {quat} a quaternion to clone
* @returns {quat} a new quaternion
* @function
*/
var clone$4 = clone$6;

/**
* Creates a new quat initialized with the given values
*
* @param {Number} x X component
* @param {Number} y Y component
* @param {Number} z Z component
* @param {Number} w W component
* @returns {quat} a new quaternion
* @function
*/
var fromValues$4 = fromValues$6;

/**
* Copy the values from one quat to another
*
* @param {quat} out the receiving quaternion
* @param {quat} a the source quaternion
* @returns {quat} out
* @function
*/
var copy$4 = copy$6;

/**
* Set the components of a quat to the given values
*
* @param {quat} out the receiving quaternion
* @param {Number} x X component
* @param {Number} y Y component
* @param {Number} z Z component
* @param {Number} w W component
* @returns {quat} out
* @function
*/
var set$4 = set$6;

/**
* Adds two quat's
*
* @param {quat} out the receiving quaternion
* @param {quat} a the first operand
* @param {quat} b the second operand
* @returns {quat} out
* @function
*/
var add$4 = add$6;

/**
* Alias for {@link quat.multiply}
* @function
*/
var mul$4 = multiply$5;

/**
* Scales a quat by a scalar number
*
* @param {quat} out the receiving vector
* @param {quat} a the vector to scale
* @param {Number} b amount to scale the vector by
* @returns {quat} out
* @function
*/
var scale$4 = scale$6;

/**
* Calculates the dot product of two quat's
*
* @param {quat} a the first operand
* @param {quat} b the second operand
* @returns {Number} dot product of a and b
* @function
*/
var dot = dot$2;

/**
* Performs a linear interpolation between two quat's
*
* @param {quat} out the receiving quaternion
* @param {quat} a the first operand
* @param {quat} b the second operand
* @param {Number} t interpolation amount between the two inputs
* @returns {quat} out
* @function
*/
var lerp = lerp$2;

/**
* Calculates the length of a quat
*
* @param {quat} a vector to calculate length of
* @returns {Number} length of a
*/
var length = length$2;

/**
* Alias for {@link quat.length}
* @function
*/
var len = length;

/**
* Calculates the squared length of a quat
*
* @param {quat} a vector to calculate squared length of
* @returns {Number} squared length of a
* @function
*/
var squaredLength = squaredLength$2;

/**
* Alias for {@link quat.squaredLength}
* @function
*/
var sqrLen = squaredLength;

/**
* Normalize a quat
*
* @param {quat} out the receiving quaternion
* @param {quat} a quaternion to normalize
* @returns {quat} out
* @function
*/
var normalize = normalize$2;

/**
* Returns whether or not the quaternions have exactly the same elements in the same position (when compared with ===)
*
* @param {quat} a The first quaternion.
* @param {quat} b The second quaternion.
* @returns {Boolean} True if the vectors are equal, false otherwise.
*/
var exactEquals$4 = exactEquals$6;

/**
* Returns whether or not the quaternions have approximately the same elements in the same position.
*
* @param {quat} a The first vector.
* @param {quat} b The second vector.
* @returns {Boolean} True if the vectors are equal, false otherwise.
*/
var equals$5 = equals$7;

/**
* Sets a quaternion to represent the shortest rotation from one
* vector to another.
*
* Both vectors are assumed to be unit length.
*
* @param {quat} out the receiving quaternion.
* @param {vec3} a the initial vector
* @param {vec3} b the destination vector
* @returns {quat} out
*/
var rotationTo = function () {
var tmpvec3 = create$5();
var xUnitVec3 = fromValues$5(1, 0, 0);
var yUnitVec3 = fromValues$5(0, 1, 0);

return function (out, a, b) {
  var dot = dot$1(a, b);
  if (dot < -0.999999) {
    cross(tmpvec3, xUnitVec3, a);
    if (len$1(tmpvec3) < 0.000001) cross(tmpvec3, yUnitVec3, a);
    normalize$1(tmpvec3, tmpvec3);
    setAxisAngle(out, tmpvec3, Math.PI);
    return out;
  } else if (dot > 0.999999) {
    out[0] = 0;
    out[1] = 0;
    out[2] = 0;
    out[3] = 1;
    return out;
  } else {
    cross(tmpvec3, a, b);
    out[0] = tmpvec3[0];
    out[1] = tmpvec3[1];
    out[2] = tmpvec3[2];
    out[3] = 1 + dot;
    return normalize(out, out);
  }
};
}();

/**
* Performs a spherical linear interpolation with two control points
*
* @param {quat} out the receiving quaternion
* @param {quat} a the first operand
* @param {quat} b the second operand
* @param {quat} c the third operand
* @param {quat} d the fourth operand
* @param {Number} t interpolation amount
* @returns {quat} out
*/
var sqlerp = function () {
var temp1 = create$4();
var temp2 = create$4();

return function (out, a, b, c, d, t) {
  slerp(temp1, a, d, t);
  slerp(temp2, b, c, t);
  slerp(out, temp1, temp2, 2 * t * (1 - t));

  return out;
};
}();

/**
* Sets the specified quaternion with values corresponding to the given
* axes. Each axis is a vec3 and is expected to be unit length and
* perpendicular to all other specified axes.
*
* @param {vec3} view  the vector representing the viewing direction
* @param {vec3} right the vector representing the local "right" direction
* @param {vec3} up    the vector representing the local "up" direction
* @returns {quat} out
*/
var setAxes = function () {
var matr = create$2();

return function (out, view, right, up) {
  matr[0] = right[0];
  matr[3] = right[1];
  matr[6] = right[2];

  matr[1] = up[0];
  matr[4] = up[1];
  matr[7] = up[2];

  matr[2] = -view[0];
  matr[5] = -view[1];
  matr[8] = -view[2];

  return normalize(out, fromMat3(out, matr));
};
}();

var quat = Object.freeze({
create: create$4,
identity: identity$4,
setAxisAngle: setAxisAngle,
getAxisAngle: getAxisAngle,
multiply: multiply$5,
rotateX: rotateX$1,
rotateY: rotateY$1,
rotateZ: rotateZ$1,
calculateW: calculateW,
slerp: slerp,
invert: invert$4,
conjugate: conjugate,
fromMat3: fromMat3,
fromEuler: fromEuler,
str: str$4,
clone: clone$4,
fromValues: fromValues$4,
copy: copy$4,
set: set$4,
add: add$4,
mul: mul$4,
scale: scale$4,
dot: dot,
lerp: lerp,
length: length,
len: len,
squaredLength: squaredLength,
sqrLen: sqrLen,
normalize: normalize,
exactEquals: exactEquals$4,
equals: equals$5,
rotationTo: rotationTo,
sqlerp: sqlerp,
setAxes: setAxes
});

/* Copyright (c) 2015, Brandon Jones, Colin MacKenzie IV.

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in
all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
THE SOFTWARE. */

/**
* Dual Quaternion<br>
* Format: [real, dual]<br>
* Quaternion format: XYZW<br>
* Make sure to have normalized dual quaternions, otherwise the functions may not work as intended.<br>
* @module quat2
*/

/**
* Creates a new identity dual quat
*
* @returns {quat2} a new dual quaternion [real -> rotation, dual -> translation]
*/
function create$7() {
var dq = new ARRAY_TYPE(8);
dq[0] = 0;
dq[1] = 0;
dq[2] = 0;
dq[3] = 1;
dq[4] = 0;
dq[5] = 0;
dq[6] = 0;
dq[7] = 0;
return dq;
}

/**
* Creates a new quat initialized with values from an existing quaternion
*
* @param {quat2} a dual quaternion to clone
* @returns {quat2} new dual quaternion
* @function
*/
function clone$7(a) {
var dq = new ARRAY_TYPE(8);
dq[0] = a[0];
dq[1] = a[1];
dq[2] = a[2];
dq[3] = a[3];
dq[4] = a[4];
dq[5] = a[5];
dq[6] = a[6];
dq[7] = a[7];
return dq;
}

/**
* Creates a new dual quat initialized with the given values
*
* @param {Number} x1 X component
* @param {Number} y1 Y component
* @param {Number} z1 Z component
* @param {Number} w1 W component
* @param {Number} x2 X component
* @param {Number} y2 Y component
* @param {Number} z2 Z component
* @param {Number} w2 W component
* @returns {quat2} new dual quaternion
* @function
*/
function fromValues$7(x1, y1, z1, w1, x2, y2, z2, w2) {
var dq = new ARRAY_TYPE(8);
dq[0] = x1;
dq[1] = y1;
dq[2] = z1;
dq[3] = w1;
dq[4] = x2;
dq[5] = y2;
dq[6] = z2;
dq[7] = w2;
return dq;
}

/**
* Creates a new dual quat from the given values (quat and translation)
*
* @param {Number} x1 X component
* @param {Number} y1 Y component
* @param {Number} z1 Z component
* @param {Number} w1 W component
* @param {Number} x2 X component (translation)
* @param {Number} y2 Y component (translation)
* @param {Number} z2 Z component (translation)
* @returns {quat2} new dual quaternion
* @function
*/
function fromRotationTranslationValues(x1, y1, z1, w1, x2, y2, z2) {
var dq = new ARRAY_TYPE(8);
dq[0] = x1;
dq[1] = y1;
dq[2] = z1;
dq[3] = w1;
var ax = x2 * 0.5,
    ay = y2 * 0.5,
    az = z2 * 0.5;
dq[4] = ax * w1 + ay * z1 - az * y1;
dq[5] = ay * w1 + az * x1 - ax * z1;
dq[6] = az * w1 + ax * y1 - ay * x1;
dq[7] = -ax * x1 - ay * y1 - az * z1;
return dq;
}

/**
* Creates a dual quat from a quaternion and a translation
*
* @param {quat2} dual quaternion receiving operation result
* @param {quat} q quaternion
* @param {vec3} t tranlation vector
* @returns {quat2} dual quaternion receiving operation result
* @function
*/
function fromRotationTranslation$1(out, q, t) {
var ax = t[0] * 0.5,
    ay = t[1] * 0.5,
    az = t[2] * 0.5,
    bx = q[0],
    by = q[1],
    bz = q[2],
    bw = q[3];
out[0] = bx;
out[1] = by;
out[2] = bz;
out[3] = bw;
out[4] = ax * bw + ay * bz - az * by;
out[5] = ay * bw + az * bx - ax * bz;
out[6] = az * bw + ax * by - ay * bx;
out[7] = -ax * bx - ay * by - az * bz;
return out;
}

/**
* Creates a dual quat from a translation
*
* @param {quat2} dual quaternion receiving operation result
* @param {vec3} t translation vector
* @returns {quat2} dual quaternion receiving operation result
* @function
*/
function fromTranslation$3(out, t) {
out[0] = 0;
out[1] = 0;
out[2] = 0;
out[3] = 1;
out[4] = t[0] * 0.5;
out[5] = t[1] * 0.5;
out[6] = t[2] * 0.5;
out[7] = 0;
return out;
}

/**
* Creates a dual quat from a quaternion
*
* @param {quat2} dual quaternion receiving operation result
* @param {quat} q the quaternion
* @returns {quat2} dual quaternion receiving operation result
* @function
*/
function fromRotation$4(out, q) {
out[0] = q[0];
out[1] = q[1];
out[2] = q[2];
out[3] = q[3];
out[4] = 0;
out[5] = 0;
out[6] = 0;
out[7] = 0;
return out;
}

/**
* Creates a new dual quat from a matrix (4x4)
* 
* @param {quat2} out the dual quaternion
* @param {mat4} a the matrix
* @returns {quat2} dual quat receiving operation result
* @function
*/
function fromMat4$1(out, a) {
//TODO Optimize this 
var outer = create$4();
getRotation(outer, a);
var t = new ARRAY_TYPE(3);
getTranslation(t, a);
fromRotationTranslation$1(out, outer, t);
return out;
}

/**
* Copy the values from one dual quat to another
*
* @param {quat2} out the receiving dual quaternion
* @param {quat2} a the source dual quaternion
* @returns {quat2} out
* @function
*/
function copy$7(out, a) {
out[0] = a[0];
out[1] = a[1];
out[2] = a[2];
out[3] = a[3];
out[4] = a[4];
out[5] = a[5];
out[6] = a[6];
out[7] = a[7];
return out;
}

/**
* Set a dual quat to the identity dual quaternion
*
* @param {quat2} out the receiving quaternion
* @returns {quat2} out
*/
function identity$5(out) {
out[0] = 0;
out[1] = 0;
out[2] = 0;
out[3] = 1;
out[4] = 0;
out[5] = 0;
out[6] = 0;
out[7] = 0;
return out;
}

/**
* Set the components of a dual quat to the given values
*
* @param {quat2} out the receiving quaternion
* @param {Number} x1 X component
* @param {Number} y1 Y component
* @param {Number} z1 Z component
* @param {Number} w1 W component
* @param {Number} x2 X component
* @param {Number} y2 Y component
* @param {Number} z2 Z component
* @param {Number} w2 W component
* @returns {quat2} out
* @function
*/
function set$7(out, x1, y1, z1, w1, x2, y2, z2, w2) {
out[0] = x1;
out[1] = y1;
out[2] = z1;
out[3] = w1;

out[4] = x2;
out[5] = y2;
out[6] = z2;
out[7] = w2;
return out;
}

/**
* Gets the real part of a dual quat
* @param  {quat} out real part
* @param  {quat2} a Dual Quaternion
* @return {quat} real part
*/
var getReal = copy$4;

/**
* Gets the dual part of a dual quat
* @param  {quat} out dual part
* @param  {quat2} a Dual Quaternion
* @return {quat} dual part
*/
function getDual(out, a) {
out[0] = a[4];
out[1] = a[5];
out[2] = a[6];
out[3] = a[7];
return out;
}

/**
* Set the real component of a dual quat to the given quaternion
*
* @param {quat2} out the receiving quaternion
* @param {quat} q a quaternion representing the real part
* @returns {quat2} out
* @function
*/
var setReal = copy$4;

/**
* Set the dual component of a dual quat to the given quaternion
*
* @param {quat2} out the receiving quaternion
* @param {quat} q a quaternion representing the dual part
* @returns {quat2} out
* @function
*/
function setDual(out, q) {
out[4] = q[0];
out[5] = q[1];
out[6] = q[2];
out[7] = q[3];
return out;
}

/**
* Gets the translation of a normalized dual quat
* @param  {vec3} out translation
* @param  {quat2} a Dual Quaternion to be decomposed
* @return {vec3} translation
*/
function getTranslation$1(out, a) {
var ax = a[4],
    ay = a[5],
    az = a[6],
    aw = a[7],
    bx = -a[0],
    by = -a[1],
    bz = -a[2],
    bw = a[3];
out[0] = (ax * bw + aw * bx + ay * bz - az * by) * 2;
out[1] = (ay * bw + aw * by + az * bx - ax * bz) * 2;
out[2] = (az * bw + aw * bz + ax * by - ay * bx) * 2;
return out;
}

/**
* Translates a dual quat by the given vector
*
* @param {quat2} out the receiving dual quaternion
* @param {quat2} a the dual quaternion to translate
* @param {vec3} v vector to translate by
* @returns {quat2} out
*/
function translate$3(out, a, v) {
var ax1 = a[0],
    ay1 = a[1],
    az1 = a[2],
    aw1 = a[3],
    bx1 = v[0] * 0.5,
    by1 = v[1] * 0.5,
    bz1 = v[2] * 0.5,
    ax2 = a[4],
    ay2 = a[5],
    az2 = a[6],
    aw2 = a[7];
out[0] = ax1;
out[1] = ay1;
out[2] = az1;
out[3] = aw1;
out[4] = aw1 * bx1 + ay1 * bz1 - az1 * by1 + ax2;
out[5] = aw1 * by1 + az1 * bx1 - ax1 * bz1 + ay2;
out[6] = aw1 * bz1 + ax1 * by1 - ay1 * bx1 + az2;
out[7] = -ax1 * bx1 - ay1 * by1 - az1 * bz1 + aw2;
return out;
}

/**
* Rotates a dual quat around the X axis
*
* @param {quat2} out the receiving dual quaternion
* @param {quat2} a the dual quaternion to rotate
* @param {number} rad how far should the rotation be
* @returns {quat2} out
*/
function rotateX$3(out, a, rad) {
var bx = -a[0],
    by = -a[1],
    bz = -a[2],
    bw = a[3],
    ax = a[4],
    ay = a[5],
    az = a[6],
    aw = a[7],
    ax1 = ax * bw + aw * bx + ay * bz - az * by,
    ay1 = ay * bw + aw * by + az * bx - ax * bz,
    az1 = az * bw + aw * bz + ax * by - ay * bx,
    aw1 = aw * bw - ax * bx - ay * by - az * bz;
rotateX$1(out, a, rad);
bx = out[0];
by = out[1];
bz = out[2];
bw = out[3];
out[4] = ax1 * bw + aw1 * bx + ay1 * bz - az1 * by;
out[5] = ay1 * bw + aw1 * by + az1 * bx - ax1 * bz;
out[6] = az1 * bw + aw1 * bz + ax1 * by - ay1 * bx;
out[7] = aw1 * bw - ax1 * bx - ay1 * by - az1 * bz;
return out;
}

/**
* Rotates a dual quat around the Y axis
*
* @param {quat2} out the receiving dual quaternion
* @param {quat2} a the dual quaternion to rotate
* @param {number} rad how far should the rotation be
* @returns {quat2} out
*/
function rotateY$3(out, a, rad) {
var bx = -a[0],
    by = -a[1],
    bz = -a[2],
    bw = a[3],
    ax = a[4],
    ay = a[5],
    az = a[6],
    aw = a[7],
    ax1 = ax * bw + aw * bx + ay * bz - az * by,
    ay1 = ay * bw + aw * by + az * bx - ax * bz,
    az1 = az * bw + aw * bz + ax * by - ay * bx,
    aw1 = aw * bw - ax * bx - ay * by - az * bz;
rotateY$1(out, a, rad);
bx = out[0];
by = out[1];
bz = out[2];
bw = out[3];
out[4] = ax1 * bw + aw1 * bx + ay1 * bz - az1 * by;
out[5] = ay1 * bw + aw1 * by + az1 * bx - ax1 * bz;
out[6] = az1 * bw + aw1 * bz + ax1 * by - ay1 * bx;
out[7] = aw1 * bw - ax1 * bx - ay1 * by - az1 * bz;
return out;
}

/**
* Rotates a dual quat around the Z axis
*
* @param {quat2} out the receiving dual quaternion
* @param {quat2} a the dual quaternion to rotate
* @param {number} rad how far should the rotation be
* @returns {quat2} out
*/
function rotateZ$3(out, a, rad) {
var bx = -a[0],
    by = -a[1],
    bz = -a[2],
    bw = a[3],
    ax = a[4],
    ay = a[5],
    az = a[6],
    aw = a[7],
    ax1 = ax * bw + aw * bx + ay * bz - az * by,
    ay1 = ay * bw + aw * by + az * bx - ax * bz,
    az1 = az * bw + aw * bz + ax * by - ay * bx,
    aw1 = aw * bw - ax * bx - ay * by - az * bz;
rotateZ$1(out, a, rad);
bx = out[0];
by = out[1];
bz = out[2];
bw = out[3];
out[4] = ax1 * bw + aw1 * bx + ay1 * bz - az1 * by;
out[5] = ay1 * bw + aw1 * by + az1 * bx - ax1 * bz;
out[6] = az1 * bw + aw1 * bz + ax1 * by - ay1 * bx;
out[7] = aw1 * bw - ax1 * bx - ay1 * by - az1 * bz;
return out;
}

/**
* Rotates a dual quat by a given quaternion (a * q)
*
* @param {quat2} out the receiving dual quaternion
* @param {quat2} a the dual quaternion to rotate
* @param {quat} q quaternion to rotate by
* @returns {quat2} out
*/
function rotateByQuatAppend(out, a, q) {
var qx = q[0],
    qy = q[1],
    qz = q[2],
    qw = q[3],
    ax = a[0],
    ay = a[1],
    az = a[2],
    aw = a[3];

out[0] = ax * qw + aw * qx + ay * qz - az * qy;
out[1] = ay * qw + aw * qy + az * qx - ax * qz;
out[2] = az * qw + aw * qz + ax * qy - ay * qx;
out[3] = aw * qw - ax * qx - ay * qy - az * qz;
ax = a[4];
ay = a[5];
az = a[6];
aw = a[7];
out[4] = ax * qw + aw * qx + ay * qz - az * qy;
out[5] = ay * qw + aw * qy + az * qx - ax * qz;
out[6] = az * qw + aw * qz + ax * qy - ay * qx;
out[7] = aw * qw - ax * qx - ay * qy - az * qz;
return out;
}

/**
* Rotates a dual quat by a given quaternion (q * a)
*
* @param {quat2} out the receiving dual quaternion
* @param {quat} q quaternion to rotate by
* @param {quat2} a the dual quaternion to rotate
* @returns {quat2} out
*/
function rotateByQuatPrepend(out, q, a) {
var qx = q[0],
    qy = q[1],
    qz = q[2],
    qw = q[3],
    bx = a[0],
    by = a[1],
    bz = a[2],
    bw = a[3];

out[0] = qx * bw + qw * bx + qy * bz - qz * by;
out[1] = qy * bw + qw * by + qz * bx - qx * bz;
out[2] = qz * bw + qw * bz + qx * by - qy * bx;
out[3] = qw * bw - qx * bx - qy * by - qz * bz;
bx = a[4];
by = a[5];
bz = a[6];
bw = a[7];
out[4] = qx * bw + qw * bx + qy * bz - qz * by;
out[5] = qy * bw + qw * by + qz * bx - qx * bz;
out[6] = qz * bw + qw * bz + qx * by - qy * bx;
out[7] = qw * bw - qx * bx - qy * by - qz * bz;
return out;
}

/**
* Rotates a dual quat around a given axis. Does the normalisation automatically
*
* @param {quat2} out the receiving dual quaternion
* @param {quat2} a the dual quaternion to rotate
* @param {vec3} axis the axis to rotate around
* @param {Number} rad how far the rotation should be
* @returns {quat2} out
*/
function rotateAroundAxis(out, a, axis, rad) {
//Special case for rad = 0
if (Math.abs(rad) < EPSILON) {
  return copy$7(out, a);
}
var axisLength = Math.sqrt(axis[0] * axis[0] + axis[1] * axis[1] + axis[2] * axis[2]);

rad = rad * 0.5;
var s = Math.sin(rad);
var bx = s * axis[0] / axisLength;
var by = s * axis[1] / axisLength;
var bz = s * axis[2] / axisLength;
var bw = Math.cos(rad);

var ax1 = a[0],
    ay1 = a[1],
    az1 = a[2],
    aw1 = a[3];
out[0] = ax1 * bw + aw1 * bx + ay1 * bz - az1 * by;
out[1] = ay1 * bw + aw1 * by + az1 * bx - ax1 * bz;
out[2] = az1 * bw + aw1 * bz + ax1 * by - ay1 * bx;
out[3] = aw1 * bw - ax1 * bx - ay1 * by - az1 * bz;

var ax = a[4],
    ay = a[5],
    az = a[6],
    aw = a[7];
out[4] = ax * bw + aw * bx + ay * bz - az * by;
out[5] = ay * bw + aw * by + az * bx - ax * bz;
out[6] = az * bw + aw * bz + ax * by - ay * bx;
out[7] = aw * bw - ax * bx - ay * by - az * bz;

return out;
}

/**
* Adds two dual quat's
*
* @param {quat2} out the receiving dual quaternion
* @param {quat2} a the first operand
* @param {quat2} b the second operand
* @returns {quat2} out
* @function
*/
function add$7(out, a, b) {
out[0] = a[0] + b[0];
out[1] = a[1] + b[1];
out[2] = a[2] + b[2];
out[3] = a[3] + b[3];
out[4] = a[4] + b[4];
out[5] = a[5] + b[5];
out[6] = a[6] + b[6];
out[7] = a[7] + b[7];
return out;
}

/**
* Multiplies two dual quat's
*
* @param {quat2} out the receiving dual quaternion
* @param {quat2} a the first operand
* @param {quat2} b the second operand
* @returns {quat2} out
*/
function multiply$8(out, a, b) {
var ax0 = a[0],
    ay0 = a[1],
    az0 = a[2],
    aw0 = a[3],
    bx1 = b[4],
    by1 = b[5],
    bz1 = b[6],
    bw1 = b[7],
    ax1 = a[4],
    ay1 = a[5],
    az1 = a[6],
    aw1 = a[7],
    bx0 = b[0],
    by0 = b[1],
    bz0 = b[2],
    bw0 = b[3];
out[0] = ax0 * bw0 + aw0 * bx0 + ay0 * bz0 - az0 * by0;
out[1] = ay0 * bw0 + aw0 * by0 + az0 * bx0 - ax0 * bz0;
out[2] = az0 * bw0 + aw0 * bz0 + ax0 * by0 - ay0 * bx0;
out[3] = aw0 * bw0 - ax0 * bx0 - ay0 * by0 - az0 * bz0;
out[4] = ax0 * bw1 + aw0 * bx1 + ay0 * bz1 - az0 * by1 + ax1 * bw0 + aw1 * bx0 + ay1 * bz0 - az1 * by0;
out[5] = ay0 * bw1 + aw0 * by1 + az0 * bx1 - ax0 * bz1 + ay1 * bw0 + aw1 * by0 + az1 * bx0 - ax1 * bz0;
out[6] = az0 * bw1 + aw0 * bz1 + ax0 * by1 - ay0 * bx1 + az1 * bw0 + aw1 * bz0 + ax1 * by0 - ay1 * bx0;
out[7] = aw0 * bw1 - ax0 * bx1 - ay0 * by1 - az0 * bz1 + aw1 * bw0 - ax1 * bx0 - ay1 * by0 - az1 * bz0;
return out;
}

/**
* Alias for {@link quat2.multiply}
* @function
*/
var mul$7 = multiply$8;

/**
* Scales a dual quat by a scalar number
*
* @param {quat2} out the receiving dual quat
* @param {quat2} a the dual quat to scale
* @param {Number} b amount to scale the dual quat by
* @returns {quat2} out
* @function
*/
function scale$7(out, a, b) {
out[0] = a[0] * b;
out[1] = a[1] * b;
out[2] = a[2] * b;
out[3] = a[3] * b;
out[4] = a[4] * b;
out[5] = a[5] * b;
out[6] = a[6] * b;
out[7] = a[7] * b;
return out;
}

/**
* Calculates the dot product of two dual quat's (The dot product of the real parts)
*
* @param {quat2} a the first operand
* @param {quat2} b the second operand
* @returns {Number} dot product of a and b
* @function
*/
var dot$3 = dot;

/**
* Performs a linear interpolation between two dual quats's
* NOTE: The resulting dual quaternions won't always be normalized (The error is most noticeable when t = 0.5)
*
* @param {quat2} out the receiving dual quat
* @param {quat2} a the first operand
* @param {quat2} b the second operand
* @param {Number} t interpolation amount between the two inputs
* @returns {quat2} out
*/
function lerp$3(out, a, b, t) {
var mt = 1 - t;
if (dot$3(a, b) < 0) t = -t;

out[0] = a[0] * mt + b[0] * t;
out[1] = a[1] * mt + b[1] * t;
out[2] = a[2] * mt + b[2] * t;
out[3] = a[3] * mt + b[3] * t;
out[4] = a[4] * mt + b[4] * t;
out[5] = a[5] * mt + b[5] * t;
out[6] = a[6] * mt + b[6] * t;
out[7] = a[7] * mt + b[7] * t;

return out;
}

/**
* Calculates the inverse of a dual quat. If they are normalized, conjugate is cheaper
*
* @param {quat2} out the receiving dual quaternion
* @param {quat2} a dual quat to calculate inverse of
* @returns {quat2} out
*/
function invert$5(out, a) {
var sqlen = squaredLength$3(a);
out[0] = -a[0] / sqlen;
out[1] = -a[1] / sqlen;
out[2] = -a[2] / sqlen;
out[3] = a[3] / sqlen;
out[4] = -a[4] / sqlen;
out[5] = -a[5] / sqlen;
out[6] = -a[6] / sqlen;
out[7] = a[7] / sqlen;
return out;
}

/**
* Calculates the conjugate of a dual quat
* If the dual quaternion is normalized, this function is faster than quat2.inverse and produces the same result.
*
* @param {quat2} out the receiving quaternion
* @param {quat2} a quat to calculate conjugate of
* @returns {quat2} out
*/
function conjugate$1(out, a) {
out[0] = -a[0];
out[1] = -a[1];
out[2] = -a[2];
out[3] = a[3];
out[4] = -a[4];
out[5] = -a[5];
out[6] = -a[6];
out[7] = a[7];
return out;
}

/**
* Calculates the length of a dual quat
*
* @param {quat2} a dual quat to calculate length of
* @returns {Number} length of a
* @function
*/
var length$3 = length;

/**
* Alias for {@link quat2.length}
* @function
*/
var len$3 = length$3;

/**
* Calculates the squared length of a dual quat
*
* @param {quat2} a dual quat to calculate squared length of
* @returns {Number} squared length of a
* @function
*/
var squaredLength$3 = squaredLength;

/**
* Alias for {@link quat2.squaredLength}
* @function
*/
var sqrLen$3 = squaredLength$3;

/**
* Normalize a dual quat
*
* @param {quat2} out the receiving dual quaternion
* @param {quat2} a dual quaternion to normalize
* @returns {quat2} out
* @function
*/
function normalize$3(out, a) {
var magnitude = squaredLength$3(a);
if (magnitude > 0) {
  magnitude = Math.sqrt(magnitude);
  out[0] = a[0] / magnitude;
  out[1] = a[1] / magnitude;
  out[2] = a[2] / magnitude;
  out[3] = a[3] / magnitude;
  out[4] = a[4] / magnitude;
  out[5] = a[5] / magnitude;
  out[6] = a[6] / magnitude;
  out[7] = a[7] / magnitude;
}
return out;
}

/**
* Returns a string representation of a dual quatenion
*
* @param {quat2} a dual quaternion to represent as a string
* @returns {String} string representation of the dual quat
*/
function str$7(a) {
return 'quat2(' + a[0] + ', ' + a[1] + ', ' + a[2] + ', ' + a[3] + ', ' + a[4] + ', ' + a[5] + ', ' + a[6] + ', ' + a[7] + ')';
}

/**
* Returns whether or not the dual quaternions have exactly the same elements in the same position (when compared with ===)
*
* @param {quat2} a the first dual quaternion.
* @param {quat2} b the second dual quaternion.
* @returns {Boolean} true if the dual quaternions are equal, false otherwise.
*/
function exactEquals$7(a, b) {
return a[0] === b[0] && a[1] === b[1] && a[2] === b[2] && a[3] === b[3] && a[4] === b[4] && a[5] === b[5] && a[6] === b[6] && a[7] === b[7];
}

/**
* Returns whether or not the dual quaternions have approximately the same elements in the same position.
*
* @param {quat2} a the first dual quat.
* @param {quat2} b the second dual quat.
* @returns {Boolean} true if the dual quats are equal, false otherwise.
*/
function equals$8(a, b) {
var a0 = a[0],
    a1 = a[1],
    a2 = a[2],
    a3 = a[3],
    a4 = a[4],
    a5 = a[5],
    a6 = a[6],
    a7 = a[7];
var b0 = b[0],
    b1 = b[1],
    b2 = b[2],
    b3 = b[3],
    b4 = b[4],
    b5 = b[5],
    b6 = b[6],
    b7 = b[7];
return Math.abs(a0 - b0) <= EPSILON * Math.max(1.0, Math.abs(a0), Math.abs(b0)) && Math.abs(a1 - b1) <= EPSILON * Math.max(1.0, Math.abs(a1), Math.abs(b1)) && Math.abs(a2 - b2) <= EPSILON * Math.max(1.0, Math.abs(a2), Math.abs(b2)) && Math.abs(a3 - b3) <= EPSILON * Math.max(1.0, Math.abs(a3), Math.abs(b3)) && Math.abs(a4 - b4) <= EPSILON * Math.max(1.0, Math.abs(a4), Math.abs(b4)) && Math.abs(a5 - b5) <= EPSILON * Math.max(1.0, Math.abs(a5), Math.abs(b5)) && Math.abs(a6 - b6) <= EPSILON * Math.max(1.0, Math.abs(a6), Math.abs(b6)) && Math.abs(a7 - b7) <= EPSILON * Math.max(1.0, Math.abs(a7), Math.abs(b7));
}

var quat2 = Object.freeze({
create: create$7,
clone: clone$7,
fromValues: fromValues$7,
fromRotationTranslationValues: fromRotationTranslationValues,
fromRotationTranslation: fromRotationTranslation$1,
fromTranslation: fromTranslation$3,
fromRotation: fromRotation$4,
fromMat4: fromMat4$1,
copy: copy$7,
identity: identity$5,
set: set$7,
getReal: getReal,
getDual: getDual,
setReal: setReal,
setDual: setDual,
getTranslation: getTranslation$1,
translate: translate$3,
rotateX: rotateX$3,
rotateY: rotateY$3,
rotateZ: rotateZ$3,
rotateByQuatAppend: rotateByQuatAppend,
rotateByQuatPrepend: rotateByQuatPrepend,
rotateAroundAxis: rotateAroundAxis,
add: add$7,
multiply: multiply$8,
mul: mul$7,
scale: scale$7,
dot: dot$3,
lerp: lerp$3,
invert: invert$5,
conjugate: conjugate$1,
length: length$3,
len: len$3,
squaredLength: squaredLength$3,
sqrLen: sqrLen$3,
normalize: normalize$3,
str: str$7,
exactEquals: exactEquals$7,
equals: equals$8
});

/* Copyright (c) 2015, Brandon Jones, Colin MacKenzie IV.

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in
all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
THE SOFTWARE. */

/**
* 2 Dimensional Vector
* @module vec2
*/

/**
* Creates a new, empty vec2
*
* @returns {vec2} a new 2D vector
*/
function create$8() {
var out = new ARRAY_TYPE(2);
out[0] = 0;
out[1] = 0;
return out;
}

/**
* Creates a new vec2 initialized with values from an existing vector
*
* @param {vec2} a vector to clone
* @returns {vec2} a new 2D vector
*/
function clone$8(a) {
var out = new ARRAY_TYPE(2);
out[0] = a[0];
out[1] = a[1];
return out;
}

/**
* Creates a new vec2 initialized with the given values
*
* @param {Number} x X component
* @param {Number} y Y component
* @returns {vec2} a new 2D vector
*/
function fromValues$8(x, y) {
var out = new ARRAY_TYPE(2);
out[0] = x;
out[1] = y;
return out;
}

/**
* Copy the values from one vec2 to another
*
* @param {vec2} out the receiving vector
* @param {vec2} a the source vector
* @returns {vec2} out
*/
function copy$8(out, a) {
out[0] = a[0];
out[1] = a[1];
return out;
}

/**
* Set the components of a vec2 to the given values
*
* @param {vec2} out the receiving vector
* @param {Number} x X component
* @param {Number} y Y component
* @returns {vec2} out
*/
function set$8(out, x, y) {
out[0] = x;
out[1] = y;
return out;
}

/**
* Adds two vec2's
*
* @param {vec2} out the receiving vector
* @param {vec2} a the first operand
* @param {vec2} b the second operand
* @returns {vec2} out
*/
function add$8(out, a, b) {
out[0] = a[0] + b[0];
out[1] = a[1] + b[1];
return out;
}

/**
* Subtracts vector b from vector a
*
* @param {vec2} out the receiving vector
* @param {vec2} a the first operand
* @param {vec2} b the second operand
* @returns {vec2} out
*/
function subtract$6(out, a, b) {
out[0] = a[0] - b[0];
out[1] = a[1] - b[1];
return out;
}

/**
* Multiplies two vec2's
*
* @param {vec2} out the receiving vector
* @param {vec2} a the first operand
* @param {vec2} b the second operand
* @returns {vec2} out
*/
function multiply$9(out, a, b) {
out[0] = a[0] * b[0];
out[1] = a[1] * b[1];
return out;
}

/**
* Divides two vec2's
*
* @param {vec2} out the receiving vector
* @param {vec2} a the first operand
* @param {vec2} b the second operand
* @returns {vec2} out
*/
function divide$2(out, a, b) {
out[0] = a[0] / b[0];
out[1] = a[1] / b[1];
return out;
}

/**
* Math.ceil the components of a vec2
*
* @param {vec2} out the receiving vector
* @param {vec2} a vector to ceil
* @returns {vec2} out
*/
function ceil$2(out, a) {
out[0] = Math.ceil(a[0]);
out[1] = Math.ceil(a[1]);
return out;
}

/**
* Math.floor the components of a vec2
*
* @param {vec2} out the receiving vector
* @param {vec2} a vector to floor
* @returns {vec2} out
*/
function floor$2(out, a) {
out[0] = Math.floor(a[0]);
out[1] = Math.floor(a[1]);
return out;
}

/**
* Returns the minimum of two vec2's
*
* @param {vec2} out the receiving vector
* @param {vec2} a the first operand
* @param {vec2} b the second operand
* @returns {vec2} out
*/
function min$2(out, a, b) {
out[0] = Math.min(a[0], b[0]);
out[1] = Math.min(a[1], b[1]);
return out;
}

/**
* Returns the maximum of two vec2's
*
* @param {vec2} out the receiving vector
* @param {vec2} a the first operand
* @param {vec2} b the second operand
* @returns {vec2} out
*/
function max$2(out, a, b) {
out[0] = Math.max(a[0], b[0]);
out[1] = Math.max(a[1], b[1]);
return out;
}

/**
* Math.round the components of a vec2
*
* @param {vec2} out the receiving vector
* @param {vec2} a vector to round
* @returns {vec2} out
*/
function round$2(out, a) {
out[0] = Math.round(a[0]);
out[1] = Math.round(a[1]);
return out;
}

/**
* Scales a vec2 by a scalar number
*
* @param {vec2} out the receiving vector
* @param {vec2} a the vector to scale
* @param {Number} b amount to scale the vector by
* @returns {vec2} out
*/
function scale$8(out, a, b) {
out[0] = a[0] * b;
out[1] = a[1] * b;
return out;
}

/**
* Adds two vec2's after scaling the second operand by a scalar value
*
* @param {vec2} out the receiving vector
* @param {vec2} a the first operand
* @param {vec2} b the second operand
* @param {Number} scale the amount to scale b by before adding
* @returns {vec2} out
*/
function scaleAndAdd$2(out, a, b, scale) {
out[0] = a[0] + b[0] * scale;
out[1] = a[1] + b[1] * scale;
return out;
}

/**
* Calculates the euclidian distance between two vec2's
*
* @param {vec2} a the first operand
* @param {vec2} b the second operand
* @returns {Number} distance between a and b
*/
function distance$2(a, b) {
var x = b[0] - a[0],
    y = b[1] - a[1];
return Math.sqrt(x * x + y * y);
}

/**
* Calculates the squared euclidian distance between two vec2's
*
* @param {vec2} a the first operand
* @param {vec2} b the second operand
* @returns {Number} squared distance between a and b
*/
function squaredDistance$2(a, b) {
var x = b[0] - a[0],
    y = b[1] - a[1];
return x * x + y * y;
}

/**
* Calculates the length of a vec2
*
* @param {vec2} a vector to calculate length of
* @returns {Number} length of a
*/
function length$4(a) {
var x = a[0],
    y = a[1];
return Math.sqrt(x * x + y * y);
}

/**
* Calculates the squared length of a vec2
*
* @param {vec2} a vector to calculate squared length of
* @returns {Number} squared length of a
*/
function squaredLength$4(a) {
var x = a[0],
    y = a[1];
return x * x + y * y;
}

/**
* Negates the components of a vec2
*
* @param {vec2} out the receiving vector
* @param {vec2} a vector to negate
* @returns {vec2} out
*/
function negate$2(out, a) {
out[0] = -a[0];
out[1] = -a[1];
return out;
}

/**
* Returns the inverse of the components of a vec2
*
* @param {vec2} out the receiving vector
* @param {vec2} a vector to invert
* @returns {vec2} out
*/
function inverse$2(out, a) {
out[0] = 1.0 / a[0];
out[1] = 1.0 / a[1];
return out;
}

/**
* Normalize a vec2
*
* @param {vec2} out the receiving vector
* @param {vec2} a vector to normalize
* @returns {vec2} out
*/
function normalize$4(out, a) {
var x = a[0],
    y = a[1];
var len = x * x + y * y;
if (len > 0) {
  //TODO: evaluate use of glm_invsqrt here?
  len = 1 / Math.sqrt(len);
  out[0] = a[0] * len;
  out[1] = a[1] * len;
}
return out;
}

/**
* Calculates the dot product of two vec2's
*
* @param {vec2} a the first operand
* @param {vec2} b the second operand
* @returns {Number} dot product of a and b
*/
function dot$4(a, b) {
return a[0] * b[0] + a[1] * b[1];
}

/**
* Computes the cross product of two vec2's
* Note that the cross product must by definition produce a 3D vector
*
* @param {vec3} out the receiving vector
* @param {vec2} a the first operand
* @param {vec2} b the second operand
* @returns {vec3} out
*/
function cross$1(out, a, b) {
var z = a[0] * b[1] - a[1] * b[0];
out[0] = out[1] = 0;
out[2] = z;
return out;
}

/**
* Performs a linear interpolation between two vec2's
*
* @param {vec2} out the receiving vector
* @param {vec2} a the first operand
* @param {vec2} b the second operand
* @param {Number} t interpolation amount between the two inputs
* @returns {vec2} out
*/
function lerp$4(out, a, b, t) {
var ax = a[0],
    ay = a[1];
out[0] = ax + t * (b[0] - ax);
out[1] = ay + t * (b[1] - ay);
return out;
}

/**
* Generates a random vector with the given scale
*
* @param {vec2} out the receiving vector
* @param {Number} [scale] Length of the resulting vector. If ommitted, a unit vector will be returned
* @returns {vec2} out
*/
function random$2(out, scale) {
scale = scale || 1.0;
var r = RANDOM() * 2.0 * Math.PI;
out[0] = Math.cos(r) * scale;
out[1] = Math.sin(r) * scale;
return out;
}

/**
* Transforms the vec2 with a mat2
*
* @param {vec2} out the receiving vector
* @param {vec2} a the vector to transform
* @param {mat2} m matrix to transform with
* @returns {vec2} out
*/
function transformMat2(out, a, m) {
var x = a[0],
    y = a[1];
out[0] = m[0] * x + m[2] * y;
out[1] = m[1] * x + m[3] * y;
return out;
}

/**
* Transforms the vec2 with a mat2d
*
* @param {vec2} out the receiving vector
* @param {vec2} a the vector to transform
* @param {mat2d} m matrix to transform with
* @returns {vec2} out
*/
function transformMat2d(out, a, m) {
var x = a[0],
    y = a[1];
out[0] = m[0] * x + m[2] * y + m[4];
out[1] = m[1] * x + m[3] * y + m[5];
return out;
}

/**
* Transforms the vec2 with a mat3
* 3rd vector component is implicitly '1'
*
* @param {vec2} out the receiving vector
* @param {vec2} a the vector to transform
* @param {mat3} m matrix to transform with
* @returns {vec2} out
*/
function transformMat3$1(out, a, m) {
var x = a[0],
    y = a[1];
out[0] = m[0] * x + m[3] * y + m[6];
out[1] = m[1] * x + m[4] * y + m[7];
return out;
}

/**
* Transforms the vec2 with a mat4
* 3rd vector component is implicitly '0'
* 4th vector component is implicitly '1'
*
* @param {vec2} out the receiving vector
* @param {vec2} a the vector to transform
* @param {mat4} m matrix to transform with
* @returns {vec2} out
*/
function transformMat4$2(out, a, m) {
var x = a[0];
var y = a[1];
out[0] = m[0] * x + m[4] * y + m[12];
out[1] = m[1] * x + m[5] * y + m[13];
return out;
}

/**
* Returns a string representation of a vector
*
* @param {vec2} a vector to represent as a string
* @returns {String} string representation of the vector
*/
function str$8(a) {
return 'vec2(' + a[0] + ', ' + a[1] + ')';
}

/**
* Returns whether or not the vectors exactly have the same elements in the same position (when compared with ===)
*
* @param {vec2} a The first vector.
* @param {vec2} b The second vector.
* @returns {Boolean} True if the vectors are equal, false otherwise.
*/
function exactEquals$8(a, b) {
return a[0] === b[0] && a[1] === b[1];
}

/**
* Returns whether or not the vectors have approximately the same elements in the same position.
*
* @param {vec2} a The first vector.
* @param {vec2} b The second vector.
* @returns {Boolean} True if the vectors are equal, false otherwise.
*/
function equals$9(a, b) {
var a0 = a[0],
    a1 = a[1];
var b0 = b[0],
    b1 = b[1];
return Math.abs(a0 - b0) <= EPSILON * Math.max(1.0, Math.abs(a0), Math.abs(b0)) && Math.abs(a1 - b1) <= EPSILON * Math.max(1.0, Math.abs(a1), Math.abs(b1));
}

/**
* Alias for {@link vec2.length}
* @function
*/
var len$4 = length$4;

/**
* Alias for {@link vec2.subtract}
* @function
*/
var sub$6 = subtract$6;

/**
* Alias for {@link vec2.multiply}
* @function
*/
var mul$8 = multiply$9;

/**
* Alias for {@link vec2.divide}
* @function
*/
var div$2 = divide$2;

/**
* Alias for {@link vec2.distance}
* @function
*/
var dist$2 = distance$2;

/**
* Alias for {@link vec2.squaredDistance}
* @function
*/
var sqrDist$2 = squaredDistance$2;

/**
* Alias for {@link vec2.squaredLength}
* @function
*/
var sqrLen$4 = squaredLength$4;

/**
* Perform some operation over an array of vec2s.
*
* @param {Array} a the array of vectors to iterate over
* @param {Number} stride Number of elements between the start of each vec2. If 0 assumes tightly packed
* @param {Number} offset Number of elements to skip at the beginning of the array
* @param {Number} count Number of vec2s to iterate over. If 0 iterates over entire array
* @param {Function} fn Function to call for each vector in the array
* @param {Object} [arg] additional argument to pass to fn
* @returns {Array} a
* @function
*/
var forEach$2 = function () {
var vec = create$8();

return function (a, stride, offset, count, fn, arg) {
  var i = void 0,
      l = void 0;
  if (!stride) {
    stride = 2;
  }

  if (!offset) {
    offset = 0;
  }

  if (count) {
    l = Math.min(count * stride + offset, a.length);
  } else {
    l = a.length;
  }

  for (i = offset; i < l; i += stride) {
    vec[0] = a[i];vec[1] = a[i + 1];
    fn(vec, vec, arg);
    a[i] = vec[0];a[i + 1] = vec[1];
  }

  return a;
};
}();

var vec2 = Object.freeze({
create: create$8,
clone: clone$8,
fromValues: fromValues$8,
copy: copy$8,
set: set$8,
add: add$8,
subtract: subtract$6,
multiply: multiply$9,
divide: divide$2,
ceil: ceil$2,
floor: floor$2,
min: min$2,
max: max$2,
round: round$2,
scale: scale$8,
scaleAndAdd: scaleAndAdd$2,
distance: distance$2,
squaredDistance: squaredDistance$2,
length: length$4,
squaredLength: squaredLength$4,
negate: negate$2,
inverse: inverse$2,
normalize: normalize$4,
dot: dot$4,
cross: cross$1,
lerp: lerp$4,
random: random$2,
transformMat2: transformMat2,
transformMat2d: transformMat2d,
transformMat3: transformMat3$1,
transformMat4: transformMat4$2,
str: str$8,
exactEquals: exactEquals$8,
equals: equals$9,
len: len$4,
sub: sub$6,
mul: mul$8,
div: div$2,
dist: dist$2,
sqrDist: sqrDist$2,
sqrLen: sqrLen$4,
forEach: forEach$2
});

/**
* @fileoverview gl-matrix - High performance matrix and vector operations
* @author Brandon Jones
* @author Colin MacKenzie IV
* @version 2.4.0
*/

/* Copyright (c) 2015, Brandon Jones, Colin MacKenzie IV.

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in
all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
THE SOFTWARE. */
// END HEADER

var asyncGenerator = function () {
function AwaitValue(value) {
  this.value = value;
}

function AsyncGenerator(gen) {
  var front, back;

  function send(key, arg) {
    return new Promise(function (resolve, reject) {
      var request = {
        key: key,
        arg: arg,
        resolve: resolve,
        reject: reject,
        next: null
      };

      if (back) {
        back = back.next = request;
      } else {
        front = back = request;
        resume(key, arg);
      }
    });
  }

  function resume(key, arg) {
    try {
      var result = gen[key](arg);
      var value = result.value;

      if (value instanceof AwaitValue) {
        Promise.resolve(value.value).then(function (arg) {
          resume("next", arg);
        }, function (arg) {
          resume("throw", arg);
        });
      } else {
        settle(result.done ? "return" : "normal", result.value);
      }
    } catch (err) {
      settle("throw", err);
    }
  }

  function settle(type, value) {
    switch (type) {
      case "return":
        front.resolve({
          value: value,
          done: true
        });
        break;

      case "throw":
        front.reject(value);
        break;

      default:
        front.resolve({
          value: value,
          done: false
        });
        break;
    }

    front = front.next;

    if (front) {
      resume(front.key, front.arg);
    } else {
      back = null;
    }
  }

  this._invoke = send;

  if (typeof gen.return !== "function") {
    this.return = undefined;
  }
}

if (typeof Symbol === "function" && Symbol.asyncIterator) {
  AsyncGenerator.prototype[Symbol.asyncIterator] = function () {
    return this;
  };
}

AsyncGenerator.prototype.next = function (arg) {
  return this._invoke("next", arg);
};

AsyncGenerator.prototype.throw = function (arg) {
  return this._invoke("throw", arg);
};

AsyncGenerator.prototype.return = function (arg) {
  return this._invoke("return", arg);
};

return {
  wrap: function (fn) {
    return function () {
      return new AsyncGenerator(fn.apply(this, arguments));
    };
  },
  await: function (value) {
    return new AwaitValue(value);
  }
};
}();





var classCallCheck = function (instance, Constructor) {
if (!(instance instanceof Constructor)) {
  throw new TypeError("Cannot call a class as a function");
}
};

var createClass = function () {
function defineProperties(target, props) {
  for (var i = 0; i < props.length; i++) {
    var descriptor = props[i];
    descriptor.enumerable = descriptor.enumerable || false;
    descriptor.configurable = true;
    if ("value" in descriptor) descriptor.writable = true;
    Object.defineProperty(target, descriptor.key, descriptor);
  }
}

return function (Constructor, protoProps, staticProps) {
  if (protoProps) defineProperties(Constructor.prototype, protoProps);
  if (staticProps) defineProperties(Constructor, staticProps);
  return Constructor;
};
}();







var get = function get(object, property, receiver) {
if (object === null) object = Function.prototype;
var desc = Object.getOwnPropertyDescriptor(object, property);

if (desc === undefined) {
  var parent = Object.getPrototypeOf(object);

  if (parent === null) {
    return undefined;
  } else {
    return get(parent, property, receiver);
  }
} else if ("value" in desc) {
  return desc.value;
} else {
  var getter = desc.get;

  if (getter === undefined) {
    return undefined;
  }

  return getter.call(receiver);
}
};

var inherits = function (subClass, superClass) {
if (typeof superClass !== "function" && superClass !== null) {
  throw new TypeError("Super expression must either be null or a function, not " + typeof superClass);
}

subClass.prototype = Object.create(superClass && superClass.prototype, {
  constructor: {
    value: subClass,
    enumerable: false,
    writable: true,
    configurable: true
  }
});
if (superClass) Object.setPrototypeOf ? Object.setPrototypeOf(subClass, superClass) : subClass.__proto__ = superClass;
};











var possibleConstructorReturn = function (self, call) {
if (!self) {
  throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
}

return call && (typeof call === "object" || typeof call === "function") ? call : self;
};



















var toConsumableArray = function (arr) {
if (Array.isArray(arr)) {
  for (var i = 0, arr2 = Array(arr.length); i < arr.length; i++) arr2[i] = arr[i];

  return arr2;
} else {
  return Array.from(arr);
}
};

var tMatrix = create$3();

var _id = 0;

var Component = function () {
function Component() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref$id = _ref.id,
      id = _ref$id === undefined ? "Component_" + _id++ : _ref$id,
      _ref$enabled = _ref.enabled,
      enabled = _ref$enabled === undefined ? true : _ref$enabled,
      _ref$transform = _ref.transform,
      transform = _ref$transform === undefined ? new Transform$1() : _ref$transform,
      _ref$visible = _ref.visible,
      visible = _ref$visible === undefined ? true : _ref$visible;

  classCallCheck(this, Component);

  this.id = id;
  this.enabled = enabled;
  this.transform = transform;
  this.visible = visible;
}

createClass(Component, [{
  key: 'update',
  value: function update(dt, scene, entity) {}
}, {
  key: 'render',
  value: function render(dt, scene, viewport, camera, stack, viewMatrix, modelMatrix, materialOverride) {}
}]);
return Component;
}();

var tMatrix$1 = create$3();

var _id$1 = 0;
var Entity = function () {
  function Entity() {
      var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
          _ref$id = _ref.id,
          id = _ref$id === undefined ? _id$1++ : _ref$id,
          _ref$components = _ref.components,
          components = _ref$components === undefined ? [] : _ref$components,
          _ref$enabled = _ref.enabled,
          enabled = _ref$enabled === undefined ? true : _ref$enabled,
          _ref$visible = _ref.visible,
          visible = _ref$visible === undefined ? true : _ref$visible,
          _ref$transform = _ref.transform,
          transform = _ref$transform === undefined ? new Transform$1() : _ref$transform;

      classCallCheck(this, Entity);

      // Extend _priv vars
      if (!this._priv) {
          this._priv = {
              events: []
          };
      }

      if (!this._priv.events) this._priv.events = [];

      this.id = id;

      this.enabled = enabled;
      this.visible = visible;
      this.opacity = 1.0;
      this.pointerEvents = true;

      this.parentScene = null;

      this.components = components;

      this.transform = transform;
  }

  /**
   * Called on every Scene update
   */


  createClass(Entity, [{
      key: 'update',
      value: function update(dt, scene) {
          for (var i = 0; i < this.components.length; i++) {
              if (this.components[i].enabled) {
                  this.components[i].update(dt, scene, this);
              }
          }
      }

      /**
       * Called on every Scene render
       */

  }, {
      key: 'render',
      value: function render(dt, scene, viewport, camera, stack, viewMatrix, modelMatrix, materialOverride, frameBufferTextureStack) {
          stack.push(this);

          // Calculate model matrix
          if (modelMatrix) {
              mul$3(tMatrix$1, modelMatrix, this.transform.localMatrix);
          } else {
              copy$3(tMatrix$1, this.transform.localMatrix);
          }

          for (var i = 0; i < this.components.length; i++) {
              if (this.components[i].visible) {
                  this.components[i].render(dt, scene, viewport, camera, stack, viewMatrix, tMatrix$1, materialOverride, frameBufferTextureStack);
              }
          }

          stack.pop();
      }

      /**
       * Add a new component
       */

  }, {
      key: 'addComponent',
      value: function addComponent(component) {
          this.components.push(component);
      }

      /**
      * Add a new component
      */

  }, {
      key: 'removeComponent',
      value: function removeComponent(component) {
          if (this.components.indexOf(component) <= -1) return;

          this.components.splice(this.components.indexOf(component), 1);
      }

      /**
       * Called on every Scene render pick
       */

  }, {
      key: 'renderPick',
      value: function renderPick(viewport, camera, entityId) {
          if (!this.visible || !this.pointerEvents) return;

          for (var i = 0; i < this.components.length; i++) {
              if (this.components[i].visible) {
                  if (this.components[i].renderPick) this.components[i].renderPick(viewport, camera, this, entityId);
              }
          }
      }
  }, {
      key: 'registerHandler',
      value: function registerHandler() {
          // keep track of eventhandlers so we can remove them on destroy
          // this._priv.events.push();
      }
  }, {
      key: 'unregisterHandler',
      value: function unregisterHandler() {}
  }, {
      key: 'onload',
      value: function onload() {
          var _iteratorNormalCompletion = true;
          var _didIteratorError = false;
          var _iteratorError = undefined;

          try {
              for (var _iterator = this._priv.events[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
                  var event = _step.value;

                  this.registerHandler(event);
              }
          } catch (err) {
              _didIteratorError = true;
              _iteratorError = err;
          } finally {
              try {
                  if (!_iteratorNormalCompletion && _iterator.return) {
                      _iterator.return();
                  }
              } finally {
                  if (_didIteratorError) {
                      throw _iteratorError;
                  }
              }
          }
      }
  }, {
      key: 'onunload',
      value: function onunload() {
          var _iteratorNormalCompletion2 = true;
          var _didIteratorError2 = false;
          var _iteratorError2 = undefined;

          try {
              for (var _iterator2 = this._priv.events[Symbol.iterator](), _step2; !(_iteratorNormalCompletion2 = (_step2 = _iterator2.next()).done); _iteratorNormalCompletion2 = true) {
                  var event = _step2.value;

                  this.unregisterHandler(event);
              }
          } catch (err) {
              _didIteratorError2 = true;
              _iteratorError2 = err;
          } finally {
              try {
                  if (!_iteratorNormalCompletion2 && _iterator2.return) {
                      _iterator2.return();
                  }
              } finally {
                  if (_didIteratorError2) {
                      throw _iteratorError2;
                  }
              }
          }
      }

      /**
      * Called every time the object is added to the scene
      */

  }, {
      key: 'onSpawn',
      value: function onSpawn(scene) {
          this.parentScene = scene;
      }

      /**
       * Called every time the object is removed from the scene
       */

  }, {
      key: 'onDestroy',
      value: function onDestroy(scene) {}

      // TODO

  }, {
      key: 'emit',
      value: function emit(id) {
          for (var _len = arguments.length, args = Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
              args[_key - 1] = arguments[_key];
          }

          var _iteratorNormalCompletion3 = true;
          var _didIteratorError3 = false;
          var _iteratorError3 = undefined;

          try {
              for (var _iterator3 = this._priv.events[Symbol.iterator](), _step3; !(_iteratorNormalCompletion3 = (_step3 = _iterator3.next()).done); _iteratorNormalCompletion3 = true) {
                  var event = _step3.value;

                  if (event.event == id) {
                      event.target[event.property].apply(this, args);
                  }
              }
          } catch (err) {
              _didIteratorError3 = true;
              _iteratorError3 = err;
          } finally {
              try {
                  if (!_iteratorNormalCompletion3 && _iterator3.return) {
                      _iterator3.return();
                  }
              } finally {
                  if (_didIteratorError3) {
                      throw _iteratorError3;
                  }
              }
          }
      }
  }]);
  return Entity;
}();

var imgCache = {};

var Texture = function () {
createClass(Texture, [{
  key: "width",
  get: function get$$1() {
    return this.img.naturalWidth || this.img.width;
  },
  set: function set(val) {
    return this.img.width = val;
  }
}, {
  key: "height",
  get: function get$$1() {
    return this.img.naturalHeight || this.img.height;
  },
  set: function set(val) {
    return this.img.height = val;
  }
}]);

function Texture() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref$src = _ref.src,
      src = _ref$src === undefined ? "" : _ref$src,
      _ref$flipY = _ref.flipY,
      flipY = _ref$flipY === undefined ? true : _ref$flipY,
      _ref$textureType = _ref.textureType,
      textureType = _ref$textureType === undefined ? "TEXTURE_2D" : _ref$textureType,
      _ref$magFilter = _ref.magFilter,
      magFilter = _ref$magFilter === undefined ? "LINEAR" : _ref$magFilter,
      _ref$minFilter = _ref.minFilter,
      minFilter = _ref$minFilter === undefined ? "LINEAR_MIPMAP_LINEAR" : _ref$minFilter,
      _ref$generateMipmap = _ref.generateMipmap,
      generateMipmap = _ref$generateMipmap === undefined ? true : _ref$generateMipmap,
      _ref$img = _ref.img,
      img = _ref$img === undefined ? new Image() : _ref$img,
      _ref$wrap = _ref.wrap,
      wrap = _ref$wrap === undefined ? { s: "CLAMP_TO_EDGE", t: "CLAMP_TO_EDGE" } : _ref$wrap;

  classCallCheck(this, Texture);

  this.src = src;

  this._loadPromise = null;
  this.loaded = false;

  this.texture = [];
  this.img = img;

  this.flipY = flipY;
  this.magFilter = magFilter;
  this.minFilter = minFilter;
  this.textureType = textureType;

  this._generateMipmap = generateMipmap;

  this.wrap = wrap;

  if (src) this.load();
}

createClass(Texture, [{
  key: "load",
  value: function load() {
    var _this = this;

    if (this._loadPromise) return this._loadPromise;

    if (imgCache[this.src]) {
      this.img = imgCache[this.src];

      this._loadPromise = new Promise(function (resolve, reject) {
        _this.loaded = true;
        _this._loadPromise = null;

        resolve(_this);
      });
    } else {
      this._loadPromise = new Promise(function (resolve, reject) {
        _this.img.onload = function () {
          _this.loaded = true;
          _this._loadPromise = null;
          imgCache[_this.src] = _this.img;

          resolve(_this);
        };
        _this.img.onerror = function (e) {
          _this.loaded = false;
          _this._loadPromise = null;
          reject("Error loading image file: '" + _this.img.src + "'");
        };
      });

      this.img.src = this.src;
    }

    return this._loadPromise;
  }
}, {
  key: "unload",
  value: function unload(viewport) {
    viewport.webgl.deleteTexture(this.texture[viewport.id]);
  }
}, {
  key: "createTexture",
  value: function createTexture(viewport) {
    this.texture[viewport.id] = viewport.webgl.createTexture();

    viewport.webgl.bindTexture(viewport.webgl[this.textureType], this.texture[viewport.id]);
    viewport.webgl.pixelStorei(viewport.webgl.UNPACK_FLIP_Y_WEBGL, this.flipY);
    // viewport.webgl.pixelStorei(viewport.webgl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
    viewport.webgl.texImage2D(viewport.webgl[this.textureType], 0, viewport.webgl.RGBA, viewport.webgl.RGBA, viewport.webgl.UNSIGNED_BYTE, this.img);
  }
}, {
  key: "updateTexture",
  value: function updateTexture(viewport) {
    if (this._generateMipmap) {
      viewport.webgl.generateMipmap(viewport.webgl[this.textureType]);
    }

    // For video
    // if(this.video.readyState === 4){
    // 	viewport.webgl.texImage2D(viewport.webgl[this.textureType], 0, viewport.webgl.RGBA, viewport.webgl.RGBA, viewport.webgl.UNSIGNED_BYTE, this.video);
    // }
    //viewport.webgl.texParameteri(viewport.webgl[this.textureType], viewport.webgl.TEXTURE_MAG_FILTER, viewport.webgl.NEAREST);
    //viewport.webgl.texParameteri(viewport.webgl[this.textureType], viewport.webgl.TEXTURE_MIN_FILTER, viewport.webgl.NEAREST);
  }
}, {
  key: "setAsTexture",
  value: function setAsTexture(viewport) {
    var textureNumber = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
    var uniformPosition = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : null;

    // TODO: Set to black texture if none loaded
    if (!this.img) {
      console.error('Texture has no image loaded');
      return false;
    }
    if (!this.loaded) {
      console.warn('Texture image not yet loaded');
      return false;
    }
    // if(viewport._currentTexture[textureNumber] == this) return;

    // Create the texture if it doesn't exist yet
    if (!this.texture[viewport.id]) {
      this.createTexture(viewport);
      this.updateTexture(viewport);
    }

    viewport.webgl.activeTexture(viewport.webgl["TEXTURE" + textureNumber]);
    viewport.webgl.bindTexture(viewport.webgl[this.textureType], this.texture[viewport.id]);

    // Allow non-square textures
    viewport.webgl.texParameteri(viewport.webgl[this.textureType], viewport.webgl.TEXTURE_WRAP_S, viewport.webgl[this.wrap.s]);
    viewport.webgl.texParameteri(viewport.webgl[this.textureType], viewport.webgl.TEXTURE_WRAP_T, viewport.webgl[this.wrap.t]);

    viewport.webgl.texParameteri(viewport.webgl[this.textureType], viewport.webgl.TEXTURE_MAG_FILTER, viewport.webgl[this.magFilter]);
    viewport.webgl.texParameteri(viewport.webgl[this.textureType], viewport.webgl.TEXTURE_MIN_FILTER, viewport.webgl[this.minFilter]);

    if (uniformPosition) {
      viewport.webgl.uniform1i(uniformPosition, textureNumber);
    }

    viewport._currentTexture[textureNumber] = this;

    return true;
  }
}]);
return Texture;
}();

var AnimationClip$1 = function () {
  function AnimationClip() {
      var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
          _ref$name = _ref.name,
          name = _ref$name === undefined ? "" : _ref$name,
          _ref$tracks = _ref.tracks,
          tracks = _ref$tracks === undefined ? [] : _ref$tracks,
          _ref$loop = _ref.loop,
          loop = _ref$loop === undefined ? true : _ref$loop,
          _ref$enabled = _ref.enabled,
          enabled = _ref$enabled === undefined ? false : _ref$enabled;

      classCallCheck(this, AnimationClip);

      this.name = name;
      this.tracks = tracks;

      this.enabled = enabled; // TODO: Should be false by default

      this.rate = 1;
      this.currentTime = 0;

      this.loop = loop;
  }

  createClass(AnimationClip, [{
      key: "update",
      value: function update(dt) {
          if (!this.enabled) return;

          this.currentTime += dt * this.rate;

          if (this.loop) {
              var allComplete = true;
              for (var i = 0; i < this.tracks.length; i++) {
                  this.tracks[i].update(dt * this.rate, this.currentTime);

                  if (!this.tracks[i].complete) allComplete = false;
              }

              if (allComplete) {
                  for (var i = 0; i < this.tracks.length; i++) {
                      this.tracks[i].reset();
                  }
                  this.currentTime = 0;
              }
          }
      }
  }]);
  return AnimationClip;
}();

var AnimationTrack$1 = function () {
  function AnimationTrack() {
      var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
          _ref$name = _ref.name,
          name = _ref$name === undefined ? "" : _ref$name,
          _ref$values = _ref.values,
          values = _ref$values === undefined ? [[]] : _ref$values,
          _ref$times = _ref.times,
          times = _ref$times === undefined ? [] : _ref$times,
          _ref$type = _ref.type,
          type = _ref$type === undefined ? "position" : _ref$type,
          _ref$parent = _ref.parent,
          parent = _ref$parent === undefined ? null : _ref$parent;

      classCallCheck(this, AnimationTrack);

      this.name = name;
      this.values = values;
      this.times = times;
      this.type = type;

      this.parent = parent;
      this.currentTime = 0.0;
      this.currentFrameStartTime = 0.0;
      this.currentFrameIndex = 0;
      this.currentFrameValues = this.values[0];
      this.currentFrameTime = this.times[0];
      this.nextFrameIndex = 0;
      this.nextFrameValues = this.values[0];
      this.nextFrameTime = this.times[0];

      this.complete = false;

      this.reset();
  }

  createClass(AnimationTrack, [{
      key: "update",
      value: function update(dt, currentClipTime) {
          if (this.complete) return;

          //TODO: Support rewind
          this.currentTime += dt;

          if (currentClipTime >= this.nextFrameTime) {
              // console.log(this.currentFrameIndex, this.nextFrameIndex);

              this.currentFrameStartTime = this.currentFrameTime;

              // Find next frame
              this.currentFrameIndex = this.nextFrameIndex;
              this.currentFrameValues = this.nextFrameValues;
              this.currentFrameTime = this.nextFrameTime;

              this.nextFrameIndex++;
              this.nextFrameValues = this.values[this.nextFrameIndex];
              this.nextFrameTime = this.times[this.nextFrameIndex];

              if (this.nextFrameIndex >= this.times.length) {
                  this.complete = true;
                  // this.nextFrameIndex = 0;
                  return;
              }
          }

          // TODO Fix Percent for last frame
          var percent = (currentClipTime - this.currentFrameTime) / (this.nextFrameTime - this.currentFrameTime);
          if (percent === Infinity || percent === -Infinity) percent = 0;
          this.interpolateLinear(this.currentFrameValues, this.nextFrameValues, percent);
      }
  }, {
      key: "interpolateLinear",
      value: function interpolateLinear(currentFrame, nextFrame, percent) {
          if (!this.parent) return;
          switch (this.type) {
              case "position":
                  this.parent.transform.translateTo(currentFrame[0] + (nextFrame[0] - currentFrame[0]) * percent, currentFrame[1] + (nextFrame[1] - currentFrame[1]) * percent, currentFrame[2] + (nextFrame[2] - currentFrame[2]) * percent);
                  break;
              case "scale":
                  this.parent.transform.scaleTo(currentFrame[0] + (nextFrame[0] - currentFrame[0]) * percent, currentFrame[1] + (nextFrame[1] - currentFrame[1]) * percent, currentFrame[2] + (nextFrame[2] - currentFrame[2]) * percent);
                  break;
              case "rotation2":
                  this.parent.transform.rotateFromEuler(currentFrame[0] + (nextFrame[0] - currentFrame[0]) * percent, currentFrame[1] + (nextFrame[1] - currentFrame[1]) * percent, currentFrame[2] + (nextFrame[2] - currentFrame[2]) * percent);
                  break;
              case "rotation":
                  //TODO: Support other rotation orders
                  switch (nextFrame[3]) {
                      case "ZYX":
                          this.parent.transform.rotateToZYX(currentFrame[2] + (nextFrame[2] - currentFrame[2]) * percent, currentFrame[1] + (nextFrame[1] - currentFrame[1]) * percent, currentFrame[0] + (nextFrame[0] - currentFrame[0]) * percent);
                          break;
                      case "XYZ":
                      default:
                          this.parent.transform.rotateToXYZ(currentFrame[0] + (nextFrame[0] - currentFrame[0]) * percent, currentFrame[1] + (nextFrame[1] - currentFrame[1]) * percent, currentFrame[2] + (nextFrame[2] - currentFrame[2]) * percent);
                          break;
                  }
                  break;
              case "quaternion":
                  // if(this.parent.FBXID == 140457576087552) console.log(percent, this.currentFrameIndex, this.nextFrameIndex);
                  slerp(this.parent.transform.rotationQuat, currentFrame, nextFrame, percent);
                  this.parent.transform.refreshMat4();

                  // this.parent.transform.rotationQuat = [
                  //     currentFrame[0] + ((nextFrame[0] - currentFrame[0]) * percent),
                  //     currentFrame[1] + ((nextFrame[1] - currentFrame[1]) * percent),
                  //     currentFrame[2] + ((nextFrame[2] - currentFrame[2]) * percent),
                  //     currentFrame[3] + ((nextFrame[3] - currentFrame[3]) * percent)];
                  // this.parent.transform.refreshMat4();
                  // this.parent.transform.generateTranslationRotationScale();

                  // this.parent.transform.rotateToXYZ(
                  //     currentFrame[0] + ((nextFrame[0] - currentFrame[0]) * percent),
                  //     currentFrame[1] + ((nextFrame[1] - currentFrame[1]) * percent),
                  //     currentFrame[2] + ((nextFrame[2] - currentFrame[2]) * percent) 
                  // );
                  break;
          }
      }
  }, {
      key: "reset",
      value: function reset() {
          this.currentTime = null;

          this.currentFrameIndex = 0;
          this.currentFrameValues = this.values[this.currentFrameIndex];
          this.currentFrameTime = this.times[this.currentFrameIndex];

          this.nextFrameIndex = 0;
          this.nextFrameValues = this.values[this.nextFrameIndex];
          this.nextFrameTime = this.times[this.nextFrameIndex];

          this.complete = false;
      }
  }]);
  return AnimationTrack;
}();

var tMatrix$2 = create$3();

var _id$2 = 0;

var Bone$1 = function () {
  function Bone() {
      var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
          _ref$name = _ref.name,
          name = _ref$name === undefined ? "" : _ref$name,
          _ref$id = _ref.id,
          id = _ref$id === undefined ? _id$2++ : _ref$id,
          _ref$parent = _ref.parent,
          parent = _ref$parent === undefined ? null : _ref$parent,
          _ref$children = _ref.children,
          children = _ref$children === undefined ? [] : _ref$children,
          _ref$isRoot = _ref.isRoot,
          isRoot = _ref$isRoot === undefined ? false : _ref$isRoot,
          _ref$inheritTransform = _ref.inheritTransform,
          inheritTransform = _ref$inheritTransform === undefined ? true : _ref$inheritTransform,
          _ref$transform = _ref.transform,
          transform = _ref$transform === undefined ? new Transform$1() : _ref$transform;

      classCallCheck(this, Bone);

      this.name = name;
      this.id = id;
      this.children = children;
      this.isRoot = isRoot;
      this.parent = parent;
      this.inheritTransform = inheritTransform;

      this.transform = transform; // Local matrix
      // this.worldMatrix = mat4.create();
      // this.localMatrix = mat4.create();
      this.bindpose = create$3();
      this.inverseBindpose = create$3();
      this.offsetMatrix = create$3();

      // this.calcOrigin();
  }

  createClass(Bone, [{
      key: 'update',
      value: function update(dt, scene, entity) {}
  }, {
      key: 'add',
      value: function add$$1(child) {
          this.children.add(child);
          child.parent = this;
      }
  }, {
      key: 'calcBindPose',
      value: function calcBindPose() {
          this.calcAbsolutePosition(this.bindpose);
          // mat4.copy(this.bindpose, this.transform.localMatrix);
          invert$3(this.inverseBindpose, this.bindpose);
      }
  }, {
      key: 'calcAbsolutePosition',
      value: function calcAbsolutePosition() {
          var matrix = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : tMatrix$2;

          if (this.parent && this.inheritTransform && this.parent.calcAbsolutePosition) {
              // && this.parent.
              var pMatrix = new Float32Array(16);
              this.parent.calcAbsolutePosition(pMatrix);
              // let pMatrix = this.parent.bindpose;
              multiply$4(matrix, pMatrix, this.transform.localMatrix);
          } else {
              copy$3(matrix, this.transform.localMatrix);
          }
      }
  }, {
      key: 'getFlatMatrix',
      value: function getFlatMatrix(bufferMatrix) {
          var offset = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
          var recursive = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;

          this.calcAbsolutePosition(tMatrix$2);
          multiply$4(tMatrix$2, tMatrix$2, this.inverseBindpose);

          // Copy to buffer
          for (var j = 0; j < 16; j++) {
              bufferMatrix[offset * 16 + j] = tMatrix$2[j];
          }

          offset++;

          // Recursive
          if (recursive) {
              for (var i = 0; i < this.children.length; i++) {
                  offset = this.children[i].getFlatMatrix(bufferMatrix, offset, true);
              }
          }

          return offset;
      }
  }]);
  return Bone;
}();

var viewMatrix = create$3();

var Camera = function (_Entity) {
inherits(Camera, _Entity);
createClass(Camera, [{
  key: 'nearPlane',
  get: function get$$1() {
    return this._nearPlane;
  },
  set: function set$$1(value) {
    this._nearPlane = value;
    // this.evaluate();
    return this._nearPlane;
  }
}, {
  key: 'farPlane',
  get: function get$$1() {
    return this._farPlane;
  },
  set: function set$$1(value) {
    this._farPlane = value;
    // this.evaluate();
    return this._farPlane;
  }
}, {
  key: 'projectionType',
  get: function get$$1() {
    return this._projectionType;
  },
  set: function set$$1(value) {
    this._projectionType = value;
    // this.evaluate();
    return this._projectionType;
  }
}]);

function Camera() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref$nearPlane = _ref.nearPlane,
      nearPlane = _ref$nearPlane === undefined ? 1 : _ref$nearPlane,
      _ref$farPlane = _ref.farPlane,
      farPlane = _ref$farPlane === undefined ? 1000 : _ref$farPlane,
      _ref$fov = _ref.fov,
      fov = _ref$fov === undefined ? 0.872665 : _ref$fov,
      _ref$projectionType = _ref.projectionType,
      projectionType = _ref$projectionType === undefined ? "projection" : _ref$projectionType,
      _ref$followType = _ref.followType,
      followType = _ref$followType === undefined ? "STATIC" : _ref$followType,
      _ref$stiffness = _ref.stiffness,
      stiffness = _ref$stiffness === undefined ? 0.65 : _ref$stiffness;

  classCallCheck(this, Camera);

  var _this = possibleConstructorReturn(this, (Camera.__proto__ || Object.getPrototypeOf(Camera)).apply(this, arguments));

  _this._nearPlane = nearPlane;
  _this._farPlane = farPlane;
  _this._projectionType = projectionType;
  _this.fov = fov;

  _this.viewports = [];
  _this.shaderPasses = [];

  _this.mvTransform = new Transform$1(); // World space

  // Set mvTransform identity
  // mat4.identity(this.transform.localMatrix);
  // mat4.identity(this.mvTransform.localMatrix);

  // Should "follow" be a component so we can use it for other things?
  _this.followEntity = null;
  _this.followOffset = [0, 0, 0]; // Should this be a transform?
  _this.followType = followType;

  _this.stiffness = stiffness;

  _this.limit = {
    minimum: {
      x: null,
      y: null,
      z: null
    },
    maximum: {
      x: null,
      y: null,
      z: null
    }
  };
  _this.lock = [null, null, null];
  return _this;
}

createClass(Camera, [{
  key: 'render',
  value: function render(dt, scene, viewport, stack, matrix, materialOverride, frameBufferTextureStack) {
    stack.push(this);

    // Calculate view matrix
    mul$3(viewMatrix, matrix, this.transform.localMatrixInversed);

    // Render entity to viewport
    var _iteratorNormalCompletion = true;
    var _didIteratorError = false;
    var _iteratorError = undefined;

    try {
      for (var _iterator = scene.entities[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
        var entity = _step.value;

        if (entity !== this && entity.visible) {
          entity.render(dt, scene, viewport, this, stack, viewMatrix, null, materialOverride, frameBufferTextureStack);
        }
      }
    } catch (err) {
      _didIteratorError = true;
      _iteratorError = err;
    } finally {
      try {
        if (!_iteratorNormalCompletion && _iterator.return) {
          _iterator.return();
        }
      } finally {
        if (_didIteratorError) {
          throw _iteratorError;
        }
      }
    }

    stack.pop();
  }
}, {
  key: 'renderScenePick',
  value: function renderScenePick(viewport) {
    for (var i = 0; i < this.scene.entities.length; i++) {
      // Render entity to viewport
      this.scene.entities[i].renderPick(viewport, this, i);
    }
  }
}, {
  key: 'update',
  value: function update(deltaTime, scene) {
    if (this.followEntity) {
      var newX = this.transform.x - this.followOffset[0];
      var newY = this.transform.y - this.followOffset[1];
      var newZ = this.transform.z - this.followOffset[2];

      var destX = this.followEntity.transform.x;
      var destY = this.followEntity.transform.y;
      var destZ = this.followEntity.transform.z;

      switch (this.followType.toUpperCase()) {
        case "EASEIN":
          newX += Math.round((destX - newX) * Math.pow(this.stiffness, 2));
          newY += Math.round((destY - newY) * Math.pow(this.stiffness, 2));
          newZ += Math.round((destZ - newZ) * Math.pow(this.stiffness, 2));
          break;
        case "EASEOUT":
          // a + ((b-a) * %)
          newX += Math.round((destX - newX) * this.stiffness);
          newY += Math.round((destY - newY) * this.stiffness);
          newZ += Math.round((destZ - newZ) * this.stiffness);
          break;
        case "SPRING":
          var vector = {
            x: this.followEntity.transform.x + this.followOffset[0] - this.transform.x,
            y: this.followEntity.transform.y + this.followOffset[1] - this.transform.y,
            z: this.followEntity.transform.z + this.followOffset[2] - this.transform.z
          };

          
          
          var springConstantX = 5.5;
          var springConstantY = 6.5;
          var springConstantZ = 5.5;

          // if(Math.abs(vector.x) > targetWidth) {
          newX += springConstantX * vector.x;
          // }

          // if(Math.abs(vector.y) > targetWidth) {
          newY += springConstantY * vector.y;
          // }

          // if(Math.abs(vector.z) > targetWidth) {
          newZ += springConstantZ * vector.z;
          // }

          break;
        case "STATIC":
        default:
          newX = destX;
          newY = destY;
          newZ = destZ;
          break;
      }

      newX += this.followOffset[0];
      newY += this.followOffset[1];
      newZ += this.followOffset[2];

      // Limit to bounds
      if (this.limit.minimum.x !== null && newX < this.limit.minimum.x) newX = this.limit.minimum.x;
      if (this.limit.minimum.y !== null && newY < this.limit.minimum.y) newY = this.limit.minimum.y;
      if (this.limit.minimum.z !== null && newZ < this.limit.minimum.z) newZ = this.limit.minimum.z;

      if (this.limit.maximum.x !== null && newX > this.limit.maximum.x) newX = this.limit.maximum.x;
      if (this.limit.maximum.y !== null && newY > this.limit.maximum.y) newY = this.limit.maximum.y;
      if (this.limit.maximum.z !== null && newZ > this.limit.maximum.z) newZ = this.limit.maximum.z;

      this.transform.translateTo(newX, newY, newZ);
    }

    if (this.lock[0] !== null) this.transform.translateToX(this.lock[0]);
    if (this.lock[1] !== null) this.transform.translateToY(this.lock[1]);
    if (this.lock[2] !== null) this.transform.translateToY(this.lock[2]);
  }
}, {
  key: 'addViewport',
  value: function addViewport(viewport) {
    this.viewports.push(viewport);
  }
}, {
  key: 'follow',
  value: function follow(entity) {
    var offset = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [this.transform.x, this.transform.y, this.transform.z];

    this.followEntity = entity;
    this.followOffset = offset;
  }
}, {
  key: 'lockX',
  value: function lockX() {
    var x = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : this.transform.x;

    this.lock[0] = x;
  }
}, {
  key: 'unlockX',
  value: function unlockX() {
    this.lock[0] = null;
  }
}, {
  key: 'lockY',
  value: function lockY() {
    var y = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : this.transform.y;

    this.lock[1] = y;
  }
}, {
  key: 'unlockY',
  value: function unlockY() {
    this.lock[1] = null;
  }
}, {
  key: 'lockZ',
  value: function lockZ() {
    var z = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : this.transform.z;

    this.lock[1] = z;
  }
}, {
  key: 'unlockZ',
  value: function unlockZ() {
    this.lock[0] = null;
  }
}, {
  key: 'getFrustum',
  value: function getFrustum(viewport) {
    var ndcPoints = [[-1, -1, -1, 1], [-1, -1, 1, 1], [-1, 1, -1, 1], [-1, 1, 1, 1], [1, -1, -1, 1], [1, -1, 1, 1], [1, 1, -1, 1], [1, 1, 1, 1]];

    // Get inverse MVP
    var m = [];
    copy$3(m, viewport.projectionMatrix);
    multiply$4(m, m, this.transform.localMatrixInversed);
    // mat4.multiply(m, m, this.mvTransform.localMatrix);
    invert$3(m, m);

    // Get world space by multiplying corners (NDC cube points) by matrix
    var _iteratorNormalCompletion2 = true;
    var _didIteratorError2 = false;
    var _iteratorError2 = undefined;

    try {
      for (var _iterator2 = ndcPoints[Symbol.iterator](), _step2; !(_iteratorNormalCompletion2 = (_step2 = _iterator2.next()).done); _iteratorNormalCompletion2 = true) {
        var point = _step2.value;

        transformMat4$1(point, point, m);

        point[0] /= point[3];
        point[1] /= point[3];
        point[2] /= point[3];
        point[3] /= point[3];
      }
    } catch (err) {
      _didIteratorError2 = true;
      _iteratorError2 = err;
    } finally {
      try {
        if (!_iteratorNormalCompletion2 && _iterator2.return) {
          _iterator2.return();
        }
      } finally {
        if (_didIteratorError2) {
          throw _iteratorError2;
        }
      }
    }

    return ndcPoints;
  }

  // #based on https://github.com/sinisterchipmunk/jax/blob/5d392c9d67cb9ae5623dc03846027c473f625925/src/jax/webgl/camera.js#L568

}, {
  key: 'unproject',
  value: function unproject(viewport) {
    var winx = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
    var winy = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0;
    var winz = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 1;

    // Note: The basic idea here is to create an inverted matrix, then project the (normalized) coordinates with it
    // Note: Near plane should be minimum 0.1;
    var tFarPlane = this.farPlane;
    this._farPlane = this.transform.z;
    viewport.evaluate(false);

    var inf = create$6();
    var transformMatrix = this.parallax ? this.transform.getInverseParallax(this) : this.transform.localMatrixInversed;
    var mvTransformMatrix = this.mvTransform.localMatrix;
    var projectionMatrix = viewport.projectionMatrix;
    var view = [0, 0, viewport.contextElement.width, viewport.contextElement.height];

    //Calculation for inverting a matrix, compute projection x modelview; then compute the inverse
    // Note: Typical shaders are using uPMatrix * uCMatrix * uMVMatrix
    var m = create$3();
    copy$3(m, projectionMatrix);
    // mat4.invert(m, m);
    multiply$4(m, m, transformMatrix);
    multiply$4(m, m, mvTransformMatrix);
    invert$3(m, m);

    // Transformation of normalized coordinates between -1 and 1
    inf[0] = (winx - view[0]) / view[2] * 2.0 - 1.0;
    inf[1] = (winy - view[1]) / view[3] * 2.0 - 1.0;
    inf[2] = 2.0 * winz - 1.0;
    // inf[2] = winz;
    inf[3] = 1.0;

    //			vec4.invert(inf, inf);

    //Objects coordinates
    var out = create$6();
    transformMat4$1(out, inf, m);

    this._farPlane = tFarPlane;
    viewport.evaluate(false);

    if (out[3] == 0.0) {
      return null;
    }

    out[3] = 1.0 / out[3];
    return [out[0] * out[3], out[1] * out[3], out[2] * out[3]];
    // return [out[0] * viewport.contextElement.width, out[1] * viewport.contextElement.height];
  }

  // # http://webglfactory.blogspot.com/2011/05/how-to-convert-world-to-screen.html

}, {
  key: 'project',
  value: function project(viewport, point3D) {
    var transformMatrix = viewport.parallax ? this.transform.getInverseParallax(this) : this.transform.localMatrixInversed;
    var mvTransformMatrix = this.mvTransform.localMatrix;
    var projectionMatrix = viewport.projectionMatrix;
    var m = create$3();
    copy$3(m, projectionMatrix);

    multiply$4(m, m, transformMatrix);
    multiply$4(m, m, mvTransformMatrix);

    var v2d = [];

    var ix = point3D.xyz[0];
    var iy = point3D.xyz[1];
    var iz = point3D.xyz[2];

    var ox = m[0] * ix + m[4] * iy + m[8] * iz + m[12];
    var oy = m[1] * ix + m[5] * iy + m[9] * iz + m[13];
    var ow = m[3] * ix + m[7] * iy + m[11] * iz + m[15];

    v2d[0] = (ox / ow + 1) / 2;
    v2d[1] = 1 - (oy / ow + 1) / 2;

    v2d[0] = Math.round(v2d[0] * viewport.contextElement.width);
    v2d[1] = Math.round(v2d[1] * viewport.contextElement.height);

    return v2d;
  }
}, {
  key: 'getParallaxOffset',
  value: function getParallaxOffset(v3) {
    return this.transform.getParallaxOffset(v3.xyz ? v3.xyz : v3, this);
  }
}]);
return Camera;
}(Entity);

'use strict';

var performance;

(function () {
if (!window || "performance" in window == false) {
  performance = {};
} else {
  performance = window.performance;
}

if ("now" in performance == false) {

  var nowOffset = new Date().getTime();

  if (performance.timing && performance.timing.navigationStart) {
    nowOffset = performance.timing.navigationStart;
  }

  performance.now = function now() {
    return new Date().getTime() - nowOffset;
  };
}
})();

var requestAnimationFrame;

(function () {
if (window && window.requestAnimationFrame) {
  requestAnimationFrame = window.requestAnimationFrame;
}

var lastTime = 0;
var vendors = ['ms', 'moz', 'webkit', 'o'];
for (var x = 0; x < vendors.length && !requestAnimationFrame; ++x) {
  requestAnimationFrame = window[vendors[x] + 'RequestAnimationFrame'];
  
}

if (!requestAnimationFrame) requestAnimationFrame = function requestAnimationFrame(callback, element) {
  var currTime = new Date().getTime();
  var timeToCall = Math.max(0, 16 - (currTime - lastTime));
  var id = setTimeout(function () {
    callback(currTime + timeToCall);
  }, timeToCall);
  lastTime = currTime + timeToCall;
  return id;
};


})();

var Clock = function () {
function Clock() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref$targetCyclesPerS = _ref.targetCyclesPerSecond,
      targetCyclesPerSecond = _ref$targetCyclesPerS === undefined ? 60 : _ref$targetCyclesPerS,
      _ref$targetFramesPerS = _ref.targetFramesPerSecond,
      targetFramesPerSecond = _ref$targetFramesPerS === undefined ? 30 : _ref$targetFramesPerS,
      _ref$autoRun = _ref.autoRun,
      autoRun = _ref$autoRun === undefined ? true : _ref$autoRun;

  classCallCheck(this, Clock);

  this._running = false;
  this.targetCyclesPerSecond = targetCyclesPerSecond;
  this.targetFramesPerSecond = targetFramesPerSecond;
  this.frameAccumulatorRemainderThreshold = 16;

  this.renderEnabled = true;

  this.stats = {
    fps: 0,
    cps: 0,
    framesThisSecond: 0,
    cyclesThisSecond: 0,
    droppedFramesThisSecond: 0,
    framesSkippedThisSecond: 0,
    renderExecutionTime: 0,
    updateExecutionTime: 0,
    currentGameTime: 0,
    lastUpdate: performance.now(),
    lastRender: performance.now(),
    lastFrame: performance.now()
  };

  this._r = {
    i: null,
    newTime: performance.now(),
    deltaTime: 1 / this.targetCyclesPerSecond * 1000, // In milliseconds
    renderDeltaTime: 1 / this.targetFramesPerSecond * 1000, // In milliseconds
    frameAccumulator: 0
  };

  this.scenes = [];
  this.viewports = [];

  // Start main loop
  if (autoRun) {
    this.start();
  }
}

/**
* Starts the engine
*/


createClass(Clock, [{
  key: 'start',
  value: function start() {
    if (this._running) return;
    this._running = true;
    this.update();
  }

  /**
 * Stops the engine
 */

}, {
  key: 'stop',
  value: function stop() {
    this._running = false;
  }
}, {
  key: 'resetAccumulator',
  value: function resetAccumulator() {
    this.stats.lastFrame = performance.now();
    this._r.frameAccumulator = 0;
  }

  /**
 * Main loop
 */

}, {
  key: 'update',
  value: function update() {
    if (!this._running) return;

    // Calculate delta time #http://gafferongames.com/game-physics/fix-your-timestep/
    this._r.newTime = performance.now(); // Milliseconds with microsecond precision
    this._r.frameTime = this._r.newTime - this.stats.lastFrame; // Frametime will always be 16ms or higher with requestAnimationFrame()
    if (this._r.frameTime > 1000) this._r.frameTime = 1000; // note: max frame time to avoid spiral of death -- 1FPS
    this.stats.lastFrame = this._r.newTime;

    this._r.frameAccumulator += this._r.frameTime;

    // Logic path

    this.stats.cyclesThisFrame = 0;
    this._r.i = Math.floor(this._r.frameAccumulator / this._r.deltaTime);
    var preexecutionTime = performance.now();
    while (this._r.i-- >= 0) {
      //			previousState = currentState;
      //			integrate( currentState, t, dt );
      var _iteratorNormalCompletion = true;
      var _didIteratorError = false;
      var _iteratorError = undefined;

      try {
        for (var _iterator = this.scenes[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
          var scene = _step.value;

          if (scene.enabled) {
            scene.update(this._r.deltaTime);
          }
        }
      } catch (err) {
        _didIteratorError = true;
        _iteratorError = err;
      } finally {
        try {
          if (!_iteratorNormalCompletion && _iterator.return) {
            _iterator.return();
          }
        } finally {
          if (_didIteratorError) {
            throw _iteratorError;
          }
        }
      }

      this.stats.currentGameTime += this._r.deltaTime * 100;
      this.stats.cyclesThisFrame++;

      // In case an update wants to stop everything (e.g. switching to a new scene)
      if (!this._running) return;
    }
    this.stats.updateExecutionTime = performance.now() - preexecutionTime;
    this.stats.cyclesThisSecond += this.stats.cyclesThisFrame;

    // if(this.stats.cyclesThisFrame) this._r.frameAccumulator -= 1 / this.stats.cyclesThisFrame;
    this._r.frameAccumulator -= this.stats.cyclesThisFrame * this._r.deltaTime;
    this.stats.droppedFramesThisSecond += this.stats.cyclesThisSecond - 1;

    // Remove remainder
    // if(this._r.frameAccumulator > 1) this._r.frameAccumulator = 0;

    this.stats.frameAccumulatorRemainder = this._r.frameAccumulator;

    //		_processing = false;

    // Render path
    preexecutionTime = performance.now();
    if (this.renderEnabled && this.stats.cyclesThisFrame) {
      // && (this._r.newTime - this.stats.lastRender >= this._r.renderDeltaTime)){
      var _iteratorNormalCompletion2 = true;
      var _didIteratorError2 = false;
      var _iteratorError2 = undefined;

      try {
        for (var _iterator2 = this.scenes[Symbol.iterator](), _step2; !(_iteratorNormalCompletion2 = (_step2 = _iterator2.next()).done); _iteratorNormalCompletion2 = true) {
          var _scene = _step2.value;

          _scene.render(performance.now() - this.stats.lastRender);
        }
      } catch (err) {
        _didIteratorError2 = true;
        _iteratorError2 = err;
      } finally {
        try {
          if (!_iteratorNormalCompletion2 && _iterator2.return) {
            _iterator2.return();
          }
        } finally {
          if (_didIteratorError2) {
            throw _iteratorError2;
          }
        }
      }

      this.stats.lastRender = performance.now();
      this.stats.framesThisSecond++;
    } else {
      this.stats.framesSkippedThisSecond++;
    }
    this.stats.renderExecutionTime = performance.now() - preexecutionTime;

    // Update stats
    if (performance.now() - this.stats.lastUpdate > 1000) {
      this.stats.fps = this.stats.framesThisSecond;
      this.stats.cps = this.stats.cyclesThisSecond;
      this.stats.framesThisSecond = 0;
      this.stats.cyclesThisSecond = 0;
      this.stats.framesSkippedThisSecond = 0;
      this.stats.lastUpdate = performance.now();
    }

    // Debug
    // document.getElementById("debug").innerHTML = "FPS: " + this.stats.fps + " | CPS: " + this.stats.cps + "<br/>Render Time: " + Math.round(this.stats.renderExecutionTime) + "ms" + "<br/>Update Time: " + Math.round(this.stats.updateExecutionTime) + "ms" + "<br/>Current Gametime: " + Math.round(this.stats.currentGameTime);

    // this.stats.lastFrame = performance.now();    
    requestAnimationFrame(this.update.bind(this));
  }
}, {
  key: 'cyclesToMilliseconds',
  value: function cyclesToMilliseconds(cycles) {
    return cycles * this._r.deltaTime;
  }
}, {
  key: 'millisecondsToCycles',
  value: function millisecondsToCycles(milliseconds) {
    return milliseconds / this._r.deltaTime;
  }
}, {
  key: 'add',
  value: function add(obj) {
    this.scenes.push(obj);
    // obj.onload();
  }
}, {
  key: 'remove',
  value: function remove(obj) {
    this.scenes.splice(this.scenes.indexOf(obj), 1);
    // obj.onunload();
  }
}]);
return Clock;
}();

var Color = function () {
createClass(Color, [{
  key: "red",
  get: function get$$1() {
    return this.color[0];
  },
  set: function set(val) {
    return this.color[0] = val;
  }
}, {
  key: "green",
  get: function get$$1() {
    return this.color[1];
  },
  set: function set(val) {
    return this.color[1] = val;
  }
}, {
  key: "blue",
  get: function get$$1() {
    return this.color[2];
  },
  set: function set(val) {
    return this.color[2] = val;
  }
}, {
  key: "alpha",
  get: function get$$1() {
    return this.color[3];
  },
  set: function set(val) {
    return this.color[3] = val;
  }
}]);

function Color() {
  classCallCheck(this, Color);

  this.set.apply(this, arguments);

  this._color3Float32Array = new Float32Array(3);
  this._color4Float32Array = new Float32Array(4);
}

createClass(Color, [{
  key: "set",
  value: function set() {
    var red = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0.0;
    var green = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0.0;
    var blue = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0.0;
    var alpha = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 1.0;

    if (typeof red === "string") {
      var rgba = this.parseHex(red);
      this.color = new Float32Array([rgba[0], rgba[1], rgba[2], rgba[3]]);
    } else if (red instanceof Array) {
      if (red[3] === undefined) red[3] = 1.0;

      this.color = new Float32Array([red[0], red[1], red[2], red[3]]);
    } else {
      this.color = new Float32Array([red, green, blue, alpha]);
    }
  }
}, {
  key: "toArray",
  value: function toArray$$1() {
    return this.color;
  }
}, {
  key: "toVec3Array",
  value: function toVec3Array() {
    this._color3Float32Array.set([this.color[0], this.color[1], this.color[2]]);

    return this._color3Float32Array;
  }
}, {
  key: "toRGBAArray",
  value: function toRGBAArray() {
    this._color4Float32Array.set([this.color[0] * 255, this.color[1] * 255, this.color[2] * 255, this.color[3]]);

    return this._color4Float32Array;
  }

  // #http://stackoverflow.com/a/5624139

}, {
  key: "parseHex",
  value: function parseHex(hex) {
    // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
    var shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    hex = hex.replace(shorthandRegex, function (m, r, g, b) {
      return r + r + g + g + b + b;
    });

    var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);

    if (result) {
      this.color[0] = parseInt(result[1], 16) / 255;
      this.color[1] = parseInt(result[2], 16) / 255;
      this.color[2] = parseInt(result[3], 16) / 255;
    }

    return this;
  }
}, {
  key: "adjustHSL",
  value: function adjustHSL(deltaH, deltaS, deltaL) {
    var hslColor = this.rgbToHsl();
    hslColor[0] += (deltaH || 0) / 100;
    hslColor[1] += (deltaS || 0) / 100;
    hslColor[2] += (deltaL || 0) / 100;

    if (hslColor[0] < 0) hslColor[0] = 0;
    if (hslColor[1] < 0) hslColor[1] = 0;
    if (hslColor[2] < 0) hslColor[2] = 0;

    if (hslColor[0] > 1) hslColor[0] = 1;
    if (hslColor[1] > 1) hslColor[1] = 1;
    if (hslColor[2] > 1) hslColor[2] = 1;

    hslColor = this.hslToRgb(hslColor[0], hslColor[1], hslColor[2]);

    this.set(hslColor[0], hslColor[1], hslColor[2], this.alpha);

    return this;
  }
}, {
  key: "adjustHue",
  value: function adjustHue(delta) {
    var hslColor = this.rgbToHsl();
    hslColor[0] += (delta || 0) / 100;
    hslColor = this.hslToRgb(hslColor[0], hslColor[1], hslColor[2]);

    this.set(hslColor[0], hslColor[1], hslColor[2], this.alpha);

    return this;
  }
}, {
  key: "adjustSaturation",
  value: function adjustSaturation(delta) {
    var hslColor = this.rgbToHsl();
    hslColor[1] += (delta || 0) / 100;
    hslColor = this.hslToRgb(hslColor[0], hslColor[1], hslColor[2]);

    this.set(hslColor[0], hslColor[1], hslColor[2], this.alpha);

    return this;
  }
}, {
  key: "adjustLightness",
  value: function adjustLightness(delta) {
    var hslColor = this.rgbToHsl();
    hslColor[2] += (delta || 0) / 100;
    hslColor = this.hslToRgb(hslColor[0], hslColor[1], hslColor[2]);

    this.set(hslColor[0], hslColor[1], hslColor[2], this.alpha);

    return this;
  }
}, {
  key: "rgbToHsl",
  value: function rgbToHsl(r, g, b) {
    if (r == undefined) {
      r = this.red;
      g = this.green;
      b = this.blue;
    }

    // r /= 255, g /= 255, b /= 255;
    var max = Math.max(r, g, b),
        min = Math.min(r, g, b);
    var h,
        s,
        l = (max + min) / 2;

    if (max == min) {
      h = s = 0; // achromatic
    } else {
      var d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0);break;
        case g:
          h = (b - r) / d + 2;break;
        case b:
          h = (r - g) / d + 4;break;
      }
      h /= 6;
    }

    return [h, s, l];
  }
}, {
  key: "hslToRgb",
  value: function hslToRgb(h, s, l) {
    var r, g, b;

    if (h == undefined) {
      return [this.red, this.green, this.blue];
    }

    if (s == 0) {
      r = g = b = l; // achromatic
    } else {
      var q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      var p = 2 * l - q;
      r = this.hue2rgb(p, q, h + 1 / 3);
      g = this.hue2rgb(p, q, h);
      b = this.hue2rgb(p, q, h - 1 / 3);
    }

    return [Math.round(r), Math.round(g), Math.round(b)];
  }
}, {
  key: "hue2rgb",
  value: function hue2rgb(p, q, t) {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  }
}]);
return Color;
}();

var Emitter = function (_Component) {
inherits(Emitter, _Component);
createClass(Emitter, [{
  key: 'count',
  get: function get$$1() {
    return this.particles.length;
  },
  set: function set(val) {
    this.particles.length = val;
    this.particleAge.length = val;
    this.warmed = false;
  }
}]);

function Emitter() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref$count = _ref.count,
      count = _ref$count === undefined ? 1000 : _ref$count,
      _ref$life = _ref.life,
      life = _ref$life === undefined ? 1000 : _ref$life,
      _ref$rate = _ref.rate,
      rate = _ref$rate === undefined ? 1000 : _ref$rate,
      _ref$offset = _ref.offset,
      offset = _ref$offset === undefined ? [0, 0, 0] : _ref$offset,
      _ref$velocity = _ref.velocity,
      velocity = _ref$velocity === undefined ? [0, 0, 0] : _ref$velocity;

  classCallCheck(this, Emitter);

  var _this = possibleConstructorReturn(this, (Emitter.__proto__ || Object.getPrototypeOf(Emitter)).call(this));

  _this._r = {
    i: null,
    frameTime: 0,
    timeAccumulator: 0
  };
  _this.warmed = false;

  _this.rate = rate;
  _this.life = life;
  _this.offset = offset;
  _this.velocity = velocity;
  // this.timeToLiveMin = timeToLiveMin;
  // this.timeToLiveMax = timeToLiveMax;
  // this.velocityMin = 1;
  // this.velocityMax = 10;

  _this.particles = [];
  _this.particleAge = [];
  _this.count = count;
  return _this;
}

createClass(Emitter, [{
  key: 'render',
  value: function render(dt, scene, viewport, camera, entity) {
    get(Emitter.prototype.__proto__ || Object.getPrototypeOf(Emitter.prototype), 'render', this).call(this, dt, scene, viewport, camera, entity);

    for (var i = 0; i < this.particles.length; i++) {
      if (this.particles[i].visible) {
        this.particles[i].render(dt, scene, viewport, camera, entity, this);
      }
    }
  }
}, {
  key: 'update',
  value: function update(dt, scene, entity) {
    get(Emitter.prototype.__proto__ || Object.getPrototypeOf(Emitter.prototype), 'update', this).call(this, dt, scene, entity);

    if (!this.warmed) this.prewarm();

    // Update particles
    for (var i = 0; i < this.particles.length; i++) {
      if (!this.particles[i].enabled) continue;

      // Update current particle positions
      this.particles[i].transform.translate(this.velocity[0], this.velocity[1], this.velocity[2]);

      // Kill any particles that are EOL
      this.particleAge[i] += dt;
      if (this.particleAge[i] >= this.life) {
        this.particles[i].enabled = false;
        this.particles[i].visible = false;
      }
    }

    if (!this.rate) return;

    // Emit new particles
    this._r.timeAccumulator += dt;

    this._r.i = Math.floor(this._r.timeAccumulator / this.rate);
    while (this._r.i-- >= 0) {
      this.emit();
      this._r.timeAccumulator -= this.rate;
    }
  }

  // Meant to be overridden 

}, {
  key: 'entityCreate',
  value: function entityCreate(index) {}

  // TODO: Not sure how I feel about this function name

}, {
  key: 'prewarm',
  value: function prewarm() {
    for (var i = 0; i < this.particles.length; i++) {
      if (!this.particles[i]) {
        this.particles[i] = this.entityCreate(i);
        this.particles[i].enabled = false;
        this.particles[i].visible = false;
        this.particleAge[i] = 0;
      }
    }
    this.warmed = true;
  }
}, {
  key: 'emit',
  value: function emit() {
    // Find next available particle
    for (var i = 0; i < this.particles.length; i++) {
      if (!this.particles[i].enabled) {
        this.particles[i].enabled = true;
        this.particles[i].visible = true;
        this.particleAge[i] = 0;
        this.particles[i].transform.translateTo(this.offset[0], this.offset[1], this.offset[2]);
        return;
      }
    }
  }
}]);
return Emitter;
}(Component);

var FrameBufferTexture2D = function (_Texture) {
  inherits(FrameBufferTexture2D, _Texture);
  createClass(FrameBufferTexture2D, [{
      key: "width",
      get: function get$$1() {
          return this.framebufferWidth;
      },
      set: function set(val) {
          return this.framebufferWidth = val;
      }
  }, {
      key: "height",
      get: function get$$1() {
          return this.framebufferHeight;
      },
      set: function set(val) {
          return this.framebufferHeight = val;
      }
  }]);

  function FrameBufferTexture2D() {
      var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
          _ref$width = _ref.width,
          width = _ref$width === undefined ? 1024 : _ref$width,
          _ref$height = _ref.height,
          height = _ref$height === undefined ? 1024 : _ref$height,
          _ref$flipY = _ref.flipY,
          flipY = _ref$flipY === undefined ? false : _ref$flipY,
          _ref$textureType = _ref.textureType,
          textureType = _ref$textureType === undefined ? "TEXTURE_2D" : _ref$textureType,
          _ref$magFilter = _ref.magFilter,
          magFilter = _ref$magFilter === undefined ? "LINEAR" : _ref$magFilter,
          _ref$minFilter = _ref.minFilter,
          minFilter = _ref$minFilter === undefined ? "LINEAR" : _ref$minFilter,
          _ref$generateMipmap = _ref.generateMipmap,
          generateMipmap = _ref$generateMipmap === undefined ? false : _ref$generateMipmap,
          _ref$wrap = _ref.wrap,
          wrap = _ref$wrap === undefined ? { s: "CLAMP_TO_EDGE", t: "CLAMP_TO_EDGE" } : _ref$wrap;

      classCallCheck(this, FrameBufferTexture2D);

      var _this = possibleConstructorReturn(this, (FrameBufferTexture2D.__proto__ || Object.getPrototypeOf(FrameBufferTexture2D)).apply(this, arguments));

      _this.framebuffer = null;
      _this.framebufferWidth = width;
      _this.framebufferHeight = height;

      _this.flipY = flipY;
      _this.magFilter = magFilter;
      _this.minFilter = minFilter;
      _this.textureType = textureType;

      _this.wrap = wrap;

      _this.generateMipmap = generateMipmap;

      _this.useDepthBuffer = true;

      _this.loaded = true;
      return _this;
  }

  createClass(FrameBufferTexture2D, [{
      key: "bind",
      value: function bind(viewport) {
          var textureNumber = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
          var target = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : viewport.webgl[this.textureType];
          var width = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : this.width;
          var height = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : this.height;

          // Create the "texture"
          if (!this.texture[viewport.id] || width !== this.width || height !== this.height) {
              this.width = width;
              this.height = height;
              // console.log(width, height);
              if (this.texture[viewport.id]) this.unload(viewport);
              this.createTexture(viewport);
          }

          viewport.webgl.bindTexture(viewport.webgl[this.textureType], this.texture[viewport.id]);

          // Create and bind the framebuffer
          if (!this.framebuffer) {
              this.framebuffer = viewport.webgl.createFramebuffer();
              this.width = width;
              this.height = height;
          }
          this.img = this.framebuffer;

          viewport.webgl.bindFramebuffer(viewport.webgl.FRAMEBUFFER, this.framebuffer);
          viewport.webgl.framebufferRenderbuffer(viewport.webgl.FRAMEBUFFER, viewport.webgl.DEPTH_ATTACHMENT, viewport.webgl.RENDERBUFFER, this.depthBuffer);

          // Set the size of the viewport to be the same size as the frame buffer
          viewport.webgl.viewport(0, 0, this.width, this.height);

          // attach the texture as the first color attachment
          viewport.webgl.framebufferTexture2D(viewport.webgl.FRAMEBUFFER, viewport.webgl.COLOR_ATTACHMENT0, target, this.texture[viewport.id], 0); // 0 = level

          viewport._currentTexture[textureNumber] = this;
      }
  }, {
      key: "unbind",
      value: function unbind(viewport) {
          viewport.webgl.bindFramebuffer(viewport.webgl.FRAMEBUFFER, null);

          // Set the size of the viewport to be the same size as the frame buffer
          viewport.webgl.viewport(0, 0, viewport.width, viewport.height);
      }
  }, {
      key: "createTexture",
      value: function createTexture(viewport) {
          // Create the texture in GL
          this.texture[viewport.id] = viewport.webgl.createTexture();
          viewport.webgl.bindTexture(viewport.webgl[this.textureType], this.texture[viewport.id]);
          viewport.webgl.pixelStorei(viewport.webgl.UNPACK_FLIP_Y_WEBGL, this.flipY);

          // Initialize the texture
          // define size and format of level 0
          var targetTextureWidth = this.framebufferWidth;
          var targetTextureHeight = this.framebufferHeight;
          var level = 0;
          var internalFormat = viewport.webgl.RGBA;
          var border = 0;
          var format = viewport.webgl.RGBA;
          var type = viewport.webgl.UNSIGNED_BYTE;
          var data = null; // Just tell WebGL to allocate the texture

          if (this.textureType === "TEXTURE_CUBE_MAP") {
              for (var i = 0; i < 6; i++) {
                  viewport.webgl.texImage2D(viewport.webgl.TEXTURE_CUBE_MAP_POSITIVE_X + i, level, internalFormat, targetTextureWidth, targetTextureHeight, border, format, type, data);
              }
          } else {
              viewport.webgl.texImage2D(viewport.webgl[this.textureType], level, internalFormat, targetTextureWidth, targetTextureHeight, border, format, type, data);
          }

          // If using a depth buffer, create one
          if (this.useDepthBuffer) {
              // create a depth renderbuffer
              this.depthBuffer = viewport.webgl.createRenderbuffer();
              viewport.webgl.bindRenderbuffer(viewport.webgl.RENDERBUFFER, this.depthBuffer);

              // make a depth buffer and the same size as the targetTexture
              viewport.webgl.renderbufferStorage(viewport.webgl.RENDERBUFFER, viewport.webgl.DEPTH_COMPONENT16, targetTextureWidth, targetTextureHeight);
          }
      }
  }, {
      key: "updateTexture",
      value: function updateTexture(viewport) {}
  }]);
  return FrameBufferTexture2D;
}(Texture);

var GLProgram = function () {
function GLProgram(viewport) {
  classCallCheck(this, GLProgram);

  this.program = viewport.webgl.createProgram();
  this.attribute = {};
  this.uniform = {};
  this.fragmentShader = "";
  this.vertexShader = "";

  // TODO: Make this an option for materials
  // viewport.webgl.getExtension('OES_standard_derivatives');
}

createClass(GLProgram, [{
  key: "addVertexShader",
  value: function addVertexShader(viewport, shader) {
    var vs = viewport.webgl.createShader(viewport.webgl.VERTEX_SHADER);
    viewport.webgl.shaderSource(vs, shader);
    viewport.webgl.compileShader(vs);
    viewport.webgl.attachShader(this.program, vs); // Maybe do this after, so that you can attach/detach shaders

    // If creating the shader program failed, alert
    if (!viewport.webgl.getShaderParameter(vs, viewport.webgl.COMPILE_STATUS)) {
      throw "An error occurred compiling the vertex shader: " + viewport.webgl.getShaderInfoLog(vs);
    }

    this.vertexShader = shader;
  }
}, {
  key: "addFragmentShader",
  value: function addFragmentShader(viewport, shader) {
    var fs = viewport.webgl.createShader(viewport.webgl.FRAGMENT_SHADER);
    viewport.webgl.shaderSource(fs, shader);
    viewport.webgl.compileShader(fs);
    viewport.webgl.attachShader(this.program, fs);

    // If creating the shader program failed, alert
    if (!viewport.webgl.getShaderParameter(fs, viewport.webgl.COMPILE_STATUS)) {
      throw "An error occurred compiling the fragment shader: " + viewport.webgl.getShaderInfoLog(fs);
    }

    this.fragmentShader = shader;
  }
}, {
  key: "getAttributesFromShaders",
  value: function getAttributesFromShaders(viewport) {
    var attributeCount = viewport.webgl.getProgramParameter(this.program, viewport.webgl.ACTIVE_ATTRIBUTES);

    for (var i = 0; i < attributeCount; i++) {
      var attributeInfo = viewport.webgl.getActiveAttrib(this.program, i);
      this.attribute[attributeInfo.name] = viewport.webgl.getAttribLocation(this.program, attributeInfo.name);

      if (this.attribute[attributeInfo.name] < 0) delete this.attribute[attributeInfo.name];
    }
  }
}, {
  key: "getUniformLocations",
  value: function getUniformLocations(viewport) {
    var uniformCount = viewport.webgl.getProgramParameter(this.program, viewport.webgl.ACTIVE_UNIFORMS);

    for (var i = 0; i < uniformCount; i++) {
      var uniformInfo = viewport.webgl.getActiveUniform(this.program, i);
      this.uniform[uniformInfo.name] = viewport.webgl.getUniformLocation(this.program, uniformInfo.name);

      if (this.uniform[uniformInfo.name] < 0) delete this.uniform[uniformInfo.name];
    }
  }
}, {
  key: "use",
  value: function use(viewport) {
    viewport.webgl.useProgram(this.program);
  }
}]);
return GLProgram;
}();

var tMVPMatrix = create$3();

var Light = function (_Entity) {
inherits(Light, _Entity);

function Light() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref$id = _ref.id,
      id = _ref$id === undefined ? "Light_" + _id$3++ : _ref$id,
      _ref$transform = _ref.transform,
      transform = _ref$transform === undefined ? new Transform$1() : _ref$transform,
      _ref$ambientColor = _ref.ambientColor,
      ambientColor = _ref$ambientColor === undefined ? new Color(0, 0, 0, 1) : _ref$ambientColor,
      _ref$diffuseColor = _ref.diffuseColor,
      diffuseColor = _ref$diffuseColor === undefined ? new Color(1, 1, 1, 1) : _ref$diffuseColor,
      _ref$specularColor = _ref.specularColor,
      specularColor = _ref$specularColor === undefined ? new Color(1, 1, 1, 1) : _ref$specularColor,
      _ref$intensity = _ref.intensity,
      intensity = _ref$intensity === undefined ? 1.0 : _ref$intensity,
      _ref$maxDistance = _ref.maxDistance,
      maxDistance = _ref$maxDistance === undefined ? 128.0 : _ref$maxDistance;

  classCallCheck(this, Light);

  var _this = possibleConstructorReturn(this, (Light.__proto__ || Object.getPrototypeOf(Light)).apply(this, arguments));

  _this.ambientColor = ambientColor;
  _this.diffuseColor = diffuseColor;
  _this.specularColor = specularColor;

  _this.intensity = intensity;
  _this.maxDistance = maxDistance;

  _this.projectionMatrix = create$3();
  identity$3(_this.projectionMatrix);
  // mat4.ortho(this.projectionMatrix, -40, 40, -40, 40, -this.maxDistance, this.maxDistance);
  // mat4.ortho(this.projectionMatrix, -40, 40, -40, 40, -40.0, 80);
  // mat4.ortho(this.projectionMatrix, -10, 10, -10, 10, -10.0, 80);

  return _this;
}

createClass(Light, [{
  key: "getMVPMatrix",
  value: function getMVPMatrix(modelMatrix, viewMatrix) {
    perspective(this.projectionMatrix, 1.5708, 1, 0.01, this.maxDistance);
    // mat4.lookAt(tMVPMatrix, this.transform.xyz, [this.transform.x, this.transform.y, this.transform.z - 100], [0,1,0]);

    // mat4.mul(tMVPMatrix, this.projectionMatrix, this.transform.localMatrix);

    copy$3(tMVPMatrix, this.projectionMatrix);

    if (viewMatrix) {
      // Cube map direction
      mul$3(tMVPMatrix, tMVPMatrix, viewMatrix);
    }

    // This position
    mul$3(tMVPMatrix, tMVPMatrix, this.transform.localMatrixInversed);

    if (modelMatrix) {
      mul$3(tMVPMatrix, tMVPMatrix, modelMatrix);
    }

    // mat4.targetTo(tMVPMatrix, this.transform.xyz, [this.transform.x, this.transform.y, this.transform.z - 100], [0,1,0]);


    return tMVPMatrix;
  }
}]);
return Light;
}(Entity);

var tMVPMatrix$1 = create$3();
var tLVMatrix = create$3();
var tLPMatrix = create$3();
var tCropMatrix = create$3();

var DirectionalLight = function (_Light) {
inherits(DirectionalLight, _Light);

function DirectionalLight() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref$id = _ref.id,
      id = _ref$id === undefined ? "DirectionalLight_" + _id$4++ : _ref$id,
      _ref$vector = _ref.vector,
      vector = _ref$vector === undefined ? [0, -1, 0] : _ref$vector;

  classCallCheck(this, DirectionalLight);

  var _this = possibleConstructorReturn(this, (DirectionalLight.__proto__ || Object.getPrototypeOf(DirectionalLight)).apply(this, arguments));

  _this.vector = vector;
  _this.maxShadowDistance = 50;
  return _this;
}

createClass(DirectionalLight, [{
  key: "getMVPMatrix",
  value: function getMVPMatrix(modelMatrix, viewport) {
    var minX = Infinity;
    var maxX = -Infinity;
    var minY = Infinity;
    var maxY = -Infinity;
    var minZ = Infinity;
    var maxZ = -Infinity;

    // Get view frustum
    // let t = viewport.camera.farPlane;

    // viewport.camera.farPlane = this.maxShadowDistance;
    // viewport.evaluate();

    var frustum$$1 = viewport.camera.getFrustum(viewport);

    // viewport.camera.farPlane = t;
    // viewport.evaluate();

    // Get max distance scale
    var scaleX = Math.abs(frustum$$1[7][0] - frustum$$1[0][0]) / this.maxShadowDistance;
    var scaleY = Math.abs(frustum$$1[7][1] - frustum$$1[0][1]) / this.maxShadowDistance;
    var scaleZ = Math.abs(frustum$$1[7][2] - frustum$$1[0][2]) / this.maxShadowDistance;
    var frustumCenter = [0, 0, 0];
    var _iteratorNormalCompletion = true;
    var _didIteratorError = false;
    var _iteratorError = undefined;

    try {
      for (var _iterator = frustum$$1[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
        var point = _step.value;

        frustumCenter[0] += point[0];
        frustumCenter[1] += point[1];
        frustumCenter[2] += point[2];
      }
    } catch (err) {
      _didIteratorError = true;
      _iteratorError = err;
    } finally {
      try {
        if (!_iteratorNormalCompletion && _iterator.return) {
          _iterator.return();
        }
      } finally {
        if (_didIteratorError) {
          throw _iteratorError;
        }
      }
    }

    for (var i = 0; i < 3; i++) {
      frustumCenter[i] /= 8;
    }

    // Rotate light view to direction
    //  - Get light view direction matrix
    var normalized = [];
    normalize$1(normalized, this.vector);
    normalized[0] += frustumCenter[0];
    normalized[1] += frustumCenter[1];
    normalized[2] += frustumCenter[2];
    lookAt(tLVMatrix, frustumCenter, normalized, [0, 1, 0]);

    //  - Rotate
    var _iteratorNormalCompletion2 = true;
    var _didIteratorError2 = false;
    var _iteratorError2 = undefined;

    try {
      for (var _iterator2 = frustum$$1[Symbol.iterator](), _step2; !(_iteratorNormalCompletion2 = (_step2 = _iterator2.next()).done); _iteratorNormalCompletion2 = true) {
        var _point = _step2.value;

        transformMat4$1(_point, _point, tLVMatrix);

        minX = Math.min(minX, _point[0]);
        maxX = Math.max(maxX, _point[0]);
        minY = Math.min(minY, _point[1]);
        maxY = Math.max(maxY, _point[1]);
        minZ = Math.min(minZ, _point[2]);
        maxZ = Math.max(maxZ, _point[2]);
      }

      // Get units per texel
    } catch (err) {
      _didIteratorError2 = true;
      _iteratorError2 = err;
    } finally {
      try {
        if (!_iteratorNormalCompletion2 && _iterator2.return) {
          _iterator2.return();
        }
      } finally {
        if (_didIteratorError2) {
          throw _iteratorError2;
        }
      }
    }

    ortho(tLPMatrix, -1, 1, -1, 1, minZ, maxZ);
    // mat4.ortho(tLPMatrix, minX, maxX, minY, maxY, minZ, maxZ);

    // Crop
    scaleX = Math.min(2 / (maxX - minX), 2 / this.maxShadowDistance);
    scaleY = Math.min(2 / (maxY - minY), 2 / this.maxShadowDistance);
    scaleZ = Math.min(1 / (maxZ - minZ), 1 / this.maxShadowDistance);
    var offsetX = -0.5 * (minX + maxX) * scaleX;
    var offsetY = -0.5 * (minY + maxY) * scaleY;
    var offsetZ = -minZ * scaleZ;

    tCropMatrix[0] = scaleX;
    tCropMatrix[5] = scaleY;
    tCropMatrix[10] = scaleZ;
    tCropMatrix[12] = offsetX;
    tCropMatrix[13] = offsetY;
    tCropMatrix[14] = offsetZ;

    // cropMatrix * lpMatrix * lvMatrix;
    mul$3(tMVPMatrix$1, tCropMatrix, tLPMatrix);
    mul$3(tMVPMatrix$1, tMVPMatrix$1, tLVMatrix);

    // lpMatrix * lvMatrix * cropMatrix;
    // mat4.mul(tMVPMatrix, tLPMatrix, tLVMatrix);
    // mat4.mul(tMVPMatrix, tMVPMatrix, tCropMatrix);

    // ... * Model
    if (modelMatrix) {
      mul$3(tMVPMatrix$1, tMVPMatrix$1, modelMatrix);
    }

    // debugger;

    return tMVPMatrix$1;
  }
}]);
return DirectionalLight;
}(Light);

var tMVPMatrix$2 = create$3();

var PointLight = function (_Light) {
inherits(PointLight, _Light);

function PointLight() {
  classCallCheck(this, PointLight);

  var _this = possibleConstructorReturn(this, (PointLight.__proto__ || Object.getPrototypeOf(PointLight)).apply(this, arguments));

  _this.constantAttenuation = 1;
  _this.linearAttenuation = 1;
  _this.quadraticAttenuation = 1;
  return _this;
}

createClass(PointLight, [{
  key: "getMVPMatrix",
  value: function getMVPMatrix(modelMatrix, viewport, viewMatrix) {
    perspective(this.projectionMatrix, 1.5708, 1, 0.01, this.maxDistance);
    // mat4.lookAt(tMVPMatrix, this.transform.xyz, [this.transform.x, this.transform.y, this.transform.z - 100], [0,1,0]);

    // mat4.mul(tMVPMatrix, this.projectionMatrix, this.transform.localMatrix);

    copy$3(tMVPMatrix$2, this.projectionMatrix);

    if (viewMatrix) {
      // Cube map direction
      mul$3(tMVPMatrix$2, tMVPMatrix$2, viewMatrix);
    }

    // This position
    mul$3(tMVPMatrix$2, tMVPMatrix$2, this.transform.localMatrixInversed);

    if (modelMatrix) {
      mul$3(tMVPMatrix$2, tMVPMatrix$2, modelMatrix);
    }

    // mat4.targetTo(tMVPMatrix, this.transform.xyz, [this.transform.x, this.transform.y, this.transform.z - 100], [0,1,0]);


    return tMVPMatrix$2;
  }
}]);
return PointLight;
}(Light);

var tMVPMatrix$3 = create$3();

var SpotLight = function (_Light) {
inherits(SpotLight, _Light);

function SpotLight() {
  classCallCheck(this, SpotLight);

  var _this = possibleConstructorReturn(this, (SpotLight.__proto__ || Object.getPrototypeOf(SpotLight)).apply(this, arguments));

  _this.spotDirection = [0, -1, 0];
  _this.spotExponent = 1;
  _this.spotCutoff = 1;

  _this.constantAttenuation = 1;
  _this.linearAttenuation = 1;
  _this.quadraticAttenuation = 1;
  return _this;
}

createClass(SpotLight, [{
  key: "getMVPMatrix",
  value: function getMVPMatrix(modelMatrix, viewMatrix) {
    perspective(this.projectionMatrix, 1.5708, 1, 0.01, this.maxDistance);
    // mat4.lookAt(tMVPMatrix, this.transform.xyz, [this.transform.x, this.transform.y, this.transform.z - 100], [0,1,0]);

    // mat4.mul(tMVPMatrix, this.projectionMatrix, this.transform.localMatrix);

    copy$3(tMVPMatrix$3, this.projectionMatrix);

    if (viewMatrix) {
      // Cube map direction
      mul$3(tMVPMatrix$3, tMVPMatrix$3, viewMatrix);
    }

    // This position
    mul$3(tMVPMatrix$3, tMVPMatrix$3, this.transform.localMatrixInversed);

    if (modelMatrix) {
      mul$3(tMVPMatrix$3, tMVPMatrix$3, modelMatrix);
    }

    // mat4.targetTo(tMVPMatrix, this.transform.xyz, [this.transform.x, this.transform.y, this.transform.z - 100], [0,1,0]);


    return tMVPMatrix$3;
  }
}]);
return SpotLight;
}(Light);

// Note: This class was adapted from THREE.JS, all kudos goes to the brilliant minds of those authors.

var NURBSCurve = function (_Component) {
  inherits(NURBSCurve, _Component);

  function NURBSCurve() {
      var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
          _ref$id = _ref.id,
          id = _ref$id === undefined ? "NURBSCurve_" + _id$7++ : _ref$id,
          _ref$arcLengthDivisio = _ref.arcLengthDivisions,
          arcLengthDivisions = _ref$arcLengthDivisio === undefined ? 5 : _ref$arcLengthDivisio,
          _ref$controlPoints = _ref.controlPoints,
          controlPoints = _ref$controlPoints === undefined ? [] : _ref$controlPoints,
          _ref$degree = _ref.degree,
          degree = _ref$degree === undefined ? 3 : _ref$degree,
          _ref$knots = _ref.knots,
          knots = _ref$knots === undefined ? [] : _ref$knots,
          _ref$startKnot = _ref.startKnot,
          startKnot = _ref$startKnot === undefined ? 0 : _ref$startKnot,
          _ref$endKnot = _ref.endKnot,
          endKnot = _ref$endKnot === undefined ? knots.length - 1 : _ref$endKnot;

      classCallCheck(this, NURBSCurve);

      var _this = possibleConstructorReturn(this, (NURBSCurve.__proto__ || Object.getPrototypeOf(NURBSCurve)).apply(this, arguments));

      _this.arcLengthDivisions = arcLengthDivisions;
      _this.controlPoints = controlPoints;
      _this.degree = degree;
      _this.knots = knots;
      _this.startKnot = startKnot;
      _this.endKnot = endKnot;

      _this.valid = true;
      return _this;
  }

  createClass(NURBSCurve, [{
      key: "calcBasisFunctions",
      value: function calcBasisFunctions(span, parametricPoint, degree, knotVector) {
          var N = [];
          var left = [];
          var right = [];

          N[0] = 1.0;

          for (var j = 1; j <= degree; ++j) {
              left[j] = parametricPoint - knotVector[span + 1 - j];
              right[j] = knotVector[span + j] - parametricPoint;

              var saved = 0.0;

              for (var r = 0; r < j; ++r) {
                  var rv = right[r + 1];
                  var lv = left[j - r];
                  var temp = N[r] / (rv + lv);
                  N[r] = saved + rv * temp;
                  saved = lv * temp;
              }

              N[j] = saved;
          }

          return N;
      }
  }, {
      key: "calcBSplinePoint",
      value: function calcBSplinePoint(degree, knotVector, controlPoints, parametricPoint) {
          var span = this.findSpan(degree, parametricPoint, knotVector);
          var N = this.calcBasisFunctions(span, parametricPoint, degree, knotVector);
          var C = [0, 0, 0, 0];

          for (var j = 0; j <= degree; ++j) {
              var point = controlPoints[span - degree + j];
              var Nj = N[j];
              var wNj = point[3] * Nj;

              C[0] += point[0] * wNj;
              C[1] += point[1] * wNj;
              C[2] += point[2] * wNj;
              C[3] += point[3] * Nj;
          }

          return C;
      }
  }, {
      key: "findSpan",
      value: function findSpan(degree, parametricValue, knotVector) {
          var n = knotVector.length - degree - 1;

          if (parametricValue >= knotVector[n]) {
              return knotVector.indexOf(knotVector[n]) - 1; //n - 1;
          }

          if (parametricValue <= knotVector[degree]) {
              return degree;
          }

          var low = degree;
          var high = n;
          var mid = Math.floor((low + high) / 2);

          while (parametricValue < knotVector[mid] || parametricValue >= knotVector[mid + 1]) {
              if (parametricValue < knotVector[mid]) {
                  high = mid;
              } else {
                  low = mid;
              }

              mid = Math.floor((low + high) / 2);
          }

          return mid;
      }
  }, {
      key: "getLengths",
      value: function getLengths() {
          var divisions = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : this.arcLengthDivisions;


          if (this.cacheArcLengths && this.cacheArcLengths.length === divisions + 1 && this.valid) {

              return this.cacheArcLengths;
          }

          this.needsUpdate = false;

          var cache = [];
          var current,
              last = this.getPoint(0);
          var p,
              sum = 0;

          cache.push(0);

          for (p = 1; p <= divisions; p++) {

              current = this.getPoint(p / divisions);
              sum += distance(current, last);
              cache.push(sum);
              last = current;
          }

          this.cacheArcLengths = cache;

          return cache; // { sums: cache, sum: sum }; Sum is in the last element.
      }
  }, {
      key: "getPoint",
      value: function getPoint(t) {
          var u = this.knots[this.startKnot] + t * (this.knots[this.endKnot] - this.knots[this.startKnot]); // linear mapping t->u

          // following results in (wx, wy, wz, w) homogeneous point
          var hpoint = this.calcBSplinePoint(this.degree, this.knots, this.controlPoints, u);

          if (hpoint[3] != 1.0) {
              // project to 3D space: (wx, wy, wz, w) -> (x, y, z, 1)
              scale$6(hpoint, hpoint, 1 / hpoint[3]);
          }

          return [hpoint[0], hpoint[1], hpoint[2], hpoint[3]];
      }
  }, {
      key: "getPointAt",
      value: function getPointAt(u) {
          var t = this.getUtoTmapping(u);
          return this.getPoint(t);
      }
  }, {
      key: "getUtoTmapping",
      value: function getUtoTmapping(u, distance$$1) {

          var arcLengths = this.getLengths();

          var i = 0,
              il = arcLengths.length;

          var targetArcLength; // The targeted u distance value to get

          if (distance$$1) {

              targetArcLength = distance$$1;
          } else {

              targetArcLength = u * arcLengths[il - 1];
          }

          // binary search for the index with largest value smaller than target u distance

          var low = 0,
              high = il - 1,
              comparison;

          while (low <= high) {

              i = Math.floor(low + (high - low) / 2); // less likely to overflow, though probably not issue here, JS doesn't really have integers, all numbers are floats

              comparison = arcLengths[i] - targetArcLength;

              if (comparison < 0) {

                  low = i + 1;
              } else if (comparison > 0) {

                  high = i - 1;
              } else {

                  high = i;
                  break;

                  // DONE
              }
          }

          i = high;

          if (arcLengths[i] === targetArcLength) {

              return i / (il - 1);
          }

          // we could get finer grain at lengths, or use simple interpolation between two points

          var lengthBefore = arcLengths[i];
          var lengthAfter = arcLengths[i + 1];

          var segmentLength = lengthAfter - lengthBefore;

          // determine where we are between the 'before' and 'after' points

          var segmentFraction = (targetArcLength - lengthBefore) / segmentLength;

          // add that fractional amount to t

          var t = (i + segmentFraction) / (il - 1);

          return t;
      }
  }]);
  return NURBSCurve;
}(Component);

var SCREEN_VERTEX_POSITION = [-1, -1, 0, 1, -1, 0, 1, 1, 0, -1, 1, 0, -1, -1, 0, 1, 1, 0];

var tMatrix$3 = create$3();

var Material = function () {
// TODO: depthFunc should be LESS by default
function Material() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref$name = _ref.name,
      name = _ref$name === undefined ? "" : _ref$name,
      _ref$depthTest = _ref.depthTest,
      depthTest = _ref$depthTest === undefined ? true : _ref$depthTest,
      _ref$depthFunc = _ref.depthFunc,
      depthFunc = _ref$depthFunc === undefined ? 'LEQUAL' : _ref$depthFunc,
      _ref$depthMask = _ref.depthMask,
      depthMask = _ref$depthMask === undefined ? true : _ref$depthMask,
      _ref$depthRangeNear = _ref.depthRangeNear,
      depthRangeNear = _ref$depthRangeNear === undefined ? 0 : _ref$depthRangeNear,
      _ref$depthRangeFar = _ref.depthRangeFar,
      depthRangeFar = _ref$depthRangeFar === undefined ? 1 : _ref$depthRangeFar,
      _ref$cullFace = _ref.cullFace,
      cullFace = _ref$cullFace === undefined ? true : _ref$cullFace,
      _ref$cullFaceMode = _ref.cullFaceMode,
      cullFaceMode = _ref$cullFaceMode === undefined ? "BACK" : _ref$cullFaceMode,
      _ref$frontFace = _ref.frontFace,
      frontFace = _ref$frontFace === undefined ? "CCW" : _ref$frontFace;

  classCallCheck(this, Material);

  this.name = name;
  this.program = [];
  this.vbuffer = null;

  this.blend = true;

  this.depthTest = depthTest;
  // this.depthWrite = true;
  this.depthFunc = depthFunc;
  this.depthMask = depthMask;
  this.depthRangeNear = depthRangeNear;
  this.depthRangeFar = depthRangeFar;

  this.cullFace = cullFace;
  this.cullFaceMode = cullFaceMode;
  this.frontFace = frontFace;

  this.timeOffset = 0;

  this.buffers = {
    screenVertices: []
  };
}

createClass(Material, [{
  key: 'render',
  value: function render(dt, scene, viewport, camera, stack, viewMatrix, modelMatrix, frameBufferTextureStack) {
    // TODO: We should compile a shader cache, so that way we aren't unnecessarily swapping shaders
    // TODO: Compile per viewport
    if (!this.program[viewport.id]) {
      this.program[viewport.id] = viewport.createProgram(this);
    }
    viewport.useProgram(this.program[viewport.id]);

    if (this.blend) {
      viewport.webgl.enable(viewport.webgl.BLEND);
      viewport.webgl.blendFuncSeparate(viewport.webgl.SRC_ALPHA, viewport.webgl.ONE_MINUS_SRC_ALPHA, viewport.webgl.ZERO, viewport.webgl.ONE);
    } else {
      viewport.webgl.disable(viewport.webgl.BLEND);
    }

    // Enable depth testing
    if (this.depthTest) {
      viewport.webgl.enable(viewport.webgl.DEPTH_TEST);
      viewport.webgl.depthMask(this.depthMask);
      viewport.webgl.depthFunc(viewport.webgl[this.depthFunc]);
      viewport.webgl.depthRange(this.depthRangeNear, this.depthRangeFar);
    } else {
      viewport.webgl.disable(viewport.webgl.DEPTH_TEST);
    }

    if (this.cullFace) {
      viewport.webgl.enable(viewport.webgl.CULL_FACE);
      viewport.webgl.cullFace(viewport.webgl[this.cullFaceMode]);
      viewport.webgl.frontFace(viewport.webgl[this.frontFace]);
    } else {
      viewport.webgl.disable(viewport.webgl.CULL_FACE);
    }

    // Set screen vertices
    if (viewport.program.attribute.aVertexScreenPosition !== undefined) {
      // Buffer mesh verts
      if (!this.buffers.screenVertices[viewport.id]) {
        this.buffers.screenVertices[viewport.id] = viewport.webgl.createBuffer();

        // Pass vertices to buffer
        // TODO: Do this once per vertices being updated (but bind on every frame)
        viewport.webgl.bindBuffer(viewport.webgl.ARRAY_BUFFER, this.buffers.screenVertices[viewport.id]);
        viewport.webgl.bufferData(viewport.webgl.ARRAY_BUFFER, new Float32Array(SCREEN_VERTEX_POSITION), viewport.webgl.STATIC_DRAW);
      }
      viewport.webgl.bindBuffer(viewport.webgl.ARRAY_BUFFER, this.buffers.screenVertices[viewport.id]);

      // Tell the vertex shader how to read the array
      viewport.webgl.vertexAttribPointer(viewport.program.attribute.aVertexScreenPosition, 3, viewport.webgl.FLOAT, false, 0, 0);
      viewport.webgl.enableVertexAttribArray(viewport.program.attribute.aVertexScreenPosition);
    }

    // Model View Camera Projection Matrix
    if (viewport.program.uniform.uMVCPMatrix !== undefined) {
      mul$3(tMatrix$3, viewMatrix, modelMatrix);
      viewport.webgl.uniformMatrix4fv(viewport.program.uniform.uMVCPMatrix, false, tMatrix$3);
    }

    // Set the camera position matrix
    if (viewport.program.uniform.uCMatrix !== undefined) {
      viewport.webgl.uniformMatrix4fv(viewport.program.uniform.uCMatrix, false, camera.transform.localMatrixInversed);
    }

    // Set the model view matrix (Inverse of camera position)
    if (viewport.program.uniform.uMVMatrix !== undefined) {
      viewport.webgl.uniformMatrix4fv(viewport.program.uniform.uMVMatrix, false, camera.mvTransform.localMatrix);
    }

    // Set the model view matrix (Inverse of camera position)
    if (viewport.program.uniform.uVMatrix !== undefined) {
      viewport.webgl.uniformMatrix4fv(viewport.program.uniform.uVMatrix, false, viewMatrix);
    }

    // Set the model matrix
    if (viewport.program.uniform.uMMatrix !== undefined) {
      viewport.webgl.uniformMatrix4fv(viewport.program.uniform.uMMatrix, false, modelMatrix);
    }

    // Set the camera position
    if (viewport.program.uniform.uCameraPosition !== undefined) {
      viewport.webgl.uniform3fv(viewport.program.uniform.uCameraPosition, camera.transform.xyz);
    }

    // Set the time
    if (viewport.program.uniform.iTime !== undefined) {
      viewport.webgl.uniform1f(viewport.program.uniform.iTime, (scene.clock.stats.currentGameTime + this.timeOffset) / 100000); // Divide by 1000 to help with intigers on lowp
    }

    if (viewport.program.uniform.uNormalMatrix !== undefined) {
      invert$3(tMatrix$3, modelMatrix);
      transpose$2(tMatrix$3, tMatrix$3);

      // TODO: Can this be optimized?
      // Note: The second argument (transpose) MUST be false according to WebGL spec
      viewport.webgl.uniformMatrix4fv(viewport.program.uniform.uNormalMatrix, false, tMatrix$3);
    }

    // Set the entity position matrix
    if (viewport.program.uniform.uTMatrix !== undefined) {
      if (viewport.parallax) {
        viewport.webgl.uniformMatrix4fv(viewport.program.uniform.uTMatrix, false, entity.transform.getParallaxMat4(camera));
      } else {
        viewport.webgl.uniformMatrix4fv(viewport.program.uniform.uTMatrix, false, entity.transform.localMatrix);
      }
    }
  }
}, {
  key: 'setValues',
  value: function setValues(obj) {
    return;
  }
}]);
return Material;
}();

var tMatrix$4 = create$3();

var Mesh = function (_Component) {
inherits(Mesh, _Component);

function Mesh() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref$id = _ref.id,
      id = _ref$id === undefined ? "Mesh_" + _id$8++ : _ref$id,
      _ref$material = _ref.material,
      material = _ref$material === undefined ? new MaterialPhong() : _ref$material,
      _ref$vertices = _ref.vertices,
      vertices = _ref$vertices === undefined ? new Float32Array(0) : _ref$vertices,
      _ref$uvs = _ref.uvs,
      uvs = _ref$uvs === undefined ? new Float32Array(0) : _ref$uvs,
      _ref$normals = _ref.normals,
      normals = _ref$normals === undefined ? new Float32Array(0) : _ref$normals,
      _ref$weights = _ref.weights,
      weights = _ref$weights === undefined ? new Float32Array(0) : _ref$weights,
      _ref$weightIndices = _ref.weightIndices,
      weightIndices = _ref$weightIndices === undefined ? new Float32Array(0) : _ref$weightIndices;

  classCallCheck(this, Mesh);

  var _this = possibleConstructorReturn(this, (Mesh.__proto__ || Object.getPrototypeOf(Mesh)).apply(this, arguments));

  _this.material = material; // TODO: Should we have multiple materials?
  _this.vertices = vertices;
  _this.uvs = uvs;
  _this.normals = normals;
  // this.parameters = parameters;
  // this.faces = faces;
  _this.weights = weights;
  _this.weightIndices = weightIndices;

  _this.castShadow = true;
  _this.receiveShadow = true;

  _this.buffers = {
    vertices: [],
    uvs: [],
    normals: [],
    weights: [],
    weightIndices: []
  };

  _this.valid = {
    vertices: [],
    uvs: [],
    normals: [],
    weights: [],
    weightIndices: []
  };
  return _this;
}

createClass(Mesh, [{
  key: 'update',
  value: function update() {
    // Anything?
  }
}, {
  key: 'render',
  value: function render(dt, scene, viewport, camera, stack, viewMatrix, modelMatrix) {
    var material = arguments.length > 7 && arguments[7] !== undefined ? arguments[7] : this.material;
    var frameBufferTextureStack = arguments[8];

    if (!material || !this.vertices.length) return;

    stack.push(this);

    // Calculate view matrix
    mul$3(tMatrix$4, modelMatrix, this.transform.localMatrix);

    if (material.render(dt, scene, viewport, camera, stack, viewMatrix, tMatrix$4, frameBufferTextureStack) === false) return;

    if (viewport.program.attribute.aVertexPosition !== undefined) {
      // Buffer mesh verts
      if (!this.buffers.vertices[viewport.id] || !this.valid.vertices[viewport.id]) {
        this.buffers.vertices[viewport.id] = viewport.webgl.createBuffer();

        // Pass vertices to buffer
        // TODO: Do this once per vertices being updated (but bind on every frame)
        viewport.webgl.bindBuffer(viewport.webgl.ARRAY_BUFFER, this.buffers.vertices[viewport.id]);
        viewport.webgl.bufferData(viewport.webgl.ARRAY_BUFFER, this.vertices, viewport.webgl.STATIC_DRAW); // TODO: Allow mesh to define type?

        this.valid.vertices[viewport.id] = true;
      }
      viewport.webgl.bindBuffer(viewport.webgl.ARRAY_BUFFER, this.buffers.vertices[viewport.id]);

      // Tell the vertex shader how to read the array
      viewport.webgl.vertexAttribPointer(viewport.program.attribute.aVertexPosition, 3, viewport.webgl.FLOAT, false, 0, 0);
      viewport.webgl.enableVertexAttribArray(viewport.program.attribute.aVertexPosition);
    }

    // UVs
    if (viewport.program.attribute.aTextureCoord !== undefined) {
      // Buffer mesh texture coordinates
      if (!this.buffers.uvs[viewport.id] || !this.valid.uvs[viewport.id]) {
        this.buffers.uvs[viewport.id] = viewport.webgl.createBuffer();

        // Pass texture coordinates to buffer
        viewport.webgl.bindBuffer(viewport.webgl.ARRAY_BUFFER, this.buffers.uvs[viewport.id]);
        viewport.webgl.bufferData(viewport.webgl.ARRAY_BUFFER, this.uvs, viewport.webgl.STATIC_DRAW);

        this.valid.uvs[viewport.id] = true;
      }
      viewport.webgl.bindBuffer(viewport.webgl.ARRAY_BUFFER, this.buffers.uvs[viewport.id]);

      // Tell the vertex shader how to read the array
      viewport.webgl.vertexAttribPointer(viewport.program.attribute.aTextureCoord, 2, viewport.webgl.FLOAT, false, 0, 0);
      viewport.webgl.enableVertexAttribArray(viewport.program.attribute.aTextureCoord);
    }

    // Normals
    if (viewport.program.attribute.aVertexNormal !== undefined) {
      // Buffer mesh normals
      if (!this.buffers.normals[viewport.id] || !this.valid.normals[viewport.id]) {
        this.buffers.normals[viewport.id] = viewport.webgl.createBuffer();

        // Pass normals to buffer
        viewport.webgl.bindBuffer(viewport.webgl.ARRAY_BUFFER, this.buffers.normals[viewport.id]);
        viewport.webgl.bufferData(viewport.webgl.ARRAY_BUFFER, this.normals, viewport.webgl.STATIC_DRAW);

        this.valid.normals[viewport.id] = true;
      }
      viewport.webgl.bindBuffer(viewport.webgl.ARRAY_BUFFER, this.buffers.normals[viewport.id]);

      // Tell the vertex shader how to read the array
      viewport.webgl.vertexAttribPointer(viewport.program.attribute.aVertexNormal, 3, viewport.webgl.FLOAT, false, 0, 0);
      viewport.webgl.enableVertexAttribArray(viewport.program.attribute.aVertexNormal);
    }

    // Weights
    if (viewport.program.attribute.aVertexWeights !== undefined) {
      // Buffer mesh weights
      if (!this.buffers.weights[viewport.id] || !this.valid.weights[viewport.id]) {
        this.buffers.weights[viewport.id] = viewport.webgl.createBuffer();

        // Pass weights to buffer
        viewport.webgl.bindBuffer(viewport.webgl.ARRAY_BUFFER, this.buffers.weights[viewport.id]);
        viewport.webgl.bufferData(viewport.webgl.ARRAY_BUFFER, this.weights, viewport.webgl.STATIC_DRAW);

        this.valid.weights[viewport.id] = true;
      }
      viewport.webgl.bindBuffer(viewport.webgl.ARRAY_BUFFER, this.buffers.weights[viewport.id]);

      // Tell the vertex shader how to read the array
      viewport.webgl.vertexAttribPointer(viewport.program.attribute.aVertexWeights, 4, viewport.webgl.FLOAT, false, 0, 0);
      viewport.webgl.enableVertexAttribArray(viewport.program.attribute.aVertexWeights);
    }

    // Weights Indices
    if (viewport.program.attribute.aVertexWeightIndices !== undefined) {
      // Buffer mesh weights
      if (!this.buffers.weightIndices[viewport.id] || !this.valid.weightIndices[viewport.id]) {
        this.buffers.weightIndices[viewport.id] = viewport.webgl.createBuffer();

        // Pass weight indicies to buffer
        viewport.webgl.bindBuffer(viewport.webgl.ARRAY_BUFFER, this.buffers.weightIndices[viewport.id]);
        viewport.webgl.bufferData(viewport.webgl.ARRAY_BUFFER, this.weightIndices, viewport.webgl.STATIC_DRAW);

        this.valid.weightIndices[viewport.id] = true;
      }
      viewport.webgl.bindBuffer(viewport.webgl.ARRAY_BUFFER, this.buffers.weightIndices[viewport.id]);

      // Tell the vertex shader how to read the array
      viewport.webgl.vertexAttribPointer(viewport.program.attribute.aVertexWeightIndices, 4, viewport.webgl.FLOAT, false, 0, 0);
      viewport.webgl.enableVertexAttribArray(viewport.program.attribute.aVertexWeightIndices);
    }

    // Joint transforms
    if (viewport.program.uniform.uDeformMatrix !== undefined) {
      viewport.webgl.uniformMatrix4fv(viewport.program.uniform.uDeformMatrix, false, model.skeleton.getFlatMatrix(entity.transform.localMatrix));
    }

    // Draw
    viewport.webgl.drawArrays(viewport.webgl.TRIANGLES, 0, this.vertices.length / 3);

    stack.pop();
  }
}, {
  key: 'invalidate',
  value: function invalidate() {
    var vertices = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;
    var uvs = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
    var normals = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : true;
    var weights = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : true;
    var weightIndices = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : true;

    //TODO: make each one optional (and default to what the current value is)
    this.valid = {
      vertices: vertices ? [] : this.valid.vertices,
      uvs: uvs ? [] : this.valid.uvs,
      normals: normals ? [] : this.valid.normals,
      weights: weights ? [] : this.valid.weights,
      weightIndices: weightIndices ? [] : this.valid.weightIndices
    };
  }

  //TODO: Should this be a geometry class?

}, {
  key: 'addPlane',
  value: function addPlane(width, height) {
    //TODO: Generalize this concept
    function Float32Concat(first, second) {
      var firstLength = first.length,
          result = new Float32Array(firstLength + second.length);

      result.set(first);
      result.set(second, firstLength);

      return result;
    }

    this.vertices = Float32Concat(this.vertices, [0.0, 0.0, 0.0, 1.0 * width, 0.0, 0.0, 1.0 * width, 1.0 * height, 0.0, 0.0, 1.0 * height, 0.0, 0.0, 0.0, 0.0, 1.0 * width, 1.0 * height, 0.0]);
    this.uvs = Float32Concat(this.uvs, [0.0, 1.0, //  left,    top, 
    1.0, 1.0, // right,    top, 
    1.0, 0.0, // right, bottom, 
    0.0, 0.0, //  left, bottom, 
    0.0, 1.0, //  left,    top, 
    1.0, 0.0 // right, bottom, 
    ]);
    this.normals = Float32Concat(this.normals, [0.0, 0.0, 1.0, 0.0, 0.0, 1.0, 0.0, 0.0, 1.0, 0.0, 0.0, 1.0, 0.0, 0.0, 1.0, 0.0, 0.0, 1.0]);

    this.invalidate();

    return this;
  }
}, {
  key: 'addCube',
  value: function addCube() {
    //TODO: Generalize this concept
    function Float32Concat(first, second) {
      var firstLength = first.length,
          result = new Float32Array(firstLength + second.length);

      result.set(first);
      result.set(second, firstLength);

      return result;
    }

    this.vertices = Float32Concat(this.vertices, [-0.5, -0.5, 0.5, 0.5, -0.5, 0.5, -0.5, 0.5, 0.5, -0.5, 0.5, 0.5, 0.5, -0.5, 0.5, 0.5, 0.5, 0.5, -0.5, 0.5, 0.5, 0.5, 0.5, 0.5, -0.5, 0.5, -0.5, -0.5, 0.5, -0.5, 0.5, 0.5, 0.5, 0.5, 0.5, -0.5, -0.5, 0.5, -0.5, 0.5, 0.5, -0.5, -0.5, -0.5, -0.5, -0.5, -0.5, -0.5, 0.5, 0.5, -0.5, 0.5, -0.5, -0.5, -0.5, -0.5, -0.5, 0.5, -0.5, -0.5, -0.5, -0.5, 0.5, -0.5, -0.5, 0.5, 0.5, -0.5, -0.5, 0.5, -0.5, 0.5, 0.5, -0.5, 0.5, 0.5, -0.5, -0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5, -0.5, -0.5, 0.5, 0.5, -0.5, -0.5, -0.5, -0.5, -0.5, -0.5, 0.5, -0.5, 0.5, -0.5, -0.5, 0.5, -0.5, -0.5, -0.5, 0.5, -0.5, 0.5, 0.5]);

    this.uvs = Float32Concat(this.vertices, [0, 0, 1, 0, 0, 1, 0, 1, 1, 0, 1, 1, 0, 0, 1, 0, 0, 1, 0, 1, 1, 0, 1, 1, 1, 1, 0, 1, 1, 0, 1, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 1, 0, 1, 1, 0, 1, 1, 0, 0, 1, 0, 0, 1, 0, 1, 1, 0, 1, 1, 0, 0, 1, 0, 0, 1, 0, 1, 1, 0, 1, 1]);

    this.normals = Float32Concat(this.vertices, [0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 0, -1, 0, 0, -1, 0, 0, -1, 0, 0, -1, 0, 0, -1, 0, 0, -1, 0, -1, 0, 0, -1, 0, 0, -1, 0, 0, -1, 0, 0, -1, 0, 0, -1, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, -1, 0, 0, -1, 0, 0, -1, 0, 0, -1, 0, 0, -1, 0, 0, -1, 0, 0]);

    this.invalidate();

    return this;
  }
}]);
return Mesh;
}(Component);

// import Skeleton from './skeleton';

var tMatrix$5 = create$3();

var Model = function (_Component) {
inherits(Model, _Component);

function Model() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref$materials = _ref.materials,
      materials = _ref$materials === undefined ? [] : _ref$materials,
      _ref$meshes = _ref.meshes,
      meshes = _ref$meshes === undefined ? [] : _ref$meshes,
      _ref$bones = _ref.bones,
      bones = _ref$bones === undefined ? [] : _ref$bones,
      _ref$skeleton = _ref.skeleton,
      skeleton = _ref$skeleton === undefined ? null : _ref$skeleton,
      _ref$skeletalAnimatio = _ref.skeletalAnimations,
      skeletalAnimations = _ref$skeletalAnimatio === undefined ? [] : _ref$skeletalAnimatio,
      _ref$animationClips = _ref.animationClips,
      animationClips = _ref$animationClips === undefined ? [] : _ref$animationClips;

  classCallCheck(this, Model);

  var _this = possibleConstructorReturn(this, (Model.__proto__ || Object.getPrototypeOf(Model)).apply(this, arguments));

  _this.meshes = meshes;

  _this.animationClips = animationClips;
  _this.bones = bones;
  _this.skeleton = skeleton;
  _this.skeletalAnimations = skeletalAnimations;
  return _this;
}

createClass(Model, [{
  key: 'update',
  value: function update(dt, scene, entity) {
    if (this.skeleton) this.skeleton.update(dt);

    for (var i = 0; i < this.animationClips.length; i++) {
      this.animationClips[i].update(dt);
    }
  }
}, {
  key: 'render',
  value: function render(dt, scene, viewport, camera, stack, viewMatrix, modelMatrix, materialOverride, frameBufferTextureStack) {
    stack.push(this);

    // Calculate view matrix
    mul$3(tMatrix$5, modelMatrix, this.transform.localMatrix);

    for (var i = 0; i < this.meshes.length; i++) {
      this.meshes[i].render(dt, scene, viewport, camera, stack, viewMatrix, tMatrix$5, materialOverride, frameBufferTextureStack);
    }

    stack.pop();
  }
}, {
  key: 'renderPick',
  value: function renderPick(viewport, camera, entity, entityId) {
    // TODO
    return;

    if (!this.vertices.length) return;

    // TODO, FIX
    for (var i = 0; i < this.materials.length; i++) {
      if (!this.materials[i].vertices.length) continue;

      var vertColors = [];
      for (var j = 0; j < this.vertices[i].length; j++) {
        vertColors.push((entityId + 1) / 256); //ID?
        vertColors.push((entityId + 1) / 256);
        vertColors.push((entityId + 1) / 256);
        vertColors.push(1);
      }

      this.materialPick.vertices = this.materials[i].vertices;
      this.materialPick.vertexColors = new Float32Array(vertColors);

      this.materialPick.render(viewport, camera, entity, this, entityId, offset);
    }
  }
}, {
  key: 'calcParentMatrix',
  value: function calcParentMatrix() {
    var cMatrix = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : tMatrix$5;
    //TODO: Confusing named function 
    return;
    if (!this.parent) {
      copy$3(cMatrix, this.transform.localMatrix);
    } else {
      this.parent.calcParentMatrix(cMatrix);
      multiply$4(cMatrix, cMatrix, this.transform.localMatrix);
    }
  }
}]);
return Model;
}(Component);

// REFERENCE: Model or Mesh? https://books.google.com/books?id=0bUJAgAAQBAJ&pg=PA38&lpg=PA38&dq=model+or+mesh&source=bl&ots=YHaPzACkgG&sig=ZGRp7qyCQbVlqZECbm0Hx-oZTYc&hl=en&sa=X&ved=0ahUKEwjNsYK59-nWAhUN8YMKHZ5UAoc4ChDoAQg8MAA#v=onepage&q&f=false

var ParticleEmitter = function (_Emitter) {
inherits(ParticleEmitter, _Emitter);

function ParticleEmitter() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref$count = _ref.count,
      count = _ref$count === undefined ? 1000 : _ref$count,
      _ref$life = _ref.life,
      life = _ref$life === undefined ? 1000 : _ref$life,
      _ref$rate = _ref.rate,
      rate = _ref$rate === undefined ? 1000 : _ref$rate,
      _ref$velocity = _ref.velocity,
      velocity = _ref$velocity === undefined ? [0, 0, 0] : _ref$velocity,
      _ref$offset = _ref.offset,
      offset = _ref$offset === undefined ? [0, 0, 0] : _ref$offset,
      _ref$spriteOptions = _ref.spriteOptions,
      spriteOptions = _ref$spriteOptions === undefined ? {} : _ref$spriteOptions;

  classCallCheck(this, ParticleEmitter);

  var _this = possibleConstructorReturn(this, (ParticleEmitter.__proto__ || Object.getPrototypeOf(ParticleEmitter)).call(this, arguments[0]));

  _this.sprites = [];
  _this.spriteOptions = spriteOptions;
  return _this;
}

createClass(ParticleEmitter, [{
  key: 'entityCreate',
  value: function entityCreate(index) {
    if (!this.sprites[index]) {
      this.sprites[index] = new Sprite(this.spriteOptions);
    }

    return this.sprites[index];
  }
}]);
return ParticleEmitter;
}(Emitter);

var Preloader = function () {
  function Preloader() {
      // TODO, keep track of pending textures and/or count for progress reports.

      classCallCheck(this, Preloader);
  }

  createClass(Preloader, [{
      key: 'preloadTextures',
      value: function preloadTextures() {
          var srcList = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
          var progressCallback = arguments[1];

          var pending = srcList.length;

          return new Promise(function (resolve, reject) {
              if (!pending) resolve();

              var _iteratorNormalCompletion = true;
              var _didIteratorError = false;
              var _iteratorError = undefined;

              try {
                  for (var _iterator = srcList[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
                      var src = _step.value;

                      new Texture({ src: src }).load().then(function () {
                          pending--;

                          if (progressCallback) {
                              var percent = (srcList.length - pending) / srcList.length;
                              progressCallback(percent, srcList.length - pending);
                          }

                          if (!pending) resolve();
                      }).catch(function () {
                          reject.apply(undefined, arguments);
                      });
                  }
              } catch (err) {
                  _didIteratorError = true;
                  _iteratorError = err;
              } finally {
                  try {
                      if (!_iteratorNormalCompletion && _iterator.return) {
                          _iterator.return();
                      }
                  } finally {
                      if (_didIteratorError) {
                          throw _iteratorError;
                      }
                  }
              }
          });
      }

      // preloadModels(srcList = [], progressCallback) {
      // 	let pending = srcList.length;

      //     return new Promise((resolve, reject) => {
      //         if(!pending) resolve();

      //         for(let src of srcList){
      //             ModelCache.load(src).then(() => {
      //                 pending--;

      //                 if(progressCallback){
      //                     let percent = (srcList.length - pending) / srcList.length;
      //                     progressCallback(percent, srcList.length - pending);
      //                 }

      //                 if(!pending) resolve();
      //             }).catch((...args) => {
      //                 reject(...args);
      //             });
      //         }
      //     });
      // }

      //      preloadAudio(srcList = []) {
      // 		let pending = 0;

      //         return new Promise(function(resolve, reject) {
      //             if(srcList.length <= 0) resolve();

      //             for(let modelSrc of srcList){
      //                 AudioCache.load(modelSrc).then(() => {
      //                     pending--;

      //                     if(!pending) resolve();
      //                 });
      //             }
      //         });
      //     }

  }]);
  return Preloader;
}();

var RenderPass = function () {
  function RenderPass() {
      var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
          _ref$renderPasses = _ref.renderPasses,
          renderPasses = _ref$renderPasses === undefined ? [] : _ref$renderPasses,
          _ref$frameBufferTextu = _ref.frameBufferTexture,
          frameBufferTexture = _ref$frameBufferTextu === undefined ? null : _ref$frameBufferTextu,
          _ref$viewportClear = _ref.viewportClear,
          viewportClear = _ref$viewportClear === undefined ? true : _ref$viewportClear;

      classCallCheck(this, RenderPass);

      this.renderPasses = renderPasses;
      this.frameBufferTexture = frameBufferTexture;
      this.viewportClear = viewportClear;
  }

  createClass(RenderPass, [{
      key: "render",
      value: function render(dt, scene, viewport, stack, matrix) {
          var frameBufferTextureStack = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : [];

          // TODO: Clear instead of viewport.clear()?
          if (this.viewportClear) viewport.clear();

          if (this.frameBufferTexture) {
              frameBufferTextureStack.push(this.frameBufferTexture);

              // Bind frameBufferTexture
              this.frameBufferTexture.bind(viewport, 0, viewport.width, viewport.height);

              viewport.render(dt, scene, stack, matrix, frameBufferTextureStack);

              // Unbind frameBufferTexture
              this.frameBufferTexture.unbind(viewport);
          } else {
              viewport.render(dt, scene, stack, matrix, undefined, frameBufferTextureStack);
          }

          var _iteratorNormalCompletion = true;
          var _didIteratorError = false;
          var _iteratorError = undefined;

          try {
              for (var _iterator = this.renderPasses[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
                  var renderPass = _step.value;

                  renderPass.render(dt, scene, viewport, stack, matrix, undefined, frameBufferTextureStack);
              }
          } catch (err) {
              _didIteratorError = true;
              _iteratorError = err;
          } finally {
              try {
                  if (!_iteratorNormalCompletion && _iterator.return) {
                      _iterator.return();
                  }
              } finally {
                  if (_didIteratorError) {
                      throw _iteratorError;
                  }
              }
          }

          if (this.frameBufferTexture) frameBufferTextureStack.pop();
      }
  }]);
  return RenderPass;
}();

var RenderPassPostProcess = function (_RenderPass) {
  inherits(RenderPassPostProcess, _RenderPass);

  function RenderPassPostProcess() {
      var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
          _ref$material = _ref.material,
          material = _ref$material === undefined ? null : _ref$material;

      classCallCheck(this, RenderPassPostProcess);

      var _this = possibleConstructorReturn(this, (RenderPassPostProcess.__proto__ || Object.getPrototypeOf(RenderPassPostProcess)).apply(this, arguments));

      _this.material = material;
      return _this;
  }

  createClass(RenderPassPostProcess, [{
      key: "render",
      value: function render(dt, scene, viewport, stack, matrix) {
          var frameBufferTextureStack = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : [];


          if (this.frameBufferTexture) {
              frameBufferTextureStack.push(this.frameBufferTexture);

              // Bind frameBufferTexture
              this.frameBufferTexture.bind(viewport, 0, "TEXTURE_2D", viewport.width, viewport.height);

              this.material.render(dt, scene, viewport, stack, matrix, frameBufferTextureStack);

              // Unbind frameBufferTexture
              this.frameBufferTexture.unbind(viewport);
          } else {
              this.material.render(dt, scene, viewport, stack, matrix, frameBufferTextureStack);
          }

          var _iteratorNormalCompletion = true;
          var _didIteratorError = false;
          var _iteratorError = undefined;

          try {
              for (var _iterator = this.renderPasses[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
                  var renderPass = _step.value;

                  renderPass.render(dt, scene, viewport, stack, matrix, frameBufferTextureStack);
              }
          } catch (err) {
              _didIteratorError = true;
              _iteratorError = err;
          } finally {
              try {
                  if (!_iteratorNormalCompletion && _iterator.return) {
                      _iterator.return();
                  }
              } finally {
                  if (_didIteratorError) {
                      throw _iteratorError;
                  }
              }
          }

          if (this.frameBufferTexture) frameBufferTextureStack.pop();
      }
  }]);
  return RenderPassPostProcess;
}(RenderPass);

var Router = function () {
  function Router() {
      var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : { map: [], clock: null };
      classCallCheck(this, Router);

      this.map = options.map;

      this.currentController = [];

      this.clock = options.clock;
      this.routeViews = [];
      // this.viewports = []; // this should be at the stage level
      this.scenes = [];

      this.queryDOM();
  }

  createClass(Router, [{
      key: "navigate",
      value: function navigate(routeName, params) {
          var _ref = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {},
              _ref$view = _ref.view,
              view = _ref$view === undefined ? "main" : _ref$view;

          // Unload previous route
          this.routeUnload(this.currentController[view], view);

          // Load new route
          var route = this.map[routeName];
          if (!route) return false;

          // Load new route
          this.queryDOM();

          // console.log("route view loop " + this.routeViews.length);
          var _iteratorNormalCompletion = true;
          var _didIteratorError = false;
          var _iteratorError = undefined;

          try {
              for (var _iterator = this.routeViews[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
                  var v = _step.value;

                  if (!view || view == v.id || view.id == v) {
                      route.module.populateDOM(v);
                  }
              }
          } catch (err) {
              _didIteratorError = true;
              _iteratorError = err;
          } finally {
              try {
                  if (!_iteratorNormalCompletion && _iterator.return) {
                      _iterator.return();
                  }
              } finally {
                  if (_didIteratorError) {
                      throw _iteratorError;
                  }
              }
          }

          route.module.onload(params);
          route.module.start();

          this.currentController[view] = route.module; // TODO: Inject params?
          this.currentRoute = route;

          if (this.clock && !route.module.clock) {
              this.clock.add(route.module);
              this.clock.resetAccumulator();
          }

          // console.log(`[Onyx] Navigation to "${route}" complete.`);

          return true;
      }
  }, {
      key: "modal",
      value: function modal(routeName) {
          var onResolve = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : function () {};
          var onReject = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : function () {};
          var params = arguments[3];

          var _ref2 = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : {},
              _ref2$view = _ref2.view,
              view = _ref2$view === undefined ? "main" : _ref2$view;

          // Load new route
          var route = this.map[routeName];
          if (!route) return false;

          // Load new route
          this.queryDOM();

          var _iteratorNormalCompletion2 = true;
          var _didIteratorError2 = false;
          var _iteratorError2 = undefined;

          try {
              for (var _iterator2 = this.routeViews[Symbol.iterator](), _step2; !(_iteratorNormalCompletion2 = (_step2 = _iterator2.next()).done); _iteratorNormalCompletion2 = true) {
                  var v = _step2.value;

                  if (!view || view == v.id || view.id == v) {
                      route.module.populateDOM(v);
                  }
              }
          } catch (err) {
              _didIteratorError2 = true;
              _iteratorError2 = err;
          } finally {
              try {
                  if (!_iteratorNormalCompletion2 && _iterator2.return) {
                      _iterator2.return();
                  }
              } finally {
                  if (_didIteratorError2) {
                      throw _iteratorError2;
                  }
              }
          }

          route.module.onload(params);
          route.module.start();
          route.module._modal = {
              callee: this,
              module: route.module,
              view: view,
              onResolve: onResolve,
              onReject: onReject
          };

          this.currentController[view] = route.module; // TODO: Inject params?
          this.currentRoute = route;

          if (this.clock) {
              this.clock.add(route.module);
              this.clock.resetAccumulator();
          }

          return route.module._modal;
      }
  }, {
      key: "modalResult",
      value: function modalResult(result, modal) {
          var unload = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : true;

          if (!modal) return;

          if (unload) this.routeUnload(modal.module, modal.view);

          if (result) {
              modal.onResolve();
          } else {
              modal.onReject();
          }
      }
  }, {
      key: "modalUnload",
      value: function modalUnload(modal) {
          if (!modal) return;

          this.routeUnload(modal.module, modal.view);
      }

      // query for <onyx-route-view> tags

  }, {
      key: "queryDOM",
      value: function queryDOM() {
          this.routeViews = [];

          // Note: CocoonJS doesn't fully support querySelectorAll, so we're doing this manually, rather than querySelectorAll
          // Any <onyx-route-view> tags
          var nodes = document.getElementsByTagName("onyx-route-view");
          for (var i = 0; i < nodes.length; i++) {
              this.routeViews.push(nodes[i]);
          }

          // Workaround for CocoonJS
          if (!this.routeViews.length && document.getElementById("onyx-route-view")) {
              this.routeViews.push(document.getElementById("onyx-route-view"));
          }
      }
  }, {
      key: "routeUnload",
      value: function routeUnload() {
          var controller = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
          var view = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;

          if (!controller) return;

          controller.stop();

          if (this.clock) {
              this.clock.remove(controller);
          }

          var _iteratorNormalCompletion3 = true;
          var _didIteratorError3 = false;
          var _iteratorError3 = undefined;

          try {
              for (var _iterator3 = this.routeViews[Symbol.iterator](), _step3; !(_iteratorNormalCompletion3 = (_step3 = _iterator3.next()).done); _iteratorNormalCompletion3 = true) {
                  var v = _step3.value;

                  // view.innerHTML = response;
                  if (!view || view == v.id || view.id == v) {
                      controller.onunload(v);
                      controller.clearDom(v);
                  }
              }
          } catch (err) {
              _didIteratorError3 = true;
              _iteratorError3 = err;
          } finally {
              try {
                  if (!_iteratorNormalCompletion3 && _iterator3.return) {
                      _iterator3.return();
                  }
              } finally {
                  if (_didIteratorError3) {
                      throw _iteratorError3;
                  }
              }
          }
      }
  }]);
  return Router;
}();

var Scene = function (_Entity) {
  inherits(Scene, _Entity);

  function Scene() {
      var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
          _ref$template = _ref.template,
          template = _ref$template === undefined ? null : _ref$template,
          _ref$templateURL = _ref.templateURL,
          templateURL = _ref$templateURL === undefined ? null : _ref$templateURL,
          _ref$sceneURL = _ref.sceneURL,
          sceneURL = _ref$sceneURL === undefined ? null : _ref$sceneURL,
          _ref$element = _ref.element,
          element = _ref$element === undefined ? "" : _ref$element,
          _ref$clock = _ref.clock,
          clock = _ref$clock === undefined ? null : _ref$clock;

      classCallCheck(this, Scene);

      var _this = possibleConstructorReturn(this, (Scene.__proto__ || Object.getPrototypeOf(Scene)).call(this));

      _this.viewports = [];
      _this.viewportElements = [];

      _this.entities = [];
      _this.removeEntities = []; // For deferred removal

      _this.cameras = [];
      _this.lights = [];

      _this.renderPasses = [new RenderPassShadow({
          renderPasses: [new RenderPass()]
      })];

      // this.renderPasses = [new RenderPass()];

      _this.templateURL = templateURL; // TODO: Should this whole "template" thing be in a "Route" class?
      _this.sceneURL = sceneURL;

      _this.template = template;

      _this.clock = clock;

      _this.ambientLightColor = new Color(0.05, 0.05, 0.05, 1.0);

      _this.directionalLight = new DirectionalLight({
          id: "DirectionalLight_Default",
          diffuseColor: new Color(0.5, 0.5, 0.5, 1),
          intensity: 2,
          vector: [-0.85, -0.8, -0.75]
      });

      _this.fogColor = new Color(1.0, 1.0, 1.0, 1.0);
      _this.fogDensity = 0.0;

      if (element) {
          _this.element = document.querySelector(element);
          if (!_this.element) {
              console.warn("[Onyx] Element '" + element + "' not found");
          }
      }

      if (clock) {
          clock.add(_this);
      }

      _this.queryDOM(_this.element);

      _this.loadSceneURL();
      _this.loadTemplateURL();
      return _this;
  }

  createClass(Scene, [{
      key: "registerHandler",
      value: function registerHandler(event) {
          var _this2 = this;

          // Get element
          var element = event.elementId ? document.getElementById(event.elementId) : document.body;
          if (!element) {
              console.warn("[Onyx] Element \"" + event.elementId + "\" not found");
              return;
          }

          // Assign event to element
          //TODO Error checking / Remove need for setTimeout
          window.setTimeout(function () {
              event.callback = event.target[event.property].bind(_this2);
              if (element.addEventListener) {
                  element.addEventListener(event.event, event.callback);
              } else {
                  element[event.event] = event.callback;
              }
          }, 0);
      }
  }, {
      key: "unregisterHandler",
      value: function unregisterHandler(event) {
          // Get element
          var element = event.elementId ? document.getElementById(event.elementId) : document.body;
          if (!element) {
              console.warn("[Onyx] Element \"" + event.elementId + "\" not found");
              return;
          }

          // Unassign event to element
          if (element.addEventListener) {
              element.removeEventListener(event.event, event.callback);
          } else {
              element[event.event] = null;
          }
      }
  }, {
      key: "modalResult",
      value: function modalResult(result, unload) {
          if (this._modal) {
              this._modal.callee.modalResult(result, this._modal, unload);
          }
      }
  }, {
      key: "modalUnload",
      value: function modalUnload() {
          if (this._modal) {
              this._modal.callee.modalUnload(this._modal);
          }
      }
  }, {
      key: "loadSceneURL",
      value: function loadSceneURL() {
          var _this3 = this;

          var url = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : this.sceneURL;

          if (!url) return false;

          this.fetchTemplate(url).then(function (response) {
              _this3.scene = response;
          });
      }
  }, {
      key: "loadTemplateURL",
      value: function loadTemplateURL() {
          var _this4 = this;

          var url = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : this.templateURL;

          if (!url) return false;

          this.fetchTemplate(url).then(function (response) {
              _this4.template = response;
          });
      }

      // TODO: rename to onLoad

  }, {
      key: "onload",
      value: function onload() {
          var _babelHelpers$get;

          for (var _len = arguments.length, args = Array(_len), _key = 0; _key < _len; _key++) {
              args[_key] = arguments[_key];
          }

          (_babelHelpers$get = get(Scene.prototype.__proto__ || Object.getPrototypeOf(Scene.prototype), "onload", this)).call.apply(_babelHelpers$get, [this].concat(args));

          if (this.element) this.queryDOM(this.element);
      }
  }, {
      key: "onunload",
      value: function onunload() {
          var _babelHelpers$get2;

          for (var _len2 = arguments.length, args = Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
              args[_key2] = arguments[_key2];
          }

          (_babelHelpers$get2 = get(Scene.prototype.__proto__ || Object.getPrototypeOf(Scene.prototype), "onunload", this)).call.apply(_babelHelpers$get2, [this].concat(args));

          // TODO Call onDestroy for everything

          this.entities = [];
          this.viewports = [];
          this.viewportElements = [];
          this.components = [];

          if (this.element) {
              this.element.innerHTML = "";
              this.element.style.display = "none";
          }
      }
  }, {
      key: "populateDOM",
      value: function populateDOM(elem) {
          if (this.template !== null && this.template !== undefined) elem.innerHTML = this.template;

          if (this.element) this.element.style.display = "block";

          this.queryDOM(this.element);
      }
  }, {
      key: "clearDom",
      value: function clearDom(elem) {
          if (elem) elem.innerHTML = "";
      }
  }, {
      key: "queryDOM",
      value: function queryDOM() {
          var elem = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : document.body;

          // if(elem)

          // Grab all viewports
          this.viewportElements = [];

          // Note: CocoonJS doesn't fully support querySelectorAll, so we're doing this manually, rather than querySelectorAll
          // Any <onyx-viewport> tags
          var nodes = elem.getElementsByTagName("onyx-viewport");
          for (var i = 0; i < nodes.length; i++) {
              // TODO(?): use this element to create a canvas inside of it
              this.viewportElements.push(nodes[i]);
          }

          // Any <ANY onyx-viewport> tags

          if (typeof document.querySelectorAll !== "undefined" && !navigator.isCocoonJS) {
              nodes = elem.querySelectorAll("[onyx-viewport]");

              for (var i = 0; i < nodes.length; i++) {
                  this.viewportElements.push(nodes[i]);
              }
          } else {
              // Fallback for older browsers / CocoonJS
              nodes = elem.getElementsByTagName("*");

              for (var i = 0; i < nodes.length; i++) {
                  var node = nodes[i];
                  if (typeof node["onyx-viewport"] !== "undefined" || node.hasAttribute && node.hasAttribute("onyx-viewport")) {
                      this.viewportElements.push(node);
                  }
              }
          }

          // Assign a new viewport for each one
          var _iteratorNormalCompletion = true;
          var _didIteratorError = false;
          var _iteratorError = undefined;

          try {
              for (var _iterator = this.viewportElements[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
                  var _elem = _step.value;

                  this.viewports.push(new Viewport(_elem));
              }
          } catch (err) {
              _didIteratorError = true;
              _iteratorError = err;
          } finally {
              try {
                  if (!_iteratorNormalCompletion && _iterator.return) {
                      _iterator.return();
                  }
              } finally {
                  if (_didIteratorError) {
                      throw _iteratorError;
                  }
              }
          }
      }
  }, {
      key: "render",
      value: function render(dt) {
          if (!this.visible) return;

          // TODO: Update DOM elements

          var _iteratorNormalCompletion2 = true;
          var _didIteratorError2 = false;
          var _iteratorError2 = undefined;

          try {
              for (var _iterator2 = this.renderPasses[Symbol.iterator](), _step2; !(_iteratorNormalCompletion2 = (_step2 = _iterator2.next()).done); _iteratorNormalCompletion2 = true) {
                  var renderPass = _step2.value;
                  var _iteratorNormalCompletion3 = true;
                  var _didIteratorError3 = false;
                  var _iteratorError3 = undefined;

                  try {
                      for (var _iterator3 = this.viewports[Symbol.iterator](), _step3; !(_iteratorNormalCompletion3 = (_step3 = _iterator3.next()).done); _iteratorNormalCompletion3 = true) {
                          var viewport = _step3.value;

                          renderPass.render(dt, this, viewport, [this], this.transform.localMatrix);
                      }
                  } catch (err) {
                      _didIteratorError3 = true;
                      _iteratorError3 = err;
                  } finally {
                      try {
                          if (!_iteratorNormalCompletion3 && _iterator3.return) {
                              _iterator3.return();
                          }
                      } finally {
                          if (_didIteratorError3) {
                              throw _iteratorError3;
                          }
                      }
                  }
              }
          } catch (err) {
              _didIteratorError2 = true;
              _iteratorError2 = err;
          } finally {
              try {
                  if (!_iteratorNormalCompletion2 && _iterator2.return) {
                      _iterator2.return();
                  }
              } finally {
                  if (_didIteratorError2) {
                      throw _iteratorError2;
                  }
              }
          }
      }

      /**
       * Called every logic cycle
       */

  }, {
      key: "update",
      value: function update(deltaTime) {
          get(Scene.prototype.__proto__ || Object.getPrototypeOf(Scene.prototype), "update", this).call(this, deltaTime, this);

          if (!this.enabled) return;

          var _iteratorNormalCompletion4 = true;
          var _didIteratorError4 = false;
          var _iteratorError4 = undefined;

          try {
              for (var _iterator4 = this.entities[Symbol.iterator](), _step4; !(_iteratorNormalCompletion4 = (_step4 = _iterator4.next()).done); _iteratorNormalCompletion4 = true) {
                  var entity = _step4.value;

                  entity.update(deltaTime, this);
              }
          } catch (err) {
              _didIteratorError4 = true;
              _iteratorError4 = err;
          } finally {
              try {
                  if (!_iteratorNormalCompletion4 && _iterator4.return) {
                      _iterator4.return();
                  }
              } finally {
                  if (_didIteratorError4) {
                      throw _iteratorError4;
                  }
              }
          }

          var _iteratorNormalCompletion5 = true;
          var _didIteratorError5 = false;
          var _iteratorError5 = undefined;

          try {
              for (var _iterator5 = this.removeEntities[Symbol.iterator](), _step5; !(_iteratorNormalCompletion5 = (_step5 = _iterator5.next()).done); _iteratorNormalCompletion5 = true) {
                  var _entity = _step5.value;

                  this.remove(_entity, false);
              }
          } catch (err) {
              _didIteratorError5 = true;
              _iteratorError5 = err;
          } finally {
              try {
                  if (!_iteratorNormalCompletion5 && _iterator5.return) {
                      _iterator5.return();
                  }
              } finally {
                  if (_didIteratorError5) {
                      throw _iteratorError5;
                  }
              }
          }

          this.removeEntities = [];
      }
  }, {
      key: "add",
      value: function add(entity) {
          this.entities.push(entity);
          if (entity.onSpawn) entity.onSpawn(this);

          if (entity instanceof Camera) this.cameras.push(entity);
          if (entity instanceof Light || entity instanceof PointLight || entity instanceof SpotLight) {
              this.lights.push(entity);
              if (this.lights.length > CONST.MAX_LIGHTS) console.warn("[Onyx] Warning: Light count greater than MAX_LIGHTS.");
          }
      }
  }, {
      key: "addRenderPass",
      value: function addRenderPass(pass) {
          this.renderPasses.push(pass);
      }
  }, {
      key: "remove",
      value: function remove(entity, defer) {
          //TODO: make defer an option object?
          if (defer) {
              this.removeEntities.push(entity);
          } else {
              if (this.entities.indexOf(entity) > -1) {
                  this.entities.splice(this.entities.indexOf(entity), 1);
                  if (entity.onDestroy) entity.onDestroy(this);
              }
          }
      }
  }, {
      key: "start",
      value: function start() {
          if (!this.clock) return;

          this.clock.start();
          // this.onload();
      }
  }, {
      key: "stop",
      value: function stop() {
          if (!this.clock) return;

          this.clock.stop();
          // this.onunload();
      }
  }, {
      key: "setTimeout",
      value: function setTimeout(callback, delay) {
          var _this5 = this;

          var timer = new Timer();
          this.add(timer);

          timer.setTimeout(function (entity, dt, progress) {
              _this5.remove(timer);
              if (callback) callback();
          }, delay);

          return timer;
      }
  }, {
      key: "setInterval",
      value: function setInterval(callback) {
          var delay = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
          var intervalCount = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0;

          var timer = new Timer();
          this.add(timer);

          timer.setInterval(function (entity, dt, progress) {
              if (callback) callback();
          }, delay, intervalCount);

          return timer;
      }
  }, {
      key: "setModulate",
      value: function setModulate(callback, delay) {
          var timer = new Timer();
          this.add(timer);

          timer.setModulate(function (entity, dt, progress) {
              if (callback) callback(entity, dt, progress);
          }, delay);

          return timer;
      }
  }, {
      key: "sortEntitiesByZ",
      value: function sortEntitiesByZ(inverse) {
          if (inverse) {
              this.entities.sort(function (a, b) {
                  return a.transform && b.transform && a.transform.z - b.transform.z;
              });
          } else {
              this.entities.sort(function (a, b) {
                  return !a.transform || !b.transform || b.transform.z - a.transform.z;
              });
          }
      }

      // Fetch Template

  }, {
      key: "fetchTemplate",
      value: function fetchTemplate(url) {
          return fetch(url, {
              "Content-Type": "text/html"
          }).then(function (response) {
              // Read response
              if (!response.ok) {
                  throw Error(response.statusText, response.url);
              }
              return response.text();
          }).then(
          // Return contents
          function (contents) {
              return contents;
          }).catch(function (e) {
              console.error("Error fetching template '" + url + "'.", e);
              // this.loadView('__loadingFailure__');
          });
      }
  }]);
  return Scene;
}(Entity);

var Skeleton$1 = function (_Component) {
  inherits(Skeleton, _Component);

  function Skeleton() {
      var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
          _ref$transform = _ref.transform,
          transform = _ref$transform === undefined ? new Transform$1() : _ref$transform,
          _ref$children = _ref.children,
          children = _ref$children === undefined ? [] : _ref$children;

      classCallCheck(this, Skeleton);

      var _this = possibleConstructorReturn(this, (Skeleton.__proto__ || Object.getPrototypeOf(Skeleton)).call(this));

      _this.transform = transform;

      _this.children = children;

      _this.matrixBuffer = new Float32Array(CONST.MAX_BONES * 16);
      _this.scaledMatrix = create$3();

      // this.calcBindPose();
      return _this;
  }

  createClass(Skeleton, [{
      key: 'calcBindPose',
      value: function calcBindPose() {
          for (var i = 0; i < this.children.length; i++) {
              // this.children[i].calcOrigin(true);
              this.children[i].calcBindPose(true);
          }
      }
  }, {
      key: 'flattenChildren',
      value: function flattenChildren() {
          var children = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : this.children;
          var flat = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
          var _iteratorNormalCompletion = true;
          var _didIteratorError = false;
          var _iteratorError = undefined;

          try {
              for (var _iterator = children[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
                  var child = _step.value;

                  flat.push(child);

                  this.flattenChildren(child.children, flat);
              }
          } catch (err) {
              _didIteratorError = true;
              _iteratorError = err;
          } finally {
              try {
                  if (!_iteratorNormalCompletion && _iterator.return) {
                      _iterator.return();
                  }
              } finally {
                  if (_didIteratorError) {
                      throw _iteratorError;
                  }
              }
          }

          return flat;
      }
  }, {
      key: 'getFlatMatrix',
      value: function getFlatMatrix(worldMatrix) {
          var offset = 0;

          // mat4.scale(this.scaledMatrix, );

          for (var i = 0; i < this.children.length; i++) {
              offset = this.children[i].getFlatMatrix(this.matrixBuffer, offset, false);
          }

          return this.matrixBuffer;
      }
  }]);
  return Skeleton;
}(Component);

var tMatrix$6 = create$3();

var FACE = [0.0, 0.0, 0.0, 1.0, 0.0, 0.0, 1.0, 1.0, 0.0, 0.0, 1.0, 0.0, 0.0, 0.0, 0.0, 1.0, 1.0, 0.0];

var FACE_UVS = [0.0, 1.0, //  left,    top, 
1.0, 1.0, // right,    top, 
1.0, 0.0, // right, bottom, 
0.0, 0.0, //  left, bottom, 
0.0, 1.0, //  left,    top, 
1.0, 0.0 // right, bottom, 
];

var FACE_NORMALS = [0.0, 0.0, 1.0, 0.0, 0.0, 1.0, 0.0, 0.0, 1.0, 0.0, 0.0, 1.0, 0.0, 0.0, 1.0, 0.0, 0.0, 1.0];

var Sprite = function (_Component) {
inherits(Sprite, _Component);
createClass(Sprite, [{
  key: 'src',
  get: function get$$1() {
    return this.texture.img.src;
  },
  set: function set$$1(val) {
    var _this2 = this;

    if (!val) {
      this.texture = null;
      this.mesh.material.diffuseTexture = this.texture;
      return;
    }

    this.texture = new Texture({ src: val, flipY: false, magFilter: this._magFilter, minFilter: this._minFilter });
    this.mesh.material.diffuseTexture = this.texture;
    this.mesh.material.buildProgram();

    this.texture.load().then(function () {
      _this2.selectFrame(0, 0, _this2.texture.img.width, _this2.texture.img.height, _this2.pivot[0], _this2.pivot[1], 0, true);
    });

    return this.texture.img.src;
  }
}, {
  key: 'minFilter',
  get: function get$$1() {
    return this._minFilter;
  },
  set: function set$$1(val) {
    this._minFilter = val;
    this.texture.minFilter = val;
  }
}, {
  key: 'magFilter',
  get: function get$$1() {
    return this._magFilter;
  },
  set: function set$$1(val) {
    this._magFilter = val;
    this.texture.magFilter = val;
  }

  // get pixelsPerUnit(){ return this._pixelsPerUnit; }
  // set pixelsPerUnit(val){
  // 	if(!this.texture || !this.texture.img){
  // 		this.mesh.vertices.set(new Float32Array(FACE));
  // 		return;
  // 	}

  // 	let change = val / this._pixelsPerUnit;

  // 	for(var i = 0; i < this.mesh.vertices.length; i++){
  // 		this.mesh.vertices[i] *= change;
  // 	} 

  // 	this._pixelsPerUnit = val;

  // 	this.mesh.invalidate(true);

  // 	return this._pixelsPerUnit; 
  // }

}]);

function Sprite() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref$src = _ref.src,
      src = _ref$src === undefined ? "" : _ref$src,
      _ref$transform = _ref.transform,
      transform = _ref$transform === undefined ? new Transform$1() : _ref$transform,
      _ref$magFilter = _ref.magFilter,
      magFilter = _ref$magFilter === undefined ? "LINEAR" : _ref$magFilter,
      _ref$minFilter = _ref.minFilter,
      minFilter = _ref$minFilter === undefined ? "LINEAR_MIPMAP_LINEAR" : _ref$minFilter,
      _ref$wrap = _ref.wrap,
      wrap = _ref$wrap === undefined ? { s: "CLAMP_TO_EDGE", t: "CLAMP_TO_EDGE" } : _ref$wrap,
      _ref$flipX = _ref.flipX,
      flipX = _ref$flipX === undefined ? false : _ref$flipX,
      _ref$flipY = _ref.flipY,
      flipY = _ref$flipY === undefined ? false : _ref$flipY,
      _ref$pivot = _ref.pivot,
      pivot = _ref$pivot === undefined ? [0, 0] : _ref$pivot,
      _ref$lighting = _ref.lighting,
      lighting = _ref$lighting === undefined ? 0 : _ref$lighting,
      _ref$shadowCastingMod = _ref.shadowCastingMode,
      shadowCastingMode = _ref$shadowCastingMod === undefined ? 1 : _ref$shadowCastingMod,
      _ref$pixelsPerUnit = _ref.pixelsPerUnit,
      pixelsPerUnit = _ref$pixelsPerUnit === undefined ? 1 : _ref$pixelsPerUnit;

  classCallCheck(this, Sprite);

  var _this = possibleConstructorReturn(this, (Sprite.__proto__ || Object.getPrototypeOf(Sprite)).apply(this, arguments));

  _this.transform = transform;

  _this._pixelsPerUnit = pixelsPerUnit;

  _this._minFilter = minFilter;
  _this._magFilter = magFilter;
  _this._wrap = wrap;

  _this.flipX = flipX;
  _this.flipY = flipY;
  _this.pivot = pivot;

  _this.width = 0;
  _this.height = 0;

  _this.animations = {};

  _this.mesh = new Mesh({
    id: "MESH_SPRITE_" + _this.id,
    material: new MaterialPhong({
      name: "SPRITE_" + src,
      lighting: lighting,
      shadowCastingMode: shadowCastingMode
      // diffuseTexture: this.texture
    }),
    vertices: new Float32Array(FACE),
    uvs: new Float32Array(FACE_UVS),
    normals: new Float32Array(FACE_NORMALS)
  });

  _this.src = src;
  // this.pixelsPerUnit = pixelsPerUnit;
  return _this;
}

createClass(Sprite, [{
  key: 'loadTextures',
  value: function loadTextures() {
    return this.texture.load();
  }
}, {
  key: 'render',
  value: function render(dt, scene, viewport, camera, stack, viewMatrix, modelMatrix, materialOverride, frameBufferTextureStack) {
    stack.push(this);

    // Calculate view matrix
    mul$3(tMatrix$6, modelMatrix, this.transform.localMatrix);

    this.mesh.render(dt, scene, viewport, camera, stack, viewMatrix, tMatrix$6, materialOverride, frameBufferTextureStack);

    stack.pop();
  }
}, {
  key: 'update',
  value: function update(dt, scene, entity) {
    get(Sprite.prototype.__proto__ || Object.getPrototypeOf(Sprite.prototype), 'update', this).call(this, dt, scene, entity);

    if (this.currentAnimation) this.currentAnimation.update(dt, scene, entity, this);
  }
}, {
  key: 'addAnimation',
  value: function addAnimation(animationId, animation) {
    this.animations[animationId] = animation;

    //TODO: This part feels a little hacky
    animation.sprite = this;
  }
}, {
  key: 'playAnimation',
  value: function playAnimation(animationId, callback) {
    if (!this.animations[animationId]) return console.warn('Animation \'' + animationId + '\' not found');

    this.currentAnimation = this.animations[animationId];
    this.currentAnimation.play(callback);
  }
}, {
  key: 'selectAnimation',
  value: function selectAnimation(animationId) {
    if (!this.animations[animationId]) return console.warn('Animation \'' + animationId + '\' not found');

    this.currentAnimation = this.animations[animationId];
    this.currentAnimation.setFrame(0);
  }
}, {
  key: 'selectFrame',
  value: function selectFrame() {
    var x = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 1;
    var y = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 1;
    var width = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0;
    var height = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 0;
    var pivotX = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : this.pivot[0];
    var pivotY = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : this.pivot[1];
    var forceVertexUpdate = arguments.length > 7 && arguments[7] !== undefined ? arguments[7] : false;

    var material = this.mesh.material; //s[materialIndex];
    var texture = material.diffuseTexture;
    if (texture === null) return;

    var left = x / texture.img.width;
    var right = (x + width) / texture.img.width;
    var bottom = y / texture.img.height;
    var top = (y + height) / texture.img.height;

    if (this.flipX) {
      
      var _ref2 = [right, left];
      left = _ref2[0];
      right = _ref2[1];
    }if (this.flipY) {
      

      var _ref3 = [bottom, top];
      top = _ref3[0];
      bottom = _ref3[1];
    }this.mesh.uvs.set([left, top, right, top, right, bottom, left, bottom, left, top, right, bottom]);

    // Set vertices
    // if(pivotX !== this.pivot[0] || pivotX !== this.pivot[1]){
    // 	let offsetX = width * pivotX;
    // 	let offsetY = height * pivotY;

    // 	this.mesh.vertices[0] = -offsetX;
    // 	this.mesh.vertices[3] =  offsetX;
    // 	this.mesh.vertices[6] =  offsetX;
    // 	this.mesh.vertices[9] = -offsetX;
    // 	this.mesh.vertices[12] = -offsetX;
    // 	this.mesh.vertices[15] =  offsetX;

    // 	this.mesh.vertices[1] = -offsetY;
    // 	this.mesh.vertices[4] = -offsetY;
    // 	this.mesh.vertices[7] =  offsetY;
    // 	this.mesh.vertices[10] =  offsetY;
    // 	this.mesh.vertices[13] = -offsetY;
    // 	this.mesh.vertices[16] =  offsetY;

    // 	this.mesh.invalidate(true, true);

    width = width * this._pixelsPerUnit;
    height = height * this._pixelsPerUnit;

    if (width != this.width || height != this.height || forceVertexUpdate) {
      this.mesh.vertices[0] = FACE[0] * width + -pivotX * width;
      this.mesh.vertices[3] = FACE[3] * width + -pivotX * width;
      this.mesh.vertices[6] = FACE[6] * width + -pivotX * width;
      this.mesh.vertices[9] = FACE[9] * width + -pivotX * width;
      this.mesh.vertices[12] = FACE[12] * width + -pivotX * width;
      this.mesh.vertices[15] = FACE[15] * width + -pivotX * width;

      this.mesh.vertices[1] = FACE[1] * height + -pivotY * height;
      this.mesh.vertices[4] = FACE[4] * height + -pivotY * height;
      this.mesh.vertices[7] = FACE[7] * height + -pivotY * height;
      this.mesh.vertices[10] = FACE[10] * height + -pivotY * height;
      this.mesh.vertices[13] = FACE[13] * height + -pivotY * height;
      this.mesh.vertices[16] = FACE[16] * height + -pivotY * height;

      this.mesh.invalidate(true, true, false, false, false);
    } else {
      this.mesh.invalidate(false, true, false, false, false);
    }

    this.width = width;
    this.height = height;
  }
}]);
return Sprite;
}(Component);

var SpriteAnimation = function () {
function SpriteAnimation() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref$loop = _ref.loop,
      loop = _ref$loop === undefined ? true : _ref$loop;

  classCallCheck(this, SpriteAnimation);

  this.frames = [];

  this.state = 0;
  this.loop = loop;

  this.currentTime = 0;
  this.currentFrame = null;
  this.currentFrameIndex = 0;

  this.callback = null;
}

createClass(SpriteAnimation, [{
  key: "update",
  value: function update(dt, scene, entity, sprite) {
    if (!this.state) return;
    var speed = this.currentFrame.speed || 0;

    if (this.currentTime + dt > speed) {
      // Advance frame index
      this.currentFrameIndex++;
      if (!this.frames[this.currentFrameIndex]) {
        if (this.callback) this.callback();

        if (this.loop) {
          this.currentFrameIndex = 0;
        } else {
          this.state = 0;
          return;
        }
      }

      // Assign frame
      this.setFrame();

      // Clear
      this.currentTime = 0;
    }

    this.currentTime += dt;
  }
}, {
  key: "play",
  value: function play() {
    var callback = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
    var loop = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;

    this.state = 1;
    this.loop = loop;
    this.callback = callback;
    this.reset();
    this.setFrame();
  }
}, {
  key: "reset",
  value: function reset() {
    this.currentTime = 0;
    this.currentFrame = this.frames[0];
    this.currentFrameIndex = 0;
  }
}, {
  key: "addFrame",
  value: function addFrame() {
    var _ref2 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
        _ref2$x = _ref2.x,
        x = _ref2$x === undefined ? 0 : _ref2$x,
        _ref2$y = _ref2.y,
        y = _ref2$y === undefined ? 0 : _ref2$y,
        _ref2$width = _ref2.width,
        width = _ref2$width === undefined ? 0 : _ref2$width,
        _ref2$height = _ref2.height,
        height = _ref2$height === undefined ? 0 : _ref2$height,
        _ref2$pivotX = _ref2.pivotX,
        pivotX = _ref2$pivotX === undefined ? 0 : _ref2$pivotX,
        _ref2$pivotY = _ref2.pivotY,
        pivotY = _ref2$pivotY === undefined ? 0 : _ref2$pivotY,
        _ref2$speed = _ref2.speed,
        speed = _ref2$speed === undefined ? 0 : _ref2$speed;

    this.frames.push({ x: x, y: y, width: width, height: height, pivotX: pivotX, pivotY: pivotY, speed: speed });
  }
}, {
  key: "setFrame",
  value: function setFrame() {
    var frameIndex = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : this.currentFrameIndex;

    this.currentFrame = this.frames[frameIndex];

    // TODO: "this.sprite" feels a little hacky
    this.sprite.selectFrame(this.currentFrame.x, this.currentFrame.y, this.currentFrame.width, this.currentFrame.height, this.currentFrame.pivotX, this.currentFrame.pivotY, this.currentFrame.materialIndex);
  }
}]);
return SpriteAnimation;
}();

var tMat4 = create$3();

var Transform$1 = function () {
/**
* A representation of position, rotation and scale in 3D space
* @param {Object} options
* @param {Array} options.position
* @param {Array} options.rotation
* @param {Array} options.scale
*/
function Transform() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref$position = _ref.position,
      position = _ref$position === undefined ? [0, 0, 0, 1] : _ref$position,
      _ref$rotation = _ref.rotation,
      rotation = _ref$rotation === undefined ? [0, 0, 0, 1] : _ref$rotation,
      _ref$scale = _ref.scale,
      scale$$1 = _ref$scale === undefined ? [1, 1, 1] : _ref$scale;

  classCallCheck(this, Transform);

  this.valid = {
    mat4: false,
    translation: false,
    rotation: false,
    rotationQuat: false,
    scale: false,
    inverse: false,
    inverseTransposed: false
  };

  this._temp = {
    mat4: create$3(),
    inverseMat4: create$3(),
    inverseTransposeMat4: create$3(),
    translation: fromValues$5(position[0] || 0, position[1] || 0, position[2] || 0, position[3] === undefined ? 1 : position[3]),
    rotation: fromValues$5(rotation[0] || 0, rotation[1] || 0, rotation[2] || 0, rotation[3] === undefined ? 1 : rotation[3]),
    rotationQuat: create$4(), //quat.fromValues(rotation[0] || 0, rotation[1] || 0, rotation[2] || 0, (rotation[3] === undefined)?1:rotation[3]),
    scale: fromValues$5(scale$$1[0] === undefined ? 1 : scale$$1[0], scale$$1[1] === undefined ? 1 : scale$$1[1], scale$$1[2] === undefined ? 1 : scale$$1[2])
  };
}

/**
* The transform's local matrix.
*/


createClass(Transform, [{
  key: 'targetTo',


  /**
 * 
 * @param {Array} target 3-Dimensional array representing the point in space to look at
 * @param {Array} up 3-Dimensional vector representing up
 */
  value: function targetTo$$1(target) {
    var up = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [0, 1, 0];

    targetTo(tMat4, this.xyz, target, up);

    getRotation(this._temp.rotationQuat, tMat4);

    var x = this._temp.rotationQuat[0];
    var y = this._temp.rotationQuat[1];
    var z = this._temp.rotationQuat[2];
    var w = this._temp.rotationQuat[3];

    this.yaw = Math.atan2(2 * y * w - 2 * x * z, 1 - 2 * y * y - 2 * z * z);
    this.pitch = Math.atan2(2 * x * w - 2 * y * z, 1 - 2 * x * x - 2 * z * z);
    this.roll = Math.asin(2 * x * y + 2 * z * w);

    this.valid.mat4 = false;
    this.valid.rotation = false;
    this.valid.rotationQuat = false;
  }

  /**
 * 
 * @param {Array} target 3-Dimensional array representing the point in space to look at
 * @param {Array} up 3-Dimensional vector representing up
 */

}, {
  key: 'lookAt',
  value: function lookAt$$1(target) {
    var up = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [0, 1, 0];

    lookAt(tMat4, this.xyz, target, up);

    getRotation(this._temp.rotationQuat, tMat4);

    var x = this._temp.rotationQuat[0];
    var y = this._temp.rotationQuat[1];
    var z = this._temp.rotationQuat[2];
    var w = this._temp.rotationQuat[3];

    this.yaw = Math.atan2(2 * y * w - 2 * x * z, 1 - 2 * y * y - 2 * z * z);
    this.pitch = Math.atan2(2 * x * w - 2 * y * z, 1 - 2 * x * x - 2 * z * z);
    this.roll = Math.asin(2 * x * y + 2 * z * w);

    this.valid.mat4 = false;
    this.valid.rotation = false;
    this.valid.rotationQuat = false;
  }
}, {
  key: 'orbitX',
  value: function orbitX(angle$$1) {
    var origin = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [0, 1, 0];

    rotateX$2(this._temp.translation, this._temp.translation, origin, angle$$1);

    this.valid.mat4 = false;
    this.valid.inverse = false;
    this.valid.translation = false;
    this.valid.rotation = false;
    this.valid.rotationQuat = false;
  }
}, {
  key: 'orbitY',
  value: function orbitY(angle$$1) {
    var origin = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [0, 0, 0];

    rotateY$2(this._temp.translation, this._temp.translation, origin, angle$$1);

    this.valid.mat4 = false;
    this.valid.inverse = false;
    this.valid.translation = false;
    this.valid.rotation = false;
    this.valid.rotationQuat = false;
  }
}, {
  key: 'orbitZ',
  value: function orbitZ(angle$$1) {
    var origin = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [0, 0, 0];

    rotateZ$2(this._temp.translation, this._temp.translation, origin, angle$$1);

    this.valid.mat4 = false;
    this.valid.inverse = false;
    this.valid.translation = false;
    this.valid.rotation = false;
    this.valid.rotationQuat = false;
  }
}, {
  key: 'localMatrix',
  get: function get$$1() {
    if (!this.valid.mat4) {
      fromRotationTranslationScale(this._temp.mat4, this.rotationQuat, this._temp.translation, this._temp.scale);

      this.valid.mat4 = true;
    }

    return this._temp.mat4;
  }

  /**
 * The transform's local matrix inversed.
 */

}, {
  key: 'localMatrixInversed',
  get: function get$$1() {
    if (!this.valid.inverse || !this.valid.mat4) {
      invert$3(this._temp.inverseMat4, this.localMatrix);

      this.valid.inverse = true;
    }

    return this._temp.inverseMat4;
  }

  /**
 * The transform's local matrix inversed and transposed.
 */

}, {
  key: 'localMatrixInversedTransposed',
  get: function get$$1() {
    if (!this.valid.inverseTransposed || !this.valid.inverse || !this.valid.mat4) {
      transpose$2(this._temp.inverseTransposeMat4, this.localMatrixInversed);

      this.valid.inverseTransposed = true;
    }

    return this._temp.inverseTransposeMat4;
  }
}, {
  key: 'rotationQuat',
  get: function get$$1() {
    if (!this.valid.rotationQuat) {
      fromEuler(this._temp.rotationQuat, this._temp.rotation[0] * (180 / Math.PI), this._temp.rotation[1] * (180 / Math.PI), this._temp.rotation[2] * (180 / Math.PI));

      this.valid.rotationQuat = true;
      this.valid.mat4 = false;
    }

    return this._temp.rotationQuat;
  }

  /**
 * Translation's X component
 */

}, {
  key: 'x',
  get: function get$$1() {
    if (!this.valid.translation) {
      getTranslation(this._temp.translation, this.localMatrix);

      this.valid.translation = true;
    }

    return this._temp.translation[0];
  }

  /**
 * Translation's Y component
 */
  ,
  set: function set$$1(val) {
    this._temp.translation[0] = val;
    this.valid.mat4 = false;

    return val;
  }
}, {
  key: 'y',
  get: function get$$1() {
    if (!this.valid.translation) {
      getTranslation(this._temp.translation, this.localMatrix);

      this.valid.translation = true;
    }

    return this._temp.translation[1];
  }

  /**
 * Translation's Z component
 */
  ,
  set: function set$$1(val) {
    this._temp.translation[1] = val;
    this.valid.mat4 = false;

    return val;
  }
}, {
  key: 'z',
  get: function get$$1() {
    if (!this.valid.translation) {
      getTranslation(this._temp.translation, this.localMatrix);

      this.valid.translation = true;
    }

    return this._temp.translation[2];
  }

  /**
 * Translation's XYZ components represented as an array
 */
  ,
  set: function set$$1(val) {
    this._temp.translation[2] = val;
    this.valid.mat4 = false;

    return this._temp.translation[2];
  }
}, {
  key: 'xyz',
  get: function get$$1() {
    if (!this.valid.translation) {
      getTranslation(this._temp.translation, this.localMatrix);

      this.valid.translation = true;
    }

    return this._temp.translation;
  },
  set: function set$$1(val) {
    this._temp.translation[0] = val[0];
    this._temp.translation[1] = val[1];
    this._temp.translation[2] = val[2];

    this.valid.mat4 = false;

    return this._temp.translation;
  }
}, {
  key: 'sx',
  get: function get$$1() {
    if (!this.valid.scale) {
      getScaling(this._temp.scale, this.localMatrix);

      this.valid.scale = true;
    }

    return this._temp.scale[0];
  },
  set: function set$$1(val) {
    this._temp.scale[0] = val;
    this.valid.mat4 = false;

    return this._temp.scale[0];
  }
}, {
  key: 'sy',
  get: function get$$1() {
    if (!this.valid.scale) {
      getScaling(this._temp.scale, this.localMatrix);

      this.valid.scale = true;
    }

    return this._temp.scale[1];
  },
  set: function set$$1(val) {
    this._temp.scale[1] = val;
    this.valid.mat4 = false;

    return this._temp.scale[1];
  }
}, {
  key: 'sz',
  get: function get$$1() {
    if (!this.valid.scale) {
      getScaling(this._temp.scale, this.localMatrix);

      this.valid.scale = true;
    }

    return this._temp.scale[2];
  },
  set: function set$$1(val) {
    this._temp.scale[2] = val;
    this.valid.mat4 = false;

    return this._temp.scale[2];
  }
}, {
  key: 'pitch',
  get: function get$$1() {
    if (!this.valid.rotation) {
      getRotation(this._temp.rotation, this.localMatrix);

      this.valid.rotation = true;
    }

    return this._temp.rotation[0];
  },
  set: function set$$1(val) {
    this._temp.rotation[0] = val;
    this.valid.rotationQuat = false;
    this.valid.mat4 = false;

    return this._temp.rotation[0];
  }
}, {
  key: 'yaw',
  get: function get$$1() {
    if (!this.valid.rotation) {
      getRotation(this._temp.rotation, this.localMatrix);

      this.valid.rotation = true;
    }

    return this._temp.rotation[1];
  },
  set: function set$$1(val) {
    this._temp.rotation[1] = val;
    this.valid.rotationQuat = false;
    this.valid.mat4 = false;

    return this._temp.rotation[1];
  }
}, {
  key: 'roll',
  get: function get$$1() {
    if (!this.valid.rotation) {
      getRotation(this._temp.rotation, this.localMatrix);

      this.valid.rotation = true;
    }

    return this._temp.rotation[2];
  },
  set: function set$$1(val) {
    this._temp.rotation[2] = val;
    this.valid.rotationQuat = false;
    this.valid.mat4 = false;

    return this._temp.rotation[2];
  }
}]);
return Transform;
}();

var tMatrix$7 = create$3();

var FACE$1 = [0.0, 0.0, 0.0, 1.0, 0.0, 0.0, 1.0, 1.0, 0.0, 0.0, 1.0, 0.0, 0.0, 0.0, 0.0, 1.0, 1.0, 0.0];

var FACE_UVS$1 = [0.0, 1.0, //  left,    top, 
1.0, 1.0, // right,    top, 
1.0, 0.0, // right, bottom, 
0.0, 0.0, //  left, bottom, 
0.0, 1.0, //  left,    top, 
1.0, 0.0 // right, bottom, 
];

var FACE_NORMALS$1 = [0.0, 0.0, 1.0, 0.0, 0.0, 1.0, 0.0, 0.0, 1.0, 0.0, 0.0, 1.0, 0.0, 0.0, 1.0, 0.0, 0.0, 1.0];

var _id$9 = 0;

var Tilemap = function (_Component) {
inherits(Tilemap, _Component);
createClass(Tilemap, [{
  key: 'atlas',
  get: function get$$1() {
    return this._atlas;
  },
  set: function set$$1(val) {
    var _this2 = this;

    if (!val) {
      this._atlas = null;
      this.mesh.material.atlas = this._atlas;
      return;
    }

    this._atlas = new Texture({ src: val, flipY: false, magFilter: this._magFilter, minFilter: this._minFilter, generateMipmap: false });
    this.mesh.material.atlas = this._atlas;

    this._atlas.load().then(function () {
      _this2.pixelsPerUnit = _this2._pixelsPerUnit; // Refresh size
    });

    return this._atlas;
  }
}, {
  key: 'map',
  get: function get$$1() {
    return this._map;
  },
  set: function set$$1(val) {
    var _this3 = this;

    if (!val) {
      this._map = null;
      this.mesh.material.map = this.map;
      return;
    }

    this._map = new Texture({ src: val, flipY: false, magFilter: this._magFilter, minFilter: this._minFilter, wrap: { s: "CLAMP_TO_EDGE", t: "CLAMP_TO_EDGE" }, generateMipmap: false });
    this.mesh.material.map = this._map;

    this._map.load().then(function () {
      _this3.pixelsPerUnit = _this3._pixelsPerUnit; // Refresh size
    });

    return this._map;
  }
}, {
  key: 'pixelsPerUnit',
  get: function get$$1() {
    return this._pixelsPerUnit;
  },
  set: function set$$1(val) {
    this._pixelsPerUnit = val;

    this.mesh.vertices[0] = 0;
    this.mesh.vertices[3] = this._map.img.width * this._pixelsPerUnit;
    this.mesh.vertices[6] = this._map.img.width * this._pixelsPerUnit;
    this.mesh.vertices[9] = 0;
    this.mesh.vertices[12] = 0;
    this.mesh.vertices[15] = this._map.img.width * this._pixelsPerUnit;

    this.mesh.vertices[1] = 0;
    this.mesh.vertices[4] = 0;
    this.mesh.vertices[7] = this._map.img.height * this._pixelsPerUnit;
    this.mesh.vertices[10] = this._map.img.height * this._pixelsPerUnit;
    this.mesh.vertices[13] = 0;
    this.mesh.vertices[16] = this._map.img.height * this._pixelsPerUnit;

    this.mesh.invalidate(true, true);
  }
}, {
  key: 'tileSize',
  get: function get$$1() {
    return this._tileSize;
  },
  set: function set$$1(val) {
    if (val instanceof Array === false) {
      val = [val, val];
    }
    this._tileSize = val;
  }
}, {
  key: 'minFilter',
  get: function get$$1() {
    return this._minFilter;
  },
  set: function set$$1(val) {
    this._minFilter = val;
    this._atlas.minFilter = val;
    this._map.minFilter = val;
  }
}, {
  key: 'magFilter',
  get: function get$$1() {
    return this._magFilter;
  },
  set: function set$$1(val) {
    this._magFilter = val;
    this._atlas.magFilter = val;
    this._map.magFilter = val;
  }
}]);

function Tilemap() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref$id = _ref.id,
      id = _ref$id === undefined ? _id$9++ : _ref$id,
      _ref$transform = _ref.transform,
      transform = _ref$transform === undefined ? new Transform$1() : _ref$transform,
      _ref$map = _ref.map,
      map = _ref$map === undefined ? "" : _ref$map,
      _ref$atlas = _ref.atlas,
      atlas = _ref$atlas === undefined ? "" : _ref$atlas,
      _ref$pixelsPerUnit = _ref.pixelsPerUnit,
      pixelsPerUnit = _ref$pixelsPerUnit === undefined ? 1.0 : _ref$pixelsPerUnit,
      _ref$tileSize = _ref.tileSize,
      tileSize = _ref$tileSize === undefined ? [16, 16] : _ref$tileSize,
      _ref$atlasPadding = _ref.atlasPadding,
      atlasPadding = _ref$atlasPadding === undefined ? 1.0 : _ref$atlasPadding,
      _ref$magFilter = _ref.magFilter,
      magFilter = _ref$magFilter === undefined ? "LINEAR" : _ref$magFilter,
      _ref$minFilter = _ref.minFilter,
      minFilter = _ref$minFilter === undefined ? "LINEAR_MIPMAP_LINEAR" : _ref$minFilter;

  classCallCheck(this, Tilemap);

  var _this = possibleConstructorReturn(this, (Tilemap.__proto__ || Object.getPrototypeOf(Tilemap)).apply(this, arguments));

  _this.id = id;

  _this.mesh = new Mesh({
    material: new MaterialTilemap("TILEMAP_" + id, {
      atlas: null,
      map: null
    }),
    vertices: new Float32Array(FACE$1),
    uvs: new Float32Array(FACE_UVS$1),
    normals: new Float32Array(FACE_NORMALS$1)
  });

  _this.transform = transform;

  _this._minFilter = minFilter;
  _this._magFilter = magFilter;
  _this._pixelsPerUnit = pixelsPerUnit;
  _this._tileSize = tileSize;

  _this.map = map;
  _this.atlas = atlas;

  _this.tileSize = tileSize;
  return _this;
}

createClass(Tilemap, [{
  key: 'render',
  value: function render(dt, scene, viewport, camera, stack, viewMatrix, modelMatrix) {
    stack.push(this);

    // Calculate view matrix
    mul$3(tMatrix$7, modelMatrix, this.transform.localMatrix);

    this.mesh.render(dt, scene, viewport, camera, stack, viewMatrix, tMatrix$7);

    stack.pop();
  }
}]);
return Tilemap;
}(Component);

var Timer = function (_Entity) {
inherits(Timer, _Entity);

function Timer() {
  classCallCheck(this, Timer);

  var _this = possibleConstructorReturn(this, (Timer.__proto__ || Object.getPrototypeOf(Timer)).call(this));

  _this.currentTime = 0;
  _this.interval = 0;
  _this.currentIntervalCount = 0;
  _this.intervalCount = -1;
  _this.mode = "timeout";
  _this.direction = 1;
  _this.enabled = true;

  _this.callback = function () {};
  return _this;
}

createClass(Timer, [{
  key: "update",
  value: function update(dt, scene) {
    if (!this.enabled) return;

    this.currentTime += dt * this.direction;

    switch (this.mode) {
      case "timeout":
        if (this.currentTime > this.interval) {
          this.callback(scene);
          this.enabled = false;
        }
        break;
      case "interval":
        if (this.currentTime > this.interval) {
          this.currentTime = 0 + (this.currentTime - this.interval); // Carry over the remainder
          this.callback(scene);

          this.currentIntervalCount++;
          if (this.intervalCount > 0 && this.currentIntervalCount >= this.intervalCount) {
            this.enabled = false;
          }
        }
        break;
      case "modulate":
        if (this.currentTime > this.interval) {
          this.currentTime = this.interval - (this.currentTime - this.interval);
          this.direction = -1;
        } else if (this.currentTime < 0) {
          this.currentTime = 0 - this.currentTime;
          this.direction = 1;
        }

        this.callback(scene, dt, this.currentTime / this.interval);
        break;
    }
  }
}, {
  key: "setTimeout",
  value: function setTimeout(callback) {
    var interval = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;

    this.interval = interval;
    this.callback = callback;
    this.mode = "timeout";

    this.currentTime = 0;

    this.enabled = true;
  }
}, {
  key: "setInterval",
  value: function setInterval(callback) {
    var interval = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
    var count = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0;

    this.interval = interval;
    this.intervalCount = count;
    this.callback = callback;
    this.mode = "interval";

    this.currentTime = 0;
    this.currentIntervalCount = 0;

    this.enabled = true;
  }
}, {
  key: "setModulate",
  value: function setModulate(callback) {
    var interval = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;

    this.interval = interval;
    this.callback = callback;
    this.mode = "modulate";

    this.currentTime = 0;

    this.enabled = true;
  }
}]);
return Timer;
}(Entity);

var Tween = function (_Component) {
          inherits(Tween, _Component);

          function Tween() {
                      classCallCheck(this, Tween);

                      var _this = possibleConstructorReturn(this, (Tween.__proto__ || Object.getPrototypeOf(Tween)).call(this));

                      _this.currentTime = 0;
                      _this.percentComplete = 1;
                      _this.duration = 0;
                      _this.direction = 1;
                      _this.mode = "linear";
                      _this.propParent = null;
                      _this.prop = "";
                      _this.from = 0;
                      _this.to = 0;

                      _this.callback = null;
                      return _this;
          }

          createClass(Tween, [{
                      key: "update",
                      value: function update(dt, scene, entity) {
                                  if (!this.enabled || this.percentComplete === 1) return;

                                  this.currentTime += dt * this.direction;
                                  this.percentComplete = Math.min(this.currentTime / this.duration, 1);

                                  switch (this.mode) {
                                              case "linear":
                                                          // a + ((b-a) * %)
                                                          this.propParent[this.prop] = this.from + (this.to - this.from) * this.percentComplete;
                                                          break;
                                              case "easeIn":
                                                          // function(a,b,percent) { return a + (b-a)*((-Math.cos(percent*Math.PI)/2) + 0.5);        }
                                                          this.propParent[this.prop] = this.from + (this.to - this.from) * (-Math.cos(this.percentComplete * Math.PI) / 2 + 0.5);
                                                          break;
                                  }

                                  if (this.percentComplete === 1 && this.callback) this.callback();
                      }
          }, {
                      key: "linear",
                      value: function linear(propParent, prop, to, duration, callback) {
                                  this.duration = duration;
                                  this.propParent = propParent;
                                  this.prop = prop;
                                  this.from = propParent[prop];
                                  this.to = to;
                                  this.callback = callback;

                                  this.mode = "linear";
                                  this.currentTime = 0;
                                  this.percentComplete = 0;
                      }
          }, {
                      key: "easeIn",
                      value: function easeIn(propParent, prop, to, duration, callback) {
                                  this.duration = duration;
                                  this.propParent = propParent;
                                  this.prop = prop;
                                  this.from = propParent[prop];
                                  this.to = to;
                                  this.callback = callback;

                                  this.mode = "easeIn";
                                  this.currentTime = 0;
                                  this.percentComplete = 0;
                      }
          }]);
          return Tween;
}(Component);

var Vertex2 = function () {
function Vertex2() {
  var x = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
  var y = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
  classCallCheck(this, Vertex2);

  this.x = x;
  this.y = y;
}

createClass(Vertex2, [{
  key: "set",
  value: function set() {
    var arr = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];

    this.x = arr[0] || 0;
    this.y = arr[1] || 0;
  }
}, {
  key: "copy",
  value: function copy(vertex2) {
    if (!vertex2) {
      vertex2 = new Vertex2(this.x, this.y);
    } else {
      vertex2.x = vertex2.x;
      vertex2.y = vertex2.y;
    }

    return vertex2;
  }
}]);
return Vertex2;
}();

var Vertex3 = function (_Vertex) {
inherits(Vertex3, _Vertex);

function Vertex3() {
  var x = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
  var y = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
  var z = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0;
  classCallCheck(this, Vertex3);

  var _this = possibleConstructorReturn(this, (Vertex3.__proto__ || Object.getPrototypeOf(Vertex3)).call(this, x, y));

  _this.z = z;
  return _this;
}

createClass(Vertex3, [{
  key: 'set',
  value: function set() {
    var arr = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];

    this.x = arr[0] || 0;
    this.y = arr[1] || 0;
    this.z = arr[2] || 0;
  }
}, {
  key: 'copy',
  value: function copy(vertex3) {
    if (!vertex3) {
      vertex3 = new Vertex3(this.x, this.y, this.z);
    } else {
      vertex3.x = vertex3.x;
      vertex3.y = vertex3.y;
      vertex3.z = vertex3.z;
    }

    return vertex3;
  }
}]);
return Vertex3;
}(Vertex2);

var face = new Float32Array([
// Face
0.0, 0.0, 0.0, 1.0, 0.0, 0.0, 1.0, 1.0, 0.0, 0.0, 1.0, 0.0, 0.0, 0.0, 0.0, 1.0, 1.0, 0.0]);

var textureFace = new Float32Array([0, 0, //  left,    top, 
1, 0, // right,    top, 
1, 1, // right,  bottom, 
0, 1, //  left,  bottom, 
0, 0, //  left,    top, 
1, 1 // right,  bottom, 
]);

var Video = function (_Component) {
inherits(Video, _Component);

function Video(src) {
  var width = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 32;
  var height = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 32;
  classCallCheck(this, Video);

  var _this = possibleConstructorReturn(this, (Video.__proto__ || Object.getPrototypeOf(Video)).call(this));

  _this.width = width;
  _this.height = height;

  _this.unitScale = 1;
  _this.centered = false;

  _this.flipX = false;
  _this.flipY = false;

  _this.antiAliasing = true;

  _this.mesh = new Mesh();
  _this.mesh.addMaterial("video", face, { uvs: textureFace, texture: src });
  _this.mesh.materials[0].textureLoad.then(function () {
    // textureCache.load(image).then((texture)=>{
    // 	this.mesh.materials[0].texture = texture;

    _this.mesh.materials[0].texture.flipY = true;

    if (!_this.antiAliasing) {
      _this.mesh.materials[0].texture.magFilter = "NEAREST";
      _this.mesh.materials[0].texture.minFilter = "NEAREST";
    }

    width = _this.width * _this.unitScale;
    height = _this.height * _this.unitScale;

    _this.mesh.materials[0].vertices[3] = width;
    _this.mesh.materials[0].vertices[6] = width;
    _this.mesh.materials[0].vertices[15] = width;

    _this.mesh.materials[0].vertices[7] = height;
    _this.mesh.materials[0].vertices[10] = height;
    _this.mesh.materials[0].vertices[16] = height;

    if (_this.centered) {
      _this.mesh.materials[0].vertices[0] -= width / 2;
      _this.mesh.materials[0].vertices[3] -= width / 2;
      _this.mesh.materials[0].vertices[6] -= width / 2;
      _this.mesh.materials[0].vertices[9] -= width / 2;
      _this.mesh.materials[0].vertices[12] -= width / 2;
      _this.mesh.materials[0].vertices[15] -= width / 2;

      _this.mesh.materials[0].vertices[1] -= height / 2;
      _this.mesh.materials[0].vertices[4] -= height / 2;
      _this.mesh.materials[0].vertices[7] -= height / 2;
      _this.mesh.materials[0].vertices[10] -= height / 2;
      _this.mesh.materials[0].vertices[13] -= height / 2;
      _this.mesh.materials[0].vertices[16] -= height / 2;
    }
  });
  return _this;
}

createClass(Video, [{
  key: "update",
  value: function update(entity, deltaTime) {}
}, {
  key: "render",
  value: function render(entity, camera, viewport) {
    this.mesh.render(entity, camera, viewport);
  }

  /**
 * Called on every Entity render pick
 */

}, {
  key: "renderPick",
  value: function renderPick(entity, camera, viewport, entityId) {
    this.mesh.renderPick(entity, camera, viewport, entityId);
  }
}, {
  key: "play",
  value: function play() {
    this.mesh.materials[0].texture.video.play();
  }
}]);
return Video;
}(Component);

var id = 0;
var viewMatrix$1 = create$3();

var Viewport = function () {
createClass(Viewport, [{
  key: 'clearColor',
  get: function get$$1() {
    return this._clearColor;
  },
  set: function set$$1(value) {
    this._clearColor = value;
    this.webgl.clearColor(this._clearColor.color[0], this._clearColor.color[1], this._clearColor.color[2], this._clearColor.color[3]);

    return this._clearColor;
  }
}, {
  key: 'width',
  get: function get$$1() {
    return this.contextElement.clientWidth * this.getDevicePixelRatio(this.webgl);
  }
}, {
  key: 'height',
  get: function get$$1() {
    return this.contextElement.clientHeight * this.getDevicePixelRatio(this.webgl);
  }
}]);

function Viewport(contextElement) {
  var _this = this;

  classCallCheck(this, Viewport);

  this._currentTexture = [];

  this.id = id++;

  this.mouseEventViewport = null;
  this.mouseEventRadius = 1.0;

  this.camera = null;
  this.contextElement = contextElement;
  this._clearColor = new Color(0, 0, 0, 1);
  this.clearOnRender = true;
  this.programs = [];
  this.program = null;

  this.enabledExtensions = {};

  this.frameBufferTextures = [];

  // TODO: This should live at the camera
  this.projectionMatrix = create$3();
  // TODO: move getAttributes to params + update scene.js
  // this._projectionType = contextElement.getAttribute("projection-type") || "Perspective";
  // this._nearPlane = (contextElement.getAttribute("nearplane") * 1) || 0;
  // this._farPlane = (contextElement.getAttribute("farplane") * 1) || 1000.0;

  this.orthoWidth = contextElement.getAttribute("ortho-width") * 1 || 0;
  this.orthoHeight = contextElement.getAttribute("ortho-height") * 1 || 0;

  this.parallax = contextElement.getAttribute("parallax") == "true";

  // Initialize WebGL
  if (!window.WebGLRenderingContext) throw new Error("[Onyx] WebGL is not supported by this browser.");

  // WebGL Options
  var webglOptions = {
    alpha: true,
    antialias: true,
    depth: true,
    failIfMajorPerformanceCaveat: false,
    powerPreference: "default",
    premultipliedAlpha: true,
    preserveDrawingBuffer: true,
    stencil: true

    // Read from HTML tag
  };if (contextElement.getAttribute("alpha") == 'false') webglOptions.alpha = false;
  if (contextElement.getAttribute("antialias") == 'false') webglOptions.antialias = false;
  if (contextElement.getAttribute("depth") == 'false') webglOptions.depth = false;
  if (contextElement.getAttribute("fail-if-major-performance-caveat") == 'true') webglOptions.failIfMajorPerformanceCaveat = true;
  if (contextElement.getAttribute("power-preference")) webglOptions.powerPreference = contextElement.getAttribute("powerPreference");
  if (contextElement.getAttribute("premultiplied-alpha") == 'false') webglOptions.premultipliedAlpha = false;
  if (contextElement.getAttribute("preserve-drawing-buffer") == 'false') webglOptions.preserveDrawingBuffer = false;
  if (contextElement.getAttribute("stencil") == 'false') webglOptions.stencil = false;

  try {
    this.webgl = contextElement.getContext("webgl", webglOptions) || contextElement.getContext("experimental-webgl", webglOptions);
  } catch (e) {
    throw new Error("[Onyx] Couldn't initialize WebGL");
    return;
  }

  if (!this.webgl) {
    throw new Error("[Onyx] WebGL appears to be disabled");
    return;
  }

  // Get the viewport ready...
  this.webgl.viewport(0, 0, contextElement.width, contextElement.height);
  this.webgl.clearColor(this._clearColor.color[0], this._clearColor.color[1], this._clearColor.color[2], this._clearColor.color[3]);
  this.webgl.blendFuncSeparate(this.webgl.SRC_ALPHA, this.webgl.ONE_MINUS_SRC_ALPHA, this.webgl.ZERO, this.webgl.ONE);

  this.projectionMatrix = create$3();

  this.enableExtension("OES_standard_derivatives");
  // this.enableExtension("OES_texture_float");
  // this.enableExtension("OES_texture_float_linear");

  this.clear();

  // Watch for changes
  window.addEventListener("resize", function () {
    _this.evaluate();
  });

  this.evaluate();
}

createClass(Viewport, [{
  key: 'clear',
  value: function clear() {
    this.webgl.clearColor(this._clearColor.color[0], this._clearColor.color[1], this._clearColor.color[2], this._clearColor.color[3]);
    this.webgl.clear(this.webgl.COLOR_BUFFER_BIT | this.webgl.DEPTH_BUFFER_BIT);
  }
}, {
  key: 'enableExtension',
  value: function enableExtension(extension) {
    if (!this.enabledExtensions[extension]) this.enabledExtensions[extension] = this.webgl.getExtension(extension);
    if (!this.enabledExtensions[extension]) console.warn('Extension "' + extension + '" not available.');
  }
}, {
  key: 'evaluate',
  value: function evaluate() {
    var clear = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;

    if (clear) {
      var _devicePixelRatio = this.getDevicePixelRatio(this.webgl);

      if (!navigator.isCocoonJS) {
        if (this.contextElement.clientWidth && this.contextElement.clientHeight) {
          this.contextElement.width = this.contextElement.clientWidth * _devicePixelRatio;
          this.contextElement.height = this.contextElement.clientHeight * _devicePixelRatio;
        }
      } else {
        this.contextElement.width = window.innerWidth * _devicePixelRatio;
        this.contextElement.height = window.innerHeight * _devicePixelRatio;
      }
    } else {
      if (this.contextElement.clientWidth && this.contextElement.clientHeight) {
        if (this.contextElement.width !== this.contextElement.clientWidth * devicePixelRatio) this.contextElement.width = this.contextElement.clientWidth * devicePixelRatio;
        if (this.contextElement.height !== this.contextElement.clientHeight * devicePixelRatio) this.contextElement.height = this.contextElement.clientHeight * devicePixelRatio;
      }
    }
    this.webgl.viewport(0, 0, this.webgl.drawingBufferWidth, this.webgl.drawingBufferHeight);

    if (!this.camera) return;

    //TODO this should be at the camera level, along with FOV, etc.
    if (this.camera._projectionType.toLowerCase() == "orthographic") {
      // Solve width / height
      var width = this.orthoWidth;
      var height = this.orthoHeight;

      if (!width && !height) {
        width = this.contextElement.clientWidth;
        height = this.contextElement.clientHeight;
      } else {
        var aspect = this.webgl.drawingBufferWidth / this.webgl.drawingBufferHeight;

        if (!width) {
          width = height * aspect;
        } else if (!height) {
          height = width / aspect;
        }
      }

      // this.width = width;
      // this.height = height;

      ortho(this.projectionMatrix, 0, width, 0, height, this.camera._nearPlane, this.camera._farPlane);
    } else {
      perspective(this.projectionMatrix, this.camera.fov, this.webgl.drawingBufferWidth / this.webgl.drawingBufferHeight, this.camera._nearPlane, this.camera._farPlane);
    }
  }
}, {
  key: 'getDevicePixelRatio',
  value: function getDevicePixelRatio(context) {
    // For hi-res displays-- For example, the Chromebook Pixel
    if ('devicePixelRatio' in window) {
      if (window.devicePixelRatio > 1) {
        // && context.webkitBackingStorePixelRatio < 2) {
        return window.devicePixelRatio;
      } else {
        return 1;
      }
    } else {
      return 1;
    }
  }
}, {
  key: 'render',
  value: function render(dt, scene, stack, matrix, materialOverride, frameBufferTextureStack) {
    // if(this.clearOnRender) this.clear();

    // Calculate view matrix
    mul$3(viewMatrix$1, matrix, this.projectionMatrix);

    if (this.camera) this.camera.render(dt, scene, this, stack, viewMatrix$1, materialOverride, frameBufferTextureStack);

    // Fixes flickering on some browsers (maybe?)
    // this.webgl.flush();
  }
}, {
  key: 'renderPick',
  value: function renderPick(mouseX, mouseY, event) {
    var radius = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 1.0;

    if (!this.camera) return;

    this.mouseEventViewport.contextElement.width = this.contextElement.width || this.contextElement.naturalWidth;
    this.mouseEventViewport.contextElement.height = this.contextElement.height || this.contextElement.naturalHeight;
    this.mouseEventViewport.evaluate(true);
    this.mouseEventViewport.canvas2d.width = this.contextElement.width || this.contextElement.naturalWidth;
    this.mouseEventViewport.canvas2d.height = this.contextElement.height || this.contextElement.naturalHeight;
    this.mouseEventViewport.webgl.clear(this.webgl.COLOR_BUFFER_BIT | this.webgl.DEPTH_BUFFER_BIT);

    this.mouseEventViewport.clear();
    // this.mouseEventViewport.webgl.disable(this.webgl.GL_BLEND);

    this.camera.renderScenePick(this.mouseEventViewport);

    var entityList = [];

    mouseX -= radius / 2;
    mouseY -= radius / 2;

    // var rgbaData = new Uint8Array(radius * radius * 4);
    // this.webgl.readPixels(mouseX | 0, mouseY | 0, radius, radius, this.webgl.RGBA, this.webgl.UNSIGNED_BYTE, rgbaData);

    // Copy scene to 2D Canvas (TODO: Is this faster if we read from the webgl buffer?)
    this.mouseEventViewport.context2d.drawImage(this.mouseEventViewport.contextElement, 0, 0);
    var rgbaData = this.mouseEventViewport.context2d.getImageData(mouseX | 0, mouseY | 0, radius, radius).data;

    for (var i = 0; i < rgbaData.length; i++) {
      // Find entity by RGB data
      var entity = this.camera.stage.entities[rgbaData[i] - 1];

      // Add entity to the candidates list
      if (entity) entityList.push(entity);
    }

    // Find most likely candidate
    var scores = [];
    var _iteratorNormalCompletion = true;
    var _didIteratorError = false;
    var _iteratorError = undefined;

    try {
      for (var _iterator = entityList[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
        var _entity = _step.value;

        if (!scores[this.camera.stage.entities.indexOf(_entity)]) scores[this.camera.stage.entities.indexOf(_entity)] = 0;

        scores[this.camera.stage.entities.indexOf(_entity)]++;
      }
    } catch (err) {
      _didIteratorError = true;
      _iteratorError = err;
    } finally {
      try {
        if (!_iteratorNormalCompletion && _iterator.return) {
          _iterator.return();
        }
      } finally {
        if (_didIteratorError) {
          throw _iteratorError;
        }
      }
    }

    var largest = 0;
    var largestEntity = 0;
    for (var _i in scores) {
      if (scores[_i] > largest) {
        largest = scores[_i];
        largestEntity = _i;
      }
    }

    if (this.camera.stage.entities[largestEntity]) this.camera.stage.entities[largestEntity].emit('click', event);
  }
}, {
  key: 'createProgram',
  value: function createProgram(material) {
    var _iteratorNormalCompletion2 = true;
    var _didIteratorError2 = false;
    var _iteratorError2 = undefined;

    try {
      for (var _iterator2 = this.programs[Symbol.iterator](), _step2; !(_iteratorNormalCompletion2 = (_step2 = _iterator2.next()).done); _iteratorNormalCompletion2 = true) {
        var _program = _step2.value;

        if (_program.vertexShader == material.vertexShader && _program.fragmentShader == material.fragmentShader) {
          return _program;
        }
      }

      // Create a new program (if one doesn't already exist)
    } catch (err) {
      _didIteratorError2 = true;
      _iteratorError2 = err;
    } finally {
      try {
        if (!_iteratorNormalCompletion2 && _iterator2.return) {
          _iterator2.return();
        }
      } finally {
        if (_didIteratorError2) {
          throw _iteratorError2;
        }
      }
    }

    this.programs.push(new GLProgram(this));

    var program = this.programs[this.programs.length - 1];

    // Compile shaders
    program.addVertexShader(this, material.vertexShader);
    program.addFragmentShader(this, material.fragmentShader);

    // Links the compiled shaders to the program
    this.webgl.linkProgram(program.program);

    // If creating the shader program failed, alert
    if (!this.webgl.getProgramParameter(program.program, this.webgl.LINK_STATUS)) {
      throw "Unable to initialize the shader program. " + this.webgl.getProgramInfoLog(program.program);
    }

    // Get attributes from shaders
    program.getAttributesFromShaders(this, material.shaderAttributes);
    program.getUniformLocations(this, material.shaderUniforms);

    return program;
  }
}, {
  key: 'useProgram',
  value: function useProgram(program) {
    if (this.program === program) return;

    this.program = program;

    this.program.use(this);
  }
}, {
  key: 'bindToCamera',
  value: function bindToCamera(camera) {
    this.camera = camera;
    camera.addViewport(this);
    this.evaluate();
  }
}, {
  key: 'listenForMouseEvents',
  value: function listenForMouseEvents() {
    var _this2 = this;

    var mouseRadius = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 1.0;

    var videoBufferCanvas = document.createElement("canvas");

    this.mouseEventViewport = new Viewport(videoBufferCanvas);
    this.mouseEventRadius = mouseRadius;

    // Initialize 2D
    try {
      this.mouseEventViewport.canvas2d = document.createElement('canvas');
      this.mouseEventViewport.context2d = this.mouseEventViewport.canvas2d.getContext("2d");
    } catch (e) {
      console.error("Couldn't initialize 2D");
    }

    // Watch for mouse events
    this.contextElement.addEventListener("mousedown", function (event) {
      _this2.renderPick(event.offsetX, event.offsetY, event, _this2.mouseEventRadius);
    });

    this.contextElement.addEventListener("touchstart", function (event) {
      // 			console.log(event);
      _this2.renderPick(Math.round(event.targetTouches[0].pageX), Math.round(event.targetTouches[0].pageY), event, event.targetTouches[0].radiusX);
    });
  }
}, {
  key: 'addFrameBufferTexture',
  value: function addFrameBufferTexture(frameBufferTexture) {
    this.frameBufferTextures.push(frameBufferTexture);
  }
}, {
  key: 'removeFrameBufferTexture',
  value: function removeFrameBufferTexture(frameBufferTexture) {
    if (this.frameBufferTextures.indexOf(frameBufferTexture) > -1) this.frameBufferTextures.splice(this.frameBufferTextures.indexOf(frameBufferTexture), 1);
  }
}]);
return Viewport;
}();

var RenderPassShader = function (_RenderPass) {
inherits(RenderPassShader, _RenderPass);

function RenderPassShader() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref$renderPasses = _ref.renderPasses,
      renderPasses = _ref$renderPasses === undefined ? [] : _ref$renderPasses,
      _ref$shader = _ref.shader,
      shader = _ref$shader === undefined ? null : _ref$shader;

  classCallCheck(this, RenderPassShader);

  var _this = possibleConstructorReturn(this, (RenderPassShader.__proto__ || Object.getPrototypeOf(RenderPassShader)).apply(this, arguments));

  _this.frameBufferTextures = [];
  _this.shader = shader;
  return _this;
}

createClass(RenderPassShader, [{
  key: 'render',
  value: function render(dt, scene, viewport, stack, matrix) {
    var frameBufferTextureStack = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : [];

    if (this.viewportClear) viewport.clear();

    if (this.shader) this.shader.render(dt, scene, viewport, scene.cameras[0], stack, matrix, frameBufferTextureStack);

    var _iteratorNormalCompletion = true;
    var _didIteratorError = false;
    var _iteratorError = undefined;

    try {
      for (var _iterator = this.renderPasses[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
        var renderPass = _step.value;

        renderPass.render(dt, scene, viewport, stack, matrix, frameBufferTextureStack);
      }
    } catch (err) {
      _didIteratorError = true;
      _iteratorError = err;
    } finally {
      try {
        if (!_iteratorNormalCompletion && _iterator.return) {
          _iterator.return();
        }
      } finally {
        if (_didIteratorError) {
          throw _iteratorError;
        }
      }
    }
  }
}]);
return RenderPassShader;
}(RenderPass);

var lightMVMatrix = create$3();
var viewMatrix$2 = create$3();

var CUBEMAP_TARGETS = [[-1, 0, 0], [1, 0, 0], [0, 1, 0], [0, -1, 0], [0, 0, -1], [0, 0, 1]];

var CUBEMAP_UPS = [[0, -1, 0], [0, -1, 0], [0, 0, -1], [0, 0, 1], [0, -1, 0], [0, -1, 0]];

var CUBEMAP_TEXTURE_TARGETS = ["TEXTURE_CUBE_MAP_POSITIVE_X", "TEXTURE_CUBE_MAP_NEGATIVE_X", "TEXTURE_CUBE_MAP_POSITIVE_Y", "TEXTURE_CUBE_MAP_NEGATIVE_Y", "TEXTURE_CUBE_MAP_POSITIVE_Z", "TEXTURE_CUBE_MAP_NEGATIVE_Z"];

var RENDER_TO_SCREEN = false; // Render shadowmap to canvas/screen. Useful for debugging

var RenderPassShadow = function (_RenderPass) {
  inherits(RenderPassShadow, _RenderPass);

  function RenderPassShadow() {
      var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
          _ref$renderPasses = _ref.renderPasses,
          renderPasses = _ref$renderPasses === undefined ? [] : _ref$renderPasses,
          _ref$width = _ref.width,
          width = _ref$width === undefined ? 1024 : _ref$width,
          _ref$height = _ref.height,
          height = _ref$height === undefined ? 1024 : _ref$height;

      classCallCheck(this, RenderPassShadow);

      var _this = possibleConstructorReturn(this, (RenderPassShadow.__proto__ || Object.getPrototypeOf(RenderPassShadow)).apply(this, arguments));

      _this.width = width;
      _this.height = height;

      _this.frameBufferTextures = [];
      _this.material = new MaterialDepth();
      return _this;
  }

  createClass(RenderPassShadow, [{
      key: 'render',
      value: function render(dt, scene, viewport, stack, matrix) {
          var frameBufferTextureStack = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : [];

          if (!CONST.DIRECTIONAL_SHADOWS_ENABLED && !CONST.POINT_SHADOWS_ENABLED) {
              var _iteratorNormalCompletion = true;
              var _didIteratorError = false;
              var _iteratorError = undefined;

              try {
                  for (var _iterator = this.renderPasses[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
                      var renderPass = _step.value;

                      if (!RENDER_TO_SCREEN) renderPass.render(dt, scene, viewport, stack, matrix, frameBufferTextureStack);
                  }
              } catch (err) {
                  _didIteratorError = true;
                  _iteratorError = err;
              } finally {
                  try {
                      if (!_iteratorNormalCompletion && _iterator.return) {
                          _iterator.return();
                      }
                  } finally {
                      if (_didIteratorError) {
                          throw _iteratorError;
                      }
                  }
              }
          } else {
              var shadowCount = 0;

              if (CONST.DIRECTIONAL_SHADOWS_ENABLED) {
                  if (!this.frameBufferTextures[shadowCount]) {
                      this.frameBufferTextures[shadowCount] = new FrameBufferTexture2D({ height: this.width, width: this.height, magFilter: "NEAREST", minFilter: "NEAREST", textureType: "TEXTURE_2D", flipY: true, wrap: { s: "CLAMP_TO_EDGE", t: "CLAMP_TO_EDGE" } });
                  }

                  // Bind frameBufferTexture
                  if (!RENDER_TO_SCREEN) this.frameBufferTextures[shadowCount].bind(viewport, 0, viewport.webgl.TEXTURE_2D);

                  viewport.clear();
                  viewport.webgl.clearColor(0, 0, 0, 0);
                  viewport.webgl.clearDepth(1.0);
                  viewport.webgl.clear(viewport.webgl.COLOR_BUFFER_BIT | viewport.webgl.DEPTH_BUFFER_BIT);

                  var _iteratorNormalCompletion2 = true;
                  var _didIteratorError2 = false;
                  var _iteratorError2 = undefined;

                  try {
                      for (var _iterator2 = scene.entities[Symbol.iterator](), _step2; !(_iteratorNormalCompletion2 = (_step2 = _iterator2.next()).done); _iteratorNormalCompletion2 = true) {
                          var entity = _step2.value;

                          if (entity instanceof Camera === false && entity.visible) {
                              //TODO: Move this to entity?
                              entity.render(dt, scene, viewport, scene.directionalLight, stack, viewMatrix$2, null, this.material);
                          }
                      }

                      // Unbind frameBufferTexture
                  } catch (err) {
                      _didIteratorError2 = true;
                      _iteratorError2 = err;
                  } finally {
                      try {
                          if (!_iteratorNormalCompletion2 && _iterator2.return) {
                              _iterator2.return();
                          }
                      } finally {
                          if (_didIteratorError2) {
                              throw _iteratorError2;
                          }
                      }
                  }

                  this.frameBufferTextures[shadowCount].unbind(viewport);

                  shadowCount++;
              }

              if (CONST.POINT_SHADOWS_ENABLED) {
                  // Render each light as a camera with a shadow material
                  for (var lightIndex = shadowCount; lightIndex < CONST.MAX_LIGHTS + shadowCount; lightIndex++) {
                      var light = scene.lights[lightIndex - shadowCount];

                      // Note: We still need to create a blank texture for the shader
                      if (!this.frameBufferTextures[lightIndex]) {
                          this.frameBufferTextures[lightIndex] = new FrameBufferTexture2D({ magFilter: "NEAREST", minFilter: "NEAREST", textureType: "TEXTURE_CUBE_MAP", flipY: true, wrap: { s: "REPEAT", t: "REPEAT" } });
                      }

                      if (!light || !light.enabled) continue;

                      stack.push(light);

                      // Calculate view matrix
                      // mat4.mul(lightMVMatrix, matrix, light.transform.localMatrixInversed);

                      // Render entity to frameBufferTexture
                      for (var i = 0; i < 6; i++) {
                          // Creates a view matrix using target and up vectors 
                          // according to each face of pointlight's cubemap.

                          lookAt(viewMatrix$2, [0, 0, 0], CUBEMAP_TARGETS[i], CUBEMAP_UPS[i]);

                          // Bind frameBufferTexture
                          if (!RENDER_TO_SCREEN) this.frameBufferTextures[lightIndex].bind(viewport, 0, viewport.webgl[CUBEMAP_TEXTURE_TARGETS[i]]);

                          viewport.clear();
                          viewport.webgl.clearColor(0, 0, 0, 0);
                          viewport.webgl.clearDepth(1.0);
                          viewport.webgl.clear(viewport.webgl.COLOR_BUFFER_BIT | viewport.webgl.DEPTH_BUFFER_BIT);

                          var _iteratorNormalCompletion3 = true;
                          var _didIteratorError3 = false;
                          var _iteratorError3 = undefined;

                          try {
                              for (var _iterator3 = scene.entities[Symbol.iterator](), _step3; !(_iteratorNormalCompletion3 = (_step3 = _iterator3.next()).done); _iteratorNormalCompletion3 = true) {
                                  var _entity = _step3.value;

                                  if (_entity instanceof Camera === false && _entity !== light && _entity.visible) {
                                      //TODO: Move this to entity?
                                      _entity.render(dt, scene, viewport, light, stack, viewMatrix$2, null, this.material);
                                  }
                              }
                          } catch (err) {
                              _didIteratorError3 = true;
                              _iteratorError3 = err;
                          } finally {
                              try {
                                  if (!_iteratorNormalCompletion3 && _iterator3.return) {
                                      _iterator3.return();
                                  }
                              } finally {
                                  if (_didIteratorError3) {
                                      throw _iteratorError3;
                                  }
                              }
                          }
                      }

                      stack.pop();

                      // Unbind frameBufferTexture
                      this.frameBufferTextures[lightIndex].unbind(viewport);
                  }

                  shadowCount += CONST.MAX_LIGHTS;
              }

              frameBufferTextureStack.push(this.frameBufferTextures);

              var _iteratorNormalCompletion4 = true;
              var _didIteratorError4 = false;
              var _iteratorError4 = undefined;

              try {
                  for (var _iterator4 = this.renderPasses[Symbol.iterator](), _step4; !(_iteratorNormalCompletion4 = (_step4 = _iterator4.next()).done); _iteratorNormalCompletion4 = true) {
                      var _renderPass = _step4.value;

                      if (!RENDER_TO_SCREEN) _renderPass.render(dt, scene, viewport, stack, matrix, frameBufferTextureStack);
                  }
              } catch (err) {
                  _didIteratorError4 = true;
                  _iteratorError4 = err;
              } finally {
                  try {
                      if (!_iteratorNormalCompletion4 && _iterator4.return) {
                          _iterator4.return();
                      }
                  } finally {
                      if (_didIteratorError4) {
                          throw _iteratorError4;
                      }
                  }
              }

              frameBufferTextureStack.pop();
          }
      }
  }]);
  return RenderPassShadow;
}(RenderPass);

var VERTEX_SHADER = '\n    attribute vec3 aVertexPosition;\n\n    uniform mat4 uMVCPMatrix;\n\n    void main(void) {\n        gl_Position = uMVCPMatrix * vec4(aVertexPosition, 1.0);\n    }\n';

var FRAG_SHADER = '\n\tprecision mediump float;\n\n    void main(void) {\n        gl_FragColor = vec4(1.0);\n    }\n';

var MaterialBasic = function (_Material) {
  inherits(MaterialBasic, _Material);

  function MaterialBasic() {
      classCallCheck(this, MaterialBasic);

      var _this = possibleConstructorReturn(this, (MaterialBasic.__proto__ || Object.getPrototypeOf(MaterialBasic)).apply(this, arguments));

      _this.vertexShader = VERTEX_SHADER;
      _this.fragmentShader = FRAG_SHADER;
      return _this;
  }

  return MaterialBasic;
}(Material);

var VERTEX_SHADER$1 = '\n    attribute vec3 aVertexPosition;\n\n    uniform mat4 uLMVMatrix;\n\n    varying float vDepth;\n\n    void main(void) {\n        gl_Position = uLMVMatrix * vec4(aVertexPosition, 1.0);\n\n        float zBuf = gl_Position.z / gl_Position.w;  // between -1 and 1\n        vDepth = 0.5 + (zBuf * 0.5);           // between 0 and 1\n    }\n';

var FRAG_SHADER$1 = '\n    #extension GL_OES_standard_derivatives: enable\n\n    #ifdef GL_FRAGMENT_PRECISION_HIGH\n        precision highp float;\n    #else\n        precision mediump float;\n    #endif\n\n    #ifdef GL_OES_standard_derivatives\n        #define USE_SVM\n    #endif\n\n    varying float vDepth;\n\n    vec2 packDepthVec2(const in float depth) {\n        const vec2 bitShift = vec2(255.0, 1.0);\n        const vec2 bitMask = vec2(0.0, 1.0 / 255.0);\n      \n        vec2 res = fract(depth * bitShift);\n        res -= res.xy * bitMask;\n      \n        return res;\n    }\n\n    vec3 packDepthVec3(const in float depth) {\n        const vec3 bitShift = vec3(255.0 * 255.0, 255.0, 1.0);\n        const vec3 bitMask = vec3(0.0, 1.0 / 255.0, 1.0 / 255.0);\n      \n        vec3 res = fract(depth * bitShift);\n        res -= res.xyz * bitMask;\n      \n        return res;\n    }\n\n    vec4 packDepthVec4(const in float depth) {\n        const vec4 bitShift = vec4(255.0 * 255.0 * 255.0, 255.0 * 255.0, 255.0, 1.0);\n        const vec4 bitMask = vec4(0.0, 1.0 / 255.0, 1.0 / 255.0, 1.0 / 255.0);\n      \n        vec4 res = fract(depth * bitShift);\n        res -= res.xxyz * bitMask;\n      \n        return res;\n    }\n\n    void main(void) {\n        #ifdef USE_SVM\n            float depth2 = pow(vDepth, 2.0);\n\n            // approximate the spatial average of vDepth^2\n            float dx = dFdx(vDepth);\n            float dy = dFdy(vDepth);\n            float depth2Avg = depth2 + 0.25 * (dx*dx + dy*dy);\n\n            // depth saved in red and green channels while average depth^2 is\n            // stored in the blue and alpha channels\n            gl_FragColor = vec4(packDepthVec3(vDepth), depth2Avg);\n        #else\n            // Packs normalized fragment\'s Z-Coordinate which is in [0,1] interval.\n            \n            gl_FragColor = packDepthVec4(gl_FragCoord.z);\n        #endif\n    }\n';

var MaterialDepth = function (_Material) {
  inherits(MaterialDepth, _Material);

  function MaterialDepth() {
      classCallCheck(this, MaterialDepth);

      var _this = possibleConstructorReturn(this, (MaterialDepth.__proto__ || Object.getPrototypeOf(MaterialDepth)).apply(this, arguments));

      _this.vertexShader = VERTEX_SHADER$1;
      _this.fragmentShader = FRAG_SHADER$1;

      if (!CONST.SHADOWS_VSM_ENABLED) _this.fragmentShader = _this.fragmentShader.replace("#define USE_SVM", "");

      _this.blend = false;
      _this.cullFaceMode = "FRONT";
      return _this;
  }

  createClass(MaterialDepth, [{
      key: 'render',
      value: function render(dt, scene, viewport, light, stack, viewMatrix, modelMatrix) {
          // TODO: Pull in parent's material's diffuse texture (for sprite alpha)
          var parent = stack[stack.length - 1];
          if (!parent || !parent.material.shadowCastingMode) return false; // TODO: Might need to move this if we're using depth for something else

          get(MaterialDepth.prototype.__proto__ || Object.getPrototypeOf(MaterialDepth.prototype), 'render', this).call(this, dt, scene, viewport, light, stack);

          viewport.webgl.disable(viewport.webgl.CULL_FACE);

          viewport.webgl.uniformMatrix4fv(viewport.program.uniform.uLMVMatrix, false, light.getMVPMatrix(modelMatrix, viewport, viewMatrix));
      }
  }]);
  return MaterialDepth;
}(Material);

'use strict';

var FACE$2 = [-1.0, -1.0, 0.0, 1.0, -1.0, 0.0, 1.0, 1.0, 0.0, -1.0, 1.0, 0.0, -1.0, -1.0, 0.0, 1.0, 1.0, 0.0];

var FACE_UVS$2 = [0.0, 0.0, //  left,    top, 
1.0, 0.0, // right,    top, 
1.0, 1.0, // right, bottom, 
0.0, 1.0, //  left, bottom, 
0.0, 0.0, //  left,    top, 
1.0, 1.0 // right, bottom, 
];

var VERTEX_SHADER$2 = '\n    attribute vec3 aVertexPosition;\n    attribute vec2 aTextureCoord;\n\n    varying vec2 vTextureCoord;\n\n    void main(void) {\n        vTextureCoord = aTextureCoord;\n\n        gl_Position = vec4(aVertexPosition, 1.0);\n    }\n';

var FRAG_SHADER$2 = '\n    #ifdef GL_FRAGMENT_PRECISION_HIGH\n        precision highp float;\n    #else\n        precision mediump float;\n    #endif\n\n    varying vec2 vTextureCoord;\n\n    uniform sampler2D uTexture;\n\n    void main(void) {\n        gl_FragColor = texture2D(uTexture, vTextureCoord);\n    }\n';

var MaterialPost = function (_Material) {
      inherits(MaterialPost, _Material);

      function MaterialPost() {
              classCallCheck(this, MaterialPost);

              var _this = possibleConstructorReturn(this, (MaterialPost.__proto__ || Object.getPrototypeOf(MaterialPost)).apply(this, arguments));

              _this.vertexShader = VERTEX_SHADER$2;
              _this.fragmentShader = FRAG_SHADER$2;

              _this.vertices = new Float32Array(FACE$2);
              _this.uvs = new Float32Array(FACE_UVS$2);

              _this.buffers = {
                      vertices: null,
                      uvs: null,
                      normals: null,
                      weights: null,
                      weightIndices: null
              };

              _this.valid = {
                      vertices: false,
                      uvs: false,
                      normals: false,
                      weights: false,
                      weightIndices: false
              };
              return _this;
      }

      createClass(MaterialPost, [{
              key: 'render',
              value: function render(dt, scene, viewport, stack, matrix, frameBufferTextureStack) {
                      if (!this.vertices.length) return;

                      get(MaterialPost.prototype.__proto__ || Object.getPrototypeOf(MaterialPost.prototype), 'render', this).apply(this, arguments);

                      if (viewport.program.attribute.aVertexPosition !== undefined) {
                              // Buffer mesh verts
                              if (!this.buffers.vertices[viewport.id] || !this.valid.vertices[viewport.id]) {
                                      this.buffers.vertices[viewport.id] = viewport.webgl.createBuffer();

                                      // Pass vertices to buffer
                                      // TODO: Do this once per vertices being updated (but bind on every frame)
                                      viewport.webgl.bindBuffer(viewport.webgl.ARRAY_BUFFER, this.buffers.vertices[viewport.id]);
                                      viewport.webgl.bufferData(viewport.webgl.ARRAY_BUFFER, this.vertices, viewport.webgl.STATIC_DRAW); // TODO: Allow mesh to define type?

                                      this.valid.vertices[viewport.id] = true;
                              }
                              viewport.webgl.bindBuffer(viewport.webgl.ARRAY_BUFFER, this.buffers.vertices[viewport.id]);

                              // Tell the vertex shader how to read the array
                              viewport.webgl.vertexAttribPointer(viewport.program.attribute.aVertexPosition, 3, viewport.webgl.FLOAT, false, 0, 0);
                              viewport.webgl.enableVertexAttribArray(viewport.program.attribute.aVertexPosition);
                      }

                      // UVs
                      if (viewport.program.attribute.aTextureCoord !== undefined) {
                              // Buffer mesh texture coordinates
                              if (!this.buffers.uvs[viewport.id] || !this.valid.uvs[viewport.id]) {
                                      this.buffers.uvs[viewport.id] = viewport.webgl.createBuffer();

                                      // Pass texture coordinates to buffer
                                      viewport.webgl.bindBuffer(viewport.webgl.ARRAY_BUFFER, this.buffers.uvs[viewport.id]);
                                      viewport.webgl.bufferData(viewport.webgl.ARRAY_BUFFER, this.uvs, viewport.webgl.STATIC_DRAW);

                                      this.valid.uvs = true;
                              }
                              viewport.webgl.bindBuffer(viewport.webgl.ARRAY_BUFFER, this.buffers.uvs[viewport.id]);

                              // Tell the vertex shader how to read the array
                              viewport.webgl.vertexAttribPointer(viewport.program.attribute.aTextureCoord, 2, viewport.webgl.FLOAT, false, 0, 0);
                              viewport.webgl.enableVertexAttribArray(viewport.program.attribute.aTextureCoord);
                      }

                      if (frameBufferTextureStack[frameBufferTextureStack.length - 1].setAsTexture(viewport, 0, viewport.program.uniform.uTexture) === false) return;

                      // Draw
                      viewport.webgl.drawArrays(viewport.webgl.TRIANGLES, 0, this.vertices.length / 3);
              }
      }]);
      return MaterialPost;
}(Material);

var tLightPositionFlatArray = new Float32Array(CONST.MAX_LIGHTS * 3);
var tLightMVPFlatArray = new Float32Array(CONST.MAX_LIGHTS * 16);
var tLighColorFlatArray = new Float32Array(CONST.MAX_LIGHTS * 3);
var tLightIntensity = new Array(CONST.MAX_LIGHTS);
var tLightMaxDistance = new Array(CONST.MAX_LIGHTS);

// Variable Qualifiers

// Qualifiers give a special meaning to the variable. The following qualifiers are available:
//		const – The declaration is of a compile time constant.
//		attribute – Global variables that may change per vertex, that are passed from the OpenGL application to vertex shaders. This qualifier can only be used in vertex shaders. For the shader this is a read-only variable. See Attribute section.
//		uniform – Global variables that may change per primitive [...], that are passed from the OpenGL application to the shaders. This qualifier can be used in both vertex and fragment shaders. For the shaders this is a read-only variable. See Uniform section.
//		varying – used for interpolated data between a vertex shader and a fragment shader. Available for writing in the vertex shader, and read-only in a fragment shader. See Varying section.

// The vertex level shader. This is typically the position on screen.
var VERTEX_SHADER$3 = "\n\tprecision highp float;\n\n\t#define MAX_BONES " + CONST.MAX_BONES + "\n\t#define MAX_LIGHTS " + CONST.MAX_LIGHTS + "\n\n\t#define SKELETAL_ENABLED\n\t#define TEXTURES_ENABLED\n\t#if MAX_LIGHTS > 0\n\t#define SHADOWS_ENABLED\n\t#endif\n\n\tattribute vec4 aVertexPosition;\n\tattribute vec3 aVertexNormal;\n\tattribute vec2 aTextureCoord;\n\tattribute vec4 aVertexWeights;\n\tattribute vec4 aVertexWeightIndices;\n\n\tuniform mat4 uMVCPMatrix; // Model View Camera Projection Matrix\n\tuniform mat4 uMMatrix; // World Matrix\n\tuniform mat4 uViewMatrix; // View Matrix\n\tuniform mat4 uNormalMatrix;\n\n\tuniform mat4 uDeformMatrix[MAX_BONES]; // Bone matrices\n\n\tuniform mat4 uDirectionalLightMVPMatrix; // Light MVP Matrix\n\tvarying vec4 vDirectionalShadowPos;\n\n\t#if MAX_LIGHTS > 0\n\tuniform mat4 uLightMVPMatrix[MAX_LIGHTS]; // Light MVP Matrix\n\tvarying vec4 vShadowPos[MAX_LIGHTS];\n\t#endif\n\n\tvarying vec3 vPositionCoord;\n\tvarying vec2 vTextureCoord;\n\tvarying vec3 vNormal;\n\n\tconst mat4 texUnitConverter = mat4(0.5, 0.0, 0.0, 0.0, 0.0, 0.5, 0.0, 0.0, 0.0, 0.0, 0.5, 0.0, 0.5, 0.5, 0.5, 1.0);\n\t\n\tmat4 boneTransformMat4() {\n\t\tmat4 ret;\n\n\t\t// Weight normalization factor\n\t\tfloat normfac = 1.0 / (aVertexWeights[0] + aVertexWeights[1] + aVertexWeights[2] + aVertexWeights[3]);\n\n\t\t// Weight1 * Bone1 + Weight2 * Bone2\n\t\tret = normfac * aVertexWeights[0] * uDeformMatrix[int(floor(aVertexWeightIndices[0] + 0.5))]\n\t\t\t+ normfac * aVertexWeights[1] * uDeformMatrix[int(floor(aVertexWeightIndices[1] + 0.5))]\n\t\t\t+ normfac * aVertexWeights[2] * uDeformMatrix[int(floor(aVertexWeightIndices[2] + 0.5))]\n\t\t\t+ normfac * aVertexWeights[3] * uDeformMatrix[int(floor(aVertexWeightIndices[3] + 0.5))];\n\n\t\treturn ret;\n\t}\n\n\tvoid main() {\n\t\t// Set the position\n\t\t#ifdef SKELETAL_ENABLED\n\t\t\tif(aVertexWeights[0] + aVertexWeights[1] + aVertexWeights[2] + aVertexWeights[3] > 0.0){\n\t\t\t\tmat4 bt = boneTransformMat4();\n\t\t\t\tvNormal = normalize((bt * vec4(aVertexNormal, 0.0)).xyz);\n\n\t\t\t\tgl_Position = uMVCPMatrix * bt * aVertexPosition;\n\t\t\t}else{\n\t\t\t\tvNormal = normalize(mat3(uNormalMatrix) * aVertexNormal);\n\n\t\t\t\tgl_Position = uMVCPMatrix * aVertexPosition;\n\t\t\t}\n\t\t#else\n\t\t\tvNormal = normalize(mat3(uNormalMatrix) * aVertexNormal);\n\n\t\t\tgl_Position = uMVCPMatrix * aVertexPosition;\n\t\t#endif\n\n\t\tvPositionCoord = vec3(uMMatrix * aVertexPosition);\n\t\t\n        // Set texture coordinate\n        #ifdef TEXTURES_ENABLED\n            vTextureCoord = aTextureCoord;\n\t\t#endif\n\n\t\t#ifdef SHADOWS_ENABLED\n\t\t\tvDirectionalShadowPos = texUnitConverter * uDirectionalLightMVPMatrix * aVertexPosition;\n\n\t\t\tfor(int i=0; i < " + CONST.MAX_LIGHTS + "; ++i){\n\t\t\t\tvShadowPos[i] = uLightMVPMatrix[i] * aVertexPosition;\n\t\t\t\t// vShadowPos[i] = aVertexPosition.xyz - uLightPosition[i];\n\t\t\t}\n\t\t#endif\n\t}\n";

// The fragment (or "pixel") level shader.
//distanceLightWeighting = alpha/(uPointLightingDistance*uPointLightingDistance)
var FRAG_SHADER$3 = "\n\t#extension GL_OES_standard_derivatives: enable\n\n\t#ifdef GL_FRAGMENT_PRECISION_HIGH\n\t\tprecision highp float;\n\t\t// precision highp int;\n\t#else\n\t\tprecision mediump float;\n\t\tprecision mediump int;\n\t#endif\n\n\t#define MAX_LIGHTS " + CONST.MAX_LIGHTS + "\n\n    #ifdef GL_OES_standard_derivatives\n        #define USE_SVM\n    #endif\n\n\t#if MAX_LIGHTS > 0\n\t#define POINT_LIGHTING_ENABLED\n\t#define DIRECTIONAL_SHADOWS_ENABLED\n\t#define POINT_SHADOWS_ENABLED\n\t#endif\n\t#define DIRECTIONAL_LIGHTING_ENABLED\n\t#define SHADOW_BIAS 0.0007\n\t#define FOG_ENABLED\n\t#define TEXTURE_DIFFUSE_ENABLED\n\t#define TEXTURE_SPECULAR_COLOR_ENABLED\n\t#define TEXTURE_AMBIENT_ENABLED\n\t#define TEXTURE_AMBIENT_BLEND_MODE 1 // 0: Additive | 1: Multiply | 2: Screen\n\t#define TEXTURE_AMBIENT_COORDINATES 0 // 0: Use UVs | 1: Use World Coordinates XY | 2: Use World Coordinates XZ\n\t#define TEXTURE_AMBIENT_SIZE 1024.00\n\t#define TEXTURE_AMBIENT_SCALE 1.0\n\t#define TEXTURE_AMBIENT_FLIPY\n\t#define TEXTURE_EMIT_ENABLED\n\n\tvarying vec3 vPositionCoord;\n\tvarying vec2 vTextureCoord;\n\tvarying vec3 vNormal;\n\n\tvarying vec4 vDirectionalShadowPos;\n\t#if MAX_LIGHTS > 0\n\tvarying vec4 vShadowPos[MAX_LIGHTS];\n\t#endif\n\n\tuniform vec3 uCameraPosition;\n\n\tuniform vec3 uSceneAmbientLightColor;\n\n\tuniform vec3 uDirectionalLightColor;\n\tuniform vec3 uDirectionalLightVector;\n\tuniform float uDirectionalLightIntensity;\n\t\n\tuniform sampler2D uDiffuseTexture;\n\tuniform sampler2D uSpecularColorTexture;\n\tuniform sampler2D uSpecularHighlightTexture;\n\tuniform sampler2D uAmbientTexture; // TODO: Should be global illumination texture\n\tuniform sampler2D uEmitTexture;\n\n\tuniform vec4 uFogColor;\n\tuniform float uFogDensity;\n\tuniform float uFogDistance;\n\n\t#if MAX_LIGHTS > 0\n\tuniform vec3 uLightPosition[MAX_LIGHTS]; // Light positions\n\tuniform vec3 uLightColor[MAX_LIGHTS];\n\tuniform float uLightIntensity[MAX_LIGHTS];\n\tuniform float uLightMaxDistance[MAX_LIGHTS];\n\tuniform samplerCube uPointLightDepth[MAX_LIGHTS];\n\t#endif\n\tuniform sampler2D uDirectionalLightDepth;\n\tuniform float uDirectionalLightDepthSize;\n\n\tuniform vec3 uMaterialDiffuseColor;\n\tuniform vec3 uMaterialSpecularColor;\n\tuniform vec3 uMaterialAmbientColor;\n\tuniform vec3 uMaterialEmitColor;\n\tuniform vec3 uMaterialTransmissionFilter;\n\tuniform float uMaterialDissolve;\n\tuniform float uMaterialSpecularExponent;\n\tuniform float uMaterialEmitExponent;\n\n\tuniform float uShadowBias;\n\n\tfloat unpackDepthVec2(in vec2 color) {\n\t\tconst vec2 bitShift = vec2(1.0 / 255.0, 1.0);\n\t\treturn dot(color, bitShift);\n\t}\n\n\tfloat unpackDepthVec3(in vec3 color) {\n\t\tconst vec3 bitShift = vec3(1.0 / (255.0 * 255.0), 1.0 / 255.0, 1.0);\n\t\treturn dot(color, bitShift);\n\t}\n\n\tfloat unpackDepthVec4(in vec4 color) {\n\t\tconst vec4 bitShift = vec4(1.0 / (255.0 * 255.0 * 255.0), 1.0 / (255.0 * 255.0), 1.0 / 255.0, 1.0);\n\t\treturn dot(color, bitShift);\n\t}\n\n\tvec4 shadow_filter(vec2 uv_shadowMap, in sampler2D depthColorTexture){\n        // if (!use_filter) {\n        \t// return texture2D(depthColorTexture, uv_shadowMap);\n\t\t// }\n\t\t\n        vec4 result = vec4(0.0);\n\t\t\n\t\tfor (int x = -1; x <= 1; x++) {\n        \tfor (int y = -1; y <= 1; y++) {\n        \t\tfloat x_l = (uv_shadowMap.x - float(x) / uDirectionalLightDepthSize);\n        \t\tfloat y_l = (uv_shadowMap.y - float(y) / uDirectionalLightDepthSize);\n        \t\tvec2 lookup = vec2(x_l, y_l);\n        \t\tresult += texture2D(depthColorTexture, lookup).rgba; //get(x,y);\n        \t}\n\t\t}\n\t\t\n        return result / 9.0;\n\t}\n\t\n\tfloat linstep(float low, float high, float v){\n\t\treturn clamp((v-low)/(high-low), 0.0, 1.0);\n\t}\n\n\tfloat directionalShadow(in vec4 shadowPosition, in sampler2D depthColorTexture) {\n\t\t#ifdef USE_SVM\n\t\t\tvec4 shadowMapColor = shadow_filter(shadowPosition.xy, depthColorTexture);\n\t\t\tfloat distance = shadowPosition.z;\n\t\t\tfloat mean = unpackDepthVec3(shadowMapColor.rgb);\n\n\t\t\t// Surface is fully lit\n\t\t\tif(distance < mean) return 1.0;\n\n\t\t\tfloat depth_2 = shadowMapColor.a;\n\n\t\t\t// The fragment is either in shadow or penumbra. We now use chebyshev's upperBound to check\n\t\t\t// How likely this pixel is to be lit (p_max)\n\t\t\tfloat variance = depth_2 - pow(mean, 2.00);\n\t\t\tvariance = max(variance, uShadowBias);\n\n\t\t\tfloat d = distance - mean;\n\t\t\tfloat p_max = variance / (variance + d*d);\n\t\t\tp_max = clamp(p_max, 0.0, 1.0);\n\t\t\tfloat p = smoothstep(distance - 0.02, distance, mean);\n\n\t\t\treturn p_max;\n\t\t\t\n\t\t\t// float p_max = linstep(0.2, 1.0, variance / (variance + d*d));\n\t\t\t// float p = smoothstep(distance - 0.02, distance, mean);\n\t\t\t// return (max(p, p_max) > 1.0 - uShadowBias)?1.0:0.0;\n\t\t\t// return clamp(max(p, p_max), 0.0, 1.0);\n\t\t\t// shadowCoeff = 0 = shadow\n\t\t\t// shadowCoeff = 1 = no shadow\n\t\t#else\n\t\t\tvec3 fragmentDepth = shadowPosition.xyz;\n\t\t\tfragmentDepth.z -= uShadowBias;\n\n\t\t\tfloat texelDepth = unpackDepthVec4(texture2D(depthColorTexture, fragmentDepth.xy));\n\t\t\treturn (fragmentDepth.z < texelDepth) ? 1.0 : 0.0;\n\n\t\t\t// float texelSize = 1.0 / 1024.0;\n\t\t\t// float amountInLight = 0.0;\n\n\t\t\t// for (int x = -1; x <= 1; x++) {\n\t\t\t// \tfor (int y = -1; y <= 1; y++) {\n\t\t\t// \t\tfloat texelDepth = unpackDepthVec4(texture2D(depthColorTexture, fragmentDepth.xy + vec2(x, y) * texelSize));\n\n\t\t\t// \t\tif (fragmentDepth.z < texelDepth) {\n\t\t\t// \t\t\tamountInLight += 1.0;\n\t\t\t// \t\t}\n\t\t\t// \t}\n\t\t\t// }\n\t\t\t// amountInLight /= 9.0;\n\n\t\t\t// return amountInLight;\n\t\t#endif\n\t}\n\n\tfloat omnidirectionalShadow(in vec3 direction, in samplerCube sampler) {\n\t\tdirection *= vec3(1.0, -1.0, 1.0);\n\t\tfloat vertexDepth = clamp(length(direction), 0.0, 1.0);\n\t\tfloat shadowMapDepth = unpackDepthVec4(textureCube(sampler, direction)) + SHADOW_BIAS;\n\n\t\treturn (vertexDepth > shadowMapDepth) ? 0.0 : 1.0;\n\t}\n\n\tfloat calculateFog(){\n\t\t// get the distance of the pixel\n\t\tfloat perspective_far = uFogDistance;\n\t\tfloat fog_cord = (gl_FragCoord.z / gl_FragCoord.w) / perspective_far;\n\n\t\t// density of fog\n\t\tfloat fog_density = uFogDensity;\n\n\t\t// increase fog by density\n\t\t// fog also thickens with distance\n\t\treturn fog_cord * fog_density;\n\t}\n\n\tvoid main(void) {\n\t\tvec3 N = normalize(vNormal);\n\t\tvec3 V = normalize(uCameraPosition); // we are in Eye Coordinates, so EyePos is (0,0,0) \n\n\t\t#if defined(DIRECTIONAL_LIGHTING_ENABLED) || defined(POINT_LIGHTING_ENABLED)\n\n\t\t\t// Calculate lighting\n\t\t\tvec4 Iamb = vec4(0.0);\n\t\t\tvec4 Ispec = vec4(0.0);\n\t\t\tvec4 Idiff = vec4(0.0);\n\t\t\tvec4 Iemit = vec4(0.0);\n\n\t\t\t#ifdef DIRECTIONAL_LIGHTING_ENABLED\n\t\t\t\tvec3 L = normalize(-uDirectionalLightVector); \n\t\t\t\tvec3 R = normalize(-reflect(L,N)); \n\t\t\t\n\t\t\t\t//calculate Ambient Term: \n\t\t\t\t// Iamb += uSceneAmbientLightColor; \n\n\t\t\t\t#ifdef DIRECTIONAL_SHADOWS_ENABLED\n\t\t\t\t\tfloat shadow = directionalShadow(vDirectionalShadowPos, uDirectionalLightDepth);\n\t\t\t\t#else\n\t\t\t\t\tfloat shadow = 1.0;\n\t\t\t\t#endif\n\t\t\t\t\n\t\t\t\t//calculate Diffuse Term: \n\t\t\t\tIdiff += vec4(clamp(shadow * uDirectionalLightColor * max(dot(N,L), 0.0), 0.0, 1.0), 0.0);\n\t\t\t\n\t\t\t\t// calculate Specular Term:\n\t\t\t\tIspec += vec4(clamp(shadow * uDirectionalLightColor * pow(max(dot(R,V),0.0),0.3*uMaterialSpecularExponent), 0.0, 1.0), 0.0);\n\t\t\t#endif\n\t\t\t\n\t\t\t#ifdef POINT_LIGHTING_ENABLED\n\t\t\t\tfor (int i=0; i<MAX_LIGHTS; i++){\n\n\t\t\t\t\tvec3 L = normalize(uLightPosition[i] - vPositionCoord); \n\n\t\t\t\t\tif(length(L) > 0.0){\n\t\t\t\t\t\tvec3 R = normalize(-reflect(L,N)); \n\n\t\t\t\t\t\t#ifdef POINT_SHADOWS_ENABLED\n\t\t\t\t\t\t\tfloat shadow = omnidirectionalShadow(L, uPointLightDepth[i]); //lightPosition, in samplerCube sampler\n\t\t\t\t\t\t\tif(shadow <= 0.0) continue;\n\t\t\t\t\t\t#else\n\t\t\t\t\t\t\tfloat shadow = 1.0;\n\t\t\t\t\t\t#endif\n\n\t\t\t\t\t\t//calculate Ambient Term: \n\t\t\t\t\t\t// vec4 Iamb = vec4(uMaterialAmbientColor, 1.0); \n\t\t\t\t\t\t\n\t\t\t\t\t\t//calculate Diffuse Term: \n\t\t\t\t\t\tIdiff += vec4(clamp(uLightColor[i] * max(dot(N,L), 0.0), 0.0, 1.0), 0.0);\n\t\t\t\t\t\n\t\t\t\t\t\t// calculate Specular Term:\n\t\t\t\t\t\tIspec += vec4(clamp(uLightColor[i] * pow(max(dot(R,V),0.0),0.3*uMaterialSpecularExponent), 0.0, 1.0), 0.0);\n\t\t\t\t\t}\n\t\t\t\t}\n\t\t\t#endif\n\n\t\t\t#if defined(TEXTURE_AMBIENT_ENABLED) || defined(TEXTURE_DIFFUSE_ENABLED)\n\t\t\t\t#if defined(TEXTURE_AMBIENT_ENABLED)\n\t\t\t\t\t\tIamb *= texture2D(uAmbientTexture, vTextureCoord);\n\t\t\t\t\t#endif\n\t\t\t\t#if defined(TEXTURE_DIFFUSE_ENABLED)\n\t\t\t\t\tIamb *= texture2D(uDiffuseTexture, vTextureCoord);\n\t\t\t\t#endif\n\t\t\t#else\n\t\t\t\tIamb *= vec4(uSceneAmbientLightColor * uMaterialAmbientColor, 1.0);\n\t\t\t#endif\n\n\t\t\t#ifdef TEXTURE_DIFFUSE_ENABLED\n\t\t\t\tIdiff *= texture2D(uDiffuseTexture, vTextureCoord);\n\t\t\t#else\n\t\t\t\tIdiff *= vec4(uMaterialDiffuseColor, 1.0);\n\t\t\t#endif\n\n\t\t\t#ifdef TEXTURE_SPECULAR_COLOR_ENABLED\n\t\t\t\tIspec *= texture2D(uSpecularColorTexture, vTextureCoord);\n\t\t\t#else\n\t\t\t\tIspec *= vec4(uMaterialSpecularColor, 1.0);\n\t\t\t#endif\n\n\t\t\t#ifdef TEXTURE_EMIT_ENABLED\n\t\t\t\tIemit += texture2D(uEmitTexture, vTextureCoord);\n\t\t\t#else\n\t\t\t\tIemit += vec4(uMaterialEmitColor, 1.0);\n\t\t\t#endif\n\t\t\tIemit.rgb *= uMaterialEmitExponent;\n\n\t\t\t// Final Color = Diffuse + Specular + Ambient + Emit\n\t\t\tgl_FragColor = Idiff + Ispec + Iamb + Iemit; \n\t\t  \tgl_FragColor.a *= uMaterialDissolve;\n\t\t#else\n\t\t\t#ifdef TEXTURE_DIFFUSE_ENABLED\n\t\t\t\tgl_FragColor = texture2D(uDiffuseTexture, vTextureCoord);\n\t\t\t#else\n\t\t\t\tgl_FragColor = vec4(uMaterialDiffuseColor, 1.0);\n\t\t\t#endif\n\t\t#endif\n\n\t\t// Discard if there's no pixel to show. This helps when there's empty space background\n\t\t// TODO: Is there a way to optimize this?\n\t\tif (gl_FragColor.a == 0.0) discard;\n\n\t\t#ifdef FOG_ENABLED\n\t\t\tfloat fog = calculateFog();\n\n\t\t\t// mix together the frag color with the fog color\n\t\t\tgl_FragColor = mix(uFogColor, gl_FragColor, clamp(1.0-fog,0.0,1.0));\n\t\t#endif\n\t}\n";

var MaterialPhong = function (_Material) {
inherits(MaterialPhong, _Material);

function MaterialPhong() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref$ambientColor = _ref.ambientColor,
      ambientColor = _ref$ambientColor === undefined ? new Color(0.0, 0.0, 0.0, 1.0) : _ref$ambientColor,
      _ref$diffuseColor = _ref.diffuseColor,
      diffuseColor = _ref$diffuseColor === undefined ? new Color(1.0, 1.0, 1.0, 1.0) : _ref$diffuseColor,
      _ref$specularColor = _ref.specularColor,
      specularColor = _ref$specularColor === undefined ? new Color(0.0, 0.0, 0.0, 1.0) : _ref$specularColor,
      _ref$specularExponent = _ref.specularExponent,
      specularExponent = _ref$specularExponent === undefined ? 128.0 : _ref$specularExponent,
      _ref$emitColor = _ref.emitColor,
      emitColor = _ref$emitColor === undefined ? new Color(0.0, 0.0, 0.0, 0.0) : _ref$emitColor,
      _ref$emitExponent = _ref.emitExponent,
      emitExponent = _ref$emitExponent === undefined ? 1 : _ref$emitExponent,
      _ref$transmissionFilt = _ref.transmissionFilter,
      transmissionFilter = _ref$transmissionFilt === undefined ? new Color(1.0, 1.0, 1.0, 1.0) : _ref$transmissionFilt,
      _ref$dissolve = _ref.dissolve,
      dissolve = _ref$dissolve === undefined ? 1 : _ref$dissolve,
      _ref$lighting = _ref.lighting,
      lighting = _ref$lighting === undefined ? 1 : _ref$lighting,
      _ref$shadowCastingMod = _ref.shadowCastingMode,
      shadowCastingMode = _ref$shadowCastingMod === undefined ? 1 : _ref$shadowCastingMod,
      _ref$receiveShadows = _ref.receiveShadows,
      receiveShadows = _ref$receiveShadows === undefined ? true : _ref$receiveShadows,
      _ref$shadowBias = _ref.shadowBias,
      shadowBias = _ref$shadowBias === undefined ? CONST.DEFAULT_SHADOW_BIAS : _ref$shadowBias,
      _ref$skeletal = _ref.skeletal,
      skeletal = _ref$skeletal === undefined ? false : _ref$skeletal,
      _ref$ambientTextureCo = _ref.ambientTextureCoordinateSpace,
      ambientTextureCoordinateSpace = _ref$ambientTextureCo === undefined ? "UVtoXZ" : _ref$ambientTextureCo,
      _ref$ambientTextureFl = _ref.ambientTextureFlipY,
      ambientTextureFlipY = _ref$ambientTextureFl === undefined ? false : _ref$ambientTextureFl,
      _ref$receivesFog = _ref.receivesFog,
      receivesFog = _ref$receivesFog === undefined ? true : _ref$receivesFog,
      _ref$ambientTexture = _ref.ambientTexture,
      ambientTexture = _ref$ambientTexture === undefined ? null : _ref$ambientTexture,
      _ref$diffuseTexture = _ref.diffuseTexture,
      diffuseTexture = _ref$diffuseTexture === undefined ? null : _ref$diffuseTexture,
      _ref$specularColorTex = _ref.specularColorTexture,
      specularColorTexture = _ref$specularColorTex === undefined ? null : _ref$specularColorTex,
      _ref$specularHighligh = _ref.specularHighlightTexture,
      specularHighlightTexture = _ref$specularHighligh === undefined ? null : _ref$specularHighligh,
      _ref$emitTexture = _ref.emitTexture,
      emitTexture = _ref$emitTexture === undefined ? null : _ref$emitTexture,
      _ref$alphaTexture = _ref.alphaTexture,
      alphaTexture = _ref$alphaTexture === undefined ? null : _ref$alphaTexture,
      _ref$bumpMapTexture = _ref.bumpMapTexture,
      bumpMapTexture = _ref$bumpMapTexture === undefined ? null : _ref$bumpMapTexture,
      _ref$displacementText = _ref.displacementTexture,
      displacementTexture = _ref$displacementText === undefined ? null : _ref$displacementText,
      _ref$decalTexture = _ref.decalTexture,
      decalTexture = _ref$decalTexture === undefined ? null : _ref$decalTexture;

  classCallCheck(this, MaterialPhong);

  var _this = possibleConstructorReturn(this, (MaterialPhong.__proto__ || Object.getPrototypeOf(MaterialPhong)).apply(this, arguments));

  _this.vertexShader = VERTEX_SHADER$3;
  _this.fragmentShader = FRAG_SHADER$3;

  _this.ambientColor = ambientColor;
  _this.diffuseColor = diffuseColor;
  _this.specularColor = specularColor;
  _this.emitColor = emitColor;
  _this.specularExponent = specularExponent;
  _this.emitExponent = emitExponent;
  _this.transmissionFilter = transmissionFilter;
  _this.dissolve = dissolve;

  _this.ambientTexture = ambientTexture;
  _this.diffuseTexture = diffuseTexture;
  _this.specularColorTexture = specularColorTexture;
  _this.specularHighlightTexture = specularHighlightTexture;
  _this.emitTexture = emitTexture;
  _this.alphaTexture = alphaTexture;
  _this.bumpMapTexture = bumpMapTexture;
  _this.displacementTexture = displacementTexture;
  _this.decalTexture = decalTexture;
  _this.shadowBias = shadowBias;

  _this.lighting = lighting;
  _this.skeletal = skeletal; // TODO Rename to "skinning"?

  _this.shadowCastingMode = shadowCastingMode;
  _this.receiveShadows = receiveShadows;
  _this.receivesFog = receivesFog;

  _this.ambientTextureCoordinateSpace = ambientTextureCoordinateSpace; // TODO: Name this var better?
  _this.ambientTextureFlipY = ambientTextureFlipY; // TODO: Name this var better?

  _this.buildProgram(); // TODO: Where should "autobuild" this live?
  return _this;
}

createClass(MaterialPhong, [{
  key: "render",
  value: function render(dt, scene, viewport, camera, stack, viewMatrix, modelMatrix, frameBufferTextureStack) {
    // Set uniforms (uPMatrix, uCMatrix, etc...)
    get(MaterialPhong.prototype.__proto__ || Object.getPrototypeOf(MaterialPhong.prototype), "render", this).call(this, dt, scene, viewport, camera, stack, viewMatrix, modelMatrix, frameBufferTextureStack);

    // Set current textures
    if (this.diffuseTexture) {
      if (this.diffuseTexture.setAsTexture(viewport, 0, viewport.program.uniform.uDiffuseTexture) === false) return;
    }

    if (this.ambientTexture) {
      if (this.ambientTexture.setAsTexture(viewport, 1, viewport.program.uniform.uAmbientTexture) === false) return;
    }

    if (this.specularColorTexture) {
      if (this.specularColorTexture.setAsTexture(viewport, 2, viewport.program.uniform.uSpecularColorTexture) === false) return;
    }

    if (this.specularHighlightTexture) {
      if (this.specularHighlightTexture.setAsTexture(viewport, 3, viewport.program.uniform.uSpecularHighlightTexture) === false) return;
    }

    if (this.emitTexture) {
      if (this.emitTexture.setAsTexture(viewport, 4, viewport.program.uniform.uEmitTexture) === false) return;
    }

    viewport.webgl.uniform4fv(viewport.program.uniform.uDiffuseColor, this.diffuseColor.toArray());

    // Set the material properties
    viewport.webgl.uniform3fv(viewport.program.uniform.uMaterialDiffuseColor, this.diffuseColor.toVec3Array());
    viewport.webgl.uniform3fv(viewport.program.uniform.uMaterialSpecularColor, this.specularColor.toVec3Array());
    viewport.webgl.uniform3fv(viewport.program.uniform.uMaterialAmbientColor, this.ambientColor.toVec3Array());
    viewport.webgl.uniform3fv(viewport.program.uniform.uMaterialEmitColor, this.emitColor.toVec3Array());
    viewport.webgl.uniform3fv(viewport.program.uniform.uMaterialTransmissionFilter, this.transmissionFilter.toVec3Array());
    viewport.webgl.uniform1f(viewport.program.uniform.uMaterialSpecularExponent, this.specularExponent);
    viewport.webgl.uniform1f(viewport.program.uniform.uMaterialEmitExponent, this.emitExponent);
    viewport.webgl.uniform1f(viewport.program.uniform.uMaterialDissolve, this.dissolve);

    // Set global lighting
    var shadowCount = 0;

    viewport.webgl.uniform3fv(viewport.program.uniform.uSceneAmbientLightColor, scene.ambientLightColor.toVec3Array()); //TODO: [Optimization] Prevent creating Float32Arrays every time
    viewport.webgl.uniform3fv(viewport.program.uniform.uDirectionalLightColor, scene.directionalLight.diffuseColor.toVec3Array());
    viewport.webgl.uniform3fv(viewport.program.uniform.uDirectionalLightVector, scene.directionalLight.vector);
    viewport.webgl.uniform1f(viewport.program.uniform.uShadowBias, this.shadowBias);

    if (CONST.DIRECTIONAL_SHADOWS_ENABLED) {
      if (viewport.program.uniform["uDirectionalLightDepth"] && frameBufferTextureStack[frameBufferTextureStack.length - 1]) {
        if (frameBufferTextureStack[frameBufferTextureStack.length - 1][shadowCount].setAsTexture(viewport, 5 + shadowCount, viewport.program.uniform["uDirectionalLightDepth"]) === false) return;
        viewport.webgl.uniform1f(viewport.program.uniform.uDirectionalLightDepthSize, frameBufferTextureStack[frameBufferTextureStack.length - 1][shadowCount].width);
      }

      viewport.webgl.uniformMatrix4fv(viewport.program.uniform.uDirectionalLightMVPMatrix, false, scene.directionalLight.getMVPMatrix(modelMatrix, viewport));

      shadowCount++;
    }

    // Build dynamic point lights
    if (viewport.program.uniform["uLightPosition[0]"] !== undefined) {
      tLightPositionFlatArray.fill(0);
      tLightMVPFlatArray.fill(0);
      tLighColorFlatArray.fill(0);
      tLightIntensity.fill(0);
      tLightMaxDistance.fill(0);

      for (var lightIndex = 0; lightIndex < CONST.MAX_LIGHTS; lightIndex++) {
        var light = scene.lights[lightIndex];

        if (CONST.POINT_SHADOWS_ENABLED && viewport.program.uniform["uPointLightDepth[" + lightIndex + "]"] && frameBufferTextureStack[frameBufferTextureStack.length - 1]) {
          if (frameBufferTextureStack[frameBufferTextureStack.length - 1][lightIndex + shadowCount].setAsTexture(viewport, 5 + shadowCount + lightIndex, viewport.program.uniform["uPointLightDepth[" + lightIndex + "]"]) === false) return;
        }

        if (!light || !light.enabled) continue;

        for (var j = 0; j < 3; j++) {
          tLightPositionFlatArray[lightIndex * 3 + j] = light.transform.xyz[j];
        }

        var tMVP = light.getMVPMatrix(modelMatrix, viewport);
        for (var j = 0; j < 16; j++) {
          tLightMVPFlatArray[lightIndex * 16 + j] = tMVP[j];
        }

        for (var j = 0; j < 3; j++) {
          tLighColorFlatArray[lightIndex * 3 + j] = light.diffuseColor.toArray()[j];
        }

        tLightIntensity[lightIndex] = light.intensity * 1;

        tLightMaxDistance[lightIndex] = light.maxDistance * 1;
      }

      shadowCount += CONST.MAX_LIGHTS;

      viewport.webgl.uniform3fv(viewport.program.uniform["uLightPosition[0]"], tLightPositionFlatArray);
      viewport.webgl.uniformMatrix4fv(viewport.program.uniform["uLightMVPMatrix[0]"], false, tLightMVPFlatArray);
      viewport.webgl.uniform3fv(viewport.program.uniform["uLightColor[0]"], tLighColorFlatArray);
      // viewport.webgl.uniform1fv(viewport.program.uniform["uLightIntensity[0]"], tLightIntensity);
      // viewport.webgl.uniform1fv(viewport.program.uniform["uLightMaxDistance[0]"], tLightMaxDistance);
    }

    // Set fog
    viewport.webgl.uniform4fv(viewport.program.uniform.uFogColor, scene.fogColor.toArray());
    viewport.webgl.uniform1f(viewport.program.uniform.uFogDensity, scene.fogDensity);
    viewport.webgl.uniform1f(viewport.program.uniform.uFogDistance, viewport.camera._farPlane);

    // Enable transparency
    viewport.webgl.blendFunc(viewport.webgl.SRC_ALPHA, viewport.webgl.ONE_MINUS_SRC_ALPHA);
    viewport.webgl.enable(viewport.webgl.BLEND);
  }
}, {
  key: "buildProgram",
  value: function buildProgram() {
    this.vertexShader = VERTEX_SHADER$3;
    this.fragmentShader = FRAG_SHADER$3;

    if (!CONST.SHADOWS_VSM_ENABLED) this.fragmentShader = this.fragmentShader.replace("#define USE_SVM", "");

    if (!CONST.POINT_SHADOWS_ENABLED) {
      this.vertexShader = this.vertexShader.replace("#define POINT_SHADOWS_ENABLED", "");
      this.fragmentShader = this.fragmentShader.replace("#define POINT_SHADOWS_ENABLED", "");
    }

    if (!CONST.LIGHTING_ENABLED) {
      this.vertexShader = this.vertexShader.replace("#define DIRECTIONAL_SHADOWS_ENABLED", "");
      this.fragmentShader = this.fragmentShader.replace("#define DIRECTIONAL_SHADOWS_ENABLED", "");
      this.vertexShader = this.vertexShader.replace("#define POINT_SHADOWS_ENABLED", "");
      this.fragmentShader = this.fragmentShader.replace("#define POINT_SHADOWS_ENABLED", "");
      this.vertexShader = this.vertexShader.replace("#define DIRECTIONAL_LIGHTING_ENABLED", "");
      this.fragmentShader = this.fragmentShader.replace("#define DIRECTIONAL_LIGHTING_ENABLED", "");
      this.vertexShader = this.vertexShader.replace("#define POINT_LIGHTING_ENABLED", "");
      this.fragmentShader = this.fragmentShader.replace("#define POINT_LIGHTING_ENABLED", "");
    }

    switch (this.lighting) {
      case 1:
        // Leave it alone
        break;
      case 0:
      default:
        this.vertexShader = this.vertexShader.replace("#define DIRECTIONAL_LIGHTING_ENABLED", "");
        this.fragmentShader = this.fragmentShader.replace("#define DIRECTIONAL_LIGHTING_ENABLED", "");
        this.vertexShader = this.vertexShader.replace("#define POINT_LIGHTING_ENABLED", "");
        this.fragmentShader = this.fragmentShader.replace("#define POINT_LIGHTING_ENABLED", "");
        this.vertexShader = this.vertexShader.replace("#define SHADOWS_ENABLED", "");
        this.fragmentShader = this.fragmentShader.replace("#define SHADOWS_ENABLED", "");
        break;
    }

    if (!this.ambientTexture && !this.diffuseTexture && !this.specularColorTexture && !this.specularHighlightTexture && !this.alphaTexture && !this.bumpMapTexture && !this.displacementTexture && !this.decalTexture) {
      this.vertexShader = this.vertexShader.replace("#define TEXTURES_ENABLED", "");
      this.fragmentShader = this.fragmentShader.replace("#define TEXTURES_ENABLED", "");
    }

    if (!this.ambientTexture) {
      this.fragmentShader = this.fragmentShader.replace("#define TEXTURE_AMBIENT_ENABLED", "");
    } else {
      this.fragmentShader = this.fragmentShader.replace("#define TEXTURE_AMBIENT_SIZE 1024.00", "#define TEXTURE_AMBIENT_SIZE " + this.ambientTexture.width.toFixed(2));
      if (this.ambientTexture.scale) this.fragmentShader = this.fragmentShader.replace("#define TEXTURE_AMBIENT_SCALE 1.0", "#define TEXTURE_AMBIENT_SCALE " + this.ambientTexture.scale.toFixed(2));

      switch (this.ambientTextureCoordinateSpace) {
        case "UV":
          this.fragmentShader = this.fragmentShader.replace("#define TEXTURE_AMBIENT_COORDINATES 0", "#define TEXTURE_AMBIENT_COORDINATES 0");
          break;
        case "UVtoXY":
          this.fragmentShader = this.fragmentShader.replace("#define TEXTURE_AMBIENT_COORDINATES 0", "#define TEXTURE_AMBIENT_COORDINATES 1");
          break;
        case "UVtoXZ":
          this.fragmentShader = this.fragmentShader.replace("#define TEXTURE_AMBIENT_COORDINATES 0", "#define TEXTURE_AMBIENT_COORDINATES 2");
          break;
      }
      if (this.ambientTextureCoordinateSpace !== "WORLD") {
        this.fragmentShader = this.fragmentShader.replace("#define TEXTURE_AMBIENT_COORDINATES 1", "#define TEXTURE_AMBIENT_COORDINATES 0");
      }
      if (!this.ambientTextureFlipY) {
        this.fragmentShader = this.fragmentShader.replace("#define TEXTURE_AMBIENT_FLIPY", "");
      }
    }

    if (!this.diffuseTexture) {
      this.fragmentShader = this.fragmentShader.replace("#define TEXTURE_DIFFUSE_ENABLED", "");
    }

    if (!this.specularColorTexture) {
      this.fragmentShader = this.fragmentShader.replace("#define TEXTURE_SPECULAR_COLOR_ENABLED", "");
    }

    if (!this.specularHighlightTexture) {
      this.fragmentShader = this.fragmentShader.replace("#define TEXTURE_SPECULAR_HIGHLIGHT_ENABLED", "");
    }

    if (!this.emitTexture) {
      this.fragmentShader = this.fragmentShader.replace("#define TEXTURE_EMIT_ENABLED", "");
    }

    if (!this.skeletal) {
      this.vertexShader = this.vertexShader.replace("#define SKELETAL_ENABLED", "");
    }

    if (!this.receivesFog) {
      this.fragmentShader = this.fragmentShader.replace("#define FOG_ENABLED", "");
    }

    if (!this.receiveShadows) {
      this.vertexShader = this.vertexShader.replace("#define SHADOWS_ENABLED", "");
      this.fragmentShader = this.fragmentShader.replace("#define SHADOWS_ENABLED", "");
    }
  }
}]);
return MaterialPhong;
}(Material);

var VERTEX_SHADER$4 = '\n\tprecision highp float;\n\n\tattribute vec3 aVertexPosition;\n\tattribute vec2 aTextureCoord;\n\n\tuniform mat4 uMVCPMatrix; // Model View Camera Projection Matrix\n\n\tuniform vec2 uAtlasSize;\n\tuniform vec2 uMapSize;\n\tuniform vec2 uTileSize;\n\t\n\tvarying vec2 vPixelCoord;\n\tvarying vec2 vTexCoord;\n\n\tvarying vec2 vAtlasSize;\n\tvarying vec2 vMapSize;\n\tvarying vec2 vTileSize;\n\n\tvoid main() {\n\t\tvPixelCoord = aTextureCoord;\n\t\tvTexCoord = vPixelCoord * (uAtlasSize / (uAtlasSize / uMapSize)) * uTileSize;\n\n\t\tvTileSize = uTileSize;\n\t\tvAtlasSize = uAtlasSize;\n\n\t\tgl_Position = uMVCPMatrix * vec4(aVertexPosition, 1.0);\n\t}\n';

var FRAG_SHADER$4 = '\n\t#ifdef GL_FRAGMENT_PRECISION_HIGH\n\t\tprecision highp float;\n\t#else\n\t\tprecision mediump float;\n\t#endif\n\n\tuniform sampler2D uAtlasTexture;\n\tuniform sampler2D uMapTexture;\n\n\tvarying vec2 vPixelCoord;\n\tvarying vec2 vTexCoord;\n\n\tvarying vec2 vAtlasSize;\n\tvarying vec2 vMapSize;\n\tvarying vec2 vTileSize;\n\n\tvoid main(void) {\n\t\t// Grab the tile number (x,y)\n\t\tvec4 tile = texture2D(uMapTexture, vPixelCoord);\n\t\tif(tile.a == 0.0 || (tile.x == 1.0 && tile.y == 1.0)) {\n\t\t\tdiscard;\n\t\t}\n\t\t// gl_FragColor = vec4(tile.x * 30.0, tile.y * 30.0, 0.0, 1.0);\n\t\t\n\t\t// Grab the tile\'s texture frag color\n\t\tvec2 spriteOffset = floor(tile.xy * 256.0) * vTileSize;\n\t\tvec2 spriteCoord = mod(vTexCoord, vTileSize);\n\t\tgl_FragColor = tile.a * texture2D(uAtlasTexture, (spriteOffset + spriteCoord) / vAtlasSize);\n\t}\n';

var MaterialTilemap = function (_Material) {
inherits(MaterialTilemap, _Material);

function MaterialTilemap() {
  var name = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : "";

  var _ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
      _ref$cullFace = _ref.cullFace,
      cullFace = _ref$cullFace === undefined ? false : _ref$cullFace,
      _ref$cullFaceMode = _ref.cullFaceMode,
      cullFaceMode = _ref$cullFaceMode === undefined ? "BACK" : _ref$cullFaceMode,
      _ref$frontFace = _ref.frontFace,
      frontFace = _ref$frontFace === undefined ? "CCW" : _ref$frontFace;

  classCallCheck(this, MaterialTilemap);

  var _this = possibleConstructorReturn(this, (MaterialTilemap.__proto__ || Object.getPrototypeOf(MaterialTilemap)).call(this, name));

  _this.vertexShader = VERTEX_SHADER$4;
  _this.fragmentShader = FRAG_SHADER$4;

  _this.cullFace = cullFace;
  _this.cullFaceMode = cullFaceMode;
  _this.frontFace = frontFace;
  return _this;
}

createClass(MaterialTilemap, [{
  key: 'render',
  value: function render(dt, scene, viewport, camera, stack, viewMatrix, modelMatrix, frameBufferTextureStack) {
    // Set uniforms (uMVMatrix, etc...)
    get(MaterialTilemap.prototype.__proto__ || Object.getPrototypeOf(MaterialTilemap.prototype), 'render', this).call(this, dt, scene, viewport, camera, stack, viewMatrix, modelMatrix, frameBufferTextureStack);

    // Set current textures
    if (this.map) {
      if (this.map.setAsTexture(viewport, 0, viewport.program.uniform.uMapTexture) === false) return;
    }

    if (this.atlas) {
      if (this.atlas.setAsTexture(viewport, 1, viewport.program.uniform.uAtlasTexture) === false) return;
    }

    if (this.cullFace) {
      viewport.webgl.enable(viewport.webgl.CULL_FACE);
      viewport.webgl.cullFace(viewport.webgl[this.cullFaceMode]);
      viewport.webgl.frontFace(viewport.webgl[this.frontFace]);
    } else {
      viewport.webgl.disable(viewport.webgl.CULL_FACE);
    }

    // viewport.webgl.uniform2fv(viewport.program.uniform.inverseAtlasTextureSize, [1 / this.atlas.width, 1 / this.atlas.height]);
    // viewport.webgl.uniform2fv(viewport.program.uniform.inverseMapTextureSize, [1 / this.map.width, 1 / this.map.height]);
    viewport.webgl.uniform2fv(viewport.program.uniform.uAtlasSize, [this.atlas.width, this.atlas.height]);
    viewport.webgl.uniform2fv(viewport.program.uniform.uMapSize, [this.map.width, this.map.height]);
    viewport.webgl.uniform2fv(viewport.program.uniform.uTileSize, stack[stack.length - 2].tileSize);
    // viewport.webgl.uniform1f(viewport.program.uniform.inverseTileSize, 1 / this.tileSize);
  }
}]);
return MaterialTilemap;
}(Material);

'use strict';

var BONE_MAX = 64;

var VERTEX_SHADER$5 = '\n\t#define SKELETAL_ENABLED\n\n\tattribute vec3 aVertexPosition, barycentric;\n\tattribute vec4 aVertexWeights;\n\tattribute vec4 aVertexWeightIndices;\n\n\tuniform mat4 uMVCPMatrix; // Model View Camera Projection Matrix\n\tuniform mat4 uMMatrix; // Model\n\tuniform mat4 uTMatrix; // Entity\n\tuniform mat4 uCMatrix; // Camera\n\tuniform mat4 uMVMatrix;// Inverse Camera\n\tuniform mat4 uPMatrix; // Projection\n\tuniform mat4 uNormalMatrix;\n\tuniform mat4 uDeformMatrix[' + BONE_MAX + ']; // Bone matrices\n\t\n\tvarying vec4 vPositionCoord;\n\tvarying vec3 vBC;\n\t\n\tmat4 boneTransformMat4() {\n\t\tmat4 ret;\n\n\t\t// Weight normalization factor\n\t\tfloat normfac = 1.0 / (aVertexWeights[0] + aVertexWeights[1] + aVertexWeights[2] + aVertexWeights[3]);\n\n\t\t// Weight1 * Bone1 + Weight2 * Bone2\n\t\tret = normfac * aVertexWeights[0] * uDeformMatrix[int(floor(aVertexWeightIndices[0] + 0.5))]\n\t\t\t+ normfac * aVertexWeights[1] * uDeformMatrix[int(floor(aVertexWeightIndices[1] + 0.5))]\n\t\t\t+ normfac * aVertexWeights[2] * uDeformMatrix[int(floor(aVertexWeightIndices[2] + 0.5))]\n\t\t\t+ normfac * aVertexWeights[3] * uDeformMatrix[int(floor(aVertexWeightIndices[3] + 0.5))];\n\n\t\treturn ret;\n\t}\n\n\tvoid main() {\n\t\tvBC = barycentric;\n\n\t\t// Set the position\n\t\t#ifdef SKELETAL_ENABLED\n\t\t\tif(aVertexWeights[0] + aVertexWeights[1] + aVertexWeights[2] + aVertexWeights[3] > 0.0){\n\t\t\t\tmat4 bt = boneTransformMat4();\n\t\t\t\tgl_Position = uMVCPMatrix * bt * vec4(aVertexPosition, 1.0);\n\n\t\t\t\tvNormal = (bt * vec4(aVertexNormal, 0.0)).xyz;\n\t\t\t}else{\n\t\t\t\tgl_Position = uMVCPMatrix * vec4(aVertexPosition, 1.0);\n\t\t\t}\n\t\t#else\n\t\t\tgl_Position = uMVCPMatrix * vec4(aVertexPosition, 1.0);\n\t\t#endif\n\t}\n';

var FRAG_SHADER$5 = '\n\t#extension GL_OES_standard_derivatives : enable\n\n\tprecision mediump float;\n\n\tvarying vec4 vPositionCoord;\n\tvarying vec3 vBC;\n    \n    float edgeFactor(){\n        vec3 d = fwidth(vBC);\n        vec3 a3 = smoothstep(vec3(0.0), d*1.5, vBC);\n        return min(min(a3.x, a3.y), a3.z);\n    }\n\n\tvoid main(void) {\n        gl_FragColor.rgb = mix(vec3(0.0), vec3(0.5), edgeFactor());\n\t}\n';

var MaterialWireframe = function (_Material) {
inherits(MaterialWireframe, _Material);

function MaterialWireframe() {
  var name = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : "";

  classCallCheck(this, MaterialWireframe);

  var _this = possibleConstructorReturn(this, (MaterialWireframe.__proto__ || Object.getPrototypeOf(MaterialWireframe)).call(this, name));

  _this.vertexShader = VERTEX_SHADER$5;
  _this.fragmentShader = FRAG_SHADER$5;

  _this.shaderAttributes = [].concat(toConsumableArray(ATTRIBUTES));
  _this.shaderUniforms = [].concat(toConsumableArray(UNIFORMS));

  _this.buildProgram(); // TODO: Where should "autobuild" this live?
  return _this;
}

createClass(MaterialWireframe, [{
  key: 'buildProgram',
  value: function buildProgram() {
    this.shaderAttributes = [].concat(toConsumableArray(ATTRIBUTES));
    this.shaderUniforms = [].concat(toConsumableArray(UNIFORMS));

    this.vertexShader = VERTEX_SHADER$5;
    this.fragmentShader = FRAG_SHADER$5;

    if (!this.skeletal) {
      this.vertexShader = this.vertexShader.replace("#define SKELETAL_ENABLED", "");

      // Because Edge doesn't optimize uniforms out automatically
      if (this.shaderUniforms.indexOf("uDeformMatrix") > -1) this.shaderUniforms.splice(this.shaderUniforms.indexOf("uDeformMatrix"), 1);
    }
  }
}]);
return MaterialWireframe;
}(Material);

var Importer = function () {
  function Importer() {
      var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      classCallCheck(this, Importer);

      this.options = options;
  }

  createClass(Importer, null, [{
      key: "load",
      value: async function load() {}
  }, {
      key: "loadFromURL",
      value: function loadFromURL(src, instance, importCache) {
          if (!src) return false;

          instance.src = src;
          instance.srcPath = src.substr(0, src.lastIndexOf("/"));
          if (instance.resPath === undefined) instance.resPath = instance.srcPath;
          instance.srcFilename = src.split("/").pop();

          if (importCache && importCache[src]) {
              instance.input = importCache[src].input;
          } else {
              return this.fetchFromURL(src).then(function (response) {
                  instance.input = response;

                  if (importCache) {
                      importCache[src] = {
                          input: response
                      };
                  }

                  // return this.load();
              });
          }
      }

      // Fetch

  }, {
      key: "fetchFromURL",
      value: async function fetchFromURL(url) {
          return fetch(url, {
              "Content-Type": "text/html"
          }).then(function (response) {
              // Read response
              if (!response.ok) {
                  throw Error(response.statusText, response.url);
              }
              return response.text();
          }).then(
          // Return contents
          function (contents) {
              return contents;
          }).catch(function (e) {
              console.error("Error fetching template '" + url + "'.", e);
          });
      }
  }]);
  return Importer;
}();

'use strict';

var importCache = {};

var ImportMaterialsFromMTL = function (_Importer) {
  inherits(ImportMaterialsFromMTL, _Importer);

  function ImportMaterialsFromMTL() {
      classCallCheck(this, ImportMaterialsFromMTL);
      return possibleConstructorReturn(this, (ImportMaterialsFromMTL.__proto__ || Object.getPrototypeOf(ImportMaterialsFromMTL)).apply(this, arguments));
  }

  createClass(ImportMaterialsFromMTL, null, [{
      key: 'clearCache',
      value: function clearCache() {
          importCache = {};
      }
  }, {
      key: 'load',
      value: async function load() {
          var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

          var instance = options;
          if (instance.resPath === undefined && options.src) instance.resPath = options.src.substr(0, options.src.lastIndexOf("/"));

          await this.loadFromURL(options.src, instance, importCache);

          if (!importCache[options.src].materials) {
              var materials = [];

              var lines = instance.input.split("\n");

              var currentMaterial = null;

              // Parse Lines
              var tLine = [];
              var _iteratorNormalCompletion = true;
              var _didIteratorError = false;
              var _iteratorError = undefined;

              try {
                  for (var _iterator = lines[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
                      var line = _step.value;

                      tLine = line.split(" ");

                      switch (tLine[0].toLowerCase()) {
                          case "newmtl":
                              materials.push(new MaterialPhong({ name: tLine[1].trim(), lighting: 1 }));

                              currentMaterial = materials[materials.length - 1];
                              break;
                          case "illum":
                              // this.setIlluminationMode(currentMaterial, tLine[1] * 1);
                              break;
                          case "ka":
                              currentMaterial.ambientColor.set(tLine[1] * 1, tLine[2] * 1, tLine[3] * 1);
                              break;
                          case "kd":
                              currentMaterial.diffuseColor.set(tLine[1] * 1, tLine[2] * 1, tLine[3] * 1);
                              break;
                          case "ks":
                              currentMaterial.specularColor.set(tLine[1] * 1, tLine[2] * 1, tLine[3] * 1);
                              // TODO: Read Ns (weight)
                              break;
                          case "ns":
                              currentMaterial.specularExponent = tLine[1] * 1;
                              break;
                          case "ke":
                              currentMaterial.emitColor.set(tLine[1] * 1, tLine[2] * 1, tLine[3] * 1);
                              break;
                          case "tf":
                              currentMaterial.transmissionFilter.set(tLine[1] * 1, tLine[2] !== undefined ? tLine[2] * 1 : tLine[1] * 1, tLine[3] !== undefined ? tLine[3] * 1 : tLine[1] * 1); // Note: G and B are optional. R is to be used in place of them if they're missing.
                              break;
                          case "d":
                              currentMaterial.dissolve = tLine[1] * 1;
                              // TODO: Read Tr
                              break;
                          case "map_ka":
                              currentMaterial.ambientTexture = new Texture({ src: instance.resPath + "/" + tLine[tLine.length - 1] });
                              await currentMaterial.ambientTexture.load();
                              break;
                          case "map_kd":
                              currentMaterial.diffuseTexture = new Texture({ src: instance.resPath + "/" + tLine[tLine.length - 1] });
                              await currentMaterial.diffuseTexture.load();
                              break;
                          case "map_ks":
                              currentMaterial.specularColorTexture = new Texture({ src: instance.resPath + "/" + tLine[tLine.length - 1] });
                              await currentMaterial.specularColorTexture.load();
                              break;
                          case "map_ke":
                              currentMaterial.emitTexture = new Texture({ src: instance.resPath + "/" + tLine[tLine.length - 1] });
                              await currentMaterial.emitTexture.load();
                              break;
                          case "map_ns":
                              currentMaterial.specularHighlightTexture = new Texture({ src: instance.resPath + "/" + tLine[tLine.length - 1] });
                              await currentMaterial.specularHighlightTexture.load();
                              break;
                          case "map_d":
                              currentMaterial.alphaTexture = new Texture({ src: instance.resPath + "/" + tLine[tLine.length - 1] });
                              await currentMaterial.alphaTexture.load();
                              break;
                          case "map_bump":
                          case "bump":
                              // synonymous
                              currentMaterial.bumpMapTexture = new Texture({ src: instance.resPath + "/" + tLine[tLine.length - 1] });
                              await currentMaterial.bumpMapTexture.load();
                              break;
                          case "disp":
                              currentMaterial.displacementTexture = new Texture({ src: instance.resPath + "/" + tLine[tLine.length - 1] });
                              await currentMaterial.displacementTexture.load();
                              break;
                          case "decal":
                              currentMaterial.decalTexture = new Texture({ src: instance.resPath + "/" + tLine[tLine.length - 1] });
                              await currentMaterial.decalTexture.load();
                              break;
                      }
                  }
              } catch (err) {
                  _didIteratorError = true;
                  _iteratorError = err;
              } finally {
                  try {
                      if (!_iteratorNormalCompletion && _iterator.return) {
                          _iterator.return();
                      }
                  } finally {
                      if (_didIteratorError) {
                          throw _iteratorError;
                      }
                  }
              }

              var _iteratorNormalCompletion2 = true;
              var _didIteratorError2 = false;
              var _iteratorError2 = undefined;

              try {
                  for (var _iterator2 = materials[Symbol.iterator](), _step2; !(_iteratorNormalCompletion2 = (_step2 = _iterator2.next()).done); _iteratorNormalCompletion2 = true) {
                      var material = _step2.value;

                      material.buildProgram();
                  }
              } catch (err) {
                  _didIteratorError2 = true;
                  _iteratorError2 = err;
              } finally {
                  try {
                      if (!_iteratorNormalCompletion2 && _iterator2.return) {
                          _iterator2.return();
                      }
                  } finally {
                      if (_didIteratorError2) {
                          throw _iteratorError2;
                      }
                  }
              }

              importCache[options.src].materials = materials;
          }

          // TODO: Instance
          return importCache[options.src].materials;
      }
  }]);
  return ImportMaterialsFromMTL;
}(Importer);

'use strict';

var importCache$1 = {};

var ImportModelFromOnyx = function (_Importer) {
inherits(ImportModelFromOnyx, _Importer);

function ImportModelFromOnyx() {
  classCallCheck(this, ImportModelFromOnyx);
  return possibleConstructorReturn(this, (ImportModelFromOnyx.__proto__ || Object.getPrototypeOf(ImportModelFromOnyx)).apply(this, arguments));
}

createClass(ImportModelFromOnyx, null, [{
  key: 'clearCache',
  value: function clearCache() {
    importCache$1 = {};
  }
}, {
  key: 'load',
  value: async function load() {
    var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

    var instance = {};
    await this.loadFromURL(options.src, instance, importCache$1);

    this.options = options;

    if (!importCache$1[options.src].model) {
      var parsed = JSON.parse(importCache$1[options.src].input);

      var meshes = this.cloneMeshes(parsed.model.meshes, instance);
      var skeleton = this.cloneSkeleton(parsed.model.bones, instance);

      // Flatten top level in skeleton children
      skeleton.children = skeleton.flattenChildren();
      skeleton.children = this.sortBones(skeleton.children, parsed.model.meshes[0].boneOrder);

      // Calc bind pose
      skeleton.calcBindPose();

      var animationClips = this.cloneAnimationClips(parsed.model.animationClips, skeleton, instance);

      importCache$1[options.src].model = new Model({
        meshes: meshes,
        skeleton: skeleton,
        animationClips: animationClips
      });

      // console.log(parsed, importCache[options.src].model);

      // Free up memory
      delete importCache$1[options.src].input;
    }

    return this.cloneModel(importCache$1[options.src].model, instance);
  }
}, {
  key: 'cloneModel',
  value: function cloneModel(cachedModel, instance) {
    var meshes = this.cloneMeshes(cachedModel.meshes, instance);
    var skeleton = this.cloneSkeleton(cachedModel.skeleton, instance);
    var animationClips = this.cloneAnimationClips(cachedModel.animationClips, skeleton, instance);

    return new Model({
      meshes: meshes,
      skeleton: skeleton,
      animationClips: animationClips
    });
  }
}, {
  key: 'cloneMeshes',
  value: function cloneMeshes(cached, instance) {
    var meshes = [];

    var _iteratorNormalCompletion = true;
    var _didIteratorError = false;
    var _iteratorError = undefined;

    try {
      for (var _iterator = cached[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
        var mesh = _step.value;

        var diffuseTexture = null;
        // Should (optionally?) allow instance of texture
        if (mesh.material.diffuseTexture) {
          if (mesh.material.diffuseTexture instanceof Texture) {
            diffuseTexture = mesh.material.diffuseTexture;
          } else {
            diffuseTexture = new Texture({ src: instance.resPath + "/" + mesh.material.diffuseTexture.src });
          }
        }

        meshes.push(new Mesh({
          material: new MaterialPhong(mesh.material.name, {
            lighting: 1,
            diffuseTexture: diffuseTexture,
            skeletal: mesh.material.skeletal || mesh.weightIndices ? true : false,
            ambientColor: mesh.material.ambientColor,
            diffuseColor: mesh.material.diffuseColor,
            specularColor: mesh.material.specularColor

          }),
          vertices: new Float32Array(mesh.vertices),
          uvs: new Float32Array(mesh.uvs),
          normals: new Float32Array(mesh.normals),
          weights: new Float32Array(mesh.weights),
          weightIndices: new Float32Array(mesh.weightIndices)
        }));

        meshes[meshes.length - 1].material.buildProgram();
      }
    } catch (err) {
      _didIteratorError = true;
      _iteratorError = err;
    } finally {
      try {
        if (!_iteratorNormalCompletion && _iterator.return) {
          _iterator.return();
        }
      } finally {
        if (_didIteratorError) {
          throw _iteratorError;
        }
      }
    }

    return meshes;
  }
}, {
  key: 'cloneSkeleton',
  value: function cloneSkeleton(cached, instance) {
    if (!cached) return;

    var skeleton = new Skeleton$1({ children: this.cloneBones(cached) });

    // Calc bind pose
    skeleton.calcBindPose();

    // Copy skeleton
    return skeleton;
  }
}, {
  key: 'sortBones',
  value: function sortBones(bones) {
    var order = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];

    var result = [];

    order.forEach(function (key) {
      var found = false;
      bones = bones.filter(function (item) {
        if (!found && item.id == key) {
          result.push(item);
          found = true;
          return false;
        } else return true;
      });
    });

    // Include any missing ones
    var _iteratorNormalCompletion2 = true;
    var _didIteratorError2 = false;
    var _iteratorError2 = undefined;

    try {
      for (var _iterator2 = bones[Symbol.iterator](), _step2; !(_iteratorNormalCompletion2 = (_step2 = _iterator2.next()).done); _iteratorNormalCompletion2 = true) {
        var bone = _step2.value;

        if (result.indexOf(bone) < 0) result.push(bone);
      }
    } catch (err) {
      _didIteratorError2 = true;
      _iteratorError2 = err;
    } finally {
      try {
        if (!_iteratorNormalCompletion2 && _iterator2.return) {
          _iterator2.return();
        }
      } finally {
        if (_didIteratorError2) {
          throw _iteratorError2;
        }
      }
    }

    return result;
  }
}, {
  key: 'cloneBones',
  value: function cloneBones(cached, parent) {
    var flatBonesArray = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : [];
    var bones = [];

    function findParentByID(id, skeleton) {
      var _iteratorNormalCompletion3 = true;
      var _didIteratorError3 = false;
      var _iteratorError3 = undefined;

      try {
        for (var _iterator3 = skeleton[Symbol.iterator](), _step3; !(_iteratorNormalCompletion3 = (_step3 = _iterator3.next()).done); _iteratorNormalCompletion3 = true) {
          var child = _step3.value;

          if (child.id == id) return child;
        }
      } catch (err) {
        _didIteratorError3 = true;
        _iteratorError3 = err;
      } finally {
        try {
          if (!_iteratorNormalCompletion3 && _iterator3.return) {
            _iterator3.return();
          }
        } finally {
          if (_didIteratorError3) {
            throw _iteratorError3;
          }
        }
      }
    }

    var _iteratorNormalCompletion4 = true;
    var _didIteratorError4 = false;
    var _iteratorError4 = undefined;

    try {
      for (var _iterator4 = (cached.children || cached)[Symbol.iterator](), _step4; !(_iteratorNormalCompletion4 = (_step4 = _iterator4.next()).done); _iteratorNormalCompletion4 = true) {
        var bone = _step4.value;

        var newBone = findParentByID(bone.id, flatBonesArray);

        if (!newBone) {
          var newTransform = new Transform$1();

          if (bone.matrixWorld) {
            // Loading from cached model
            newTransform.localMatrix = new Float32Array(bone.matrix);
            newTransform.generateTranslationRotationScale();
          } else if (bone.transform.translation) {
            // If loading from file
            newTransform.translate(bone.transform.translation[0], bone.transform.translation[1], bone.transform.translation[2]);
            newTransform.scale(bone.transform.scale[0], bone.transform.scale[1], bone.transform.scale[2]);
            switch (bone.transform.rotation[3]) {
              case "ZYX":
                newTransform.rotateToZYX(bone.transform.rotation[2], bone.transform.rotation[1], bone.transform.rotation[0]);
                break;
              case "XYZ":
              default:
                newTransform.rotateToXYZ(bone.transform.rotation[0], bone.transform.rotation[1], bone.transform.rotation[2]);
                break;
            }
          } else {
            // Loading from cached model
            newTransform.localMatrix = new Float32Array(bone.transform.localMatrix);
            newTransform.generateTranslationRotationScale();
          }

          newBone = new Bone$1({
            name: bone.name,
            id: bone.id || bone.FBXID,
            parent: parent,
            inheritTransform: bone.inheritTransform,
            transform: newTransform
          });

          newBone.children = this.cloneBones(bone, newBone, flatBonesArray);
        }

        bones.push(newBone);
        flatBonesArray.push(newBone);
      }
    } catch (err) {
      _didIteratorError4 = true;
      _iteratorError4 = err;
    } finally {
      try {
        if (!_iteratorNormalCompletion4 && _iterator4.return) {
          _iterator4.return();
        }
      } finally {
        if (_didIteratorError4) {
          throw _iteratorError4;
        }
      }
    }

    return bones;
  }
}, {
  key: 'cloneAnimationClips',
  value: function cloneAnimationClips(cached, skeleton) {
    var clips = [];
    var _iteratorNormalCompletion5 = true;
    var _didIteratorError5 = false;
    var _iteratorError5 = undefined;

    try {
      for (var _iterator5 = cached[Symbol.iterator](), _step5; !(_iteratorNormalCompletion5 = (_step5 = _iterator5.next()).done); _iteratorNormalCompletion5 = true) {
        var clip = _step5.value;

        // console.log(clip);
        clips.push(new AnimationClip$1({
          name: clip.name,
          loop: clip.loop,
          enabled: clip.enabled,
          tracks: this.cloneAnimationTracks(clip.tracks, skeleton)
        }));
      }
    } catch (err) {
      _didIteratorError5 = true;
      _iteratorError5 = err;
    } finally {
      try {
        if (!_iteratorNormalCompletion5 && _iterator5.return) {
          _iterator5.return();
        }
      } finally {
        if (_didIteratorError5) {
          throw _iteratorError5;
        }
      }
    }

    return clips;
  }
}, {
  key: 'cloneAnimationTracks',
  value: function cloneAnimationTracks(cached, skeleton) {
    var tracks = [];

    function findParentByID(id, skeleton) {
      var _iteratorNormalCompletion6 = true;
      var _didIteratorError6 = false;
      var _iteratorError6 = undefined;

      try {
        for (var _iterator6 = skeleton.children[Symbol.iterator](), _step6; !(_iteratorNormalCompletion6 = (_step6 = _iterator6.next()).done); _iteratorNormalCompletion6 = true) {
          var child = _step6.value;

          if (child.id == id) return child;
        }
      } catch (err) {
        _didIteratorError6 = true;
        _iteratorError6 = err;
      } finally {
        try {
          if (!_iteratorNormalCompletion6 && _iterator6.return) {
            _iterator6.return();
          }
        } finally {
          if (_didIteratorError6) {
            throw _iteratorError6;
          }
        }
      }
    }

    function findParentByName(name, skeleton) {
      var _iteratorNormalCompletion7 = true;
      var _didIteratorError7 = false;
      var _iteratorError7 = undefined;

      try {
        for (var _iterator7 = skeleton.children[Symbol.iterator](), _step7; !(_iteratorNormalCompletion7 = (_step7 = _iterator7.next()).done); _iteratorNormalCompletion7 = true) {
          var child = _step7.value;

          if (child.name == name) return child;
        }
      } catch (err) {
        _didIteratorError7 = true;
        _iteratorError7 = err;
      } finally {
        try {
          if (!_iteratorNormalCompletion7 && _iterator7.return) {
            _iterator7.return();
          }
        } finally {
          if (_didIteratorError7) {
            throw _iteratorError7;
          }
        }
      }
    }

    var _iteratorNormalCompletion8 = true;
    var _didIteratorError8 = false;
    var _iteratorError8 = undefined;

    try {
      for (var _iterator8 = cached[Symbol.iterator](), _step8; !(_iteratorNormalCompletion8 = (_step8 = _iterator8.next()).done); _iteratorNormalCompletion8 = true) {
        var track = _step8.value;

        var parent = null;
        if (!track.parent.id) {
          // Reading from file
          parent = findParentByName(track.parent, skeleton);
        } else {
          // Reading from cached model
          parent = findParentByID(track.parent.id, skeleton);
        }

        // console.log(track);
        tracks.push(new AnimationTrack({
          name: track.name,
          type: track.type,
          times: track.times.slice(),
          values: track.values.slice(),
          parent: parent
        }));
      }
    } catch (err) {
      _didIteratorError8 = true;
      _iteratorError8 = err;
    } finally {
      try {
        if (!_iteratorNormalCompletion8 && _iterator8.return) {
          _iterator8.return();
        }
      } finally {
        if (_didIteratorError8) {
          throw _iteratorError8;
        }
      }
    }

    return tracks;
  }
}]);
return ImportModelFromOnyx;
}(Importer);

'use strict';

var importCache$2 = {};

var ImportModelFromOBJ = function (_Importer) {
  inherits(ImportModelFromOBJ, _Importer);

  function ImportModelFromOBJ() {
      classCallCheck(this, ImportModelFromOBJ);
      return possibleConstructorReturn(this, (ImportModelFromOBJ.__proto__ || Object.getPrototypeOf(ImportModelFromOBJ)).apply(this, arguments));
  }

  createClass(ImportModelFromOBJ, [{
      key: 'setIlluminationMode',
      value: function setIlluminationMode(material) {
          
      }
  }], [{
      key: 'clearCache',
      value: function clearCache() {
          importCache$2 = {};
      }
  }, {
      key: 'load',
      value: async function load() {
          var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

          var instance = {};
          await this.loadFromURL(options.src, instance, importCache$2);

          this.options = options;

          if (!importCache$2[options.src].model) {
              var lines = instance.input.split("\n");

              var currentMesh = null;
              var meshes = [];
              var materials = [];
              var verts = [];
              var vertTs = [];
              var vertNs = [];
              var vertPs = [];
              // let faces = [];

              // Parse Lines
              var tLine = [];
              var _iteratorNormalCompletion = true;
              var _didIteratorError = false;
              var _iteratorError = undefined;

              try {
                  for (var _iterator = lines[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
                      var line = _step.value;

                      tLine = line.split(" ");

                      // Sanitize
                      if (tLine[1] === "") tLine.splice(1, 1);

                      switch (tLine[0]) {
                          case "g":
                              if (tLine[tLine.length - 1] == "default") continue;

                              currentMesh = this.getMaterialById(tLine[tLine.length - 1], meshes);

                              if (!currentMesh) {
                                  var mesh = new Mesh({
                                      id: tLine[tLine.length - 1],
                                      material: null,
                                      vertices: [], // Creating standard arrays to make it easier to push to  
                                      uvs: [],
                                      normals: []
                                      // parameters: vertPs,
                                      // faces: faces
                                  });
                                  meshes.push(mesh);

                                  currentMesh = mesh;
                              }

                              break;
                          case "mtllib":
                              materials = await ImportMaterialsFromMTL.load({ resPath: instance.resPath, src: instance.resPath + "/" + tLine[1].trim() });
                              break;
                          case "usemtl":
                              currentMesh.material = this.getMaterialByName(tLine[1].trim(), materials);
                              break;
                          case "v":
                              this.parseGeometricVertices(tLine, verts);
                              break;
                          case "vt":
                              this.parseTextureCoordinates(tLine, vertTs);
                              break;
                          case "vn":
                              this.parseVertexNormals(tLine, vertNs);
                              break;
                          case "vp":
                              this.parseParameterSpaceVertices(tLine, vertPs);
                              break;
                          case "f":
                              this.parsePolygonalFaces(tLine, verts, vertTs, vertNs, vertPs, currentMesh);
                              break;
                          case "o":
                              // Object name
                              break;
                          case "g":
                              // Group name
                              break;
                          case "s":
                              // Smooth shading
                              break;
                      }
                  }

                  // for(let mesh of meshes){
                  //     let diffuseTexture = null;
                  //     if(mesh.material.diffuseTexture){
                  //         // TODO: Make this optional for cases where a new instance of the texture is needed
                  //         // if(!cacheTexture){
                  //         //     diffuseTexture = new Texture(mesh.material.diffuseTexture.src);
                  //         //     await diffuseTexture.load();
                  //         // }else{
                  //             diffuseTexture = mesh.material.diffuseTexture;
                  //         // }
                  //     }

                  //     meshes.push(new Mesh({
                  //         material: new MaterialPhong({
                  //             name: mesh.material.name,
                  //             diffuseTexture: diffuseTexture,
                  //             lighting: 1
                  //         }),
                  //         vertices: new Float32Array(mesh.vertices), // Convert to Float32Arrays (more efficient and required for WebGL)
                  //         uvs: new Float32Array(mesh.uvs),
                  //         normals: new Float32Array(mesh.normals)
                  //     }));

                  //     // let vertCs = [];
                  //     // for(var i = 0; i < mesh.vertices.length * 4; i += 4){
                  //     //     vertCs[i] = i / mesh.vertices.length;
                  //     //     // vertCs[i + 1] = 1 / (i / mesh.vertices.length);
                  //     //     vertCs[i + 1] = 0;
                  //     //     vertCs[i + 2] = 1 / (i / mesh.vertices.length);
                  //     //     // vertCs[i + 2] = 0;
                  //     //     vertCs[i + 3] = 1;
                  //     // }
                  //     // mesh.material.vertexColors = new Float32Array(vertCs);
                  // }
              } catch (err) {
                  _didIteratorError = true;
                  _iteratorError = err;
              } finally {
                  try {
                      if (!_iteratorNormalCompletion && _iterator.return) {
                          _iterator.return();
                      }
                  } finally {
                      if (_didIteratorError) {
                          throw _iteratorError;
                      }
                  }
              }

              importCache$2[options.src].model = new Model({
                  meshes: meshes
              });
          }

          // console.log(importCache[options.src].model);

          return this.cloneModel(importCache$2[options.src].model);
      }
  }, {
      key: 'getMeshById',
      value: function getMeshById(id, meshes) {
          var _iteratorNormalCompletion2 = true;
          var _didIteratorError2 = false;
          var _iteratorError2 = undefined;

          try {
              for (var _iterator2 = meshes[Symbol.iterator](), _step2; !(_iteratorNormalCompletion2 = (_step2 = _iterator2.next()).done); _iteratorNormalCompletion2 = true) {
                  var mesh = _step2.value;

                  if (mesh.id === id) return mesh;
              }
          } catch (err) {
              _didIteratorError2 = true;
              _iteratorError2 = err;
          } finally {
              try {
                  if (!_iteratorNormalCompletion2 && _iterator2.return) {
                      _iterator2.return();
                  }
              } finally {
                  if (_didIteratorError2) {
                      throw _iteratorError2;
                  }
              }
          }

          return null;
      }
  }, {
      key: 'getMaterialById',
      value: function getMaterialById(id, materials) {
          var _iteratorNormalCompletion3 = true;
          var _didIteratorError3 = false;
          var _iteratorError3 = undefined;

          try {
              for (var _iterator3 = materials[Symbol.iterator](), _step3; !(_iteratorNormalCompletion3 = (_step3 = _iterator3.next()).done); _iteratorNormalCompletion3 = true) {
                  var material = _step3.value;

                  if (material.id === id) return material;
              }
          } catch (err) {
              _didIteratorError3 = true;
              _iteratorError3 = err;
          } finally {
              try {
                  if (!_iteratorNormalCompletion3 && _iterator3.return) {
                      _iterator3.return();
                  }
              } finally {
                  if (_didIteratorError3) {
                      throw _iteratorError3;
                  }
              }
          }

          return null;
      }
  }, {
      key: 'getMeshByName',
      value: function getMeshByName(name, meshes) {
          var _iteratorNormalCompletion4 = true;
          var _didIteratorError4 = false;
          var _iteratorError4 = undefined;

          try {
              for (var _iterator4 = meshes[Symbol.iterator](), _step4; !(_iteratorNormalCompletion4 = (_step4 = _iterator4.next()).done); _iteratorNormalCompletion4 = true) {
                  var mesh = _step4.value;

                  if (mesh.name === name) return mesh;
              }
          } catch (err) {
              _didIteratorError4 = true;
              _iteratorError4 = err;
          } finally {
              try {
                  if (!_iteratorNormalCompletion4 && _iterator4.return) {
                      _iterator4.return();
                  }
              } finally {
                  if (_didIteratorError4) {
                      throw _iteratorError4;
                  }
              }
          }

          return null;
      }
  }, {
      key: 'getMaterialByName',
      value: function getMaterialByName(name, materials) {
          var _iteratorNormalCompletion5 = true;
          var _didIteratorError5 = false;
          var _iteratorError5 = undefined;

          try {
              for (var _iterator5 = materials[Symbol.iterator](), _step5; !(_iteratorNormalCompletion5 = (_step5 = _iterator5.next()).done); _iteratorNormalCompletion5 = true) {
                  var material = _step5.value;

                  if (material.name === name) return material;
              }
          } catch (err) {
              _didIteratorError5 = true;
              _iteratorError5 = err;
          } finally {
              try {
                  if (!_iteratorNormalCompletion5 && _iterator5.return) {
                      _iterator5.return();
                  }
              } finally {
                  if (_didIteratorError5) {
                      throw _iteratorError5;
                  }
              }
          }

          return null;
      }
  }, {
      key: 'parseGeometricVertices',
      value: function parseGeometricVertices(line, output) {
          // # List of geometric vertices, with (x,y,z[,w]) coordinates, w is optional and defaults to 1.0.
          // v 0.123 0.234 0.345 1.0
          // v ...
          // ...

          // w to 1.0
          if (line[4] === undefined) line[4] = 1.0;

          // return new Vertex3(line[0], line[1], line[2], line[3]);
          output.push(line[1] * 1, line[2] * 1, line[3] * 1);
      }
  }, {
      key: 'parseTextureCoordinates',
      value: function parseTextureCoordinates(line, output) {
          // # List of texture coordinates, in (u, v [,w]) coordinates, these will vary between 0 and 1, w is optional and defaults to 0.
          // vt 0.500 1 [0]
          // vt ...
          // ...

          if (line[3] === undefined) {
              output.push(line[1] * 1, line[2] * 1);
          } else {
              output.push(line[1] * 1, line[2] * 1); //, line[3] * 1 || 0);
          }
      }
  }, {
      key: 'parseVertexNormals',
      value: function parseVertexNormals(line, output) {
          // # List of vertex normals in (x,y,z) form; normals might not be unit vectors.
          // vn 0.707 0.000 0.707
          // vn ...
          // ...

          output.push(line[1] * 1, line[2] * 1, line[3] * 1);
      }
  }, {
      key: 'parseParameterSpaceVertices',
      value: function parseParameterSpaceVertices(line, output) {
          // # Parameter space vertices in ( u [,v] [,w] ) form; free form geometry statement ( see below )
          // vp 0.310000 3.210000 2.100000
          // vp ...
          // ...

          output.push(line[1] * 1, line[2] * 1, line[3] * 1);
      }
  }, {
      key: 'parsePolygonalFaces',
      value: function parsePolygonalFaces(line, verts, vertTs, vertNs, vertPs, mesh) {
          // # Polygonal face element
          // f 1 2 3 (just vertices)
          // f 3/1 4/2 5/3 (vertices/texturecoordinates)
          // f 6/4/1 3/5/3 7/6/5 (vertices/texturecoordinates/normals)
          // f 7//1 8//2 9//3 (vertices//normals)
          // f ...

          var parts = [];
          for (var i = 1; i < line.length; i++) {
              parts[i - 1] = line[i].split("/"); // 6/4/1 3/5/3 7/6/5 -> [[6,4,1], [3,5,3], [7,6,5]]
          }

          // Sanitize
          for (var i = 0; i < parts.length; i++) {
              if (parts[i].length < 2) parts.splice(i, 1);
          }

          // let face = {
          if (parts.length === 3) {
              mesh.vertices.push(verts[(parts[0][0] * 1 - 1) * 3 + 0], verts[(parts[0][0] * 1 - 1) * 3 + 1], verts[(parts[0][0] * 1 - 1) * 3 + 2], verts[(parts[1][0] * 1 - 1) * 3 + 0], verts[(parts[1][0] * 1 - 1) * 3 + 1], verts[(parts[1][0] * 1 - 1) * 3 + 2], verts[(parts[2][0] * 1 - 1) * 3 + 0], verts[(parts[2][0] * 1 - 1) * 3 + 1], verts[(parts[2][0] * 1 - 1) * 3 + 2]);
              if (parts[0][1] !== "") mesh.uvs.push(vertTs[(parts[0][1] * 1 - 1) * 2 + 0], vertTs[(parts[0][1] * 1 - 1) * 2 + 1], vertTs[(parts[1][1] * 1 - 1) * 2 + 0], vertTs[(parts[1][1] * 1 - 1) * 2 + 1], vertTs[(parts[2][1] * 1 - 1) * 2 + 0], vertTs[(parts[2][1] * 1 - 1) * 2 + 1]);
              if (parts[0][2] !== "") mesh.normals.push(vertNs[(parts[0][2] * 1 - 1) * 3 + 0], vertNs[(parts[0][2] * 1 - 1) * 3 + 1], vertNs[(parts[0][2] * 1 - 1) * 3 + 2], vertNs[(parts[1][2] * 1 - 1) * 3 + 0], vertNs[(parts[1][2] * 1 - 1) * 3 + 1], vertNs[(parts[1][2] * 1 - 1) * 3 + 2], vertNs[(parts[2][2] * 1 - 1) * 3 + 0], vertNs[(parts[2][2] * 1 - 1) * 3 + 1], vertNs[(parts[2][2] * 1 - 1) * 3 + 2]);
          } else {
              mesh.vertices.push(verts[(parts[0][0] * 1 - 1) * 3 + 0], verts[(parts[0][0] * 1 - 1) * 3 + 1], verts[(parts[0][0] * 1 - 1) * 3 + 2], verts[(parts[1][0] * 1 - 1) * 3 + 0], verts[(parts[1][0] * 1 - 1) * 3 + 1], verts[(parts[1][0] * 1 - 1) * 3 + 2], verts[(parts[2][0] * 1 - 1) * 3 + 0], verts[(parts[2][0] * 1 - 1) * 3 + 1], verts[(parts[2][0] * 1 - 1) * 3 + 2], verts[(parts[0][0] * 1 - 1) * 3 + 0], verts[(parts[0][0] * 1 - 1) * 3 + 1], verts[(parts[0][0] * 1 - 1) * 3 + 2], verts[(parts[2][0] * 1 - 1) * 3 + 0], verts[(parts[2][0] * 1 - 1) * 3 + 1], verts[(parts[2][0] * 1 - 1) * 3 + 2], verts[(parts[3][0] * 1 - 1) * 3 + 0], verts[(parts[3][0] * 1 - 1) * 3 + 1], verts[(parts[3][0] * 1 - 1) * 3 + 2]);
              if (parts[0][1] !== "") mesh.uvs.push(vertTs[(parts[0][1] * 1 - 1) * 2 + 0], vertTs[(parts[0][1] * 1 - 1) * 2 + 1], vertTs[(parts[1][1] * 1 - 1) * 2 + 0], vertTs[(parts[1][1] * 1 - 1) * 2 + 1], vertTs[(parts[2][1] * 1 - 1) * 2 + 0], vertTs[(parts[2][1] * 1 - 1) * 2 + 1], vertTs[(parts[0][1] * 1 - 1) * 2 + 0], vertTs[(parts[0][1] * 1 - 1) * 2 + 1], vertTs[(parts[2][1] * 1 - 1) * 2 + 0], vertTs[(parts[2][1] * 1 - 1) * 2 + 1], vertTs[(parts[3][1] * 1 - 1) * 2 + 0], vertTs[(parts[3][1] * 1 - 1) * 2 + 1]);
              if (parts[0][2] !== "") mesh.normals.push(vertNs[(parts[0][2] * 1 - 1) * 3 + 0], vertNs[(parts[0][2] * 1 - 1) * 3 + 1], vertNs[(parts[0][2] * 1 - 1) * 3 + 2], vertNs[(parts[1][2] * 1 - 1) * 3 + 0], vertNs[(parts[1][2] * 1 - 1) * 3 + 1], vertNs[(parts[1][2] * 1 - 1) * 3 + 2], vertNs[(parts[2][2] * 1 - 1) * 3 + 0], vertNs[(parts[2][2] * 1 - 1) * 3 + 1], vertNs[(parts[2][2] * 1 - 1) * 3 + 2], vertNs[(parts[0][2] * 1 - 1) * 3 + 0], vertNs[(parts[0][2] * 1 - 1) * 3 + 1], vertNs[(parts[0][2] * 1 - 1) * 3 + 2], vertNs[(parts[2][2] * 1 - 1) * 3 + 0], vertNs[(parts[2][2] * 1 - 1) * 3 + 1], vertNs[(parts[2][2] * 1 - 1) * 3 + 2], vertNs[(parts[3][2] * 1 - 1) * 3 + 0], vertNs[(parts[3][2] * 1 - 1) * 3 + 1], vertNs[(parts[3][2] * 1 - 1) * 3 + 2]);
          }

          // };
      }
  }, {
      key: 'cloneModel',
      value: function cloneModel(cachedModel) {
          var meshes = this.cloneMeshes(cachedModel.meshes);
          var skeleton = this.cloneSkeleton(cachedModel.skeleton);
          var animationClips = this.cloneAnimationClips(cachedModel.animationClips, skeleton);

          return new Model({
              id: cachedModel.id,
              meshes: meshes,
              skeleton: skeleton,
              animationClips: animationClips
          });
      }
  }, {
      key: 'cloneMeshes',
      value: function cloneMeshes(cached) {
          var meshes = [];

          var _iteratorNormalCompletion6 = true;
          var _didIteratorError6 = false;
          var _iteratorError6 = undefined;

          try {
              for (var _iterator6 = cached[Symbol.iterator](), _step6; !(_iteratorNormalCompletion6 = (_step6 = _iterator6.next()).done); _iteratorNormalCompletion6 = true) {
                  var mesh = _step6.value;

                  if (!mesh.material) continue;

                  meshes.push(new Mesh({
                      id: mesh.id,
                      material: new MaterialPhong(mesh.material),
                      vertices: new Float32Array(mesh.vertices),
                      uvs: new Float32Array(mesh.uvs),
                      normals: new Float32Array(mesh.normals),
                      weights: new Float32Array(mesh.weights),
                      weightIndices: new Float32Array(mesh.weightIndices)
                  }));
              }
          } catch (err) {
              _didIteratorError6 = true;
              _iteratorError6 = err;
          } finally {
              try {
                  if (!_iteratorNormalCompletion6 && _iterator6.return) {
                      _iterator6.return();
                  }
              } finally {
                  if (_didIteratorError6) {
                      throw _iteratorError6;
                  }
              }
          }

          return meshes;
      }
  }, {
      key: 'cloneSkeleton',
      value: function cloneSkeleton(cached) {
          if (!cached) return;

          // Copy skeleton
          return new Skeleton({ children: this.cloneBones(cached) });
      }
  }, {
      key: 'cloneBones',
      value: function cloneBones(cached, parent) {
          var flatBonesArray = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : [];

          // Copy bones
          var bones = [];

          function findParentByID(id, skeleton) {
              var _iteratorNormalCompletion7 = true;
              var _didIteratorError7 = false;
              var _iteratorError7 = undefined;

              try {
                  for (var _iterator7 = skeleton[Symbol.iterator](), _step7; !(_iteratorNormalCompletion7 = (_step7 = _iterator7.next()).done); _iteratorNormalCompletion7 = true) {
                      var child = _step7.value;

                      if (child.id == id) return child;
                  }
              } catch (err) {
                  _didIteratorError7 = true;
                  _iteratorError7 = err;
              } finally {
                  try {
                      if (!_iteratorNormalCompletion7 && _iterator7.return) {
                          _iterator7.return();
                      }
                  } finally {
                      if (_didIteratorError7) {
                          throw _iteratorError7;
                      }
                  }
              }
          }

          var _iteratorNormalCompletion8 = true;
          var _didIteratorError8 = false;
          var _iteratorError8 = undefined;

          try {
              for (var _iterator8 = cached.children[Symbol.iterator](), _step8; !(_iteratorNormalCompletion8 = (_step8 = _iterator8.next()).done); _iteratorNormalCompletion8 = true) {
                  var bone = _step8.value;

                  var newBone = findParentByID(bone.id, flatBonesArray);

                  if (!newBone) {
                      var newTransform = new Transform();
                      newTransform.localMatrix = new Float32Array(bone.transform.localMatrix);
                      newTransform.generateTranslationRotationScale();

                      newBone = new Bone({
                          name: bone.name,
                          id: bone.id || bone.FBXID,
                          parent: parent,
                          inheritTransform: bone.inheritTransform,
                          transform: newTransform
                      });

                      newBone.children = this.cloneBones(bone, newBone, flatBonesArray);
                  }

                  bones.push(newBone);
                  flatBonesArray.push(newBone);
              }
          } catch (err) {
              _didIteratorError8 = true;
              _iteratorError8 = err;
          } finally {
              try {
                  if (!_iteratorNormalCompletion8 && _iterator8.return) {
                      _iterator8.return();
                  }
              } finally {
                  if (_didIteratorError8) {
                      throw _iteratorError8;
                  }
              }
          }

          return bones;
      }
  }, {
      key: 'cloneAnimationClips',
      value: function cloneAnimationClips(cached, skeleton) {
          var clips = [];
          var _iteratorNormalCompletion9 = true;
          var _didIteratorError9 = false;
          var _iteratorError9 = undefined;

          try {
              for (var _iterator9 = cached[Symbol.iterator](), _step9; !(_iteratorNormalCompletion9 = (_step9 = _iterator9.next()).done); _iteratorNormalCompletion9 = true) {
                  var clip = _step9.value;

                  // console.log(clip);
                  clips.push(new AnimationClip({
                      name: clip.name,
                      loop: clip.loop,
                      enabled: clip.enabled,
                      tracks: this.cloneAnimationTracks(clip.tracks, skeleton)
                  }));
              }
          } catch (err) {
              _didIteratorError9 = true;
              _iteratorError9 = err;
          } finally {
              try {
                  if (!_iteratorNormalCompletion9 && _iterator9.return) {
                      _iterator9.return();
                  }
              } finally {
                  if (_didIteratorError9) {
                      throw _iteratorError9;
                  }
              }
          }

          return clips;
      }
  }, {
      key: 'cloneAnimationTracks',
      value: function cloneAnimationTracks(cached, skeleton) {
          var tracks = [];

          function findParentByID(id, skeleton) {
              var _iteratorNormalCompletion10 = true;
              var _didIteratorError10 = false;
              var _iteratorError10 = undefined;

              try {
                  for (var _iterator10 = skeleton.children[Symbol.iterator](), _step10; !(_iteratorNormalCompletion10 = (_step10 = _iterator10.next()).done); _iteratorNormalCompletion10 = true) {
                      var child = _step10.value;

                      if (child.id == id) return child;
                  }
              } catch (err) {
                  _didIteratorError10 = true;
                  _iteratorError10 = err;
              } finally {
                  try {
                      if (!_iteratorNormalCompletion10 && _iterator10.return) {
                          _iterator10.return();
                      }
                  } finally {
                      if (_didIteratorError10) {
                          throw _iteratorError10;
                      }
                  }
              }
          }

          var _iteratorNormalCompletion11 = true;
          var _didIteratorError11 = false;
          var _iteratorError11 = undefined;

          try {
              for (var _iterator11 = cached[Symbol.iterator](), _step11; !(_iteratorNormalCompletion11 = (_step11 = _iterator11.next()).done); _iteratorNormalCompletion11 = true) {
                  var track = _step11.value;

                  // console.log(track);
                  tracks.push(new AnimationTrack({
                      name: track.name,
                      type: track.type,
                      times: track.times.slice(),
                      values: track.values.slice(),
                      parent: findParentByID(track.parent.id, skeleton)
                  }));
              }
          } catch (err) {
              _didIteratorError11 = true;
              _iteratorError11 = err;
          } finally {
              try {
                  if (!_iteratorNormalCompletion11 && _iterator11.return) {
                      _iterator11.return();
                  }
              } finally {
                  if (_didIteratorError11) {
                      throw _iteratorError11;
                  }
              }
          }

          return tracks;
      }
  }]);
  return ImportModelFromOBJ;
}(Importer);

'use strict';

var importCache$3 = {};

var ImportSpriteFromTexturePacker = function (_Importer) {
  inherits(ImportSpriteFromTexturePacker, _Importer);

  function ImportSpriteFromTexturePacker() {
      classCallCheck(this, ImportSpriteFromTexturePacker);
      return possibleConstructorReturn(this, (ImportSpriteFromTexturePacker.__proto__ || Object.getPrototypeOf(ImportSpriteFromTexturePacker)).apply(this, arguments));
  }

  createClass(ImportSpriteFromTexturePacker, null, [{
      key: 'clearCache',
      value: function clearCache() {
          importCache$3 = {};
      }
  }, {
      key: 'load',
      value: async function load() {
          var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

          var instance = {};
          await this.loadFromURL(options.src, instance, importCache$3);

          instance.options = options;

          if (!importCache$3[options.src].sprite) {
              var obj = JSON.parse(instance.input);

              var src = options.src;
              options.src = instance.resPath + "/" + obj.meta.image;
              var sprite = new Sprite(options);
              options.src = src;

              // sprite.src = instance.resPath + "/" + obj.meta.image;
              await sprite.loadTextures();
              var name = instance.srcFilename.split(".")[0];

              var animation = new SpriteAnimation(name);
              var _iteratorNormalCompletion = true;
              var _didIteratorError = false;
              var _iteratorError = undefined;

              try {
                  for (var _iterator = obj.frames[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
                      var frame = _step.value;

                      animation.addFrame({
                          speed: instance.options.speed || 15 / 1000, // of 15 FPS
                          x: frame.frame.x * 1,
                          y: frame.frame.y * 1,
                          width: frame.frame.w * 1,
                          height: frame.frame.h * 1,
                          pivotX: frame.pivot.x * 1,
                          pivotY: frame.pivot.y * 1,
                          materialIndex: 0
                      });
                  }

                  // if(options.sortByFilename === undefined || options.sortByFilename){

                  // }
              } catch (err) {
                  _didIteratorError = true;
                  _iteratorError = err;
              } finally {
                  try {
                      if (!_iteratorNormalCompletion && _iterator.return) {
                          _iterator.return();
                      }
                  } finally {
                      if (_didIteratorError) {
                          throw _iteratorError;
                      }
                  }
              }

              sprite.addAnimation(name, animation);

              importCache$3[options.src].sprite = sprite;
          }

          //TODO: Clone
          instance.sprite = importCache$3[options.src].sprite;

          if (instance.options.defaultAnimation !== undefined) {
              if (instance.options.autoplay || instance.options.autoplay === undefined) {
                  instance.sprite.playAnimation(instance.options.defaultAnimation);
              } else {
                  instance.sprite.selectAnimation(instance.options.defaultAnimation);
              }
          }

          return instance.sprite;

          // console.log(model);

          // return this.cloneModel(importCache[options.src].model);
      }
  }]);
  return ImportSpriteFromTexturePacker;
}(Importer);

function handles(event) {
  var _ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : { elementId: null },
      _ref$elementId = _ref.elementId,
      elementId = _ref$elementId === undefined ? elementId : _ref$elementId;

  return function (target, property, descriptor) {
      // console.log(target, property, descriptor);

      if (!target._priv) {
          target._priv = { events: [] };
      }
      target._priv.events.push({ target: target, property: property, descriptor: descriptor, event: event, elementId: elementId });
  };
}

function route() {
  return function (target, property, descriptor) {};
}

export { handles, route, CONST, common as glMatrix, mat2, mat2d, mat3, mat4, quat, quat2, vec2, vec3, vec4$1 as vec4, Component, Entity, Texture, AnimationClip$1 as AnimationClip, AnimationTrack$1 as AnimationTrack, Bone$1 as Bone, Camera, Clock, Color, Emitter, FrameBufferTexture2D, GLProgram, Light, DirectionalLight, PointLight, SpotLight, NURBSCurve, Material, Mesh, Model, ParticleEmitter, Preloader, RenderPass, RenderPassPostProcess, Router, Scene, Skeleton$1 as Skeleton, Sprite, SpriteAnimation, Transform$1 as Transform, Tilemap, Timer, Tween, Vertex2, Vertex3, Video, Viewport, RenderPassShader, RenderPassShadow, MaterialBasic, MaterialDepth, MaterialPost, MaterialPhong, MaterialTilemap, MaterialWireframe, Importer, ImportMaterialsFromMTL, ImportModelFromOnyx, ImportModelFromOBJ, ImportSpriteFromTexturePacker };

//# sourceMappingURL=main.min.js.map
